import { useState, useEffect, useRef } from 'react';
// Scheda tecnica PDF sospesa (vedi nota in GestioneRicette): gli import di
// PDFDownloadLink e SchedaTecnicaDocument sono commentati perché altrimenti
// il build fallirebbe con 'variabile non utilizzata'. Da ripristinare
// insieme al pulsante di download.
// import { PDFDownloadLink } from '@react-pdf/renderer';
// import { SchedaTecnicaDocument } from './SchedaTecnicaDocument';
import { AppShell, HomeOperatore } from './ui/shell';
import LT, { catFor } from './ui/tokens';
import I, { Ico } from './ui/icons';
import { Btn, Card, Chip, CatTile } from './ui/components';
import { ModaleStampaQR } from './ui/cartello-qr';
import CentroAssistenza from './help/CentroAssistenza';
import MatriceSettimanale from './MatriceSettimanale';
import GestioneOrigineNc from './GestioneOrigineNc';

const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const API     = isLocal ? 'http://localhost:3000/api' : 'https://app.labthree.it/api';
const BACKEND = isLocal ? 'http://localhost:3000'    : 'https://app.labthree.it';
const TOKEN_KEY = 'labthree_token';

// Espone BACKEND a moduli UI che non hanno accesso a questa costante
// (es. shell.js per risolvere gli URL dei logo aziendali/fornitore).
if (typeof window !== 'undefined') window.__LT_BACKEND__ = BACKEND;

const PIANI_ORDINE = ['START', 'TRACE', 'MASTER'];
// Ritorna true se l'azienda ha almeno il piano richiesto (i master bypassano sempre)
function hasPiano(azienda, operatore, pianoMinimo) {
  if (operatore?.master) return true;
  return PIANI_ORDINE.indexOf(azienda?.piano ?? 'START') >= PIANI_ORDINE.indexOf(pianoMinimo);
}

let _onLogout = null;

let _showToast = null;
// ─── DISPONIBILITA' STIMATA DI UN LOTTO ──────────────────────────────────────
// I lotti non vengono scalati dal sistema: la quantita' registrata resta quella
// ricevuta e il consumo e' "virtuale", stimato dalle lavorazioni che agganciano
// il lotto. `consumo_altre` esclude la lavorazione che si sta guardando, ed e'
// quello che serve: il fabbisogno della lavorazione corrente viene confrontato
// con questo numero, sottrarlo qui lo conterebbe due volte.
const consumoAltreLotto = (l) => {
  if (!l) return 0;
  const v = l.consumo_altre != null ? l.consumo_altre : l.consumo_stimato;
  return parseFloat(v || 0) || 0;
};
// Mai negativa. Sotto zero vuol dire soltanto che il lotto risulta finito, e un
// numero negativo a schermo si legge come un errore del programma.
const residuoLotto = (l) => {
  if (!l || l.quantita == null) return null;
  return Math.max(0, parseFloat(l.quantita) - consumoAltreLotto(l));
};
const lottoEsaurito = (l) => {
  if (!l || l.quantita == null) return false;
  return parseFloat(l.quantita) - consumoAltreLotto(l) <= 0.0005;
};

function toast(msg, tipo = 'successo') {
  if (_showToast) _showToast(msg, tipo);
}

let _confirm = null;
function chiediConferma(messaggio, dettaglio = '') {
  return new Promise(resolve => {
    if (_confirm) _confirm(messaggio, dettaglio, resolve);
    else resolve(false);
  });
}

function apiFetch(url, options = {}) {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers }).then(res => {
    if (res.status === 401 && _onLogout) _onLogout();
    return res;
  });
}

const CATEGORIE_DOC = [
  'Piano HACCP', 'Procedure', 'Istruzioni operative', 'Moduli',
  'Schede tecniche', 'Documenti tecnici', 'Formazione', 'Certificazioni', 'Altro',
];

const _STATO_DOC_CONFIG = {
  bozza:      { label: 'Bozza',      colore: '#7a4500', bg: '#fff3e0' },
  in_vigore:  { label: 'In vigore',  colore: '#16a34a', bg: '#eaf4ea' },
  archiviato: { label: 'Archiviato', colore: '#777',    bg: '#f0f0f0' },
};

function _Header({ azienda, impostazioni }) {
  return (
    <div style={styles.appHeader} className="app-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {azienda?.logo
          ? <img src={`${BACKEND}${azienda.logo}`} alt="logo"
              style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain' }} />
          : <span style={styles.appHeaderNome}>{azienda?.ragione_sociale || 'LabThree'}</span>
        }
        {azienda && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {azienda.logo && <span style={{ fontWeight: 'bold', fontSize: 15, color: '#222' }}>{azienda.ragione_sociale}</span>}
            {azienda.citta && <span style={{ fontSize: 12, color: '#888' }}>{azienda.citta}</span>}
            {azienda.telefono && <span style={{ fontSize: 12, color: '#888' }}>📞 {azienda.telefono}</span>}
          </div>
        )}
      </div>
      <img src="/icona-app.svg" alt="LabThree" style={{ height: 30, width: 30, opacity: 0.9, flexShrink: 0 }} />
    </div>
  );
}

function Footer({ impostazioni }) {
  if (!impostazioni.fornitore_nome && !impostazioni.fornitore_logo) return null;
  return (
    <div style={styles.appFooter}>
      {impostazioni.fornitore_logo
        ? <img src={`${BACKEND}${impostazioni.fornitore_logo}`} alt="fornitore"
            style={{ maxHeight: 24, maxWidth: 60, objectFit: 'contain', opacity: 0.7 }} />
        : null
      }
      <span style={{ fontSize: 11, color: '#aaa' }}>
        {impostazioni.fornitore_nome}
        {impostazioni.fornitore_indirizzo && ` · ${impostazioni.fornitore_indirizzo}`}
        {impostazioni.fornitore_telefono && ` · ${impostazioni.fornitore_telefono}`}
        {impostazioni.fornitore_email && ` · ${impostazioni.fornitore_email}`}
      </span>
    </div>
  );
}

function Toast() {
  const [msg,      setMsg]      = useState('');
  const [tipo,     setTipo]     = useState('successo');
  const [visibile, setVisibile] = useState(false);

  useEffect(() => {
    _showToast = (m, t = 'successo') => {
      setMsg(m); setTipo(t); setVisibile(true);
      setTimeout(() => setVisibile(false), 2200);
    };
    return () => { _showToast = null; };
  }, []);

  if (!visibile) return null;

  // Mappa tipo → palette + icona. 'successo' default; 'errore' rosso; 'warn'/'attenzione' arancio.
  const conf = tipo === 'errore'
    ? { bg: LT.rosso,   icon: I.alert,       shadow: '0 10px 24px rgba(220,38,38,.30), 0 2px 6px rgba(220,38,38,.18)' }
    : (tipo === 'warn' || tipo === 'attenzione')
      ? { bg: LT.arancio, icon: I.alert,     shadow: '0 10px 24px rgba(245,158,11,.30), 0 2px 6px rgba(245,158,11,.18)' }
      : { bg: LT.verde,  icon: I.checkCircle, shadow: '0 10px 24px rgba(34,197,94,.30), 0 2px 6px rgba(34,197,94,.18)' };

  // Rimuovi eventuali emoji/caratteri di successo o errore iniziali (retro-compat con i vecchi toast('✓ …')).
  const testo = String(msg).replace(/^[\s]*[✓✔✕✗⚠❌🚫⛔]+[\s]*/u, '');

  return (
    <div style={{
      position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
      background: conf.bg, color: '#fff',
      padding: '10px 18px 10px 14px', borderRadius: LT.r.full,
      boxShadow: conf.shadow,
      fontSize: 14, fontWeight: 600, fontFamily: LT.sans,
      letterSpacing: '.005em',
      display: 'inline-flex', alignItems: 'center', gap: 10,
      maxWidth: 'calc(100vw - 32px)',
      zIndex: 9999, pointerEvents: 'none',
      animation: 'lt_toast_in .22s ease-out',
    }}>
      <Ico d={conf.icon} size={18} />
      <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{testo}</span>
    </div>
  );
}

function ModalConferma() {
  const [stato, setStato] = useState(null);

  useEffect(() => {
    _confirm = (messaggio, dettaglio, resolve) => setStato({ messaggio, dettaglio, resolve });
    return () => { _confirm = null; };
  }, []);

  if (!stato) return null;

  const risposta = ok => { stato.resolve(ok); setStato(null); };

  return (
    <div style={lt_overlay} onClick={() => risposta(false)}>
      <div style={{ ...lt_modal, maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.md,
            background: LT.rossoSoft, color: LT.rosso,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.alert} size={24} />
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>
              {stato.messaggio}
            </div>
            {/* whiteSpace pre-line: i messaggi che elencano le conseguenze dei
                due pulsanti vanno a capo. Quelli su una riga sola, cioè tutti
                gli altri, restano identici a prima. */}
            {stato.dettaglio && (
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 6, lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                {stato.dettaglio}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <Btn kind="ghost" block onClick={() => risposta(false)}>Annulla</Btn>
          <Btn kind="primary" block icon="check" onClick={() => risposta(true)}
            style={{ background: LT.rosso, boxShadow: '0 6px 18px rgba(220,38,38,.22)' }}>
            Conferma
          </Btn>
        </div>
      </div>
    </div>
  );
}

function PaginaRegistrazione() {
  const [step,              setStep]              = useState(1); // 1 | 2 | 'done'
  const [ragioneSociale,    setRagioneSociale]    = useState('');
  const [emailAz,           setEmailAz]           = useState('');
  const [indirizzo,         setIndirizzo]         = useState('');
  const [citta,             setCitta]             = useState('');
  const [telefono,          setTelefono]          = useState('');
  const [piano,             setPiano]             = useState('START');
  const [codice,            setCodice]            = useState('');
  const [codiceDisponibile, setCodiceDisponibile] = useState(null); // null | true | false
  const [verificandoCodice, setVerificandoCodice] = useState(false);
  const [nome,              setNome]              = useState('');
  const [cognome,           setCognome]           = useState('');
  const [pin,               setPin]               = useState('');
  const [confermaPin,       setConfermaPin]       = useState('');
  const [errore,            setErrore]            = useState('');
  const [loading,           setLoading]           = useState(false);
  const [risultato,         setRisultato]         = useState(null);

  const debounceRef = useRef(null);

  const generaCodice = (rs) =>
    rs.toUpperCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9\s]/g, '')
      .trim()
      .split(/\s+/)
      .slice(0, 3)
      .join('-')
      .substring(0, 20);

  // Genera codice automatico dalla ragione sociale
  useEffect(() => {
    if (ragioneSociale.trim()) setCodice(generaCodice(ragioneSociale));
  }, [ragioneSociale]);

  // Verifica disponibilità codice con debounce 600ms
  useEffect(() => {
    setCodiceDisponibile(null);
    if (!codice) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setVerificandoCodice(true);
    debounceRef.current = setTimeout(() => {
      fetch(`${API}/registrazione/verifica-codice?codice=${encodeURIComponent(codice)}`)
        .then(r => r.json())
        .then(d => { setCodiceDisponibile(d.disponibile); setVerificandoCodice(false); })
        .catch(() => setVerificandoCodice(false));
    }, 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [codice]);

  const validaPin = (p) => {
    if (p.length < 5) return 'Il PIN deve avere almeno 5 caratteri';
    if (!/[a-zA-Z]/.test(p)) return 'Il PIN deve contenere almeno una lettera';
    if (!/[0-9]/.test(p)) return 'Il PIN deve contenere almeno un numero';
    return '';
  };

  const handleStep1 = () => {
    setErrore('');
    if (!ragioneSociale.trim()) return setErrore('La ragione sociale è obbligatoria');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailAz.trim() || !emailRegex.test(emailAz)) return setErrore("Inserisci un'email valida");
    if (!codice) return setErrore('Il codice URL è obbligatorio');
    if (verificandoCodice) return setErrore('Attendi la verifica del codice…');
    if (codiceDisponibile === null) return setErrore('Attendi la verifica del codice…');
    if (codiceDisponibile === false) return setErrore('Il codice non è disponibile. Modificalo.');
    setStep(2);
  };

  const handleSubmit = async () => {
    setErrore('');
    if (!nome.trim() || !cognome.trim()) return setErrore('Nome e cognome sono obbligatori');
    const errPin = validaPin(pin);
    if (errPin) return setErrore(errPin);
    if (pin !== confermaPin) return setErrore('I PIN non corrispondono');
    setLoading(true);
    try {
      const res = await fetch(`${API}/registrazione`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ragione_sociale: ragioneSociale.trim(),
          email:           emailAz.trim(),
          indirizzo:       indirizzo.trim() || undefined,
          citta:           citta.trim()     || undefined,
          telefono:        telefono.trim()  || undefined,
          piano,
          codice,
          nome:    nome.trim(),
          cognome: cognome.trim(),
          pin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrore(data.errore || 'Errore durante la registrazione');
      } else {
        setRisultato(data);
        setStep('done');
      }
    } catch {
      setErrore('Errore di connessione. Riprova.');
    }
    setLoading(false);
  };

  const pagina = {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #f0f4ff 0%, #e8eeff 50%, #eef2ff 100%)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 16px 32px',
    boxSizing: 'border-box',
    fontFamily: "'Outfit', system-ui, sans-serif",
  };

  const card = {
    width: '100%', maxWidth: 480,
    background: '#fff', borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
  };

  const inp = {
    width: '100%', padding: '11px 14px',
    fontSize: 14, borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    boxSizing: 'border-box',
    outline: 'none', background: '#fafafa',
    fontFamily: 'inherit',
  };

  const lbl = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };

  const btnPrimary = {
    width: '100%', padding: '13px',
    background: '#2563EB', color: '#fff',
    border: 'none', borderRadius: 10,
    fontSize: 15, fontWeight: 700,
    cursor: 'pointer',
  };

  const scadenza = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });

  // ── Schermata conferma ────────────────────────────────────────────────────────
  if (step === 'done' && risultato) {
    const appUrl = `https://app.labthree.it/${risultato.codice}`;
    return (
      <div style={pagina}>
        <div style={card}>
          <div style={{ height: 5, background: 'linear-gradient(90deg,#2563EB,#22C55E)' }} />
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🎉</div>
            <h2 style={{ margin: '0 0 8px', color: '#0F172A', fontSize: 22 }}>Registrazione completata!</h2>
            <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 28px' }}>
              Benvenuto in LabThree, <strong>{ragioneSociale}</strong>
            </p>
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 20, marginBottom: 20, textAlign: 'left' }}>
              <div style={{ marginBottom: 14 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#64748B', textTransform: 'uppercase' }}>Il tuo URL dedicato</span>
                <br />
                <a href={appUrl} style={{ color: '#2563EB', fontWeight: 600, fontSize: 15, wordBreak: 'break-all' }}>{appUrl}</a>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: 1 }}>PIANO</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', marginTop: 2 }}>{piano}</div>
                </div>
                <div style={{ flex: 1, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: 1 }}>PROVA GRATUITA FINO AL</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>{scadenza}</div>
                </div>
              </div>
            </div>
            <a href={appUrl}
               style={{ display: 'block', padding: '13px', background: '#2563EB', color: '#fff',
                        borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}>
              Accedi ora →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────────
  return (
    <div style={pagina}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/logo-orizzontale.svg" alt="LabThree" style={{ display: 'block', margin: '0 auto 8px', width: '100%', maxWidth: 480, height: 'auto' }} />
        <div style={{ fontSize: 13, color: '#64748B' }}>Crea il tuo account — prova gratuita 30 giorni</div>
      </div>

      <div style={card}>
        <div style={{ height: 5, background: 'linear-gradient(90deg,#2563EB,#22C55E)' }} />

        {/* Step indicator */}
        <div style={{ padding: '18px 32px 0', display: 'flex', alignItems: 'center' }}>
          {[
            { n: 1, label: 'Dati azienda' },
            { n: 2, label: 'Amministratore' },
          ].map(({ n, label }, i) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', flex: i === 0 ? '0 0 auto' : 1 }}>
              {i > 0 && <div style={{ flex: 1, height: 1, background: step > 1 ? '#2563EB' : '#e2e8f0', margin: '0 8px' }} />}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                              background: step >= n ? '#2563EB' : '#e2e8f0',
                              color: step >= n ? '#fff' : '#94a3b8',
                              fontSize: 12, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {n}
                </div>
                <span style={{ fontSize: 12, color: step >= n ? '#2563EB' : '#94a3b8', fontWeight: step === n ? 700 : 400, whiteSpace: 'nowrap' }}>
                  {label}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '20px 32px 32px' }}>
          {errore && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                          padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#b00020', fontWeight: 500 }}>
              {errore}
            </div>
          )}

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Ragione sociale *</label>
                <input style={inp} value={ragioneSociale}
                       onChange={e => setRagioneSociale(e.target.value)}
                       placeholder="Es. Caseificio Rossi Srl" />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Email azienda *</label>
                <input style={inp} type="email" value={emailAz}
                       onChange={e => setEmailAz(e.target.value)}
                       placeholder="info@azienda.it" />
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 2 }}>
                  <label style={lbl}>Indirizzo</label>
                  <input style={inp} value={indirizzo}
                         onChange={e => setIndirizzo(e.target.value)}
                         placeholder="Via Roma 1" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Città</label>
                  <input style={inp} value={citta}
                         onChange={e => setCitta(e.target.value)}
                         placeholder="Milano" />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Telefono</label>
                  <input style={inp} value={telefono}
                         onChange={e => setTelefono(e.target.value)}
                         placeholder="02 1234567" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Piano</label>
                  <select style={{ ...inp, cursor: 'pointer' }} value={piano}
                          onChange={e => setPiano(e.target.value)}>
                    <option value="START">START</option>
                    <option value="TRACE">TRACE</option>
                    <option value="MASTER" disabled style={{ color: '#94a3b8' }}>MASTER — coming soon</option>
                  </select>
                  {piano === 'MASTER' && (
                    <div style={{ marginTop: 5, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
                      Il piano MASTER non è ancora disponibile. Seleziona START o TRACE.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Codice URL *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ ...inp, paddingRight: 38, fontFamily: 'monospace', letterSpacing: 1 }}
                    value={codice}
                    onChange={e => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').substring(0, 20);
                      setCodice(val);
                    }}
                    placeholder="NOME-AZIENDA"
                    maxLength={20}
                  />
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none' }}>
                    {verificandoCodice
                      ? <span style={{ fontSize: 11, color: '#94a3b8' }}>…</span>
                      : codiceDisponibile === true  ? '✅'
                      : codiceDisponibile === false ? '❌'
                      : null}
                  </span>
                </div>
                {codice && (
                  <div style={{ marginTop: 5, fontSize: 12,
                                color: codiceDisponibile === false ? '#b00020'
                                     : codiceDisponibile === true  ? '#16a34a'
                                     : '#94a3b8' }}>
                    {codiceDisponibile === false
                      ? 'Codice non disponibile — scegline un altro'
                      : <>app.labthree.it/<strong>{codice}</strong></>}
                  </div>
                )}
              </div>

              <button style={btnPrimary} onClick={handleStep1}>
                Continua →
              </button>
              <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#64748B' }}>
                Hai già un account?{' '}
                <a href="/" style={{ color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>Accedi</a>
              </p>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Nome *</label>
                <input style={inp} value={nome}
                       onChange={e => setNome(e.target.value)}
                       placeholder="Mario" />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Cognome *</label>
                <input style={inp} value={cognome}
                       onChange={e => setCognome(e.target.value)}
                       placeholder="Rossi" />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>PIN *</label>
                <input style={inp} type="password" value={pin}
                       onChange={e => setPin(e.target.value)}
                       placeholder="Almeno 5 caratteri, lettera + numero" />
                {pin && validaPin(pin) && (
                  <div style={{ fontSize: 12, color: '#b00020', marginTop: 4 }}>{validaPin(pin)}</div>
                )}
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Conferma PIN *</label>
                <input style={inp} type="password" value={confermaPin}
                       onChange={e => setConfermaPin(e.target.value)}
                       placeholder="Ripeti il PIN" />
                {confermaPin && pin !== confermaPin && (
                  <div style={{ fontSize: 12, color: '#b00020', marginTop: 4 }}>I PIN non corrispondono</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  style={{ padding: '13px 18px', background: '#64748B', color: '#fff',
                           border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => { setErrore(''); setStep(1); }}>
                  ← Indietro
                </button>
                <button
                  style={{ ...btnPrimary, flex: 1, opacity: loading ? 0.6 : 1 }}
                  onClick={handleSubmit}
                  disabled={loading}>
                  {loading ? 'Registrazione…' : 'Completa registrazione'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [pin,          setPin]          = useState('');
  const [errore,       setErrore]       = useState('');
  const [loading,      setLoading]      = useState(false);
  const [azienda,      setAzienda]      = useState(null);
  const [fornitore,    setFornitore]    = useState(null);
  const [statoAzienda, setStatoAzienda] = useState('loading'); // 'loading' | 'ok' | 'notfound'

  const codiceAzienda = window.location.pathname.replace(/^\/+/, '').toUpperCase() || '';

  // Inietta CSS "globali" solo per ciò che inline-style non può gestire:
  // keyframes di entrata, placeholder, focus-ring del campo PIN.
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'lt-login-styles';
    style.textContent = `
      @keyframes lt-fadein { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }
      @keyframes lt-pulse  { 0%,100% { opacity: 1; } 50% { opacity: .45; } }
      .lt-pin { caret-color: ${LT.blu}; font-family: ${LT.mono}; }
      .lt-pin::placeholder { color: ${LT.n300}; letter-spacing: .35em; }
      .lt-pin:focus { outline: none; border-color: ${LT.blu} !important; box-shadow: 0 0 0 4px rgba(37,99,235,.14) !important; background: #fff !important; }
      .lt-fade  { animation: lt-fadein .45s cubic-bezier(.22,1,.36,1) both; }
      .lt-fade-2{ animation: lt-fadein .45s cubic-bezier(.22,1,.36,1) .06s both; }
      .lt-fade-3{ animation: lt-fadein .45s cubic-bezier(.22,1,.36,1) .14s both; }
      .lt-pulse { animation: lt-pulse 1.4s ease-in-out infinite; }
    `;
    document.head.appendChild(style);
    return () => { const s = document.getElementById('lt-login-styles'); if (s) document.head.removeChild(s); };
  }, []);

  useEffect(() => {
    if (!codiceAzienda) { setStatoAzienda('notfound'); return; }
    Promise.all([
      fetch(`${API}/organico/azienda-info/${codiceAzienda}`),
      fetch(`${API}/uploads/impostazioni-pubbliche`),
    ]).then(async ([rAz, rForn]) => {
      if (!rAz.ok) { setStatoAzienda('notfound'); return; }
      setAzienda(await rAz.json());
      if (rForn.ok) setFornitore(await rForn.json());
      setStatoAzienda('ok');
    }).catch(() => setStatoAzienda('notfound'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    if (!pin) return;
    setLoading(true);
    setErrore('');
    try {
      const res = await fetch(`${API}/organico/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ pin, codiceAzienda })
      });
      const data = await res.json();
      if (!res.ok) {
        setErrore(data.errore || 'PIN non valido');
      } else {
        sessionStorage.setItem(TOKEN_KEY, data.token);
        const { token: _, ...datiOperatore } = data;
        onLogin(datiOperatore);
      }
    } catch {
      setErrore('Errore di connessione');
    }
    setLoading(false);
  };

  // ── Stili pagina (D2 Evoluto) ────────────────────────────────────────────────
  const pagina = {
    minHeight: '100vh',
    background:
      'radial-gradient(1200px 600px at 20% -10%, rgba(37,99,235,.10), transparent 60%),' +
      'radial-gradient(900px 500px at 110% 20%, rgba(34,197,94,.08), transparent 55%),' +
      LT.nBg,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'space-between',
    padding: '40px 16px 28px',
    boxSizing: 'border-box',
    fontFamily: LT.sans,
    color: LT.n800,
  };

  // ── Schermata errore (codice azienda non valido) ─────────────────────────────
  if (statoAzienda === 'notfound') return (
    <div style={pagina}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <div
          className="lt-fade"
          style={{
            width: '100%', maxWidth: 420, background: '#fff',
            borderRadius: LT.r.xl, overflow: 'hidden',
            boxShadow: LT.sh3, border: `1px solid ${LT.n150}`,
            textAlign: 'center',
          }}
        >
          <div style={{ height: 4, background: `linear-gradient(90deg, ${LT.rosso} 0%, #F87171 100%)` }} />
          <div style={{ padding: '44px 32px' }}>
            <div
              style={{
                width: 64, height: 64, borderRadius: 999,
                background: LT.rossoSoft, color: LT.rosso,
                margin: '0 auto 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Ico d={I.lock} size={28} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 20, color: LT.n900, marginBottom: 8, letterSpacing: '-.01em' }}>
              Accesso non autorizzato
            </div>
            <div style={{ fontSize: 14, color: LT.n500, lineHeight: 1.7 }}>
              Il codice azienda nell'URL non è valido.<br />
              Contatta il tuo amministratore.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Schermata caricamento ────────────────────────────────────────────────────
  if (statoAzienda === 'loading') return (
    <div style={{ ...pagina, alignItems: 'center', justifyContent: 'center' }}>
      <div className="lt-pulse" style={{ display: 'flex', alignItems: 'center', gap: 10, color: LT.n500, fontSize: 14 }}>
        <div
          style={{
            width: 18, height: 18, borderRadius: 999,
            background: LT.grad, boxShadow: LT.shBlu,
          }}
        />
        Caricamento…
      </div>
    </div>
  );

  // ── Pagina login ─────────────────────────────────────────────────────────────
  const anno = new Date().getFullYear();
  const nomeForn = fornitore?.fornitore_nome || '';

  return (
    <div style={pagina}>

      {/* ── Header azienda (personalizzazione multi-tenant) ─────────────────── */}
      <div className="lt-fade" style={{ textAlign: 'center', marginBottom: 28, width: '100%', maxWidth: 480 }}>
        {azienda?.logo && (
          <img
            src={`${BACKEND}${azienda.logo}`}
            alt={azienda.ragione_sociale}
            style={{ maxHeight: 120, maxWidth: 220, objectFit: 'contain', display: 'block', margin: '0 auto 14px' }}
          />
        )}
        <div style={{ fontSize: 22, fontWeight: 700, color: LT.n900, letterSpacing: '-.02em' }}>
          {azienda?.ragione_sociale}
        </div>
        {azienda?.citta && (
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginTop: 6, fontSize: 12, color: LT.n500, fontWeight: 500,
            }}
          >
            <Ico d={I.pin} size={13} />
            {azienda.citta}
          </div>
        )}
        <div
          style={{
            marginTop: 12, fontSize: 14, color: LT.n600, lineHeight: 1.55,
            maxWidth: 420, margin: '12px auto 0',
          }}
        >
          Benvenuto in <span style={{ fontWeight: 600, color: LT.n800 }}>LabThree</span>, la piattaforma
          per la gestione delle registrazioni HACCP.
        </div>
      </div>

      {/* ── Card login ──────────────────────────────────────────────────────── */}
      <div
        className="lt-fade-2"
        style={{
          width: '100%', maxWidth: 420,
          background: '#fff',
          borderRadius: LT.r.xl,
          overflow: 'hidden',
          boxShadow: '0 18px 50px rgba(15,23,42,.10), 0 4px 14px rgba(37,99,235,.08)',
          border: `1px solid ${LT.n150}`,
          flex: '0 0 auto',
        }}
      >
        {/* Filo brand (gradient) */}
        <div style={{ height: 4, background: LT.grad }} />

        <div style={{ padding: '32px 32px 30px' }}>

          {/* Logo prodotto (LabThree orizzontale) */}
          <div style={{ textAlign: 'center', marginBottom: 26 }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/brand/logo-orizzontale.svg`}
              alt="LabThree"
              style={{ maxWidth: 260, width: '80%', height: 'auto', display: 'block', margin: '0 auto' }}
            />
          </div>

          {/* Label PIN */}
          <label
            htmlFor="lt-pin-input"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 700, color: LT.n500,
              textTransform: 'uppercase', letterSpacing: '.08em',
              marginBottom: 10,
            }}
          >
            <Ico d={I.lock} size={13} />
            Inserisci il tuo PIN
          </label>

          {/* Campo PIN */}
          <input
            id="lt-pin-input"
            className="lt-pin"
            type="password"
            inputMode="text"
            autoComplete="current-password"
            placeholder="••••••"
            value={pin}
            onChange={e => { setPin(e.target.value); setErrore(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '15px 16px',
              fontSize: 26, textAlign: 'center', letterSpacing: '10px',
              border: `1.5px solid ${errore ? LT.rosso : LT.n200}`,
              borderRadius: LT.r.md,
              color: LT.n900,
              transition: 'border-color .18s, box-shadow .18s, background .18s',
              background: LT.n50,
            }}
          />

          {/* Errore PIN */}
          {errore && (
            <div
              style={{
                marginTop: 12, padding: '10px 12px',
                background: LT.rossoSoft,
                border: `1px solid ${LT.rosso}33`,
                borderRadius: LT.r.md,
                fontSize: 13, color: LT.rosso, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <Ico d={I.alert} size={15} />
              <span>{errore}</span>
            </div>
          )}

          {/* Bottone accedi */}
          <Btn
            kind="primary"
            size="lg"
            block
            iconRight="arrowRight"
            onClick={handleLogin}
            disabled={loading || !pin}
            style={{ marginTop: 18, padding: '14px 20px', fontSize: 15, letterSpacing: '.01em' }}
          >
            {loading ? 'Accesso in corso…' : 'Accedi'}
          </Btn>
        </div>
      </div>

      {/* ── Footer fornitore ─────────────────────────────────────────────────── */}
      <div className="lt-fade-3" style={{ marginTop: 28, textAlign: 'center', width: '100%', maxWidth: 480 }}>

        {fornitore?.fornitore_logo && (
          <img
            src={`${BACKEND}${fornitore.fornitore_logo}`}
            alt={nomeForn}
            style={{
              maxHeight: 36, maxWidth: 220, objectFit: 'contain',
              display: 'block', margin: '0 auto 8px', opacity: 0.8,
            }}
          />
        )}

        {nomeForn && (
          <div style={{ fontSize: 12, fontWeight: 600, color: LT.n600, marginBottom: 6 }}>
            {nomeForn}
          </div>
        )}

        {/* Contatti */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, fontSize: 12, color: LT.n500, flexWrap: 'wrap' }}>
          {fornitore?.fornitore_email && (
            <a
              href={`mailto:${fornitore.fornitore_email}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: LT.blu, textDecoration: 'none', fontWeight: 500,
              }}
            >
              <Ico d={I.mail} size={13} />
              {fornitore.fornitore_email}
            </a>
          )}
          {fornitore?.fornitore_telefono && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.phone} size={13} />
              {fornitore.fornitore_telefono}
            </span>
          )}
        </div>

        {/* Divisore */}
        <div style={{ margin: '14px auto 10px', width: 40, height: 2, borderRadius: 2, background: LT.n200 }} />

        {/* Copyright + versione */}
        <div style={{ fontSize: 11, color: LT.n400, lineHeight: 1.75 }}>
          <div>© {anno}{nomeForn ? ` ${nomeForn}` : ''} — Tutti i diritti riservati</div>
          <div style={{ fontWeight: 600, letterSpacing: '.04em', color: LT.n500 }}>LabThree v1.0</div>
          <div>Software protetto da copyright. L'uso non autorizzato è vietato.</div>
        </div>
      </div>

    </div>
  );
}

// ─── Stili condivisi D2 per modali (overlay + card) ─────────────────────────
const lt_overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
  backdropFilter: 'blur(2px)',
};
const lt_modal = {
  background: '#fff', borderRadius: LT.r.xl, padding: 22,
  width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: LT.sh3, border: `1px solid ${LT.n150}`, fontFamily: LT.sans,
};

function FormValore({ attivita, intento, onConferma, onAnnulla, sessioneData = null, sessioneOra = null }) {
  const [valore,      setValore]      = useState('');
  const [errore,      setErrore]      = useState('');
  const [dataCustom,  setDataCustom]  = useState(sessioneData || '');
  const [oraCustom,   setOraCustom]   = useState(sessioneOra || '');

  const handleConferma = () => {
    setErrore('');
    const v = parseFloat(valore.replace(',', '.'));
    if (isNaN(v)) {
      setErrore('Inserisci un valore numerico valido');
      return;
    }
    const min = attivita.valore_min;
    const max = attivita.valore_max;
    const neiLimiti = (min === null || v >= min) && (max === null || v <= max);
    if (intento === 'conforme' && !neiLimiti) {
      setErrore(`Valore fuori dai limiti (min: ${min ?? '—'}, max: ${max ?? '—'}) — non puoi registrare come CONFORME`);
      return;
    }
    if (intento === 'nonconforme' && neiLimiti) {
      setErrore(`Valore nei limiti (min: ${min ?? '—'}, max: ${max ?? '—'}) — non puoi registrare come NON CONFORME`);
      return;
    }
    onConferma(v, neiLimiti, dataCustom || null, oraCustom || null);
  };

  // ── D2 Evoluto: stili modal coerenti con AppShell ────────────────────────
  const isConforme = intento === 'conforme';
  const coloreIntento = isConforme ? LT.verde : LT.rosso;
  const bgIntento     = isConforme ? LT.verdeSoft : LT.rossoSoft;
  const labelStyle    = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 10, marginBottom: 6 };
  const inputStyle    = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={lt_overlay}>
      <div style={lt_modal}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: bgIntento, color: coloreIntento,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={isConforme ? I.check : I.x} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>
              Registra valore
            </div>
            <div style={{ fontSize: 12, color: coloreIntento, fontWeight: 600, marginTop: 2 }}>
              {isConforme ? 'Esito conforme' : 'Esito non conforme'}
            </div>
          </div>
        </div>

        <div style={{ background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.md, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n900 }}>
            {attivita.oggetto}{attivita.collocazione ? ` — ${attivita.collocazione}` : ''}
          </div>
          <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
            {attivita.ambito}{attivita.tipo_attivita ? ` · ${attivita.tipo_attivita}` : ''}
          </div>
        </div>

        {(attivita.valore_min !== null || attivita.valore_max !== null) && (
          <div style={{
            background: LT.bluSoft, color: LT.blu, borderRadius: LT.r.md,
            padding: '8px 12px', marginBottom: 8, fontSize: 13, fontWeight: 600,
            display: 'flex', gap: 14,
          }}>
            {attivita.valore_min !== null && <span>Min: <strong>{attivita.valore_min}</strong></span>}
            {attivita.valore_max !== null && <span>Max: <strong>{attivita.valore_max}</strong></span>}
          </div>
        )}

        <label style={labelStyle}>Valore misurato</label>
        {/* type="text", non "number": con type="number" il browser scarta la
            virgola e "2,4" diventa "24" senza dire niente. inputMode="decimal"
            tiene il tastierino numerico sul telefono. La conversione la fa
            handleConferma con replace(',', '.'). */}
        <input
          style={{ ...inputStyle, textAlign: 'center', fontSize: 22, fontFamily: LT.mono, fontWeight: 700, padding: '14px 12px' }}
          type="text" inputMode="decimal" value={valore}
          onChange={e => setValore(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleConferma()}
          placeholder="es. 2,4" autoFocus
        />

        {sessioneData ? (
          <div style={{
            background: LT.bluSoft, color: LT.blu, borderRadius: LT.r.md,
            padding: '8px 12px', marginTop: 10, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Ico d={I.clock} size={15} />
            <span>Sessione: <strong>{sessioneData.split('-').reverse().join('/')}</strong> alle <strong>{sessioneOra}</strong></span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Data (opzionale)</label>
              <input style={inputStyle} type="date" value={dataCustom}
                onChange={e => setDataCustom(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Ora (opzionale)</label>
              <input style={inputStyle} type="time" value={oraCustom}
                onChange={e => setOraCustom(e.target.value)} />
            </div>
          </div>
        )}

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '8px 12px', marginTop: 10, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Ico d={I.alert} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{errore}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="outline" block onClick={onAnnulla}>Annulla</Btn>
          <Btn
            kind={isConforme ? 'success' : 'primary'}
            block
            onClick={handleConferma}
            icon={isConforme ? 'check' : 'alert'}
            style={!isConforme ? { background: LT.rosso, color: '#fff', boxShadow: '0 4px 12px rgba(220,38,38,.25)' } : undefined}
          >
            Conferma
          </Btn>
        </div>
      </div>
    </div>
  );
}

function FormNC({ attivita, operatore, onConferma, onAnnulla, eventoPrecompilato = '', valore = null, dataCustom: dataInit = '', oraCustom: oraInit = '' }) {
  const [dataCustom, setDataCustom] = useState(dataInit);
  const [oraCustom,  setOraCustom]  = useState(oraInit);
  const [evento,           setEvento]           = useState(eventoPrecompilato);
  const [causa,            setCausa]            = useState('');
  const [trattamento,      setTrattamento]      = useState('');
  const [azioneCorrettiva, setAzioneCorrettiva] = useState('');
  const [note,             setNote]             = useState('');
  const [idCategoria,      setIdCategoria]      = useState('');
  const [categorie,        setCategorie]        = useState([]);
  const [idOrigine,        setIdOrigine]        = useState('');
  const [origini,          setOrigini]          = useState([]);
  const [salvando,         setSalvando]         = useState(false);
  const [errore,           setErrore]           = useState('');

  useEffect(() => {
    apiFetch(`${API}/admin/categorie-nc?soloAttive=1`)
      .then(r => r.ok ? r.json() : [])
      .then(setCategorie)
      .catch(() => setCategorie([]));
    apiFetch(`${API}/admin/origini-nc?soloAttive=1`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        setOrigini(list);
        // Questa finestra si apre solo dai monitoraggi, quindi l'origine e'
        // nota: e' l'autocontrollo interno. Preselezionarla evita che il campo
        // resti vuoto — nessuno lo compilava — e che le statistiche per origine
        // restino cieche. Resta modificabile. Se la voce non c'e', niente.
        const auto = (list || []).find(o =>
          (o.descrizione || '').toLowerCase().startsWith('autocontrollo'));
        if (auto) setIdOrigine(prev => prev || String(auto.id));
      })
      .catch(() => setOrigini([]));
  }, []);

  const catSelezionata  = categorie.find(c => c.id === parseInt(idCategoria));
  const origSelezionata = origini.find(o => o.id === parseInt(idOrigine));

  const handleSalva = async () => {
    setSalvando(true);
    setErrore('');
    try {
      const res = await apiFetch(`${API}/attivita/nc`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idScadenziario:  attivita.id_scadenziario,
          idOperatore:     operatore.id,
          esitoValore:     valore,
          evento, causa, trattamento, azioneCorrettiva, note,
          idCategoria:     idCategoria ? parseInt(idCategoria) : null,
          idOrigine:       idOrigine ? parseInt(idOrigine) : null,
          dataCustom:  dataCustom || null,
          oraCustom:   oraCustom  || null
        })
      });
      if (!res.ok) throw new Error();
      toast('✓ Non conformità registrata!');
      const data = await res.json();
      onConferma(data.idAttivita);
    } catch {
      setErrore('Errore nella registrazione NC');
    }
    setSalvando(false);
  };

  // ── D2 Evoluto: stili field coerenti ─────────────────────────────────────
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 12, marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };
  const taStyle    = { ...inputStyle, resize: 'vertical', minHeight: 56, fontFamily: LT.sans };

  return (
    <div style={lt_overlay}>
      <div style={lt_modal}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.rossoSoft, color: LT.rosso,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.alert} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>Non conformità</div>
            <div style={{ fontSize: 12, color: LT.rosso, fontWeight: 600, marginTop: 2 }}>
              Registra evento e azioni correttive
            </div>
          </div>
        </div>

        <div style={{ background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.md, padding: '10px 12px', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n900 }}>
            {attivita.oggetto}{attivita.collocazione ? ` — ${attivita.collocazione}` : ''}
          </div>
          <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
            {attivita.ambito}{attivita.tipo_attivita ? ` · ${attivita.tipo_attivita}` : ''}
          </div>
        </div>

        <label style={labelStyle}>Descrizione Non Conformità / Reclamo</label>
        <textarea style={taStyle} value={evento}
          onChange={e => setEvento(e.target.value)}
          placeholder="Cosa è successo?" rows={2} />

        <label style={labelStyle}>Categoria <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
        <select style={inputStyle} value={idCategoria}
          onChange={e => setIdCategoria(e.target.value)}>
          <option value=''>— Non categorizzata —</option>
          {categorie.map(c => (
            <option key={c.id} value={c.id}>{c.descrizione}</option>
          ))}
        </select>
        {catSelezionata?.descrizione_estesa && (
          <div style={{
            marginTop: 6, fontSize: 11.5, color: LT.n500, lineHeight: 1.4,
            background: LT.n50, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.sm, padding: '8px 10px',
          }}>
            {catSelezionata.descrizione_estesa}
          </div>
        )}

        <label style={labelStyle}>Origine <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
        <select style={inputStyle} value={idOrigine}
          onChange={e => setIdOrigine(e.target.value)}>
          <option value=''>— Non specificata —</option>
          {origini.map(o => (
            <option key={o.id} value={o.id}>{o.descrizione}</option>
          ))}
        </select>
        {origSelezionata?.descrizione_estesa && (
          <div style={{
            marginTop: 6, fontSize: 11.5, color: LT.n500, lineHeight: 1.4,
            background: LT.n50, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.sm, padding: '8px 10px',
          }}>
            {origSelezionata.descrizione_estesa}
          </div>
        )}

        <label style={labelStyle}>Causa</label>
        <textarea style={taStyle} value={causa}
          onChange={e => setCausa(e.target.value)}
          placeholder="Causa del problema" rows={2} />

        <label style={labelStyle}>Trattamento immediato <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
        <textarea style={taStyle} value={trattamento}
          onChange={e => setTrattamento(e.target.value)}
          placeholder="Intervento immediato di contenimento" rows={2} />

        <label style={labelStyle}>Azione correttiva</label>
        <textarea style={taStyle} value={azioneCorrettiva}
          onChange={e => setAzioneCorrettiva(e.target.value)}
          placeholder="Cosa hai fatto per risolvere il problema alla radice?" rows={2} />

        <label style={labelStyle}>Note aggiuntive <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
        <textarea style={taStyle} value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Altre informazioni utili sul monitoraggio" rows={2} />

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Data (opzionale)</label>
            <input style={inputStyle} type="date" value={dataCustom}
              onChange={e => setDataCustom(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Ora (opzionale)</label>
            <input style={inputStyle} type="time" value={oraCustom}
              onChange={e => setOraCustom(e.target.value)} />
          </div>
        </div>

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '8px 12px', marginTop: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Ico d={I.alert} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{errore}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="outline" block onClick={onAnnulla} disabled={salvando}>Annulla</Btn>
          <Btn
            kind="primary" block
            icon={salvando ? undefined : 'alert'}
            onClick={handleSalva}
            disabled={salvando}
            style={{ background: LT.rosso, color: '#fff', boxShadow: '0 4px 12px rgba(220,38,38,.25)' }}
          >
            {salvando ? 'Salvataggio…' : 'Conferma NC'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Attivita({ operatore, azienda }) {
  const [attivita,           setAttivita]           = useState([]);
  const [,                   setCaricamento]         = useState(true);
  const [eseguendo,          setEseguendo]           = useState(null);
  const [ncAperta,           setNcAperta]            = useState(null);
  const [valoreAperto,       setValoreAperto]        = useState(null);
  const [sessione,           setSessione]            = useState(null); // null | { data, ora }
  const [mostraFormSessione, setMostraFormSessione]  = useState(false);
  const [nuovaData,          setNuovaData]           = useState('');
  const [nuovaOra,           setNuovaOra]            = useState('');
  const [erroreCaricamento,  setErroreCaricamento]   = useState('');
  const [erroreSessione,     setErroreSessione]      = useState('');
  const [allegatiProposta,   setAllegatiProposta]    = useState(null); // { idAttivita, registroSpeciale }
  const [filtroAmbito,       setFiltroAmbito]        = useState('tutti');
  const [mostraTutto,        setMostraTutto]         = useState(false);
  const [vistaModo,          setVistaModo]           = useState(() => {
    try { return localStorage.getItem('labthree_vista_attivita_modo') || 'classica'; }
    catch { return 'classica'; }
  });

  useEffect(() => {
    if (vistaModo === 'classica') caricaAttivita(mostraTutto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostraTutto, vistaModo]);

  useEffect(() => {
    try { localStorage.setItem('labthree_vista_attivita_modo', vistaModo); } catch {}
  }, [vistaModo]);

  const caricaAttivita = async (tutto = false) => {
    setCaricamento(true);
    setErroreCaricamento('');
    try {
      const qs  = tutto ? '?tutto=1' : '';
      const res  = await apiFetch(`${API}/attivita/scadenza/${operatore.id}${qs}`);
      const data = await res.json();
      setAttivita(data);
    } catch {
      setErroreCaricamento('Errore nel caricamento attività');
    }
    setCaricamento(false);
  };

  const registraEsito = async (att, esitoConforme, esitoValore = null, dataCustom = null, oraCustom = null) => {
    const idScadenziario = att.id_scadenziario;
    setEseguendo(idScadenziario);
    try {
      const res = await apiFetch(`${API}/attivita/esegui`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          idScadenziario,
          idOperatore:  operatore.id,
          esitoConforme,
          esitoValore,
          note: null,
          dataCustom,
          oraCustom
        })
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      await caricaAttivita(mostraTutto);
      toast('✓ Attività registrata!');
      if (att.registro_speciale) {
        setAllegatiProposta({ idAttivita: data.id, registroSpeciale: att.registro_speciale });
      }
    } catch {
      toast('Errore nella registrazione', 'errore');
    }
    setEseguendo(null);
  };

  // Registra l'attivita' come "N.A." (non applicabile oggi).
  // Sale dall'endpoint /salta del backend: scrive una riga in elenco_attivita
  // con saltata=true, esito_conforme=NULL, esito_valore=NULL.
  const registraSalta = async (att, dataCustom = null, oraCustom = null) => {
    const idScadenziario = att.id_scadenziario;
    setEseguendo(idScadenziario);
    try {
      const res = await apiFetch(`${API}/attivita/salta`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          idScadenziario,
          idOperatore: operatore.id,
          note: null,
          dataCustom,
          oraCustom
        })
      });
      if (!res.ok) throw new Error();
      await caricaAttivita(mostraTutto);
      toast('Attività marcata come N.A.');
    } catch {
      toast('Errore nella registrazione', 'errore');
    }
    setEseguendo(null);
  };

  const handlePulsante = (att, intento) => {
    const haLimiti = att.valore_min !== null || att.valore_max !== null;
    if (haLimiti) {
      setValoreAperto({ attivita: att, intento });
    } else {
      if (intento === 'conforme') {
        registraEsito(att, true, null, sessione?.data || null, sessione?.ora || null);
      } else {
        const descrizioneQualitativa =
          `${att.tipo_attivita} - ${att.oggetto} (${att.collocazione}): esito non conforme`;
        setNcAperta({ attivita: att, valore: null,
          eventoPrecompilato: descrizioneQualitativa,
          dataCustom: sessione?.data || null,
          oraCustom:  sessione?.ora  || null });
      }
    }
  };

  const handleValoreConferma = (valore, neiLimiti, dataCustom, oraCustom) => {
    const { attivita: att, intento } = valoreAperto;
    setValoreAperto(null);
    if (intento === 'conforme') {
      registraEsito(att, true, valore, dataCustom, oraCustom);
    } else {
      const um  = att.um ? ` ${att.um}` : '';
      const num = parseFloat(valore);
      let violazione = '';
      if (!isNaN(num)) {
        if (att.valore_min !== null && num < att.valore_min) violazione = ' (valore inferiore al minimo)';
        else if (att.valore_max !== null && num > att.valore_max) violazione = ' (valore superiore al massimo)';
      }
      const descrizione =
        `${att.tipo_attivita} - ${att.oggetto} (${att.collocazione}): ` +
        `valore misurato ${valore}${um}${violazione}` +
        (att.valore_min !== null ? ` | limite min: ${att.valore_min}${um}` : '') +
        (att.valore_max !== null ? ` | limite max: ${att.valore_max}${um}` : '');
      setNcAperta({ attivita: att, valore, eventoPrecompilato: descrizione, dataCustom, oraCustom });
    }
  };

  // Separazione attività in ritardo / in scadenza
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);

  const giorniRitardo = (a) => {
    if (!a.data_scadenza) return 0;
    const scad = new Date(a.data_scadenza);
    scad.setHours(0, 0, 0, 0);
    return Math.floor((oggi - scad) / 86400000);
  };

  const ambitiDisponibili = [...new Set(attivita.map(a => a.ambito || 'Altro'))];
  const filtra = (lista) => filtroAmbito === 'tutti' ? lista : lista.filter(a => (a.ambito || 'Altro') === filtroAmbito);

  const inRitardo  = filtra(attivita.filter(a => a.data_scadenza && new Date(a.data_scadenza) < oggi));
  const inScadenza = filtra(attivita.filter(a => !a.data_scadenza || new Date(a.data_scadenza) >= oggi));

  const raggruppa = (lista) => lista.reduce((acc, a) => {
    const k = a.ambito || 'Altro';
    if (!acc[k]) acc[k] = [];
    acc[k].push(a);
    return acc;
  }, {});

  // ── Card attività singola (D2 Evoluto) ───────────────────────────────────
  const renderCard = (a, isRitardo) => {
    const ritardo = isRitardo ? giorniRitardo(a) : 0;
    const isContinuo = a.frequenza_gg == null;
    const disabled = eseguendo === a.id_scadenziario;

    return (
      <div
        key={a.id_scadenziario}
        style={{
          background: '#fff',
          border: `1px solid ${LT.n150}`,
          borderLeft: isRitardo ? `4px solid ${LT.rosso}` : `1px solid ${LT.n150}`,
          borderRadius: LT.r.lg,
          padding: 16,
          marginBottom: 10,
          boxShadow: LT.sh1,
        }}
      >
        {/* Riga badge superiore */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {isContinuo ? (
            <Chip kind="viola" icon="repeat" size="sm">Controllo continuo</Chip>
          ) : (
            <Chip kind="neutro" icon="clock" size="sm">Ogni {a.frequenza_gg}gg</Chip>
          )}
          {isRitardo && (
            <Chip kind="rosso" icon="alert" size="sm" soft={false} style={{ letterSpacing: '.03em' }}>
              In ritardo{ritardo > 0 ? ` +${ritardo}gg` : ''}
            </Chip>
          )}
        </div>

        {/* Titolo + dettagli */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <CatTile cat={a.ambito} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>
              {a.oggetto}
            </div>
            <div style={{ fontSize: 13, color: LT.n600, marginTop: 3 }}>
              {a.collocazione}
              {a.tipo_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {a.tipo_attivita}</>}
              {a.sub_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {a.sub_attivita}</>}
            </div>

            {/* Limiti min/max */}
            {(a.valore_min !== null || a.valore_max !== null) && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: LT.bluSoft, color: LT.blu,
                padding: '4px 10px', borderRadius: LT.r.sm,
                marginTop: 8, fontSize: 12, fontWeight: 600,
              }}>
                {a.valore_min !== null && <span>Min: <strong>{a.valore_min}</strong></span>}
                {a.valore_max !== null && <span>Max: <strong>{a.valore_max}</strong></span>}
              </div>
            )}

            {/* Ultima esecuzione */}
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.clock} size={13} />
              {isContinuo
                ? (a.ultima_esecuzione_ore != null
                    ? `Ultima esecuzione: ${parseFloat(a.ultima_esecuzione_ore).toFixed(1).replace('.', ',')}h fa`
                    : 'Nessuna esecuzione oggi')
                : (a.ultimo_intervento
                    ? `Ultimo: ${new Date(a.ultimo_intervento).toLocaleDateString('it-IT')}`
                    : 'Mai eseguita')}
            </div>
          </div>
        </div>

        {/* Bottoni esito */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <Btn
            kind="success"
            block
            icon="check"
            disabled={disabled}
            onClick={() => handlePulsante(a, 'conforme')}
          >
            Conforme
          </Btn>
          <Btn
            kind="primary"
            block
            icon="alert"
            disabled={disabled}
            onClick={() => handlePulsante(a, 'nonconforme')}
            style={{ background: LT.rosso, color: '#fff', boxShadow: '0 4px 12px rgba(220,38,38,.18)' }}
          >
            Non conforme
          </Btn>
        </div>

        {/* Azione secondaria: marca come N.A. (non applicabile oggi) */}
        <div style={{ marginTop: 6, textAlign: 'right' }}>
          <button
            disabled={disabled}
            onClick={() => registraSalta(a, sessione?.data || null, sessione?.ora || null)}
            style={{
              fontSize: 11, fontWeight: 600,
              color: LT.n500, background: 'transparent',
              border: 'none', padding: '4px 8px',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              fontFamily: LT.sans,
            }}
            title="Non applicabile oggi (es. macchina non in uso)"
          >
            N.A. — non applicabile oggi
          </button>
        </div>
      </div>
    );
  };

  // ── Gruppi per ambito ────────────────────────────────────────────────────
  const renderGruppi = (lista, isRitardo) =>
    Object.entries(raggruppa(lista)).map(([ambito, items]) => (
      <div key={ambito} style={{ marginBottom: 18 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 4px', marginBottom: 6,
        }}>
          <CatTile cat={ambito} size={26} />
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: LT.n800 }}>
            {ambito}
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700,
            color: isRitardo ? LT.rosso : LT.blu,
            background: isRitardo ? LT.rossoSoft : LT.bluSoft,
            padding: '3px 9px', borderRadius: 999,
          }}>
            {items.length}
          </span>
        </div>
        {items.map(a => renderCard(a, isRitardo))}
      </div>
    ));

  const avviaSessione = () => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    setNuovaData(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
    setNuovaOra(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
    setMostraFormSessione(true);
  };

  // ── Hero banner: data + saluto (coerente con Home/Dashboard) ─────────────
  const oggiData = new Date();
  const dataStr = oggiData.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  // ── Stili field del modal sessione ───────────────────────────────────────
  const sesLabel = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 12, marginBottom: 6 };
  const sesInput = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Modal avvio sessione */}
      {mostraFormSessione && (
        <div style={lt_overlay}>
          <div style={lt_modal}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Ico d={I.clock} size={22} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>Avvia sessione di registrazione</div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Data e ora vengono usate per tutte le registrazioni successive
                </div>
              </div>
            </div>

            <label style={sesLabel}>Data</label>
            <input style={sesInput} type="date" value={nuovaData}
              onChange={e => setNuovaData(e.target.value)} />

            <label style={sesLabel}>Ora</label>
            <input style={sesInput} type="time" value={nuovaOra}
              onChange={e => setNuovaOra(e.target.value)} />

            {erroreSessione && (
              <div style={{
                background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
                padding: '8px 12px', marginTop: 12, fontSize: 13, fontWeight: 500,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <Ico d={I.alert} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{erroreSessione}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Btn kind="outline" block onClick={() => { setMostraFormSessione(false); setErroreSessione(''); }}>
                Annulla
              </Btn>
              <Btn kind="primary" block icon="play" onClick={() => {
                if (!nuovaData || !nuovaOra) { setErroreSessione("Inserisci sia la data che l'ora"); return; }
                setSessione({ data: nuovaData, ora: nuovaOra });
                setMostraFormSessione(false);
                setErroreSessione('');
              }}>
                Avvia sessione
              </Btn>
            </div>
          </div>
        </div>
      )}

      {valoreAperto && (
        <FormValore
          attivita={valoreAperto.attivita}
          intento={valoreAperto.intento}
          onConferma={handleValoreConferma}
          onAnnulla={() => setValoreAperto(null)}
          sessioneData={sessione?.data || null}
          sessioneOra={sessione?.ora || null}
        />
      )}
      {ncAperta && (
        <FormNC
          attivita={ncAperta.attivita}
          operatore={operatore}
          eventoPrecompilato={ncAperta.eventoPrecompilato ?? ''}
          valore={ncAperta.valore}
          dataCustom={ncAperta.dataCustom ?? null}
          oraCustom={ncAperta.oraCustom ?? null}
          onConferma={(idAttivita) => {
            const reg = ncAperta.attivita?.registro_speciale;
            setNcAperta(null);
            caricaAttivita(mostraTutto);
            if (reg && idAttivita) setAllegatiProposta({ idAttivita, registroSpeciale: reg });
          }}
          onAnnulla={() => setNcAperta(null)}
        />
      )}

      {allegatiProposta && (
        <ModalProponiAllegato
          idAttivita={allegatiProposta.idAttivita}
          registroSpeciale={allegatiProposta.registroSpeciale}
          operatore={operatore}
          onFine={() => setAllegatiProposta(null)}
        />
      )}

      {/* Header pagina (compatto, senza gradient — il saluto lo fa la Home) */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {dataStr}
        </div>
        <div style={{
          fontSize: 22, fontWeight: 700, color: LT.n900,
          letterSpacing: '-.01em', lineHeight: 1.2,
        }}>
          Attività da eseguire
        </div>
        {azienda?.ragione_sociale && (
          <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
            {azienda.ragione_sociale}
          </div>
        )}
      </div>

      {/* Toggle vista classica / settimanale */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14,
        padding: 4, background: LT.n100, borderRadius: 999, width: 'fit-content',
      }}>
        {[
          { v: 'classica',    label: 'Classica' },
          { v: 'settimanale', label: 'Settimanale' },
        ].map(opt => {
          const on = vistaModo === opt.v;
          return (
            <button
              key={opt.v}
              onClick={() => setVistaModo(opt.v)}
              style={{
                padding: '6px 16px', borderRadius: 999, border: 'none',
                background: on ? '#fff' : 'transparent',
                color: on ? LT.blu : LT.n600,
                fontSize: 12, fontWeight: on ? 700 : 500,
                cursor: 'pointer', fontFamily: LT.sans,
                boxShadow: on ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                transition: 'all .15s',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {vistaModo === 'settimanale' ? (
        <MatriceSettimanale
          operatore={operatore}
          azienda={azienda}
          apiFetch={apiFetch}
          apiBase={API}
          FormValore={FormValore}
          FormNC={FormNC}
          ModalProponiAllegato={ModalProponiAllegato}
          toast={toast}
        />
      ) : (
      <>

      {/* Banner counter attività in ritardo */}
      {inRitardo.length > 0 && (
        <div style={{
          background: LT.rossoSoft, border: `1px solid ${LT.rosso}22`,
          borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: LT.r.md,
            background: LT.rosso, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.alert} size={18} />
          </div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: LT.rosso }}>
            {inRitardo.length} {inRitardo.length === 1 ? 'attività in ritardo' : 'attività in ritardo'}
          </div>
        </div>
      )}

      {/* Banner sessione attiva / pulsante avvio */}
      {sessione ? (
        <div style={{
          background: LT.bluSoft, border: `1px solid ${LT.blu}22`,
          borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: LT.r.md,
            background: LT.blu, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.clock} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: LT.blu }}>Sessione attiva</div>
            <div style={{ fontSize: 12, color: LT.blu, marginTop: 1 }}>
              {sessione.data.split('-').reverse().join('/')} alle {sessione.ora}
            </div>
          </div>
          <Btn kind="outline" size="sm" icon="x" onClick={() => setSessione(null)}
            style={{ borderColor: LT.rosso, color: LT.rosso }}>
            Termina
          </Btn>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Btn kind="soft" size="sm" icon="clock" onClick={avviaSessione}>
            Avvia sessione
          </Btn>
        </div>
      )}

      {/* Filtro rapido per ambito */}
      {ambitiDisponibili.length > 1 && (
        <div style={{
          display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12,
          paddingBottom: 4, WebkitOverflowScrolling: 'touch',
        }}>
          {['tutti', ...ambitiDisponibili].map(a => {
            const on = filtroAmbito === a;
            return (
              <button
                key={a}
                onClick={() => setFiltroAmbito(a)}
                style={{
                  flexShrink: 0, padding: '6px 14px', borderRadius: 999,
                  border: `1px solid ${on ? LT.blu : LT.n200}`,
                  background: on ? LT.blu : '#fff',
                  color: on ? '#fff' : LT.n600,
                  fontSize: 12, fontWeight: on ? 700 : 500,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: LT.sans, transition: 'all .15s',
                }}
              >
                {a === 'tutti' ? 'Tutti' : a}
              </button>
            );
          })}
        </div>
      )}

      {/* Toggle mostra tutto */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button
          onClick={() => setMostraTutto(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${mostraTutto ? LT.viola : LT.n200}`,
            background: mostraTutto ? LT.violaSoft : '#fff',
            color: mostraTutto ? LT.viola : LT.n600,
            fontSize: 12, fontWeight: mostraTutto ? 700 : 500,
            cursor: 'pointer', fontFamily: LT.sans,
          }}
        >
          <span style={{
            width: 14, height: 14, borderRadius: 3,
            border: `1.5px solid ${mostraTutto ? LT.viola : LT.n400}`,
            background: mostraTutto ? LT.viola : 'transparent',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {mostraTutto && <Ico d={I.check} size={10} stroke="#fff" sw={2.5} />}
          </span>
          Mostra tutto
        </button>
        {mostraTutto && (
          <span style={{ fontSize: 11, color: LT.viola, fontWeight: 500 }}>
            Tutte le attività, anche non in scadenza
          </span>
        )}
      </div>

      {/* Sezione IN RITARDO */}
      {inRitardo.length > 0 && (
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.rosso,
            textTransform: 'uppercase', letterSpacing: '.08em',
            margin: '8px 4px 10px',
          }}>
            In ritardo
          </div>
          {renderGruppi(inRitardo, true)}
        </>
      )}

      {/* Sezione IN SCADENZA */}
      {inScadenza.length > 0 && (
        <>
          {inRitardo.length > 0 && (
            <div style={{
              fontSize: 11, fontWeight: 700, color: LT.blu,
              textTransform: 'uppercase', letterSpacing: '.08em',
              margin: '16px 4px 10px',
            }}>
              In scadenza
            </div>
          )}
          {renderGruppi(inScadenza, false)}
        </>
      )}

      {erroreCaricamento && (
        <div style={{
          background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
          padding: '10px 14px', marginTop: 12, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <Ico d={I.alert} size={16} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{erroreCaricamento}</span>
        </div>
      )}

      {/* Empty state: nessuna attività */}
      {attivita.length === 0 && !erroreCaricamento && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 999,
            background: LT.verdeSoft, color: LT.verde,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 14,
          }}>
            <Ico d={I.checkCircle} size={32} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: LT.n800 }}>
            Nessuna attività in scadenza
          </div>
          <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
            Tutto in regola: ottimo lavoro!
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function AziendeSelect({ value, onChange }) {
  const [aziende, setAziende] = useState([]);

  useEffect(() => {
    apiFetch(`${API}/admin/aziende`)
      .then(r => r.json())
      .then(data => setAziende(data.filter(a => a.attivo)));
  }, []);

  return (
    <select style={styles.inputText} value={value} onChange={e => onChange(e.target.value)}>
      <option value=''>-- nessuna --</option>
      {aziende.map(a => (
        <option key={a.id} value={a.id}>{a.ragione_sociale}</option>
      ))}
    </select>
  );
}

function GestioneOrganico({ operatore, onBack }) {
  const [lista,     setLista]     = useState([]);
  const [form,      setForm]      = useState(null); // null=lista, {}=nuovo, {id,...}=modifica
  const [salvando,  setSalvando]  = useState(false);
  const [errore,    setErrore]    = useState('');
  const [cerca,     setCerca]     = useState('');

  useEffect(() => {
    caricaOrganico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caricaOrganico = async () => {
    const idAzienda = operatore.master ? '' : `?idAzienda=${operatore.id_azienda}`;
    const res  = await apiFetch(`${API}/admin/organico${idAzienda}`);
    const data = await res.json();
    setLista(data);
  };

  const validaPin = (pin) => {
    if (!pin) return null; // in modifica il PIN è opzionale
    if (pin.length < 5) return 'Il PIN deve essere di almeno 5 caratteri';
    if (!/[a-zA-Z]/.test(pin)) return 'Il PIN deve contenere almeno una lettera';
    if (!/[0-9]/.test(pin)) return 'Il PIN deve contenere almeno un numero';
    return null;
  };

  const handleSalva = async () => {
    const errPin = validaPin(form.pin);
    if (errPin) { setErrore(errPin); return; }
    if (!form.id && !form.pin) { setErrore('Il PIN è obbligatorio per un nuovo operatore'); return; }

    setSalvando(true);
    setErrore('');
    try {
      const url    = form.id ? `${API}/admin/organico/${form.id}` : `${API}/admin/organico`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome:       form.nome,
          cognome:    form.cognome,
          pin:        form.pin,
          admin:      !!form.admin,
          master:     !!form.master,
          attivo:     form.attivo !== false,
          idAzienda:  operatore.master ? form.id_azienda : operatore.id_azienda
        })
      });
      if (!res.ok) throw new Error();
      setForm(null);
      setErrore('');
      setCerca('');
      await caricaOrganico();
      toast('✓ Operatore salvato!');
    } catch {
      setErrore("Errore nel salvataggio dell'operatore. Riprova.");
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };

  // ── FORM (nuovo / modifica) ─────────────────────────────────────────────
  if (form !== null) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>

        {/* Hero compatto */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Organico · {form.id ? 'Modifica' : 'Nuovo'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
              }}>
                {form.id ? `${form.nome || ''} ${form.cognome || ''}`.trim() || 'Operatore' : 'Nuovo operatore'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {form.id ? 'Modifica dati e permessi' : 'Inserisci i dati del nuovo operatore'}
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); setCerca(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        <Card pad={20} style={{ marginBottom: 14 }}>
          {/* Dati anagrafici */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.user} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati anagrafici</div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Nome</label>
              <input style={inputStyle} value={form.nome || ''}
                onChange={e => setForm({ ...form, nome: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Cognome</label>
              <input style={inputStyle} value={form.cognome || ''}
                onChange={e => setForm({ ...form, cognome: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              {form.id ? 'Nuovo PIN (lascia vuoto per non modificare)' : 'PIN *'}
            </label>
            <input style={inputStyle}
              type="password"
              value={form.pin || ''}
              onChange={e => setForm({ ...form, pin: e.target.value })}
              placeholder={form.id ? '••••' : 'Almeno 5 caratteri, una lettera e un numero'} />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.lock} size={12} color={LT.n400} />
              Minimo 5 caratteri, almeno una lettera e un numero
            </div>
          </div>

          {operatore.master && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Azienda</label>
              <AziendeSelect
                value={form.id_azienda || ''}
                onChange={val => setForm({ ...form, id_azienda: val })}
              />
            </div>
          )}
        </Card>

        {/* Firma */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.violaSoft, color: LT.viola,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.signature} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Firma operatore</div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Immagine PNG/JPG trasparente</div>
            </div>
          </div>

          {form.firma && (
            <div style={{
              padding: 12, background: LT.n50, border: `1px solid ${LT.n150}`,
              borderRadius: LT.r.md, marginBottom: 10, textAlign: 'center',
            }}>
              <img src={`${BACKEND}${form.firma}`} alt="firma"
                style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }} />
            </div>
          )}

          {form.id ? (
            <input type="file" accept="image/*"
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('firma', file);
                const res = await apiFetch(`${API}/uploads/firma-operatore/${form.id}`, {
                  method: 'POST',
                  body: fd
                });
                const data = await res.json();
                if (data.firma) setForm({ ...form, firma: data.firma });
              }}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', fontSize: 13, fontFamily: LT.sans,
                border: `1px dashed ${LT.n200}`, borderRadius: LT.r.md,
                background: LT.n50,
              }}
            />
          ) : (
            <div style={{
              fontSize: 12, color: LT.n500, padding: '10px 12px',
              background: LT.n50, border: `1px dashed ${LT.n200}`,
              borderRadius: LT.r.md, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Ico d={I.alert} size={13} color={LT.arancio} />
              Salva prima l'operatore per poter caricare la firma
            </div>
          )}
        </Card>

        {/* Permessi */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.verdeSoft, color: LT.verde,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.shield} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Permessi e stato</div>
          </div>

          <label style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: LT.r.md,
            border: `1px solid ${LT.n150}`, cursor: 'pointer',
            background: form.admin ? LT.bluSoft : '#fff', marginBottom: 8,
          }}>
            <input type="checkbox" checked={!!form.admin}
              onChange={e => setForm({ ...form, admin: e.target.checked })}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: LT.n900 }}>Amministratore</div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>
                Accesso alla gestione (organico, oggetti, scadenziario, impostazioni)
              </div>
            </div>
          </label>

          {form.id && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', borderRadius: LT.r.md,
              border: `1px solid ${LT.n150}`, cursor: 'pointer',
              background: form.attivo !== false ? LT.verdeSoft : LT.rossoSoft,
            }}>
              <input type="checkbox" checked={form.attivo !== false}
                onChange={async e => {
                  if (!e.target.checked) {
                    const ok = await chiediConferma(
                      'Disattivare operatore?',
                      `${form.nome} ${form.cognome} non potrà più accedere al sistema.`
                    );
                    if (!ok) return;
                  }
                  setForm({ ...form, attivo: e.target.checked });
                }}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: LT.n900 }}>
                  {form.attivo !== false ? 'Operatore attivo' : 'Operatore disattivato'}
                </div>
                <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>
                  {form.attivo !== false ? 'Può accedere e registrare attività' : 'Non può più accedere al sistema'}
                </div>
              </div>
            </label>
          )}
        </Card>

        {/* Errore */}
        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        {/* Azioni */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="outline" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva operatore'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const q = cerca.toLowerCase();
  const filtrati = lista.filter(op =>
    `${op.nome} ${op.cognome}`.toLowerCase().includes(q)
  );

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero compatto */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Organico
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Gestione operatori, permessi e firme
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({})}>
            Nuovo operatore
          </Btn>
        </div>
      </div>

      {/* Card ricerca */}
      <Card pad={14} style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, display: 'flex',
          }}>
            <Ico d={I.search} size={15} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per nome o cognome..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>
      </Card>

      {/* Conteggio */}
      <div style={{
        fontSize: 11, fontWeight: 700, color: LT.n500,
        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
      }}>
        {filtrati.length} {filtrati.length === 1 ? 'operatore' : 'operatori'}
        {cerca && ` per "${cerca}"`}
      </div>

      {/* Lista */}
      {filtrati.length === 0 ? (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.lg,
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.users} size={20} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessun operatore registrato'}
          </div>
        </Card>
      ) : (
        filtrati.map(op => {
          const ruolo = op.master
            ? { kind: 'viola',  icon: 'shield', label: 'Master' }
            : op.admin
            ? { kind: 'blu',    icon: 'settings', label: 'Admin' }
            : { kind: 'neutro', icon: 'user', label: 'Operatore' };
          return (
            <Card
              key={op.id}
              pad={14}
              hover
              onClick={() => setForm({ ...op })}
              style={{
                marginBottom: 10,
                opacity: op.attivo ? 1 : 0.6,
                borderLeft: `4px solid ${op.attivo ? LT.verde : LT.n300}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Avatar */}
                <div style={{
                  width: 44, height: 44, borderRadius: LT.r.full,
                  background: LT.bluSoft, color: LT.blu,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, fontSize: 15, fontWeight: 700,
                }}>
                  {(op.nome?.[0] || '').toUpperCase()}{(op.cognome?.[0] || '').toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4,
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>
                      {op.cognome} {op.nome}
                    </div>
                    <Chip kind={ruolo.kind} icon={ruolo.icon} size="sm">
                      {ruolo.label}
                    </Chip>
                    {!op.attivo && (
                      <Chip kind="rosso" icon="x" size="sm">Disattivato</Chip>
                    )}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: LT.n500,
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.lock} size={12} color={LT.n400} />
                      <span style={{ fontFamily: LT.mono }}>{'•'.repeat(Math.max(4, op.pin?.length || 4))}</span>
                    </div>
                    {op.firma && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.signature} size={12} color={LT.viola} />
                        <span>Firma caricata</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chevron */}
                <Ico d={I.chevronR} size={16} color={LT.n300} />
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}

function GestioneOggetti({ operatore, onBack, onAssegnaAttivita }) {
  const [lista,       setLista]       = useState([]);
  const [form,        setForm]        = useState(null);
  const [salvando,    setSalvando]    = useState(false);
  const [errore,      setErrore]      = useState('');
  const [cerca,       setCerca]       = useState('');
  const [filtroStato, setFiltroStato] = useState('attivi'); // 'attivi' | 'obsoleti' | 'tutti'
  const [dettaglio,   setDettaglio]   = useState(null);  // oggetto di cui si vede la scheda
  const [scheda,      setScheda]      = useState(null);  // { oggetto, attivita: [...] }
  const [loadingScheda, setLoadingScheda] = useState(false);

  const apriDettaglio = async (o) => {
    setDettaglio(o);
    setScheda(null);
    setLoadingScheda(true);
    try {
      const res = await apiFetch(`${API}/admin/attrezzature/${o.id}/scheda`);
      if (res.ok) setScheda(await res.json());
    } catch { /* la scheda resta vuota: mostriamo solo l'anagrafica */ }
    setLoadingScheda(false);
  };

  useEffect(() => {
    caricaOggetti();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caricaOggetti = async () => {
    const idAzienda = operatore.master ? '' : `?idAzienda=${operatore.id_azienda}`;
    const res  = await apiFetch(`${API}/admin/attrezzature${idAzienda}`);
    const data = await res.json();
    setLista(data);
  };

  const handleSalva = async () => {
    setSalvando(true);
    setErrore('');
    try {
      const url    = form.id ? `${API}/admin/attrezzature/${form.id}` : `${API}/admin/attrezzature`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          descrizione:     form.descrizione,
          collocazione:    form.collocazione || null,
          note:            form.note || null,
          riferimento:     form.riferimento || null,
          tipoTaratura:    form.tipo_taratura || null,
          attivo:          form.attivo !== false,
          cascadeObsoleto: form._cascadeObsoleto === true,
          idAzienda:       operatore.master ? form.id_azienda : operatore.id_azienda
        })
      });
      if (!res.ok) throw new Error();
      setForm(null);
      setErrore('');
      setCerca('');
      await caricaOggetti();
      toast('✓ Oggetto salvato!');
    } catch {
      setErrore('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 60, fontFamily: LT.sans };

  // ── FORM ────────────────────────────────────────────────────────────────
  if (form !== null) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>

        {/* Hero */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Oggetti · {form.id ? 'Modifica' : 'Nuovo'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
              }}>
                {form.id ? (form.descrizione || 'Oggetto') : 'Nuovo oggetto'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {form.id ? 'Modifica dati dell\'oggetto monitorato' : 'Registra un nuovo oggetto o attrezzatura'}
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); setCerca(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati oggetto</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Descrizione *</label>
            <input style={inputStyle} value={form.descrizione || ''}
              onChange={e => setForm({ ...form, descrizione: e.target.value })}
              placeholder="es. Frigorifero cucina, Affettatrice, Magazzino secco..." />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Collocazione / mansione / scopo</label>
            <input style={inputStyle} value={form.collocazione || ''}
              onChange={e => setForm({ ...form, collocazione: e.target.value })}
              placeholder="es. Cucina piano terra" />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Note aggiuntive</label>
            <textarea style={textareaStyle} value={form.note || ''}
              onChange={e => setForm({ ...form, note: e.target.value })}
              rows={2}
              placeholder="Note opzionali..." />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Strumento di riferimento</label>
            <input style={inputStyle} value={form.riferimento || ''}
              onChange={e => setForm({ ...form, riferimento: e.target.value })}
              placeholder="es. Termometro taratura T-001, Bilancia BX-200..." />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.wrench} size={12} color={LT.n400} />
              Opzionale · identificativo dello strumento associato a questo oggetto
            </div>
          </div>

          {operatore.master && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Azienda</label>
              <AziendeSelect
                value={form.id_azienda || ''}
                onChange={val => setForm({ ...form, id_azienda: val })}
              />
            </div>
          )}
        </Card>

        {/* Stato (solo modifica) */}
        {form.id && (
          <Card pad={20} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 34, height: 34, borderRadius: LT.r.md,
                background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.shield} size={16} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Stato</div>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', borderRadius: LT.r.md,
              border: `1px solid ${LT.n150}`,
              background: form.attivo !== false ? LT.verdeSoft : LT.rossoSoft,
            }}>
              <Chip
                kind={form.attivo !== false ? 'verde' : 'rosso'}
                icon={form.attivo !== false ? 'check' : 'x'}
                size="md"
              >
                {form.attivo !== false ? 'Attivo' : 'Obsoleto'}
              </Chip>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.35 }}>
                  {form.attivo !== false
                    ? 'Presente nel monitoraggio e nello scadenziario.'
                    : 'Non compare più nei monitoraggi. I dati storici restano consultabili.'}
                </div>
              </div>
              <Btn
                kind={form.attivo === false ? 'success' : 'outline'}
                size="sm"
                icon={form.attivo === false ? 'check' : 'x'}
                onClick={async () => {
                  if (form.attivo !== false) {
                    // Sta per diventare obsoleto → fetch count + eventuale cascade dialog
                    let scadCount = 0;
                    try {
                      const r = await apiFetch(`${API}/admin/attrezzature/${form.id}/scadenziario-count`);
                      if (r.ok) {
                        const d = await r.json();
                        scadCount = d.count || 0;
                      }
                    } catch { /* in caso di errore procediamo senza cascade */ }

                    const okPrimo = await chiediConferma(
                      'Rendere obsoleto questo oggetto?',
                      `"${form.descrizione}" non comparirà più nei monitoraggi. I dati storici restano consultabili.`
                    );
                    if (!okPrimo) return;

                    let cascade = false;
                    if (scadCount > 0) {
                      cascade = await chiediConferma(
                        `E le ${scadCount} ${scadCount === 1 ? 'attività collegata' : 'attività collegate'}?`,
                        `"${form.descrizione}" diventerà obsoleto in ogni caso.\n\n` +
                        `• CONFERMA → rende obsolete anche ${scadCount === 1 ? "l'attività collegata" : `le ${scadCount} attività collegate`} dello scadenziario.\n` +
                        `• ANNULLA → rende obsoleto solo l'oggetto e lascia ${scadCount === 1 ? "l'attività attiva" : 'le attività attive'}.`
                      );
                    }
                    setForm({ ...form, attivo: false, _cascadeObsoleto: cascade });
                  } else {
                    // Riattivazione: nessun cascade
                    setForm({ ...form, attivo: true, _cascadeObsoleto: false });
                  }
                }}
              >
                {form.attivo === false ? 'Riattiva' : 'Rendi obsoleto'}
              </Btn>
            </div>
          </Card>
        )}

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="outline" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva oggetto'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── SCHEDA OGGETTO (dettaglio) ───────────────────────────────────────────
  if (dettaglio !== null) {
    const o = dettaglio;
    const attivita = scheda?.attivita || [];
    const fmtData = (d) => d
      ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';

    return (
      <div style={{ padding: 24, maxWidth: 860, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6,
          }}>
            Scheda oggetto
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div style={{
                width: 44, height: 44, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Ico d={I.package} size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: LT.n900, letterSpacing: '-.01em', lineHeight: 1.2 }}>
                    {o.descrizione}
                  </div>
                  {!o.attivo && <Chip kind="rosso" icon="x" size="sm">Obsoleto</Chip>}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: LT.n500, marginTop: 3 }}>
                  {o.collocazione && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.pin} size={12} color={LT.n400} />{o.collocazione}
                    </span>
                  )}
                  {o.riferimento && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.wrench} size={12} color={LT.viola} />Rif: {o.riferimento}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn kind="outline" icon="arrowLeft" onClick={() => { setDettaglio(null); setScheda(null); }}>Indietro</Btn>
              <Btn kind="outline" icon="edit" onClick={() => setForm({ ...o })}>Modifica</Btn>
              <Btn kind="primary" icon="plus" onClick={() => onAssegnaAttivita && onAssegnaAttivita(o.id)}>Assegna attività</Btn>
            </div>
          </div>
        </div>

        {o.note && (
          <Card pad={14} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.4 }}>{o.note}</div>
          </Card>
        )}

        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
        }}>
          Attività collegate{attivita.length > 0 ? ` · ${attivita.length}` : ''}
        </div>

        {loadingScheda ? (
          <Card pad={20} style={{ textAlign: 'center', color: LT.n500, fontSize: 13 }}>Caricamento…</Card>
        ) : attivita.length === 0 ? (
          <Card pad={24} style={{ textAlign: 'center' }}>
            <div style={{
              width: 44, height: 44, borderRadius: LT.r.lg, background: LT.n100, color: LT.n400,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
            }}>
              <Ico d={I.calendar} size={20} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700, marginBottom: 10 }}>
              Nessuna attività assegnata a questo oggetto
            </div>
            <Btn kind="primary" icon="plus" onClick={() => onAssegnaAttivita && onAssegnaAttivita(o.id)}>
              Assegna la prima attività
            </Btn>
          </Card>
        ) : (
          attivita.map(a => {
            const esec = a.ultime_esecuzioni || [];
            return (
              <Card key={a.id_scadenziario} pad={16} style={{
                marginBottom: 10,
                opacity: a.obsoleto ? 0.6 : 1,
                borderLeft: `4px solid ${a.obsoleto ? LT.n300 : LT.verde}`,
              }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{a.tipo_attivita}</div>
                    {a.obsoleto && <Chip kind="rosso" icon="x" size="sm">Obsoleta</Chip>}
                  </div>
                  {a.sub_attivita && (
                    <div style={{ fontSize: 12.5, color: LT.n600, marginBottom: 6 }}>{a.sub_attivita}</div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {a.ambito && <Chip kind="neutro" size="sm">{a.ambito}</Chip>}
                    {a.frequenza_gg && (
                      <Chip kind="neutro" icon="repeat" size="sm">
                        {a.frequenza_gg === 1 ? 'Giornaliera' : `Ogni ${a.frequenza_gg}gg`}
                      </Chip>
                    )}
                    {(a.valore_min != null || a.valore_max != null) && (
                      <Chip kind="neutro" icon="target" size="sm">
                        {a.valore_min ?? '—'} / {a.valore_max ?? '—'}
                      </Chip>
                    )}
                    {a.op_monitoraggio && <Chip kind="neutro" icon="user" size="sm">{a.op_monitoraggio}</Chip>}
                    {a.op_verifica && <Chip kind="viola" icon="check" size="sm">Verifica: {a.op_verifica}</Chip>}
                  </div>
                </div>

                {esec.length > 0 ? (
                  <div style={{ borderTop: `1px solid ${LT.n150}`, paddingTop: 10 }}>
                    <div style={{
                      fontSize: 10.5, fontWeight: 700, color: LT.n500,
                      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
                    }}>
                      Ultimi esiti
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {esec.map(e => {
                        const stato = e.saltata ? 'warn'
                          : (e.esito_conforme === false ? 'nc'
                          : (e.esito_conforme === true ? 'ok' : 'pending'));
                        const label = e.saltata ? 'N.A.'
                          : (e.esito_conforme === false ? 'Non conforme'
                          : (e.esito_conforme === true ? 'Conforme' : '—'));
                        const chipKind = stato === 'ok' ? 'verde'
                          : stato === 'nc' ? 'rosso'
                          : stato === 'warn' ? 'arancio' : 'neutro';
                        const chipIcon = stato === 'ok' ? 'check'
                          : stato === 'nc' ? 'x'
                          : stato === 'warn' ? 'skip' : 'dot';
                        return (
                          <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                            <span style={{ color: LT.n500, minWidth: 78 }}>{fmtData(e.data_effettuazione)}</span>
                            <Chip kind={chipKind} icon={chipIcon} size="sm">{label}</Chip>
                            {e.esito_valore != null && e.esito_valore !== '' && (
                              <span style={{ color: LT.n700, fontWeight: 600 }}>{e.esito_valore}</span>
                            )}
                            {e.operatore && <span style={{ color: LT.n400, marginLeft: 'auto' }}>{e.operatore}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ borderTop: `1px solid ${LT.n150}`, paddingTop: 10, fontSize: 12, color: LT.n400 }}>
                    Nessuna esecuzione registrata
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const q = cerca.toLowerCase();
  const filtrati = lista.filter(o => {
    if (filtroStato === 'attivi'   &&  o.attivo === false) return false;
    if (filtroStato === 'obsoleti' &&  o.attivo !== false) return false;
    return `${o.descrizione || ''} ${o.collocazione || ''}`.toLowerCase().includes(q);
  });
  const countAttivi   = lista.filter(o => o.attivo !== false).length;
  const countObsoleti = lista.filter(o => o.attivo === false).length;

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Oggetti da monitorare
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Attrezzature, aree e oggetti sotto controllo HACCP
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({})}>
            Nuovo oggetto
          </Btn>
        </div>
      </div>

      {/* Filtri */}
      <Card pad={14} style={{ marginBottom: 14 }}>
        {/* Segmented 3-way: Attivi / Obsoleti / Tutti */}
        <div style={{
          display: 'flex', gap: 0, marginBottom: 10,
          padding: 3, borderRadius: LT.r.md,
          background: LT.n100, border: `1px solid ${LT.n150}`,
        }}>
          {[
            { key: 'attivi',   label: 'Attivi',   count: countAttivi   },
            { key: 'obsoleti', label: 'Obsoleti', count: countObsoleti },
            { key: 'tutti',    label: 'Tutti',    count: lista.length  },
          ].map(opt => {
            const on = filtroStato === opt.key;
            return (
              <button key={opt.key} onClick={() => setFiltroStato(opt.key)} style={{
                flex: 1, padding: '7px 10px', borderRadius: LT.r.sm,
                border: 'none',
                background: on ? '#fff' : 'transparent',
                color: on ? LT.n900 : LT.n500,
                fontSize: 12.5, fontWeight: on ? 700 : 500,
                cursor: 'pointer', fontFamily: LT.sans,
                boxShadow: on ? LT.sh1 : 'none',
                transition: 'all .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {opt.label}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '1px 6px', borderRadius: LT.r.full,
                  background: on ? LT.n100 : 'transparent',
                  color: on ? LT.n600 : LT.n400,
                }}>{opt.count}</span>
              </button>
            );
          })}
        </div>

        {/* Ricerca con icona */}
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, display: 'flex',
          }}>
            <Ico d={I.search} size={15} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per descrizione o collocazione..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>
      </Card>

      <div style={{
        fontSize: 11, fontWeight: 700, color: LT.n500,
        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
      }}>
        {filtrati.length} {filtrati.length === 1 ? 'oggetto' : 'oggetti'}
        {cerca && ` per "${cerca}"`}
      </div>

      {filtrati.length === 0 ? (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.lg,
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.package} size={20} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessun oggetto registrato'}
          </div>
        </Card>
      ) : (
        filtrati.map(o => (
          <Card
            key={o.id}
            pad={14}
            hover
            onClick={() => apriDettaglio(o)}
            style={{
              marginBottom: 10,
              opacity: o.attivo ? 1 : 0.6,
              borderLeft: `4px solid ${o.attivo ? LT.blu : LT.n300}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Ico d={I.package} size={18} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2,
                }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>
                    {o.descrizione}
                  </div>
                  {!o.attivo && (
                    <Chip kind="rosso" icon="x" size="sm">Obsoleto</Chip>
                  )}
                </div>
                <div style={{
                  display: 'flex', gap: 10, flexWrap: 'wrap',
                  fontSize: 12, color: LT.n500,
                }}>
                  {o.collocazione && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.pin} size={12} color={LT.n400} />
                      {o.collocazione}
                    </div>
                  )}
                  {o.riferimento && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.wrench} size={12} color={LT.viola} />
                      Rif: {o.riferimento}
                    </div>
                  )}
                </div>
              </div>

              <Ico d={I.chevronR} size={16} color={LT.n300} />
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

function GestioneScadenziario({ operatore, onBack, initOggetto, onInitConsumed }) {
  const [lista,      setLista]      = useState([]);
  const [oggetti,    setOggetti]    = useState([]);
  const [tipiAtt,    setTipiAtt]    = useState([]);
  const [organicoOp, setOrganicoOp] = useState([]);
  const [form,         setForm]         = useState(null);
  const [salvando,     setSalvando]     = useState(false);
  const [errore,       setErrore]       = useState('');
  const [cerca,        setCerca]        = useState('');
  const [filtroAmbito, setFiltroAmbito] = useState('tutti');
  const [filtroStato,  setFiltroStato]  = useState('attive'); // 'attive' | 'obsolete' | 'tutte'

  useEffect(() => {
    (async () => {
      await caricaTutto();
      // Apertura diretta del form precompilato quando si arriva dalla scheda oggetto.
      if (initOggetto) {
        setForm({ id_attrezzatura: String(initOggetto) });
        if (onInitConsumed) onInitConsumed();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caricaTutto = async () => {
    const idAz = operatore.master ? '' : `?idAzienda=${operatore.id_azienda}`;
    const idAzParam = operatore.master ? '' : `idAzienda=${operatore.id_azienda}`;
    const [l, o, t, op] = await Promise.all([
      apiFetch(`${API}/admin/scadenziario${idAz}`).then(r => r.json()),
      apiFetch(`${API}/admin/attrezzature${idAz}`).then(r => r.json()),
      apiFetch(`${API}/admin/tipi-attivita`).then(r => r.json()),
      apiFetch(`${API}/admin/organico-per-azienda?${idAzParam}`).then(r => r.json()),
    ]);
    setLista(l);
    setOggetti(o);
    setTipiAtt(t);
    setOrganicoOp(op);
  };

  const handleSalva = async (forceDuplicato = false) => {
    if (!form.id_attrezzatura || !form.id_tipo_attivita) {
      setErrore('Oggetto e tipo attività sono obbligatori');
      return;
    }
    // Soglie: i campi sono di testo per accettare la virgola (con type="number"
    // il browser la scarta e 0,5 diventa 5 — vedi la nota su FormValore).
    // Qui si normalizza e si valida prima di spedire.
    const normalizzaSoglia = (v) => {
      if (v === null || v === undefined || String(v).trim() === '') return null;
      const n = parseFloat(String(v).replace(',', '.'));
      return isNaN(n) ? NaN : n;
    };
    const vMin = normalizzaSoglia(form.valore_min);
    const vMax = normalizzaSoglia(form.valore_max);
    if (Number.isNaN(vMin) || Number.isNaN(vMax)) {
      setErrore('Le soglie devono essere numeri — virgola o punto per i decimali');
      return;
    }
    if (vMin !== null && vMax !== null && vMin > vMax) {
      setErrore('Il valore minimo non può essere maggiore del massimo');
      return;
    }
    setErrore('');
    setSalvando(true);
    try {
      const url    = form.id ? `${API}/admin/scadenziario/${form.id}` : `${API}/admin/scadenziario`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAttrezzatura:   parseInt(form.id_attrezzatura),
          idTipoAttivita:   parseInt(form.id_tipo_attivita),
          subAttivita:      form.sub_attivita || null,
          frequenzaGg:      form.frequenza_gg ? parseInt(form.frequenza_gg) : null,
          valoreMin:        vMin,
          valoreMax:        vMax,
          idOpMonitoraggio: form.id_op_monitoraggio ? parseInt(form.id_op_monitoraggio) : null,
          idOpSupplenti:    Array.isArray(form.supplenti)
                              ? form.supplenti.map(sp => parseInt(sp.id)).filter(Number.isFinite)
                              : [],
          idOpVerifica:     form.id_op_verifica     ? parseInt(form.id_op_verifica)     : null,
          idOpVerificatoriAggiuntivi: Array.isArray(form.verificatori_aggiuntivi)
                              ? form.verificatori_aggiuntivi.map(v => parseInt(v.id)).filter(Number.isFinite)
                              : [],
          note:             form.note || null,
          obsoleto:         form.obsoleto || false,
          saltaSabato:      form.salta_sabato === true,
          saltaDomenica:    form.salta_domenica === true,
          forceDuplicato:   forceDuplicato === true
        })
      });
      if (res.status === 409) {
        // Duplicato rilevato dal backend: chiedi conferma e ritenta con force.
        const data = await res.json().catch(() => ({}));
        const dup = data.duplicato || {};
        const dettagli =
          `Esiste già: "${dup.oggetto || '?'}" · "${dup.tipo_attivita || '?'}"` +
          (dup.op_monitoraggio ? ` (assegnata a ${dup.op_monitoraggio})` : '') +
          '. Salvare comunque?';
        const ok = await chiediConferma('Attività duplicata?', dettagli);
        setSalvando(false);
        if (ok) return handleSalva(true);
        return;
      }
      if (!res.ok) throw new Error();
      setForm(null);
      setErrore('');
      setCerca('');
      await caricaTutto();
      toast('✓ Attività scadenziario salvata!');
    } catch {
      setErrore('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 60, fontFamily: LT.sans };

  // ── FORM ─────────────────────────────────────────────────────────────────
  if (form !== null) {
    const attSelezionata = oggetti.find(o => o.id === parseInt(form.id_attrezzatura));
    const organicoFiltrato = attSelezionata
      ? organicoOp.filter(op => op.id_azienda === attSelezionata.id_azienda)
      : organicoOp;

    // Warning duplicato: stessa coppia (oggetto, tipo) già presente in una voce
    // non obsoleta, escludendo se stessa in modifica.
    const duplicatoEsistente =
      form.id_attrezzatura && form.id_tipo_attivita && !form.obsoleto
        ? lista.find(s =>
            s.id_attrezzatura === parseInt(form.id_attrezzatura) &&
            s.id_tipo_attivita === parseInt(form.id_tipo_attivita) &&
            !s.obsoleto &&
            s.id !== form.id
          )
        : null;

    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>

        {/* Hero */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Scadenziario · {form.id ? 'Modifica' : 'Nuova'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
              }}>
                {form.id ? 'Modifica attività' : 'Nuova attività programmata'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                Configura oggetto, tipo attività, frequenza e responsabili
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); setCerca(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        {/* Oggetto + tipo attività */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Oggetto e tipo attività</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Oggetto da monitorare *</label>
            <select style={inputStyle} value={form.id_attrezzatura || ''}
              onChange={e => setForm({ ...form, id_attrezzatura: e.target.value })}>
              <option value=''>— Seleziona oggetto —</option>
              {oggetti.map(o => (
                <option key={o.id} value={o.id}>
                  {o.descrizione}{o.collocazione ? ` — ${o.collocazione}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Tipo attività *</label>
            <select style={inputStyle} value={form.id_tipo_attivita || ''}
              onChange={e => setForm({ ...form, id_tipo_attivita: e.target.value })}>
              <option value=''>— Seleziona tipo —</option>
              {tipiAtt.map(t => (
                <option key={t.id} value={t.id}>
                  {t.ambito} / {t.descrizione}
                </option>
              ))}
            </select>
          </div>

          {duplicatoEsistente && (
            <div style={{
              background: '#fff7ed', color: '#9a3412',
              border: '1px solid #fed7aa',
              borderRadius: LT.r.md, padding: '10px 12px',
              marginBottom: 12, fontSize: 12.5, lineHeight: 1.4,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <div style={{ flexShrink: 0, marginTop: 1 }}>
                <Ico d={I.alert} size={14} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  Attività già presente nello scadenziario
                </div>
                <div>
                  Esiste già: <b>{duplicatoEsistente.oggetto}</b> · <b>{duplicatoEsistente.tipo_attivita}</b>
                  {duplicatoEsistente.op_monitoraggio ? ` (assegnata a ${duplicatoEsistente.op_monitoraggio})` : ''}.
                  {' '}Probabile errore di inserimento: verifica prima di salvare.
                </div>
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>Sotto-attività (opzionale)</label>
            <input style={inputStyle} value={form.sub_attivita || ''}
              onChange={e => setForm({ ...form, sub_attivita: e.target.value })}
              placeholder="Specifica aggiuntiva" />
          </div>
        </Card>

        {/* Frequenza + limiti */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.cianoSoft, color: LT.ciano,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.calendar} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Frequenza e limiti</div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>
                Quando ripetere l'attività e soglie di conformità
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Frequenza (giorni)</label>
            <input style={inputStyle} type="number" value={form.frequenza_gg || ''}
              onChange={e => setForm({ ...form, frequenza_gg: e.target.value })}
              placeholder="es. 1 = giornaliero, 7 = settimanale, 30 = mensile" />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4 }}>
              Lascia vuoto se l'attività è a esecuzione manuale (non ha scadenza)
            </div>
          </div>

          {/* Flag weekend: visibili solo per le giornaliere (freq = 1) */}
          {parseInt(form.frequenza_gg) === 1 && (() => {
            const toggle = (label, key) => {
              const on = form[key] === true;
              return (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, [key]: !on })}
                  style={{
                    flex: '1 1 140px',
                    padding: '10px 12px', borderRadius: LT.r.md,
                    border: `1px solid ${on ? LT.blu : LT.n200}`,
                    background: on ? LT.bluSoft : '#fff',
                    color: on ? LT.blu : LT.n700,
                    fontSize: 13, fontWeight: on ? 700 : 500,
                    cursor: 'pointer', fontFamily: LT.sans,
                    display: 'flex', alignItems: 'center', gap: 8,
                    transition: 'all .15s',
                  }}
                >
                  <Ico d={I[on ? 'check' : 'x']} size={14} />
                  {label}
                </button>
              );
            };
            return (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Giorni saltati</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {toggle('Salta sabato',   'salta_sabato')}
                  {toggle('Salta domenica', 'salta_domenica')}
                </div>
                <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 6, lineHeight: 1.4 }}>
                  Aspettativa, non divieto: nei giorni saltati l'attività non compare fra quelle da eseguire e
                  non genera ritardo, ma resta comunque registrabile.
                </div>
              </div>
            );
          })()}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Valore minimo</label>
              {/* type="text": con "number" la virgola sparisce e 0,5 diventa 5 */}
              <input style={inputStyle} type="text" inputMode="decimal"
                value={form.valore_min ?? ''}
                onChange={e => setForm({ ...form, valore_min: e.target.value })}
                placeholder="es. 0 oppure -18,5" />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Valore massimo</label>
              <input style={inputStyle} type="text" inputMode="decimal"
                value={form.valore_max ?? ''}
                onChange={e => setForm({ ...form, valore_max: e.target.value })}
                placeholder="es. 4" />
            </div>
          </div>
        </Card>

        {/* Responsabili */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.violaSoft, color: LT.viola,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.users} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Responsabili</div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>
                {attSelezionata
                  ? `Filtrati per l'azienda dell'oggetto selezionato`
                  : 'Seleziona prima un oggetto per filtrare il personale'}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Monitoraggio</label>
            <select style={inputStyle} value={form.id_op_monitoraggio || ''}
              onChange={e => setForm({ ...form, id_op_monitoraggio: e.target.value })}>
              <option value=''>— Nessuno —</option>
              {organicoFiltrato.map(op => (
                <option key={op.id} value={op.id}>{op.cognome} {op.nome}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Supplenti monitoraggio <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale, multipli)</span></label>
            {/* Lista chip dei supplenti già scelti */}
            {Array.isArray(form.supplenti) && form.supplenti.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {form.supplenti.map(sp => (
                  <span key={sp.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 8px 4px 10px', borderRadius: 999,
                    background: LT.bluSoft, color: LT.blu,
                    border: `1px solid #bfdbfe`,
                    fontSize: 12.5, fontWeight: 600,
                  }}>
                    {sp.cognome} {sp.nome}
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        supplenti: form.supplenti.filter(x => x.id !== sp.id),
                      })}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: LT.blu, padding: 0, lineHeight: 1, fontSize: 14, fontWeight: 700,
                      }}
                      title="Rimuovi supplente"
                    >×</button>
                  </span>
                ))}
              </div>
            )}
            {/* Dropdown per aggiungere: solo operatori non già scelti e diversi dal titolare */}
            <select
              style={inputStyle}
              value=""
              onChange={e => {
                const id = parseInt(e.target.value);
                if (!Number.isFinite(id)) return;
                const op = organicoFiltrato.find(o => o.id === id);
                if (!op) return;
                const correnti = Array.isArray(form.supplenti) ? form.supplenti : [];
                setForm({
                  ...form,
                  supplenti: [...correnti, { id: op.id, nome: op.nome, cognome: op.cognome }],
                });
              }}
            >
              <option value="">— Aggiungi supplente —</option>
              {organicoFiltrato
                .filter(op => op.id !== parseInt(form.id_op_monitoraggio))
                .filter(op => !(Array.isArray(form.supplenti) && form.supplenti.some(sp => sp.id === op.id)))
                .map(op => (
                  <option key={op.id} value={op.id}>{op.cognome} {op.nome}</option>
                ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Verifica</label>
            <select style={inputStyle} value={form.id_op_verifica || ''}
              onChange={e => setForm({ ...form, id_op_verifica: e.target.value })}>
              <option value=''>— Nessuno —</option>
              {organicoFiltrato.map(op => (
                <option key={op.id} value={op.id}>{op.cognome} {op.nome}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Verificatori aggiuntivi <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale, multipli)</span></label>
            {Array.isArray(form.verificatori_aggiuntivi) && form.verificatori_aggiuntivi.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {form.verificatori_aggiuntivi.map(vp => (
                  <span key={vp.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 8px 4px 10px', borderRadius: 999,
                    background: LT.verdeSoft, color: LT.verde,
                    border: `1px solid #bbf7d0`,
                    fontSize: 12.5, fontWeight: 600,
                  }}>
                    {vp.cognome} {vp.nome}
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        verificatori_aggiuntivi: form.verificatori_aggiuntivi.filter(x => x.id !== vp.id),
                      })}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: LT.verde, padding: 0, lineHeight: 1, fontSize: 14, fontWeight: 700,
                      }}
                      title="Rimuovi verificatore"
                    >×</button>
                  </span>
                ))}
              </div>
            )}
            <select
              style={inputStyle}
              value=""
              onChange={e => {
                const id = parseInt(e.target.value);
                if (!Number.isFinite(id)) return;
                const op = organicoFiltrato.find(o => o.id === id);
                if (!op) return;
                const correnti = Array.isArray(form.verificatori_aggiuntivi) ? form.verificatori_aggiuntivi : [];
                setForm({
                  ...form,
                  verificatori_aggiuntivi: [...correnti, { id: op.id, nome: op.nome, cognome: op.cognome }],
                });
              }}
            >
              <option value="">— Aggiungi verificatore aggiuntivo —</option>
              {organicoFiltrato
                .filter(op => op.id !== parseInt(form.id_op_verifica))
                .filter(op => !(Array.isArray(form.verificatori_aggiuntivi) && form.verificatori_aggiuntivi.some(vp => vp.id === op.id)))
                .map(op => (
                  <option key={op.id} value={op.id}>{op.cognome} {op.nome}</option>
                ))}
            </select>
          </div>
        </Card>

        {/* Note */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Note</label>
          <textarea style={textareaStyle} value={form.note || ''}
            onChange={e => setForm({ ...form, note: e.target.value })}
            rows={2}
            placeholder="Note opzionali..." />
        </Card>

        {/* Stato obsoleto (solo modifica) */}
        {form.id && (
          <Card pad={20} style={{ marginBottom: 14 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', borderRadius: LT.r.md,
              border: `1px solid ${LT.n150}`,
              background: form.obsoleto ? LT.rossoSoft : LT.verdeSoft,
              flexWrap: 'wrap',
            }}>
              <Chip
                kind={form.obsoleto ? 'rosso' : 'verde'}
                icon={form.obsoleto ? 'x' : 'check'}
                size="md"
              >
                {form.obsoleto ? 'Obsoleta' : 'Attiva'}
              </Chip>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.35 }}>
                  {form.obsoleto
                    ? 'Non compare più nei monitoraggi. I dati storici restano consultabili.'
                    : 'Compare nei monitoraggi secondo la frequenza impostata.'}
                </div>
              </div>
              <Btn
                kind={form.obsoleto ? 'success' : 'outline'}
                size="sm"
                icon={form.obsoleto ? 'check' : 'x'}
                onClick={async () => {
                  if (!form.obsoleto) {
                    const ok = await chiediConferma(
                      'Rendere obsoleta questa attività?',
                      'Verrà disattivata e non apparirà più nei monitoraggi. I dati storici restano consultabili.'
                    );
                    if (!ok) return;
                  }
                  setForm({ ...form, obsoleto: !form.obsoleto });
                }}
              >
                {form.obsoleto ? 'Riattiva' : 'Rendi obsoleta'}
              </Btn>
            </div>
          </Card>
        )}

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="outline" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva attività'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const ambitiDisponibili = [...new Set(lista.map(s => s.ambito || 'Altro'))];
  const q = cerca.toLowerCase();
  const filtrati = lista.filter(s => {
    if (filtroStato === 'attive'   && s.obsoleto)  return false;
    if (filtroStato === 'obsolete' && !s.obsoleto) return false;
    if (filtroAmbito !== 'tutti' && (s.ambito || 'Altro') !== filtroAmbito) return false;
    return `${s.oggetto || ''} ${s.tipo_attivita || ''}`.toLowerCase().includes(q);
  });
  const countAttive   = lista.filter(s => !s.obsoleto).length;
  const countObsolete = lista.filter(s =>  s.obsoleto).length;

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Scadenziario
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Programmazione attività ricorrenti e responsabili
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({})}>
            Nuova attività
          </Btn>
        </div>
      </div>

      {/* Filtri */}
      <Card pad={14} style={{ marginBottom: 14 }}>
        {/* Segmented 3-way: Attive / Obsolete / Tutte */}
        <div style={{
          display: 'flex', gap: 0, marginBottom: 10,
          padding: 3, borderRadius: LT.r.md,
          background: LT.n100, border: `1px solid ${LT.n150}`,
        }}>
          {[
            { key: 'attive',   label: 'Attive',   count: countAttive   },
            { key: 'obsolete', label: 'Obsolete', count: countObsolete },
            { key: 'tutte',    label: 'Tutte',    count: lista.length  },
          ].map(opt => {
            const on = filtroStato === opt.key;
            return (
              <button key={opt.key} onClick={() => setFiltroStato(opt.key)} style={{
                flex: 1, padding: '7px 10px', borderRadius: LT.r.sm,
                border: 'none',
                background: on ? '#fff' : 'transparent',
                color: on ? LT.n900 : LT.n500,
                fontSize: 12.5, fontWeight: on ? 700 : 500,
                cursor: 'pointer', fontFamily: LT.sans,
                boxShadow: on ? LT.sh1 : 'none',
                transition: 'all .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {opt.label}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '1px 6px', borderRadius: LT.r.full,
                  background: on ? LT.n100 : 'transparent',
                  color: on ? LT.n600 : LT.n400,
                }}>{opt.count}</span>
              </button>
            );
          })}
        </div>

        {/* Ricerca con icona */}
        <div style={{ position: 'relative', marginBottom: ambitiDisponibili.length > 1 ? 10 : 0 }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, display: 'flex',
          }}>
            <Ico d={I.search} size={15} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per oggetto o tipo attività..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>

        {/* Filtro ambiti */}
        {ambitiDisponibili.length > 1 && (
          <div style={{
            display: 'flex', gap: 6, overflowX: 'auto',
            paddingBottom: 4, WebkitOverflowScrolling: 'touch',
          }}>
            {['tutti', ...ambitiDisponibili].map(a => {
              const attivo = filtroAmbito === a;
              return (
                <button key={a} onClick={() => setFiltroAmbito(a)} style={{
                  flexShrink: 0, padding: '6px 12px', borderRadius: LT.r.full,
                  border: `1px solid ${attivo ? LT.blu : LT.n200}`,
                  background: attivo ? LT.blu : '#fff',
                  color: attivo ? '#fff' : LT.n700,
                  fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: LT.sans,
                  transition: 'all .15s',
                }}>
                  {a === 'tutti' ? 'Tutti' : a}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Conteggio */}
      <div style={{
        fontSize: 11, fontWeight: 700, color: LT.n500,
        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
      }}>
        {filtrati.length} {filtrati.length === 1 ? 'attività' : 'attività'}
        {filtroAmbito !== 'tutti' && ` in ${filtroAmbito}`}
        {cerca && ` per "${cerca}"`}
      </div>

      {/* Lista */}
      {filtrati.length === 0 ? (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.lg,
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.calendar} size={20} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessuna attività programmata'}
          </div>
        </Card>
      ) : (
        filtrati.map(s => {
          const cat = catFor(s.ambito);
          return (
            <Card
              key={s.id}
              pad={14}
              hover
              onClick={() => setForm({ ...s })}
              style={{
                marginBottom: 10,
                opacity: s.obsoleto ? 0.55 : 1,
                borderLeft: `4px solid ${s.obsoleto ? LT.n300 : cat.c}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                {/* Tile ambito */}
                <div style={{
                  width: 40, height: 40, borderRadius: LT.r.md,
                  background: cat.bg, color: cat.c,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Ico d={I[cat.icon] || I.list} size={18} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Riga top: oggetto + chip freq/obsoleto */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    flexWrap: 'wrap', marginBottom: 4,
                  }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>
                      {s.oggetto}
                    </div>
                    {s.obsoleto && (
                      <Chip kind="rosso" icon="x" size="sm">Obsoleto</Chip>
                    )}
                    <Chip
                      kind={s.frequenza_gg ? 'blu' : 'neutro'}
                      icon={s.frequenza_gg ? 'repeat' : 'edit'}
                      size="sm"
                    >
                      {s.frequenza_gg ? `Ogni ${s.frequenza_gg}gg` : 'Manuale'}
                    </Chip>
                    {s.frequenza_gg === 1 && (s.salta_sabato || s.salta_domenica) && (
                      <Chip kind="neutro" icon="calendar" size="sm">
                        {s.salta_sabato && s.salta_domenica
                          ? 'No sab/dom'
                          : s.salta_sabato ? 'No sabato' : 'No domenica'}
                      </Chip>
                    )}
                  </div>

                  {/* Tipo attività */}
                  <div style={{ fontSize: 12.5, color: LT.n600, marginBottom: 6 }}>
                    {s.collocazione && <>{s.collocazione} <span style={{ color: LT.n300 }}>·</span> </>}
                    <span style={{ fontWeight: 500 }}>{s.tipo_attivita}</span>
                    {s.sub_attivita && <> <span style={{ color: LT.n300 }}>·</span> {s.sub_attivita}</>}
                  </div>

                  {/* Limiti */}
                  {(s.valore_min !== null || s.valore_max !== null) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      {s.valore_min !== null && (
                        <Chip kind="ciano" icon="arrowDown" size="sm">
                          Min {s.valore_min}
                        </Chip>
                      )}
                      {s.valore_max !== null && (
                        <Chip kind="ciano" icon="trend" size="sm">
                          Max {s.valore_max}
                        </Chip>
                      )}
                    </div>
                  )}

                  {/* Responsabili */}
                  <div style={{
                    display: 'flex', gap: 12, flexWrap: 'wrap',
                    fontSize: 12, color: LT.n500,
                    borderTop: `1px solid ${LT.n100}`, paddingTop: 6, marginTop: 4,
                  }}>
                    {s.op_monitoraggio ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.user} size={12} color={LT.blu} />
                        {s.op_monitoraggio}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontStyle: 'italic' }}>
                        <Ico d={I.user} size={12} color={LT.n300} />
                        Nessun responsabile
                      </div>
                    )}
                    {Array.isArray(s.supplenti) && s.supplenti.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.users} size={12} color={LT.n400} />
                        {s.supplenti.map(sp => `${sp.cognome} ${sp.nome}`).join(', ')}
                      </div>
                    )}
                    {s.op_verifica && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.checkCircle} size={12} color={LT.verde} />
                        {s.op_verifica}
                        {Array.isArray(s.verificatori_aggiuntivi) && s.verificatori_aggiuntivi.length > 0 && (
                          <span style={{ color: LT.n500, fontWeight: 500 }}>
                            {' '}+ {s.verificatori_aggiuntivi.map(v => `${v.cognome} ${v.nome}`).join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <Ico d={I.chevronR} size={16} color={LT.n300} />
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}

function GestioneAmbiti({ operatore, onBack }) {
  const [ambiti,     setAmbiti]     = useState([]);
  const [tipi,       setTipi]       = useState([]);
  const [formAmbito, setFormAmbito] = useState(null);
  const [formTipo,   setFormTipo]   = useState(null);
  const [salvando,   setSalvando]   = useState(false);
  const [vista,      setVista]      = useState('ambiti'); // 'ambiti' | 'tipi'
  const [errore,     setErrore]     = useState('');

  useEffect(() => {
    caricaTutto();
  }, []);

  const caricaTutto = async () => {
    const [a, t] = await Promise.all([
      apiFetch(`${API}/admin/ambiti`).then(r => r.json()),
      apiFetch(`${API}/admin/tipi-attivita`).then(r => r.json()),
    ]);
    setAmbiti(a);
    setTipi(t);
  };

  const handleSalvaAmbito = async () => {
    if (!formAmbito.descrizione) { setErrore('Descrizione obbligatoria'); return; }
    setErrore('');
    setSalvando(true);
    try {
      const url    = formAmbito.id ? `${API}/admin/ambiti/${formAmbito.id}` : `${API}/admin/ambiti`;
      const method = formAmbito.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descrizione: formAmbito.descrizione })
      });
      if (!res.ok) throw new Error();
      setFormAmbito(null);
      await caricaTutto();
      toast('✓ Salvato!');
    } catch { setErrore('Errore nel salvataggio. Riprova.'); }
    setSalvando(false);
  };

  const handleSalvaTipo = async () => {
    if (!formTipo.descrizione || !formTipo.id_ambito) { setErrore('Ambito e descrizione obbligatori'); return; }
    setErrore('');
    setSalvando(true);
    try {
      const url    = formTipo.id ? `${API}/admin/tipi-attivita/${formTipo.id}` : `${API}/admin/tipi-attivita`;
      const method = formTipo.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAmbito:         parseInt(formTipo.id_ambito),
          descrizione:      formTipo.descrizione,
          note:             formTipo.note || null,
          registroSpeciale: formTipo.registro_speciale || null
        })
      });
      if (!res.ok) throw new Error();
      setFormTipo(null);
      await caricaTutto();
      toast('✓ Salvato!');
    } catch { setErrore('Errore nel salvataggio. Riprova.'); }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 60, fontFamily: LT.sans };

  // ── FORM AMBITO ─────────────────────────────────────────────────────────
  if (formAmbito !== null) return (
    <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          Ambiti · {formAmbito.id ? 'Modifica' : 'Nuovo'}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              {formAmbito.id ? 'Modifica ambito' : 'Nuovo ambito'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Area di attività (es. temperature, pulizie, manutenzioni)
            </div>
          </div>
          <Btn kind="outline" icon="x" onClick={() => { setFormAmbito(null); setErrore(''); }}>
            Annulla
          </Btn>
        </div>
      </div>

      <Card pad={20} style={{ marginBottom: 14 }}>
        <div style={{ marginBottom: 4 }}>
          <label style={labelStyle}>Descrizione *</label>
          <input style={inputStyle} value={formAmbito.descrizione || ''}
            onChange={e => setFormAmbito({ ...formAmbito, descrizione: e.target.value })}
            placeholder="es. Temperature, Pulizie, Manutenzioni..." />
        </div>
      </Card>

      {errore && (
        <div style={{
          background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
        }}>
          <Ico d={I.alert} size={14} /> {errore}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Btn kind="outline" onClick={() => { setFormAmbito(null); setErrore(''); }}>Annulla</Btn>
        <div style={{ flex: 1 }}>
          <Btn kind="primary" block icon="check" onClick={handleSalvaAmbito} disabled={salvando}>
            {salvando ? 'Salvataggio…' : 'Salva ambito'}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── FORM TIPO ATTIVITÀ ──────────────────────────────────────────────────
  if (formTipo !== null) return (
    <div style={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          Tipi attività · {formTipo.id ? 'Modifica' : 'Nuovo'}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              {formTipo.id ? 'Modifica tipo attività' : 'Nuovo tipo attività'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Azione specifica all'interno di un ambito
            </div>
          </div>
          <Btn kind="outline" icon="x" onClick={() => { setFormTipo(null); setErrore(''); }}>
            Annulla
          </Btn>
        </div>
      </div>

      <Card pad={20} style={{ marginBottom: 14 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Ambito *</label>
          <select style={inputStyle} value={formTipo.id_ambito || ''}
            onChange={e => setFormTipo({ ...formTipo, id_ambito: e.target.value })}>
            <option value=''>— Seleziona ambito —</option>
            {ambiti.map(a => <option key={a.id} value={a.id}>{a.descrizione}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Descrizione *</label>
          <input style={inputStyle} value={formTipo.descrizione || ''}
            onChange={e => setFormTipo({ ...formTipo, descrizione: e.target.value })}
            placeholder="es. Rilevamento temperatura, Sanificazione..." />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Note</label>
          <textarea style={textareaStyle} value={formTipo.note || ''}
            onChange={e => setFormTipo({ ...formTipo, note: e.target.value })}
            rows={2}
            placeholder="Note opzionali..." />
        </div>

        <div>
          <label style={labelStyle}>Registro speciale</label>
          <select style={inputStyle} value={formTipo.registro_speciale || ''}
            onChange={e => setFormTipo({ ...formTipo, registro_speciale: e.target.value || null })}>
            <option value="">— Nessuno —</option>
            <option value="TARATURA">TARATURA</option>
            <option value="MANUTENZIONE">MANUTENZIONE</option>
            <option value="ANALISI">ANALISI</option>
            <option value="VERIFICA">VERIFICA</option>
          </select>
          <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ico d={I.book} size={12} color={LT.n400} />
            Opzionale · marca questo tipo per la stampa in registri dedicati
          </div>
        </div>
      </Card>

      {errore && (
        <div style={{
          background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
        }}>
          <Ico d={I.alert} size={14} /> {errore}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Btn kind="outline" onClick={() => { setFormTipo(null); setErrore(''); }}>Annulla</Btn>
        <div style={{ flex: 1 }}>
          <Btn kind="primary" block icon="check" onClick={handleSalvaTipo} disabled={salvando}>
            {salvando ? 'Salvataggio…' : 'Salva tipo attività'}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── LISTA (con tab switcher) ────────────────────────────────────────────
  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Ambiti e tipi attività
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Tassonomia delle aree di controllo HACCP
            </div>
          </div>
          <Btn
            kind="primary"
            icon="plus"
            onClick={() => vista === 'ambiti' ? setFormAmbito({}) : setFormTipo({})}
          >
            {vista === 'ambiti' ? 'Nuovo ambito' : 'Nuovo tipo'}
          </Btn>
        </div>
      </div>

      {/* Tab switcher D2 */}
      <div style={{
        display: 'flex', gap: 4, padding: 4,
        background: LT.n100, borderRadius: LT.r.md, marginBottom: 14,
      }}>
        {[
          { key: 'ambiti', label: 'Ambiti', icon: 'folder', count: ambiti.length },
          { key: 'tipi',   label: 'Tipi attività', icon: 'list', count: tipi.length },
        ].map(tab => {
          const attivo = vista === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setVista(tab.key)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: LT.r.sm,
                border: 'none',
                background: attivo ? '#fff' : 'transparent',
                color: attivo ? LT.n900 : LT.n500,
                fontSize: 13, fontWeight: 600, fontFamily: LT.sans,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                boxShadow: attivo ? LT.sh1 : 'none',
                transition: 'all .15s',
              }}
            >
              <Ico d={I[tab.icon]} size={14} />
              {tab.label}
              <span style={{
                fontSize: 11, padding: '1px 7px', borderRadius: LT.r.full,
                background: attivo ? LT.bluSoft : 'transparent',
                color: attivo ? LT.blu : LT.n500, fontWeight: 700,
              }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Vista AMBITI */}
      {vista === 'ambiti' && (
        <>
          {ambiti.length === 0 ? (
            <Card pad={24} style={{ textAlign: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: LT.r.lg,
                background: LT.n100, color: LT.n400,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 10px',
              }}>
                <Ico d={I.folder} size={20} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
                Nessun ambito definito
              </div>
            </Card>
          ) : (
            ambiti.map(a => {
              const cat = catFor(a.descrizione);
              const numTipi = tipi.filter(t => t.id_ambito === a.id).length;
              return (
                <Card
                  key={a.id}
                  pad={14}
                  hover
                  onClick={() => setFormAmbito({ ...a })}
                  style={{
                    marginBottom: 10,
                    borderLeft: `4px solid ${cat.c}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: LT.r.md,
                      background: cat.bg, color: cat.c,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I[cat.icon] || I.folder} size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900, marginBottom: 2 }}>
                        {a.descrizione}
                      </div>
                      <div style={{ fontSize: 12, color: LT.n500, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.list} size={12} color={LT.n400} />
                        {numTipi} {numTipi === 1 ? 'tipo' : 'tipi'} attività
                      </div>
                    </div>
                    <Ico d={I.chevronR} size={16} color={LT.n300} />
                  </div>
                </Card>
              );
            })
          )}
        </>
      )}

      {/* Vista TIPI ATTIVITÀ */}
      {vista === 'tipi' && (
        <>
          {tipi.length === 0 ? (
            <Card pad={24} style={{ textAlign: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: LT.r.lg,
                background: LT.n100, color: LT.n400,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 10px',
              }}>
                <Ico d={I.list} size={20} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
                Nessun tipo attività definito
              </div>
            </Card>
          ) : (
            // Raggruppo per ambito
            (() => {
              const gruppi = {};
              tipi.forEach(t => {
                const k = t.ambito || 'Altro';
                if (!gruppi[k]) gruppi[k] = [];
                gruppi[k].push(t);
              });
              return Object.entries(gruppi).map(([ambito, lista]) => {
                const cat = catFor(ambito);
                return (
                  <div key={ambito} style={{ marginBottom: 18 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: LT.r.sm,
                        background: cat.bg, color: cat.c,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Ico d={I[cat.icon] || I.folder} size={12} />
                      </div>
                      <div style={{
                        fontSize: 11, fontWeight: 700, color: LT.n700,
                        textTransform: 'uppercase', letterSpacing: '.06em',
                      }}>
                        {ambito}
                      </div>
                      <div style={{
                        fontSize: 11, padding: '1px 7px', borderRadius: LT.r.full,
                        background: LT.n100, color: LT.n500, fontWeight: 600,
                      }}>
                        {lista.length}
                      </div>
                    </div>
                    {lista.map(t => (
                      <Card
                        key={t.id}
                        pad={12}
                        hover
                        onClick={() => setFormTipo({ ...t })}
                        style={{
                          marginBottom: 8,
                          borderLeft: `3px solid ${cat.c}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2,
                            }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: LT.n900 }}>
                                {t.descrizione}
                              </div>
                              {t.registro_speciale && (
                                <Chip kind="viola" icon="book" size="sm">
                                  {t.registro_speciale}
                                </Chip>
                              )}
                            </div>
                            {t.note && (
                              <div style={{ fontSize: 12, color: LT.n500, lineHeight: 1.4 }}>
                                {t.note}
                              </div>
                            )}
                          </div>
                          <Ico d={I.chevronR} size={16} color={LT.n300} />
                        </div>
                      </Card>
                    ))}
                  </div>
                );
              });
            })()
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// GESTIONE CATEGORIE NC (solo master)
// Tassonomia globale delle Non Conformità: descrizione, descrizione_estesa,
// ordine, flag attivo. Pattern simile a GestioneAmbiti ma con filtro
// Attive/Archiviate/Tutte (le categorie non si cancellano, si archiviano).
// ═══════════════════════════════════════════════════════════════

function GestioneCategorieNc({ operatore, onBack }) {
  const [lista,       setLista]       = useState([]);
  const [form,        setForm]        = useState(null);
  const [salvando,    setSalvando]    = useState(false);
  const [errore,      setErrore]      = useState('');
  const [cerca,       setCerca]       = useState('');
  const [filtroStato, setFiltroStato] = useState('attive'); // 'attive' | 'archiviate' | 'tutte'

  useEffect(() => { carica(); }, []);

  const carica = async () => {
    const res  = await apiFetch(`${API}/admin/categorie-nc`);
    const data = await res.json().catch(() => []);
    setLista(Array.isArray(data) ? data : []);
  };

  const handleSalva = async () => {
    if (!form.descrizione || !form.descrizione.trim()) {
      setErrore('Descrizione obbligatoria');
      return;
    }
    setErrore('');
    setSalvando(true);
    try {
      const url    = form.id ? `${API}/admin/categorie-nc/${form.id}` : `${API}/admin/categorie-nc`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          descrizione:       form.descrizione.trim(),
          descrizioneEstesa: form.descrizione_estesa || null,
          ordine:            form.ordine ? parseInt(form.ordine) : 0,
          attivo:            form.attivo !== false,
        })
      });
      if (res.status === 409) {
        setErrore('Esiste già una categoria con questa descrizione.');
        setSalvando(false);
        return;
      }
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Categoria salvata!');
    } catch {
      setErrore('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: LT.sans };

  // ── FORM ────────────────────────────────────────────────────────────────
  if (form !== null) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Categorie NC · {form.id ? 'Modifica' : 'Nuova'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
              }}>
                {form.id ? (form.descrizione || 'Categoria') : 'Nuova categoria NC'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {form.id ? 'Modifica la categoria e la sua definizione operativa' : 'Registra una nuova categoria per le non conformità'}
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.violaSoft, color: LT.viola,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.layers} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati categoria</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Descrizione *</label>
            <input style={inputStyle} value={form.descrizione || ''}
              onChange={e => setForm({ ...form, descrizione: e.target.value })}
              placeholder="es. Corpi estranei" />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Definizione operativa</label>
            <textarea style={textareaStyle} value={form.descrizione_estesa || ''}
              onChange={e => setForm({ ...form, descrizione_estesa: e.target.value })}
              placeholder="Descrizione estesa che aiuta l'operatore a scegliere correttamente questa categoria. Viene mostrata sotto al select durante la compilazione di una NC." />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4 }}>
              Spiega chiaramente in quali casi usare questa categoria e in quali no.
            </div>
          </div>

          <div style={{ marginBottom: 0 }}>
            <label style={labelStyle}>Ordine di visualizzazione</label>
            <input type="number" style={inputStyle} value={form.ordine ?? 0}
              onChange={e => setForm({ ...form, ordine: e.target.value })}
              placeholder="0" />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4 }}>
              Numero usato per l'ordinamento nelle liste (più basso = più in alto).
            </div>
          </div>
        </Card>

        {/* Stato (solo modifica) */}
        {form.id && (
          <Card pad={20} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 34, height: 34, borderRadius: LT.r.md,
                background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.shield} size={16} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Stato</div>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', borderRadius: LT.r.md,
              border: `1px solid ${LT.n150}`,
              background: form.attivo !== false ? LT.verdeSoft : LT.rossoSoft,
            }}>
              <Chip
                kind={form.attivo !== false ? 'verde' : 'rosso'}
                icon={form.attivo !== false ? 'check' : 'x'}
                size="md"
              >
                {form.attivo !== false ? 'Attiva' : 'Archiviata'}
              </Chip>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.35 }}>
                  {form.attivo !== false
                    ? 'Selezionabile dagli utenti nella compilazione di nuove NC.'
                    : 'Non più selezionabile per nuove NC. Lo storico resta filtrabile.'}
                </div>
              </div>
              <Btn
                kind={form.attivo === false ? 'success' : 'outline'}
                size="sm"
                icon={form.attivo === false ? 'check' : 'x'}
                onClick={async () => {
                  if (form.attivo !== false) {
                    const ok = await chiediConferma(
                      'Archiviare questa categoria?',
                      `"${form.descrizione}" non sarà più selezionabile per nuove NC. Le NC già categorizzate restano invariate e filtrabili.`
                    );
                    if (!ok) return;
                    setForm({ ...form, attivo: false });
                  } else {
                    setForm({ ...form, attivo: true });
                  }
                }}
              >
                {form.attivo === false ? 'Riattiva' : 'Archivia'}
              </Btn>
            </div>
          </Card>
        )}

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="outline" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva categoria'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const q = cerca.toLowerCase();
  const filtrati = lista.filter(c => {
    if (filtroStato === 'attive'     && c.attivo === false) return false;
    if (filtroStato === 'archiviate' && c.attivo !== false) return false;
    return `${c.descrizione || ''} ${c.descrizione_estesa || ''}`.toLowerCase().includes(q);
  });
  const countAttive      = lista.filter(c => c.attivo !== false).length;
  const countArchiviate  = lista.filter(c => c.attivo === false).length;

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Categorie NC
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Tassonomia globale delle non conformità (visibile a tutte le aziende)
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({ attivo: true, ordine: 0 })}>
            Nuova categoria
          </Btn>
        </div>
      </div>

      <Card pad={14} style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', gap: 0, marginBottom: 10,
          padding: 3, borderRadius: LT.r.md,
          background: LT.n100, border: `1px solid ${LT.n150}`,
        }}>
          {[
            { key: 'attive',     label: 'Attive',     count: countAttive     },
            { key: 'archiviate', label: 'Archiviate', count: countArchiviate },
            { key: 'tutte',      label: 'Tutte',      count: lista.length    },
          ].map(opt => {
            const on = filtroStato === opt.key;
            return (
              <button key={opt.key} onClick={() => setFiltroStato(opt.key)} style={{
                flex: 1, padding: '7px 10px', borderRadius: LT.r.sm,
                border: 'none',
                background: on ? '#fff' : 'transparent',
                color: on ? LT.n900 : LT.n500,
                fontSize: 12.5, fontWeight: on ? 700 : 500,
                cursor: 'pointer', fontFamily: LT.sans,
                boxShadow: on ? LT.sh1 : 'none',
                transition: 'all .15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {opt.label}
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '1px 6px', borderRadius: LT.r.full,
                  background: on ? LT.n100 : 'transparent',
                  color: on ? LT.n600 : LT.n400,
                }}>{opt.count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, display: 'flex',
          }}>
            <Ico d={I.search} size={15} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per descrizione o definizione..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>
      </Card>

      <div style={{
        fontSize: 11, fontWeight: 700, color: LT.n500,
        textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
      }}>
        {filtrati.length} {filtrati.length === 1 ? 'categoria' : 'categorie'}
        {cerca && ` per "${cerca}"`}
      </div>

      {filtrati.length === 0 ? (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.lg,
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.layers} size={20} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessuna categoria in questa vista'}
          </div>
        </Card>
      ) : (
        filtrati.map(c => (
          <Card
            key={c.id}
            pad={14}
            hover
            onClick={() => setForm({ ...c })}
            style={{
              marginBottom: 10,
              opacity: c.attivo === false ? 0.65 : 1,
              borderLeft: `4px solid ${c.attivo === false ? LT.n300 : LT.viola}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.violaSoft, color: LT.viola,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Ico d={I.layers} size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2,
                }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>
                    {c.descrizione}
                  </div>
                  {c.attivo === false && (
                    <Chip kind="rosso" icon="x" size="sm">Archiviata</Chip>
                  )}
                </div>
                {c.descrizione_estesa && (
                  <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.4,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {c.descrizione_estesa}
                  </div>
                )}
              </div>
              <Ico d={I.chevronR} size={16} color={LT.n300} />
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

function GestioneAziende({ onBack }) {
  const [lista,              setLista]              = useState([]);
  const [form,               setForm]               = useState(null);
  const [salvando,           setSalvando]           = useState(false);
  const [errore,             setErrore]             = useState('');
  const [cerca,              setCerca]              = useState('');
  const [eliminaModal,       setEliminaModal]       = useState(null); // { id, ragione_sociale }
  const [confermaTestoElim,  setConfermaTestoElim]  = useState('');
  const [eliminando,         setEliminando]         = useState(false);
  const [erroreElimina,      setErroreElimina]      = useState('');

  useEffect(() => {
    caricaAziende();
  }, []);

  const caricaAziende = async () => {
    const res  = await apiFetch(`${API}/admin/aziende`);
    const data = await res.json();
    setLista(data);
  };

  const handleSalva = async () => {
    if (!form.ragione_sociale) { setErrore('Ragione sociale obbligatoria'); return; }
    setErrore('');
    setSalvando(true);
    try {
      const url    = form.id ? `${API}/admin/aziende/${form.id}` : `${API}/admin/aziende`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ragioneSociale:       form.ragione_sociale,
          indirizzo:            form.indirizzo || null,
          citta:                form.citta || null,
          telefono:             form.telefono || null,
          email:                form.email || null,
          codice:               form.codice || null,
          attivo:               form.attivo !== false,
          piano:                ['START', 'TRACE', 'MASTER'].includes(form.piano) ? form.piano : 'START',
        })
      });
      if (!res.ok) throw new Error();
      setForm(null);
      setErrore('');
      await caricaAziende();
      toast('✓ Azienda salvata!');
    } catch { setErrore('Errore nel salvataggio. Riprova.'); }
    setSalvando(false);
  };

  const apriEliminaModal = (az) => {
    setEliminaModal(az);
    setConfermaTestoElim('');
    setErroreElimina('');
  };

  const chiudiEliminaModal = () => {
    setEliminaModal(null);
    setConfermaTestoElim('');
    setErroreElimina('');
  };

  const handleElimina = async () => {
    if (!eliminaModal || confermaTestoElim !== eliminaModal.ragione_sociale) return;
    setEliminando(true);
    setErroreElimina('');
    try {
      const res  = await apiFetch(`${API}/admin/aziende/${eliminaModal.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) { setErroreElimina(data.errore || 'Errore durante l\'eliminazione'); setEliminando(false); return; }
      setLista(prev => prev.filter(a => a.id !== eliminaModal.id));
      chiudiEliminaModal();
      toast(`Azienda "${data.azienda}" eliminata definitivamente`);
    } catch { setErroreElimina('Errore di connessione. Riprova.'); }
    setEliminando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };

  // ── Chip piano (START / TRACE / MASTER) ────────────────────────────────
  const planChip = (piano) => {
    const p = String(piano || 'START').toUpperCase();
    if (p === 'MASTER') return <Chip kind="viola" icon="shield">MASTER</Chip>;
    if (p === 'TRACE')  return <Chip kind="blu"   icon="qr">TRACE</Chip>;
    return <Chip kind="neutro" icon="home">START</Chip>;
  };

  // ── Chip stato abbonamento (attivo / prova / sola_lettura / cancellato) ────
  const statoChip = (s) => {
    const m = {
      attivo:       { kind: 'verde',   label: 'Attivo' },
      prova:        { kind: 'blu',     label: 'In prova' },
      sola_lettura: { kind: 'arancio', label: 'Sola lettura' },
      cancellato:   { kind: 'rosso',   label: 'Cancellato' },
    }[s];
    if (!m) return null;
    return <Chip kind={m.kind}>{m.label}</Chip>;
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const giorniProvaResidui = (data_scadenza) => {
    if (!data_scadenza) return null;
    const ms = new Date(data_scadenza).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  };

  // ── Estendi periodo di prova (master) ─────────────────────────────────────
  const estendiProva = async (idAzienda, giorni) => {
    if (!idAzienda || !Number.isInteger(giorni) || giorni === 0) return;
    try {
      const res = await apiFetch(`${API}/admin/aziende/${idAzienda}/estendi-prova`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giorni }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrore(data.errore || 'Errore aggiornamento periodo di prova.');
        return;
      }
      // Aggiorna lista e form locali con i nuovi valori
      setLista(prev => prev.map(a => a.id === idAzienda
        ? { ...a, data_scadenza_prova: data.data_scadenza_prova, stato_abbonamento: data.stato_abbonamento }
        : a));
      if (form && form.id === idAzienda) {
        setForm(f => ({ ...f, data_scadenza_prova: data.data_scadenza_prova, stato_abbonamento: data.stato_abbonamento }));
      }
      toast(`✓ Periodo di prova ${giorni > 0 ? 'esteso' : 'ridotto'} di ${Math.abs(giorni)} giorni`);
    } catch (e) {
      setErrore('Errore di connessione.');
    }
  };

  // ── Estendi abbonamento manuale (out-of-Stripe) ──────────────────────────
  const estendiAbbonamento = async (idAzienda, giorni) => {
    if (!idAzienda || !Number.isInteger(giorni) || giorni === 0) return;
    try {
      const res = await apiFetch(`${API}/admin/aziende/${idAzienda}/estendi-abbonamento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giorni }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrore(data.errore || 'Errore aggiornamento periodo di abbonamento.');
        return;
      }
      setLista(prev => prev.map(a => a.id === idAzienda
        ? { ...a, data_scadenza_abbonamento: data.data_scadenza_abbonamento, stato_abbonamento: data.stato_abbonamento }
        : a));
      if (form && form.id === idAzienda) {
        setForm(f => ({ ...f, data_scadenza_abbonamento: data.data_scadenza_abbonamento, stato_abbonamento: data.stato_abbonamento }));
      }
      toast(`✓ Abbonamento ${giorni > 0 ? 'esteso' : 'ridotto'} di ${Math.abs(giorni)} giorni`);
    } catch (e) {
      setErrore('Errore di connessione.');
    }
  };

  // ── FORM (nuovo / modifica) ─────────────────────────────────────────────
  if (form !== null) {
    const oggiData = new Date();
    const _dataStr = oggiData.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>

        {/* Hero compatto */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            Aziende · {form.id ? 'Modifica' : 'Nuova'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {form.id ? (form.ragione_sociale || 'Azienda') : 'Nuova azienda'}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {form.id ? 'Modifica i dati dell\'azienda' : 'Inserisci i dati della nuova azienda'}
              </div>
            </div>
            <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>
              Annulla
            </Btn>
          </div>
        </div>

        {/* Dati anagrafici */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.building} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati anagrafici</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Ragione sociale *</label>
            <input style={inputStyle} value={form.ragione_sociale || ''}
              onChange={e => setForm({ ...form, ragione_sociale: e.target.value })}
              placeholder="Es. Rossi S.r.l." />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Indirizzo</label>
            <input style={inputStyle} value={form.indirizzo || ''}
              onChange={e => setForm({ ...form, indirizzo: e.target.value })}
              placeholder="Via e numero civico" />
          </div>

          <div>
            <label style={labelStyle}>Città</label>
            <input style={inputStyle} value={form.citta || ''}
              onChange={e => setForm({ ...form, citta: e.target.value })} />
          </div>
        </Card>

        {/* Contatti */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.violaSoft, color: LT.viola,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.mail} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Contatti</div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={labelStyle}>Telefono</label>
              <input style={inputStyle} value={form.telefono || ''}
                onChange={e => setForm({ ...form, telefono: e.target.value })}
                placeholder="+39 ..." />
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={form.email || ''}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="info@azienda.it" />
            </div>
          </div>
        </Card>

        {/* Logo */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.arancioSoft, color: LT.arancio,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.camera} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Logo aziendale</div>
              <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Immagine PNG/JPG, max 200px larghezza</div>
            </div>
          </div>

          {form.logo && (
            <div style={{
              padding: 12, background: LT.n50, border: `1px solid ${LT.n150}`,
              borderRadius: LT.r.md, marginBottom: 10, textAlign: 'center',
            }}>
              <img src={`${BACKEND}${form.logo}`} alt="logo"
                style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }} />
            </div>
          )}

          {form.id ? (
            <input type="file" accept="image/*"
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('logo', file);
                const res = await apiFetch(`${API}/uploads/logo-azienda/${form.id}`, {
                  method: 'POST',
                  body: fd
                });
                const data = await res.json();
                if (data.logo) setForm({ ...form, logo: data.logo });
              }}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', fontSize: 13, fontFamily: LT.sans,
                border: `1px dashed ${LT.n200}`, borderRadius: LT.r.md,
                background: LT.n50,
              }}
            />
          ) : (
            <div style={{
              fontSize: 12, color: LT.n500, padding: '10px 12px',
              background: LT.n50, border: `1px dashed ${LT.n200}`,
              borderRadius: LT.r.md, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Ico d={I.alert} size={13} color={LT.arancio} />
              Salva prima l'azienda per poter caricare il logo
            </div>
          )}
        </Card>

        {/* Accesso e piano */}
        <Card pad={20} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: LT.r.md,
              background: LT.verdeSoft, color: LT.verde,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.lock} size={16} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Accesso e piano</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Codice accesso *</label>
            <input
              style={{ ...inputStyle, fontFamily: LT.mono, textTransform: 'uppercase', letterSpacing: '.05em' }}
              value={form.codice || ''}
              onChange={e => setForm({ ...form, codice: e.target.value.toUpperCase() })}
              placeholder="DEMO, ROSSI2024..."
              maxLength={20} />
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.lock} size={12} color={LT.n400} />
              Usato dagli operatori per accedere (max 20 caratteri)
            </div>
          </div>

          {form.id && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Piano abbonamento</label>
              <select value={form.piano || 'START'} onChange={e => setForm({ ...form, piano: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="START">START — HACCP base</option>
                <option value="TRACE">TRACE — HACCP + Tracciabilità</option>
                <option value="MASTER">MASTER — TRACE + Amministrazione avanzata</option>
              </select>
              <div style={{ marginTop: 8 }}>{planChip(form.piano || 'START')}</div>
            </div>
          )}

          {form.id && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              background: form.attivo !== false ? LT.verdeSoft : LT.rossoSoft,
              border: `1px solid ${form.attivo !== false ? '#BBF7D0' : '#FECACA'}`,
              borderRadius: LT.r.md, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: LT.verde }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: form.attivo !== false ? '#166534' : '#991B1B' }}>
                  Azienda {form.attivo !== false ? 'attiva' : 'disattivata'}
                </div>
                <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>
                  {form.attivo !== false ? 'Gli operatori possono accedere' : 'Accesso bloccato per tutti gli utenti'}
                </div>
              </div>
            </label>
          )}
        </Card>

        {/* Abbonamento e prova — solo in modifica (azienda già esistente) */}
        {form.id && (
          <Card pad={20} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 34, height: 34, borderRadius: LT.r.md, background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
              }}>
                <Ico d={I.package} size={18} stroke={LT.verde} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: LT.navy }}>Abbonamento e prova</div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Stato corrente, periodo di prova e collegamenti Stripe.
                </div>
              </div>
            </div>

            {/* Riga: stato + scadenza + Stripe */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12, marginBottom: 16,
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Stato</div>
                <div>{statoChip(form.stato_abbonamento) || <Chip kind="neutro">—</Chip>}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Registrata</div>
                <div style={{ fontSize: 13.5, color: LT.n800, fontWeight: 600 }}>{fmtDate(form.data_registrazione)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Scadenza prova</div>
                <div style={{ fontSize: 13.5, color: LT.n800, fontWeight: 600 }}>
                  {fmtDate(form.data_scadenza_prova)}
                  {form.stato_abbonamento === 'prova' && form.data_scadenza_prova && (() => {
                    const g = giorniProvaResidui(form.data_scadenza_prova);
                    const c = g <= 3 ? LT.rosso : (g <= 7 ? LT.arancio : LT.n500);
                    return <span style={{ display: 'block', fontSize: 11.5, color: c, marginTop: 3, fontWeight: 600 }}>
                      {g >= 0 ? `${g} giorni residui` : `scaduta da ${Math.abs(g)} gg`}
                    </span>;
                  })()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Scadenza abbonamento (manuale)</div>
                <div style={{ fontSize: 13.5, color: LT.n800, fontWeight: 600 }}>
                  {form.has_stripe_subscription
                    ? <span style={{ color: LT.n400, fontWeight: 500 }}>n/a (gestito da Stripe)</span>
                    : (form.data_scadenza_abbonamento
                        ? <>
                            {fmtDate(form.data_scadenza_abbonamento)}
                            {(() => {
                              const g = giorniProvaResidui(form.data_scadenza_abbonamento);
                              const c = g <= 7 ? LT.rosso : (g <= 30 ? LT.arancio : LT.n500);
                              return <span style={{ display: 'block', fontSize: 11.5, color: c, marginTop: 3, fontWeight: 600 }}>
                                {g >= 0 ? `${g} giorni residui` : `scaduto da ${Math.abs(g)} gg`}
                              </span>;
                            })()}
                          </>
                        : <span style={{ color: LT.n400, fontWeight: 500 }}>Non impostato</span>
                      )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Stripe</div>
                <div style={{ fontSize: 13, color: LT.n700 }}>
                  {form.has_stripe_customer ? (
                    <a
                      href={`https://dashboard.stripe.com/${form.stripe_customer_id?.startsWith('cus_') ? '' : 'test/'}customers/${form.stripe_customer_id}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ color: LT.blu, fontWeight: 600, textDecoration: 'none' }}
                    >
                      Apri customer ↗
                    </a>
                  ) : <span style={{ color: LT.n400 }}>Non collegato</span>}
                </div>
              </div>
            </div>

            {/* Estendi prova rapido */}
            <div style={{
              padding: '12px 14px', background: LT.bluSoft, border: `1px solid ${LT.blu}22`,
              borderRadius: LT.r.md, marginBottom: 10,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: LT.navy, marginBottom: 8 }}>
                Estendi periodo di prova
              </div>
              <div style={{ fontSize: 12, color: LT.n600, marginBottom: 12, lineHeight: 1.5 }}>
                Aggiunge giorni di prova alla scadenza corrente. Se la prova è già scaduta, ricomincia da oggi.
                Riporta automaticamente lo stato a "in prova" se era in sola lettura.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {[7, 14, 30, 60].map(g => (
                  <Btn key={g} kind="soft" size="sm" onClick={() => estendiProva(form.id, g)}>
                    +{g} giorni
                  </Btn>
                ))}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                  <input
                    type="number"
                    placeholder="N"
                    min="-3650"
                    max="3650"
                    style={{
                      width: 70, padding: '7px 10px', fontSize: 13, fontFamily: LT.sans,
                      color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                      borderRadius: LT.r.sm, outline: 'none',
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v !== 0) { estendiProva(form.id, v); e.target.value = ''; }
                      }
                    }}
                    id={`est-prova-custom-${form.id}`}
                  />
                  <Btn kind="soft" size="sm" onClick={() => {
                    const el = document.getElementById(`est-prova-custom-${form.id}`);
                    const v = parseInt(el?.value || '', 10);
                    if (!isNaN(v) && v !== 0) { estendiProva(form.id, v); if (el) el.value = ''; }
                  }}>
                    Applica
                  </Btn>
                  <span style={{ fontSize: 11.5, color: LT.n500 }}>
                    giorni (negativo per ridurre)
                  </span>
                </div>
              </div>
            </div>

            {/* Estendi abbonamento manuale (out-of-Stripe) */}
            {form.has_stripe_subscription ? (
              <div style={{
                padding: '12px 14px', background: LT.n50, border: `1px dashed ${LT.n200}`,
                borderRadius: LT.r.md, fontSize: 12.5, color: LT.n500, lineHeight: 1.55,
              }}>
                <strong style={{ color: LT.n700 }}>Abbonamento Stripe attivo:</strong> la scadenza
                è gestita da Stripe (rinnovo automatico). Per modificare il piano o disdire, usa
                il <strong>Customer Portal</strong> dall'app cliente o cliccando "Apri customer ↗".
              </div>
            ) : (
              <div style={{
                padding: '12px 14px', background: LT.verdeSoft, border: `1px solid ${LT.verde}33`,
                borderRadius: LT.r.md,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: LT.navy, marginBottom: 8 }}>
                  Abbonamento manuale (fuori da Stripe)
                </div>
                <div style={{ fontSize: 12, color: LT.n600, marginBottom: 12, lineHeight: 1.5 }}>
                  Per clienti pagati a forfait (es. consulenza completa con software incluso).
                  Aggiunge giorni alla scadenza corrente; lo stato passa ad <strong>attivo</strong> se
                  la nuova data è in futuro, altrimenti a <strong>sola lettura</strong>.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {[30, 90, 180, 365].map(g => (
                    <Btn key={g} kind="soft" size="sm" onClick={() => estendiAbbonamento(form.id, g)}>
                      +{g} giorni
                    </Btn>
                  ))}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                    <input
                      type="number"
                      placeholder="N"
                      min="-3650"
                      max="3650"
                      style={{
                        width: 70, padding: '7px 10px', fontSize: 13, fontFamily: LT.sans,
                        color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                        borderRadius: LT.r.sm, outline: 'none',
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          const v = parseInt(e.target.value, 10);
                          if (!isNaN(v) && v !== 0) { estendiAbbonamento(form.id, v); e.target.value = ''; }
                        }
                      }}
                      id={`est-abb-custom-${form.id}`}
                    />
                    <Btn kind="soft" size="sm" onClick={() => {
                      const el = document.getElementById(`est-abb-custom-${form.id}`);
                      const v = parseInt(el?.value || '', 10);
                      if (!isNaN(v) && v !== 0) { estendiAbbonamento(form.id, v); if (el) el.value = ''; }
                    }}>
                      Applica
                    </Btn>
                    <span style={{ fontSize: 11.5, color: LT.n500 }}>
                      giorni (negativo per ridurre)
                    </span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {errore && (
          <div style={{
            padding: '10px 12px', background: LT.rossoSoft,
            border: `1px solid #FECACA`, borderRadius: LT.r.md,
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
          }}>
            <Ico d={I.alert} size={14} color={LT.rosso} />
            <span style={{ fontSize: 13, color: '#991B1B', fontWeight: 600 }}>{errore}</span>
          </div>
        )}

        <Btn kind="primary" icon="check" block onClick={handleSalva} disabled={salvando}>
          {salvando ? 'Salvataggio…' : (form.id ? 'Salva modifiche' : 'Crea azienda')}
        </Btn>
      </div>
    );
  }

  // ── LISTA ────────────────────────────────────────────────────────────────
  const q = cerca.trim().toLowerCase();
  const listaFiltrata = q
    ? lista.filter(a =>
        (a.ragione_sociale || '').toLowerCase().includes(q) ||
        (a.codice         || '').toLowerCase().includes(q) ||
        (a.citta          || '').toLowerCase().includes(q)
      )
    : lista;
  const nAttive   = lista.filter(a => a.attivo).length;
  const nDisattive = lista.length - nAttive;

  const oggiData = new Date();
  const dataStr = oggiData.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* ── Modal conferma eliminazione ─────────────────────────────── */}
      {eliminaModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: LT.r.lg,
            padding: 24, maxWidth: 460, width: '100%',
            boxShadow: LT.sh3, fontFamily: LT.sans,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{
                width: 40, height: 40, borderRadius: LT.r.md,
                background: LT.rossoSoft, color: LT.rosso,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.alert} size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: LT.rosso }}>
                  Eliminazione irreversibile
                </div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Controlla attentamente prima di procedere
                </div>
              </div>
            </div>

            <div style={{
              padding: '12px 14px', background: LT.rossoSoft,
              border: `1px solid #FECACA`, borderRadius: LT.r.md,
              fontSize: 13, color: '#7F1D1D', lineHeight: 1.55, marginBottom: 16,
            }}>
              Stai per eliminare definitivamente l'azienda{' '}
              <strong style={{ color: LT.rosso }}>{eliminaModal.ragione_sociale}</strong> e{' '}
              <strong>tutti i dati associati</strong> (operatori, attività, documenti, NC,
              tracciabilità, file allegati). L'operazione non è reversibile.
            </div>

            <label style={{ ...labelStyle, marginBottom: 6 }}>
              Digita la ragione sociale per confermare
            </label>
            <input
              style={{
                ...inputStyle,
                borderColor: confermaTestoElim === eliminaModal.ragione_sociale ? LT.verde : LT.n200,
                borderWidth: confermaTestoElim === eliminaModal.ragione_sociale ? 1.5 : 1,
              }}
              placeholder={eliminaModal.ragione_sociale}
              value={confermaTestoElim}
              onChange={e => setConfermaTestoElim(e.target.value)}
              autoFocus
            />

            {erroreElimina && (
              <div style={{
                padding: '8px 10px', background: LT.rossoSoft,
                border: `1px solid #FECACA`, borderRadius: LT.r.md,
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
              }}>
                <Ico d={I.alert} size={13} color={LT.rosso} />
                <span style={{ fontSize: 12.5, color: '#991B1B', fontWeight: 600 }}>{erroreElimina}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <Btn kind="outline" block onClick={chiudiEliminaModal} disabled={eliminando}>
                Annulla
              </Btn>
              <Btn kind="danger" block
                onClick={handleElimina}
                disabled={confermaTestoElim !== eliminaModal.ragione_sociale || eliminando}>
                {eliminando ? 'Eliminazione…' : 'Conferma eliminazione'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Hero compatto */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {dataStr}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Aziende
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} {lista.length === 1 ? 'azienda registrata' : 'aziende registrate'}
              {nDisattive > 0 && ` · ${nDisattive} disattivat${nDisattive === 1 ? 'a' : 'e'}`}
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => setForm({})}>
            Nuova azienda
          </Btn>
        </div>
      </div>

      {/* Ricerca */}
      {lista.length > 4 && (
        <Card pad={12} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.search} size={15} />
            </div>
            <input
              placeholder="Cerca per ragione sociale, codice o città…"
              value={cerca}
              onChange={e => setCerca(e.target.value)}
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', fontSize: 14,
                color: LT.n900, fontFamily: LT.sans,
              }}
            />
            {cerca && (
              <button onClick={() => setCerca('')} style={{
                border: 'none', background: LT.n100, cursor: 'pointer',
                width: 28, height: 28, borderRadius: LT.r.full,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: LT.n500, padding: 0,
              }}>
                <Ico d={I.x} size={14} />
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Lista */}
      {listaFiltrata.length === 0 ? (
        <Card pad={28} style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: LT.r.full,
            background: LT.n100, color: LT.n400,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 12,
          }}>
            <Ico d={I.building} size={24} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: LT.n700, marginBottom: 4 }}>
            {q ? 'Nessuna azienda trovata' : 'Nessuna azienda registrata'}
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            {q ? 'Prova a modificare i criteri di ricerca.' : 'Crea la prima azienda usando il pulsante in alto.'}
          </div>
        </Card>
      ) : (
        listaFiltrata.map(az => {
          const attiva = az.attivo !== false;
          const borderCol = !attiva ? LT.n300 : (az.piano === 'MASTER' ? LT.viola : az.piano === 'TRACE' ? LT.blu : LT.verde);
          return (
            <Card key={az.id} pad={14} style={{
              marginBottom: 10,
              borderLeft: `4px solid ${borderCol}`,
              opacity: attiva ? 1 : 0.85,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                {/* Tile logo / building */}
                <div style={{
                  width: 44, height: 44, borderRadius: LT.r.md,
                  background: az.logo ? '#fff' : LT.bluSoft,
                  color: LT.blu,
                  border: az.logo ? `1px solid ${LT.n150}` : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, overflow: 'hidden',
                }}>
                  {az.logo
                    ? <img src={`${BACKEND}${az.logo}`} alt=""
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <Ico d={I.building} size={20} />
                  }
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Titolo + chip */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    flexWrap: 'wrap', marginBottom: 6,
                  }}>
                    <div style={{
                      fontSize: 15, fontWeight: 700, color: LT.n900,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {az.ragione_sociale}
                    </div>
                    {planChip(az.piano)}
                    {statoChip(az.stato_abbonamento)}
                    {!attiva && <Chip kind="rosso" icon="x">Disattivata</Chip>}
                  </div>

                  {/* Meta */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12.5, color: LT.n500 }}>
                    {az.codice && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.lock} size={12} color={LT.n400} />
                        <span style={{ fontFamily: LT.mono, color: LT.n700, fontWeight: 600 }}>{az.codice}</span>
                      </span>
                    )}
                    {az.stato_abbonamento === 'prova' && az.data_scadenza_prova && (() => {
                      const g = giorniProvaResidui(az.data_scadenza_prova);
                      const colore = g <= 3 ? LT.rosso : (g <= 7 ? LT.arancio : LT.n700);
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: colore, fontWeight: 600 }}>
                          <Ico d={I.clock} size={12} color={colore} />
                          Prova: {g >= 0 ? `${g} gg residui` : `scaduta da ${Math.abs(g)} gg`} · {fmtDate(az.data_scadenza_prova)}
                        </span>
                      );
                    })()}
                    {az.stato_abbonamento === 'sola_lettura' && az.data_scadenza_prova && !az.data_scadenza_abbonamento && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: LT.arancio, fontWeight: 600 }}>
                        <Ico d={I.clock} size={12} color={LT.arancio} />
                        Prova scaduta il {fmtDate(az.data_scadenza_prova)}
                      </span>
                    )}
                    {az.stato_abbonamento === 'attivo' && !az.has_stripe_subscription && az.data_scadenza_abbonamento && (() => {
                      const g = giorniProvaResidui(az.data_scadenza_abbonamento);
                      const colore = g <= 7 ? LT.rosso : (g <= 30 ? LT.arancio : LT.verde);
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: colore, fontWeight: 600 }}>
                          <Ico d={I.package} size={12} color={colore} />
                          Manuale: {g >= 0 ? `${g} gg` : `scaduto da ${Math.abs(g)} gg`} · {fmtDate(az.data_scadenza_abbonamento)}
                        </span>
                      );
                    })()}
                    {az.stato_abbonamento === 'attivo' && az.has_stripe_subscription && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: LT.verde, fontWeight: 600 }}>
                        <Ico d={I.qr} size={12} color={LT.verde} />
                        Stripe (auto-rinnovo)
                      </span>
                    )}
                    {az.stato_abbonamento === 'sola_lettura' && az.data_scadenza_abbonamento && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: LT.arancio, fontWeight: 600 }}>
                        <Ico d={I.package} size={12} color={LT.arancio} />
                        Manuale scaduto il {fmtDate(az.data_scadenza_abbonamento)}
                      </span>
                    )}
                    {az.citta && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.pin} size={12} color={LT.n400} />{az.citta}
                      </span>
                    )}
                    {az.telefono && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.phone} size={12} color={LT.n400} />{az.telefono}
                      </span>
                    )}
                    {az.email && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                        <Ico d={I.mail} size={12} color={LT.n400} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{az.email}</span>
                      </span>
                    )}
                  </div>

                  {/* Footer azioni */}
                  <div style={{
                    display: 'flex', gap: 8, marginTop: 12,
                    paddingTop: 10, borderTop: `1px solid ${LT.n100}`,
                  }}>
                    <Btn kind="outline" icon="edit" onClick={() => setForm({ ...az })}>
                      Modifica
                    </Btn>
                    <Btn kind="ghost" icon="x" onClick={() => apriEliminaModal(az)}
                      style={{ color: LT.rosso }}>
                      Elimina
                    </Btn>
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}


function Verifiche({ operatore, azienda }) {
  const [lista,       setLista]       = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [verificando, setVerificando] = useState(null);
  const [errore,      setErrore]      = useState('');
  const [tab,         setTab]         = useState('singola');

  useEffect(() => {
    caricaVerifiche();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caricaVerifiche = async () => {
    setCaricamento(true);
    setErrore('');
    try {
      const res  = await apiFetch(`${API}/attivita/da-verificare/${operatore.id}`);
      const data = await res.json();
      setLista(data);
    } catch {
      setErrore('Errore nel caricamento verifiche');
    }
    setCaricamento(false);
  };

  const handleVerifica = async (id) => {
    setVerificando(id);
    try {
      const res = await apiFetch(`${API}/attivita/verifica/${id}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ idVerificatore: operatore.id })
      });
      if (!res.ok) throw new Error();
      await caricaVerifiche();
      toast('✓ Verifica registrata!');
    } catch {
      toast('Errore nella verifica', 'errore');
    }
    setVerificando(null);
  };

  // ── Header compatto (coerente con Attività) ──────────────────────────────
  const oggiData = new Date();
  const dataStr = oggiData.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  // Tab pill style
  const tabPill = (on) => ({
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${on ? LT.blu : LT.n200}`,
    background: on ? LT.blu : '#fff',
    color: on ? '#fff' : LT.n600,
    borderRadius: 999,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: LT.sans,
    transition: 'all .15s',
  });

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Header compatto, senza hero gradient */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {dataStr}
        </div>
        <div style={{
          fontSize: 22, fontWeight: 700, color: LT.n900,
          letterSpacing: '-.01em', lineHeight: 1.2,
        }}>
          Verifiche da eseguire
        </div>
        {azienda?.ragione_sociale && (
          <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
            {azienda.ragione_sociale}
          </div>
        )}
      </div>

      {/* Tab bar a pillole */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setTab('singola')} style={tabPill(tab === 'singola')}>
          <Ico d={I.checkCircle} size={14} /> Verifiche singole
          {lista.length > 0 && (
            <span style={{
              marginLeft: 2, fontSize: 11, fontWeight: 700,
              padding: '1px 7px', borderRadius: 999,
              background: tab === 'singola' ? 'rgba(255,255,255,.22)' : LT.bluSoft,
              color: tab === 'singola' ? '#fff' : LT.blu,
            }}>
              {lista.length}
            </span>
          )}
        </button>
        <button onClick={() => setTab('blocco')} style={tabPill(tab === 'blocco')}>
          <Ico d={I.layers} size={14} /> Verifica a blocchi
        </button>
      </div>

      {tab === 'singola' && (
        <>
          {caricamento && (
            <div style={{ color: LT.n500, fontSize: 13, padding: 12 }}>Caricamento…</div>
          )}

          {errore && (
            <div style={{
              background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
              padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Ico d={I.alert} size={16} /> {errore}
            </div>
          )}

          {!caricamento && lista.length === 0 && !errore && (
            <div style={{
              background: '#fff', border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
              padding: 36, textAlign: 'center', boxShadow: LT.sh1,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 999, margin: '0 auto 14px',
                background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.checkCircle} size={34} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n800 }}>
                Nessuna verifica in attesa
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
                Tutte le attività di tua competenza risultano già verificate.
              </div>
            </div>
          )}

          {lista.map(a => {
            // Saltata (N.A.) ha esito_conforme = NULL: trattiamola come categoria
            // a sé, NON come "Non conforme". Difensivo: il backend la esclude
            // dalla coda, ma se una sgusciasse fuori per qualche caso limite
            // qui non deve diventare un rosso indebito.
            const saltata   = a.saltata === true;
            const conforme  = a.esito_conforme;
            const chipKind  = saltata ? 'neutro' : (conforme ? 'verde' : 'rosso');
            const chipIcon  = saltata ? 'skip'   : (conforme ? 'check' : 'alert');
            const chipLbl   = saltata ? 'N.A.'   : (conforme ? 'Conforme' : 'Non conforme');
            const bordoLat  = saltata ? LT.n400  : (conforme ? LT.blu : LT.rosso);
            const disabled  = verificando === a.id;

            return (
              <div
                key={a.id}
                style={{
                  background: '#fff',
                  border: `1px solid ${LT.n150}`,
                  borderLeft: `4px solid ${bordoLat}`,
                  borderRadius: LT.r.lg,
                  padding: 16,
                  marginBottom: 10,
                  boxShadow: LT.sh1,
                }}
              >
                {/* Riga badge superiore */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <Chip kind={chipKind} icon={chipIcon} size="sm" soft={false}>
                    {chipLbl}
                  </Chip>
                  <Chip kind="neutro" icon="calendar" size="sm">
                    {new Date(a.data_effettuazione).toLocaleDateString('it-IT')}
                    {a.ora_effettuazione ? ` · ${String(a.ora_effettuazione).substring(0, 5)}` : ''}
                  </Chip>
                  <Chip kind="blu" icon="user" size="sm">
                    {a.operatore}
                  </Chip>
                </div>

                {/* Titolo + dettagli */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <CatTile cat={a.ambito} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>
                      {a.oggetto}
                    </div>
                    <div style={{ fontSize: 13, color: LT.n600, marginTop: 3 }}>
                      {a.collocazione}
                      {a.tipo_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {a.tipo_attivita}</>}
                      {a.sub_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {a.sub_attivita}</>}
                    </div>
                  </div>
                </div>

                {/* Box esito valore (se presente) */}
                {a.esito_valore !== null && a.esito_valore !== undefined && (
                  <div style={{
                    marginTop: 10,
                    background: conforme ? LT.bluSoft : LT.rossoSoft,
                    border: `1px solid ${conforme ? '#bfdbfe' : '#fecaca'}`,
                    borderRadius: LT.r.md,
                    padding: '8px 12px',
                    fontSize: 13,
                    color: conforme ? LT.blu : LT.rosso,
                    fontFamily: LT.mono,
                  }}>
                    Valore: <strong>{a.esito_valore}</strong>
                    {a.valore_min !== null && a.valore_min !== undefined && <> · min {a.valore_min}</>}
                    {a.valore_max !== null && a.valore_max !== undefined && <> · max {a.valore_max}</>}
                  </div>
                )}

                {/* Note operatore */}
                {a.note && (
                  <div style={{
                    marginTop: 8, fontSize: 13, color: LT.n600,
                    padding: '8px 12px', background: LT.n50,
                    borderRadius: LT.r.md, border: `1px solid ${LT.n150}`,
                  }}>
                    <span style={{ fontWeight: 600, color: LT.n700 }}>Note: </span>{a.note}
                  </div>
                )}

                {/* Bottone verifica */}
                <div style={{ marginTop: 14 }}>
                  <Btn
                    kind="success"
                    block
                    icon="check"
                    disabled={disabled}
                    onClick={() => handleVerifica(a.id)}
                  >
                    {disabled ? 'Salvataggio…' : 'Confermo la verifica'}
                  </Btn>
                </div>
              </div>
            );
          })}
        </>
      )}

      {tab === 'blocco' && <VerificaBlocco operatore={operatore} onVerificaCompletata={caricaVerifiche} />}
    </div>
  );
}

// ── VerificaBlocco ────────────────────────────────────────────────────────────
const _tdStyle = { padding: '7px 10px', borderBottom: '1px solid #eee', fontSize: 13 };

function VerificaBlocco({ operatore, onVerificaCompletata }) {
  const fmtDate = d => { const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`; };
  const initDatDa = () => { const d = new Date(); return fmtDate(new Date(d.getFullYear(), d.getMonth(), 1)); };
  const initDataA = () => fmtDate(new Date());

  const [ambiti,           setAmbiti]           = useState([]);
  const [tipiAll,          setTipiAll]          = useState([]);
  const [ambito,           setAmbito]           = useState('');
  const [tipo,             setTipo]             = useState('');
  const [dataDa,           setDataDa]           = useState(initDatDa);
  const [dataA,            setDataA]            = useState(initDataA);
  const [tipiVerifica,     setTipiVerifica]     = useState(['generica']);
  const [note,             setNote]             = useState('');
  const [anteprima,        setAnteprima]        = useState(null);
  const [caricandoAnt,     setCaricandoAnt]     = useState(false);
  const [confermando,      setConfermando]      = useState(false);
  const [esito,            setEsito]            = useState(null);
  const [errore,           setErrore]           = useState('');
  const [storico,          setStorico]          = useState([]);
  const [caricandoStor,    setCaricandoStor]    = useState(false);
  // Filtri opzionali "una zona alla volta"
  const [aree,             setAree]             = useState([]);
  const [responsabili,     setResponsabili]     = useState([]);
  const [collocazione,     setCollocazione]     = useState('');
  const [opMonitoraggio,   setOpMonitoraggio]   = useState('');

  useEffect(() => {
    apiFetch(`${API}/admin/ambiti`).then(r => r.json()).then(setAmbiti).catch(() => {});
    apiFetch(`${API}/admin/tipi-attivita`).then(r => r.json()).then(setTipiAll).catch(() => {});
    apiFetch(`${API}/verifiche/filtri?id_azienda=${operatore.id_azienda}`)
      .then(r => r.json())
      .then(d => {
        setAree(Array.isArray(d.aree) ? d.aree : []);
        setResponsabili(Array.isArray(d.responsabili) ? d.responsabili : []);
      })
      .catch(() => {});
    caricaStorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tipiFiltrati = ambito ? tipiAll.filter(t => String(t.id_ambito) === String(ambito)) : tipiAll;

  const caricaStorico = async () => {
    setCaricandoStor(true);
    try {
      const res  = await apiFetch(`${API}/verifiche/blocco?id_azienda=${operatore.id_azienda}`);
      const data = await res.json();
      setStorico(Array.isArray(data) ? data : []);
    } catch {}
    setCaricandoStor(false);
  };

  const resetForm = () => {
    setAmbito(''); setTipo('');
    setCollocazione(''); setOpMonitoraggio('');
    setDataDa(initDatDa()); setDataA(initDataA());
    setTipiVerifica(['generica']); setNote('');
    setAnteprima(null);
  };

  const toggleTipoVerifica = v =>
    setTipiVerifica(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const handleAnteprima = async () => {
    if (!ambito)               { setErrore('Seleziona un ambito');             return; }
    if (!tipo)                 { setErrore('Seleziona un tipo attività');       return; }
    if (!dataDa || !dataA)     { setErrore('Inserisci le date');               return; }
    if (tipiVerifica.length === 0) { setErrore('Seleziona almeno un tipo di verifica'); return; }
    setErrore(''); setAnteprima(null); setEsito(null);
    setCaricandoAnt(true);
    try {
      const res = await apiFetch(`${API}/verifiche/anteprima`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id_ambito: ambito, id_tipo: tipo,
          data_da: dataDa, data_a: dataA,
          id_azienda: operatore.id_azienda,
          collocazione:       collocazione   || null,
          id_op_monitoraggio: opMonitoraggio || null,
        })
      });
      if (!res.ok) throw new Error();
      setAnteprima(await res.json());
    } catch {
      setErrore('Errore nel calcolo anteprima');
    }
    setCaricandoAnt(false);
  };

  const handleConferma = async () => {
    setConfermando(true); setErrore('');
    try {
      const res = await apiFetch(`${API}/verifiche/blocco`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id_ambito: ambito, id_tipo: tipo,
          data_da: dataDa, data_a: dataA,
          tipo_verifica: tipiVerifica.join(', '),
          note: note || null,
          id_azienda: operatore.id_azienda,
          id_verificatore: operatore.id,
          collocazione:       collocazione   || null,
          id_op_monitoraggio: opMonitoraggio || null
        })
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEsito(data);
      resetForm();
      await caricaStorico();
      // Notifica il parent così il contatore delle "Verifiche singole" si aggiorna
      if (typeof onVerificaCompletata === 'function') {
        onVerificaCompletata();
      }
    } catch {
      setErrore('Errore nella verifica a blocchi');
    }
    setConfermando(false);
  };

  // ── Stili locali riutilizzati ──────────────────────────────────────────────
  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: LT.n600,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    marginBottom: 6,
  };
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: LT.sans,
    color: LT.n900,
    background: '#fff',
    border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color .15s',
  };
  const pillCheckStyle = (on) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${on ? LT.blu : LT.n200}`,
    background: on ? LT.bluSoft : '#fff',
    color: on ? LT.blu : LT.n600,
    borderRadius: 999,
    cursor: 'pointer',
    transition: 'all .15s',
    textTransform: 'capitalize',
  });

  return (
    <div>
      {/* ── Card Filtri ─────────────────────────────────────────────────── */}
      <Card pad={18} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.filter} size={18} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Parametri verifica a blocchi</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>Definisci ambito, tipo e periodo</div>
          </div>
        </div>

        {/* Ambito */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Ambito *</label>
          <select
            style={inputStyle}
            value={ambito}
            onChange={e => { setAmbito(e.target.value); setTipo(''); setAnteprima(null); setEsito(null); }}
          >
            <option value="">— seleziona ambito —</option>
            {ambiti.map(a => <option key={a.id} value={a.id}>{a.descrizione}</option>)}
          </select>
        </div>

        {/* Tipo attività */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Tipo attività *</label>
          <select
            style={{ ...inputStyle, opacity: ambito ? 1 : .6, cursor: ambito ? 'pointer' : 'not-allowed' }}
            value={tipo}
            disabled={!ambito}
            onChange={e => { setTipo(e.target.value); setAnteprima(null); setEsito(null); }}
          >
            <option value="">— seleziona tipo —</option>
            {tipiFiltrati.map(t => <option key={t.id} value={t.id}>{t.descrizione}</option>)}
          </select>
        </div>

        {/* Restringi a un'area / un responsabile — opzionale.
            Serve a chi ha reparti con responsabili diversi e verifica una zona
            alla volta. Il responsabile e' quello ASSEGNATO nello scadenziario:
            cosi' i giorni coperti da un supplente restano dentro al blocco. */}
        {(aree.length > 0 || responsabili.length > 0) && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            {aree.length > 0 && (
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>Area / reparto</label>
                <select
                  style={inputStyle}
                  value={collocazione}
                  onChange={e => { setCollocazione(e.target.value); setAnteprima(null); setEsito(null); }}
                >
                  <option value="">— tutte le aree —</option>
                  {aree.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            {responsabili.length > 0 && (
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>Responsabile assegnato</label>
                <select
                  style={inputStyle}
                  value={opMonitoraggio}
                  onChange={e => { setOpMonitoraggio(e.target.value); setAnteprima(null); setEsito(null); }}
                >
                  <option value="">— tutti —</option>
                  {responsabili.map(o => (
                    <option key={o.id} value={o.id}>{o.cognome} {o.nome}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: LT.n500, marginTop: 4, lineHeight: 1.35 }}>
                  Chi ha in carico l'attività a scadenziario, non chi ha firmato
                  la singola registrazione: i giorni coperti da un supplente
                  restano dentro al blocco.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Periodo */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Dal *</label>
            <input
              style={inputStyle}
              type="date"
              value={dataDa}
              onChange={e => { setDataDa(e.target.value); setAnteprima(null); setEsito(null); }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Al *</label>
            <input
              style={inputStyle}
              type="date"
              value={dataA}
              onChange={e => { setDataA(e.target.value); setAnteprima(null); setEsito(null); }}
            />
          </div>
        </div>

        {/* Tipo verifica — pill selector */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Tipo verifica *</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['on-site', 'documentale', 'generica'].map(v => {
              const on = tipiVerifica.includes(v);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => { toggleTipoVerifica(v); setAnteprima(null); setEsito(null); }}
                  style={pillCheckStyle(on)}
                >
                  {on && <Ico d={I.check} size={13} />}
                  {v}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Note (opzionale)</label>
          <textarea
            style={{ ...inputStyle, minHeight: 64, resize: 'vertical', fontFamily: LT.sans }}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Note sulla verifica..."
          />
        </div>

        {/* Errore */}
        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8,
            border: `1px solid #fecaca`,
          }}>
            <Ico d={I.alert} size={16} /> {errore}
          </div>
        )}

        <Btn
          kind="primary"
          block
          icon="search"
          onClick={handleAnteprima}
          disabled={caricandoAnt}
        >
          {caricandoAnt ? 'Calcolo…' : 'Verifica disponibilità'}
        </Btn>
      </Card>

      {/* ── Anteprima / Riepilogo ───────────────────────────────────────── */}
      {anteprima && (
        <Card pad={18} style={{ marginBottom: 14, borderLeft: `4px solid ${LT.blu}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: LT.n600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
            Riepilogo anteprima
          </div>

          {/* KPI grid 3 colonne */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
            {/* Da verificare */}
            <div style={{
              background: LT.bluSoft, border: '1px solid #bfdbfe',
              borderRadius: LT.r.md, padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: LT.blu, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Da verificare
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: LT.blu, lineHeight: 1.1, marginTop: 4, fontFamily: LT.mono }}>
                {anteprima.totale}
              </div>
            </div>

            {/* Di cui NC */}
            <div style={{
              background: anteprima.nc > 0 ? LT.rossoSoft : LT.n50,
              border: `1px solid ${anteprima.nc > 0 ? '#fecaca' : LT.n200}`,
              borderRadius: LT.r.md, padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: anteprima.nc > 0 ? LT.rosso : LT.n500, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Di cui NC
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: anteprima.nc > 0 ? LT.rosso : LT.n500, lineHeight: 1.1, marginTop: 4, fontFamily: LT.mono }}>
                {anteprima.nc}
              </div>
            </div>

            {/* Già verificate */}
            <div style={{
              background: LT.n50, border: `1px solid ${LT.n200}`,
              borderRadius: LT.r.md, padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Già verificate
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: LT.n700, lineHeight: 1.1, marginTop: 4, fontFamily: LT.mono }}>
                {anteprima.gia_verificate}
              </div>
            </div>
          </div>

          {/* Warning NC */}
          {anteprima.nc > 0 && (
            <div style={{
              background: LT.arancioSoft, border: '1px solid #fed7aa',
              borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{ color: LT.arancio, flexShrink: 0, marginTop: 1 }}>
                <Ico d={I.alert} size={18} />
              </div>
              <div style={{ fontSize: 13, color: LT.n700, lineHeight: 1.4 }}>
                <strong style={{ color: LT.arancio }}>Attenzione:</strong> sono presenti <strong>{anteprima.nc}</strong> non conformità.
                Si consiglia di verificarle singolarmente prima di procedere con la verifica a blocchi.
              </div>
            </div>
          )}

          {/* Attività escluse dal filtro area/responsabile: rete di sicurezza
              contro il "ho verificato tre reparti su quattro e credo di aver
              chiuso il periodo". */}
          {anteprima.fuori_filtro > 0 && (
            <div style={{
              background: LT.n50, border: `1px solid ${LT.n200}`,
              borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{ color: LT.n400, flexShrink: 0, marginTop: 1 }}>
                <Ico d={I.filter} size={16} />
              </div>
              <div style={{ fontSize: 12.5, color: LT.n600, lineHeight: 1.45 }}>
                Restano <strong style={{ color: LT.n800 }}>{anteprima.fuori_filtro}</strong> attività
                non verificate nello stesso periodo, fuori da
                {collocazione && <> l'area <strong>{collocazione}</strong></>}
                {collocazione && opMonitoraggio && ' /'}
                {opMonitoraggio && <> il responsabile selezionato</>}
                {!collocazione && !opMonitoraggio && ' questo filtro'}.
                Ricordati di verificarle con un altro blocco.
              </div>
            </div>
          )}

          {/* Azione conferma o empty */}
          {anteprima.totale === 0 ? (
            <div style={{
              background: LT.n50, border: `1px solid ${LT.n200}`,
              borderRadius: LT.r.md, padding: '12px 14px', fontSize: 13, color: LT.n500,
              textAlign: 'center',
            }}>
              Nessuna attività da verificare nel periodo selezionato.
            </div>
          ) : (
            <Btn
              kind="success"
              block
              icon="check"
              onClick={handleConferma}
              disabled={confermando}
            >
              {confermando ? 'Salvataggio…' : `Conferma verifica a blocchi (${anteprima.totale} attività)`}
            </Btn>
          )}
        </Card>
      )}

      {/* ── Esito successo ──────────────────────────────────────────────── */}
      {esito && (
        <div style={{
          background: LT.verdeSoft, border: '1px solid #bbf7d0',
          borderRadius: LT.r.lg, padding: '14px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 999,
            background: LT.verde, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.check} size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>
              Verifica completata
            </div>
            <div style={{ fontSize: 13, color: LT.n600, marginTop: 2 }}>
              {esito.aggiornate} {esito.aggiornate === 1 ? 'attività verificata' : 'attività verificate'}
              {esito.nc_count > 0 && (
                <> · <span style={{ color: LT.rosso, fontWeight: 600 }}>{esito.nc_count} NC incluse</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Storico verifiche a blocchi ─────────────────────────────────── */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Ico d={I.history} size={16} color={LT.n500} />
          <div style={{ fontSize: 11, fontWeight: 700, color: LT.n600, textTransform: 'uppercase', letterSpacing: '.08em' }}>
            Storico verifiche a blocchi
          </div>
        </div>

        {caricandoStor && (
          <div style={{ color: LT.n500, fontSize: 13, padding: '8px 2px' }}>Caricamento…</div>
        )}

        {!caricandoStor && storico.length === 0 && (
          <Card pad={24} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              Nessuna verifica a blocchi eseguita.
            </div>
          </Card>
        )}

        {storico.length > 0 && (
          <div style={{
            background: '#fff',
            border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.lg,
            overflow: 'hidden',
            boxShadow: LT.sh1,
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: LT.bluSoft }}>
                    {['Data verifica','Verificatore','Ambito','Tipo','Area / Responsabile','Periodo','Totale','NC','Tipo verifica','Note'].map(h => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 12px',
                          textAlign: 'left',
                          borderBottom: `1px solid ${LT.n200}`,
                          whiteSpace: 'nowrap',
                          color: LT.blu,
                          fontWeight: 700,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '.06em',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {storico.map((r, i) => (
                    <tr
                      key={r.id}
                      style={{
                        background: i % 2 === 0 ? '#fff' : LT.n50,
                        borderBottom: i < storico.length - 1 ? `1px solid ${LT.n100}` : 'none',
                      }}
                    >
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: LT.n700, fontFamily: LT.mono, fontSize: 12 }}>
                        {new Date(r.data_verifica).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td style={{ padding: '10px 12px', color: LT.n800, fontWeight: 500 }}>
                        {r.verificatore}
                      </td>
                      <td style={{ padding: '10px 12px', color: LT.n600 }}>
                        {r.ambito || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: LT.n600 }}>
                        {r.tipo || '—'}
                      </td>
                      {/* Ambito del blocco: se vuoto la verifica copriva tutta
                          l'azienda, altrimenti era ristretta a un'area/persona */}
                      <td style={{ padding: '10px 12px', color: LT.n600, fontSize: 12 }}>
                        {[r.collocazione, r.op_monitoraggio].filter(Boolean).join(' · ') || (
                          <span style={{ color: LT.n400 }}>Tutte</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: LT.n600, fontFamily: LT.mono, fontSize: 12 }}>
                        {new Date(r.data_da).toLocaleDateString('it-IT')} – {new Date(r.data_a).toLocaleDateString('it-IT')}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Chip kind="blu" size="sm" soft>
                          {r.attivita_count}
                        </Chip>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {r.nc_count > 0 ? (
                          <Chip kind="rosso" size="sm" icon="alert">
                            {r.nc_count}
                          </Chip>
                        ) : (
                          <span style={{ color: LT.n400, fontFamily: LT.mono }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', color: LT.n600, textTransform: 'capitalize' }}>
                        {r.tipo_verifica}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: LT.n500,
                        fontSize: 12,
                      }} title={r.note || ''}>
                        {r.note || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ModalProponiAllegato ───────────────────────────────────────────────────────
// Propone di caricare un allegato subito dopo la registrazione di un'attività tecnica
const TITOLI_ALLEGATO = {
  TARATURA:     'Carica rapporto di taratura',
  MANUTENZIONE: 'Carica rapporto di manutenzione',
  ANALISI:      'Carica referto di analisi',
  VERIFICA:     'Carica rapporto di verifica',
};

function ModalProponiAllegato({ idAttivita, registroSpeciale, operatore, onFine }) {
  const [file,      setFile]      = useState(null);
  const [nome,      setNome]      = useState('');
  const [uploading, setUploading] = useState(false);
  const [errore,    setErrore]    = useState('');
  const fileRef = useRef();

  const titolo = TITOLI_ALLEGATO[registroSpeciale] || 'Carica documento tecnico';

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (!nome) setNome(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleCarica = async () => {
    if (!file) { setErrore('Seleziona un file'); return; }
    setUploading(true);
    setErrore('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('idOperatore', operatore.id);
    const res = await apiFetch(`${API}/allegati/${idAttivita}`, { method: 'POST', body: fd });
    if (res.ok) {
      toast('✓ Allegato caricato');
      onFine();
    } else {
      const d = await res.json();
      setErrore(d.errore || 'Errore nel caricamento');
    }
    setUploading(false);
  };

  // Palette per tipo di allegato
  const confTipo = {
    TARATURA:     { c: LT.blu,    bg: LT.bluSoft,    icon: I.chart  },
    MANUTENZIONE: { c: LT.arancio, bg: LT.arancioSoft, icon: I.wrench },
    ANALISI:      { c: LT.ciano,  bg: LT.cianoSoft,  icon: I.flask  },
    VERIFICA:     { c: LT.verde,  bg: LT.verdeSoft,  icon: I.checkCircle },
  }[registroSpeciale] || { c: LT.blu, bg: LT.bluSoft, icon: I.folder };

  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 10, marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={lt_overlay}>
      <div style={{ ...lt_modal, maxWidth: 440 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: LT.r.md,
            background: confTipo.bg, color: confTipo.c,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={confTipo.icon} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>{titolo}</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 4, lineHeight: 1.4 }}>
              Puoi caricarlo subito o aggiungerlo in seguito dallo storico.
            </div>
          </div>
        </div>

        <label style={labelStyle}>File (PDF o immagine, max 10 MB)</label>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif"
          onChange={handleFileChange} disabled={uploading}
          style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }} />

        {file && <>
          <label style={labelStyle}>Nome documento</label>
          <input style={inputStyle}
            value={nome} onChange={e => setNome(e.target.value)}
            onFocus={e => trFocus(e, confTipo.c)} onBlur={trBlur}
            placeholder="es. Rapporto taratura bilancia" />
        </>}

        {errore && (
          <div style={{
            marginTop: 12, padding: '8px 12px',
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}33`, borderRadius: LT.r.md,
            color: LT.rosso, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Ico d={I.alert} size={16} />
            <span>{errore}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <Btn kind="ghost" block onClick={onFine} disabled={uploading}>
            Salta — aggiungerò in seguito
          </Btn>
          <Btn kind="primary" block icon="download" onClick={handleCarica}
            disabled={uploading || !file}
            style={{ background: confTipo.c, boxShadow: `0 6px 18px ${confTipo.c}30` }}>
            {uploading ? 'Caricamento…' : 'Carica allegato'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── ModalAllegati ──────────────────────────────────────────────────────────────
function ModalAllegati({ idAttivita, operatore, tipoLabel, onChiudi }) {
  const [allegati,   setAllegati]   = useState([]);
  const [caricando,  setCaricando]  = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const fileRef = useRef();

  const carica = async () => {
    setCaricando(true);
    const res  = await apiFetch(`${API}/allegati/${idAttivita}`);
    const data = await res.json();
    setAllegati(Array.isArray(data) ? data : []);
    setCaricando(false);
  };

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('idOperatore', operatore.id);
    const res = await apiFetch(`${API}/allegati/${idAttivita}`, { method: 'POST', body: fd });
    if (res.ok) { toast('✓ Allegato caricato'); await carica(); }
    else { const d = await res.json(); toast(d.errore || 'Errore caricamento', 'errore'); }
    setUploading(false);
    fileRef.current.value = '';
  };

  const handleElimina = async (id, nome) => {
    const ok = await chiediConferma('Eliminare allegato?', nome);
    if (!ok) return;
    const res = await apiFetch(`${API}/allegati/${id}`, { method: 'DELETE' });
    if (res.ok) { toast('✓ Allegato eliminato'); await carica(); }
    else toast('Errore eliminazione', 'errore');
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const iconPerFile = (nome) => {
    if (/\.pdf$/i.test(nome))                return { c: LT.rosso,  bg: LT.rossoSoft,  ico: I.receipt };
    if (/\.(jpg|jpeg|png|gif)$/i.test(nome)) return { c: LT.viola,  bg: LT.violaSoft,  ico: I.camera  };
    if (/\.(xls|xlsx|csv)$/i.test(nome))     return { c: LT.verde,  bg: LT.verdeSoft,  ico: I.chart   };
    if (/\.(doc|docx|txt)$/i.test(nome))     return { c: LT.blu,    bg: LT.bluSoft,    ico: I.book    };
    return                                          { c: LT.n500,  bg: LT.n100,       ico: I.folder  };
  };

  return (
    <div style={lt_overlay} onClick={onChiudi}>
      <div style={{ ...lt_modal, maxWidth: 520, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.folder} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, lineHeight: 1.25 }}>
              Allegati tecnici
            </div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 2, wordBreak: 'break-word' }}>
              {tipoLabel}
            </div>
          </div>
          <Btn kind="ghost" size="sm" icon="x" onClick={onChiudi} title="Chiudi" />
        </div>

        {/* Upload */}
        <div style={{
          marginBottom: 16, padding: 12,
          background: LT.n50, border: `1px dashed ${LT.n300}`, borderRadius: LT.r.md,
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Ico d={I.download} size={18} style={{ color: LT.n500, flexShrink: 0 }} />
          <input ref={fileRef} type="file" onChange={handleUpload} disabled={uploading}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
            style={{ flex: 1, fontSize: 13, minWidth: 0 }} />
          {uploading && <span style={{ fontSize: 12, color: LT.blu, fontWeight: 600 }}>Caricamento…</span>}
        </div>

        {/* Lista allegati */}
        {caricando ? (
          <div style={{ color: LT.n400, textAlign: 'center', padding: '24px 0', fontSize: 13 }}>
            Caricamento…
          </div>
        ) : allegati.length === 0 ? (
          <div style={{
            color: LT.n400, textAlign: 'center', padding: '32px 0', fontSize: 13,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          }}>
            <Ico d={I.folder} size={36} style={{ color: LT.n300 }} />
            <div>Nessun allegato</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allegati.map(al => {
              const ic = iconPerFile(al.nome);
              return (
                <div key={al.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: 10, borderRadius: LT.r.md,
                  background: LT.n50, border: `1px solid ${LT.n150}`,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: LT.r.sm,
                    background: ic.bg, color: ic.c,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Ico d={ic.ico} size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={`${BACKEND}${al.path_file}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 13, color: LT.blu, fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}>
                      {al.nome}
                    </a>
                    <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>
                      {fmtSize(al.dimensione)}
                      {al.caricato_da && ` · ${al.caricato_da}`}
                      {al.created_at && ` · ${new Date(al.created_at).toLocaleDateString('it-IT')}`}
                    </div>
                  </div>
                  {(operatore.admin || operatore.master) && (
                    <Btn kind="ghost" size="sm" icon="trash"
                      onClick={() => handleElimina(al.id, al.nome)}
                      style={{ color: LT.rosso }}
                      title="Elimina" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ModalCorreggi({ record, operatore, onConferma, onAnnulla }) {
  const [data,        setData]        = useState(new Date(record.data_effettuazione).toISOString().split('T')[0]);
  const [ora,         setOra]         = useState(record.ora_effettuazione ? String(record.ora_effettuazione).substring(0, 5) : '');
  const [esito,       setEsito]       = useState(record.saltata ? 'na' : (record.esito_conforme ? 'true' : 'false'));
  const [valore,      setValore]      = useState(record.esito_valore !== null && record.esito_valore !== undefined ? String(record.esito_valore) : '');
  const [note,        setNote]        = useState(record.note || '');
  const [motivazione, setMotivazione] = useState('');
  const [errore,      setErrore]      = useState('');
  const [salvando,    setSalvando]    = useState(false);

  const min = record.valore_min;
  const max = record.valore_max;
  const haLimiti = min !== null || max !== null;

  const validaLimiti = (esitoCorrente, valoreCorrente) => {
    if (!haLimiti) return '';
    const v = valoreCorrente !== '' ? parseFloat(String(valoreCorrente).replace(',', '.')) : null;
    if (v === null || isNaN(v)) return '';
    const neiLimiti = (min === null || v >= min) && (max === null || v <= max);
    if (esitoCorrente === 'true' && !neiLimiti)
      return `Valore ${v} fuori dai limiti (min: ${min ?? '—'}, max: ${max ?? '—'}) — non puoi registrare come CONFORME`;
    if (esitoCorrente === 'false' && neiLimiti)
      return `Valore ${v} nei limiti (min: ${min ?? '—'}, max: ${max ?? '—'}) — non puoi registrare come NON CONFORME`;
    return '';
  };

  const handleEsito = (val) => { setEsito(val);   setErrore(validaLimiti(val,   valore)); };
  const handleValore = (val) => { setValore(val);  setErrore(validaLimiti(esito, val));   };

  const handleSalva = async () => {
    if (!motivazione.trim()) { setErrore('La motivazione è obbligatoria'); return; }
    const errLimiti = validaLimiti(esito, valore);
    if (errLimiti) { setErrore(errLimiti); return; }
    // Il campo e' di testo: senza questo controllo un valore non numerico
    // diventerebbe NaN e poi null nel JSON, cancellando il dato in silenzio.
    if (esito !== 'na' && valore !== '' &&
        isNaN(parseFloat(String(valore).replace(',', '.')))) {
      setErrore('Inserisci un valore numerico valido');
      return;
    }

    setSalvando(true);
    setErrore('');
    try {
      const res = await apiFetch(`${API}/attivita/${record.id}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idOperatore:       operatore.id,
          motivazione:       motivazione.trim(),
          dataEffettuazione: data,
          oraEffettuazione:  ora || null,
          nonApplicabile:    esito === 'na',
          esitoConforme:     esito === 'na' ? null : (esito === 'true'),
          esitoValore:       esito === 'na' ? null : (valore !== '' ? parseFloat(valore.replace(',', '.')) : null),
          note:              note || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrore(body.errore || 'Errore nel salvataggio della correzione');
        setSalvando(false);
        return;
      }
      toast('✓ Correzione salvata!');
      onConferma();
    } catch {
      setErrore('Errore di connessione');
    }
    setSalvando(false);
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 60, fontFamily: LT.sans };

  return (
    <div style={lt_overlay} onClick={() => !salvando && onAnnulla()}>
      <div style={{ ...lt_modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.arancioSoft, color: LT.arancio,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.edit} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>Correggi monitoraggio</div>
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 2 }}>
              Tutte le modifiche verranno tracciate nel log
            </div>
          </div>
          <Btn kind="ghost" size="sm" icon="x"
            onClick={() => !salvando && onAnnulla()}
            disabled={salvando}
            title="Chiudi" />
        </div>

        {/* Record info */}
        <div style={{
          background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.md,
          padding: '10px 12px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: LT.n900, marginBottom: 2 }}>
            {record.oggetto}{record.collocazione && <span style={{ color: LT.n500, fontWeight: 500 }}> — {record.collocazione}</span>}
          </div>
          <div style={{ fontSize: 12, color: LT.n500 }}>
            {record.ambito}{record.tipo_attivita && <> <span style={{ color: LT.n300 }}>·</span> {record.tipo_attivita}</>}
          </div>
        </div>

        {/* Chip limiti */}
        {haLimiti && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {min !== null && min !== undefined && (
              <Chip kind="blu" size="sm" icon="arrowDown">Min: {min}</Chip>
            )}
            {max !== null && max !== undefined && (
              <Chip kind="blu" size="sm" icon="trend">Max: {max}</Chip>
            )}
          </div>
        )}

        {/* Data / Ora */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Data</label>
            <input style={inputStyle} type="date" value={data}
              onChange={e => setData(e.target.value)}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Ora</label>
            <input style={inputStyle} type="time" value={ora}
              onChange={e => setOra(e.target.value)}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
          </div>
        </div>

        {/* Esito */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Esito</label>
          <select style={inputStyle} value={esito}
            onChange={e => handleEsito(e.target.value)}
            onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}>
            <option value="true">CONFORME</option>
            <option value="false">NON CONFORME</option>
            <option value="na">NON APPLICABILE (N.A.)</option>
          </select>
        </div>

        {/* Valore — non applicabile quando l'esito è N.A. */}
        {esito !== 'na' && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Valore misurato</label>
            {/* type="text": vedi la nota in FormValore — con "number" la
                virgola sparisce e 2,4 diventa 24. */}
            <input
              style={inputStyle} type="text" inputMode="decimal" value={valore}
              onChange={e => handleValore(e.target.value)}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}
              placeholder="(vuoto = nessun valore)"
            />
          </div>
        )}

        {/* Note */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Note</label>
          <textarea style={textareaStyle} rows={2} value={note}
            onChange={e => setNote(e.target.value)}
            onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}
            placeholder="Note opzionali..." />
        </div>

        {/* Motivazione (obbligatoria) */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...labelStyle, color: LT.arancio }}>
            Motivazione correzione *
          </label>
          <textarea
            style={{ ...textareaStyle, borderColor: LT.arancio, background: LT.arancioSoft }}
            rows={2}
            value={motivazione}
            onChange={e => setMotivazione(e.target.value)}
            placeholder="Spiegare il motivo della correzione"
            autoFocus
          />
        </div>

        {/* Errore */}
        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '8px 12px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={14} /> {errore}
          </div>
        )}

        {/* Azioni */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="ghost" onClick={onAnnulla} disabled={salvando}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn
              kind="primary"
              block
              icon="check"
              style={{ background: LT.arancio, boxShadow: '0 6px 18px rgba(245,158,11,.25)' }}
              onClick={handleSalva}
              disabled={salvando}
            >
              {salvando ? 'Salvataggio…' : 'Salva correzione'}
            </Btn>
          </div>
        </div>

      </div>
    </div>
  );
}

function StoricoCorrezioni({ operatore, onBack }) {
  const [lista,      setLista]      = useState([]);
  const [organico,   setOrganico]   = useState([]);
  const [caricando,  setCaricando]  = useState(false);
  const [cercato,    setCercato]    = useState(false);
  const [filtroDal,  setFiltroDal]  = useState('');
  const [filtroAl,   setFiltroAl]   = useState('');
  const [filtroOp,   setFiltroOp]   = useState('');
  const [errore,     setErrore]     = useState('');

  useEffect(() => {
    const qs = operatore.id_azienda ? `?idAzienda=${operatore.id_azienda}` : '';
    apiFetch(`${API}/admin/organico${qs}`)
      .then(r => r.json())
      .then(setOrganico)
      .catch(() => {});
  }, [operatore.id_azienda]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aprendo la sezione, l'admin marca le correzioni come "viste":
  // azzera il banner "correzioni nuove" in Dashboard.
  useEffect(() => {
    if (operatore.admin || operatore.master) {
      apiFetch(`${API}/attivita/correzioni-viste`, { method: 'POST' }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cerca = async () => {
    setCaricando(true);
    setErrore('');
    try {
      const params = new URLSearchParams();
      if (operatore.id_azienda) params.append('idAzienda', operatore.id_azienda);
      if (filtroDal) params.append('dal',         filtroDal);
      if (filtroAl)  params.append('al',          filtroAl);
      if (filtroOp)  params.append('idOperatore', filtroOp);
      const res  = await apiFetch(`${API}/attivita/log?${params}`);
      const data = await res.json();
      setLista(data);
      setCercato(true);
    } catch {
      setErrore('Errore nel caricamento');
    }
    setCaricando(false);
  };

  const fmtDateTime = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return `${dt.toLocaleDateString('it-IT')} ${dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const _labelCampo = (c) => ({
    data_effettuazione: 'Data',
    ora_effettuazione:  'Ora',
    esito_conforme:     'Esito',
    esito_valore:       'Valore',
    note:               'Note',
  }[c] || c);

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };

  // Mappa label+icona per il campo modificato
  const campoMeta = {
    data_effettuazione: { label: 'Data',   icon: 'calendar' },
    ora_effettuazione:  { label: 'Ora',    icon: 'clock'    },
    esito_conforme:     { label: 'Esito',  icon: 'shield'   },
    esito_valore:       { label: 'Valore', icon: 'chart'    },
    note:               { label: 'Note',   icon: 'edit'     },
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero compatto */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Storico correzioni
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Log modifiche monitoraggi con tracciamento completo
            </div>
          </div>
          <Btn kind="outline" icon="x" onClick={onBack}>Chiudi</Btn>
        </div>
      </div>

      {/* Filtri */}
      <Card pad={16} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.filter} size={16} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Filtri</div>
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Periodo e operatore</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={labelStyle}>Dal</label>
            <input style={inputStyle} type="date" value={filtroDal}
              onChange={e => setFiltroDal(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={labelStyle}>Al</label>
            <input style={inputStyle} type="date" value={filtroAl}
              onChange={e => setFiltroAl(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Operatore che ha modificato</label>
          <select style={inputStyle} value={filtroOp} onChange={e => setFiltroOp(e.target.value)}>
            <option value=''>— Tutti gli operatori —</option>
            {organico.map(op => (
              <option key={op.id} value={op.id}>{op.cognome} {op.nome}</option>
            ))}
          </select>
        </div>

        <Btn kind="primary" icon="search" onClick={cerca} disabled={caricando}>
          {caricando ? 'Ricerca…' : 'Cerca'}
        </Btn>
      </Card>

      {/* Errore */}
      {errore && (
        <div style={{
          background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
        }}>
          <Ico d={I.alert} size={14} /> {errore}
        </div>
      )}

      {/* Loading */}
      {caricando && (
        <div style={{ textAlign: 'center', color: LT.n500, padding: 32, fontSize: 14 }}>
          Caricamento…
        </div>
      )}

      {/* Empty state */}
      {cercato && !caricando && lista.length === 0 && (
        <Card pad={24} style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: LT.r.lg,
            background: LT.verdeSoft, color: LT.verde,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}>
            <Ico d={I.checkCircle} size={22} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, marginBottom: 4 }}>
            Nessuna correzione trovata
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Nei criteri impostati non risultano modifiche ai monitoraggi.
          </div>
        </Card>
      )}

      {/* Lista correzioni */}
      {!caricando && lista.length > 0 && (
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8,
          }}>
            {lista.length} {lista.length === 1 ? 'correzione' : 'correzioni'} trovate
          </div>

          {lista.map(entry => {
            const meta = campoMeta[entry.campo_modificato] || { label: entry.campo_modificato, icon: 'edit' };
            return (
              <Card key={entry.id} pad={14} style={{
                marginBottom: 10,
                borderLeft: `4px solid ${LT.arancio}`,
              }}>
                {/* Riga top: timestamp + chip campo */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 8, marginBottom: 8, flexWrap: 'wrap',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 12, color: LT.n500, fontFamily: LT.mono,
                  }}>
                    <Ico d={I.history} size={13} color={LT.n400} />
                    {fmtDateTime(entry.data_modifica)}
                  </div>
                  <Chip kind="arancio" icon={meta.icon} size="sm">
                    {meta.label}
                  </Chip>
                </div>

                {/* Oggetto */}
                <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900, marginBottom: 2 }}>
                  {entry.oggetto}
                  {entry.collocazione && (
                    <span style={{ color: LT.n500, fontWeight: 500 }}> — {entry.collocazione}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: LT.n500, marginBottom: 10 }}>
                  {entry.ambito}
                  {entry.tipo_attivita && <> <span style={{ color: LT.n300 }}>·</span> {entry.tipo_attivita}</>}
                  {entry.data_effettuazione && (
                    <> <span style={{ color: LT.n300 }}>·</span> {new Date(entry.data_effettuazione).toLocaleDateString('it-IT')}</>
                  )}
                </div>

                {/* Valori prima → dopo */}
                <div style={{
                  display: 'flex', gap: 8, alignItems: 'center',
                  flexWrap: 'wrap', marginBottom: 10,
                }}>
                  <div style={{
                    background: LT.rossoSoft, color: LT.rosso,
                    padding: '5px 12px', borderRadius: LT.r.sm,
                    fontSize: 13, fontWeight: 600,
                    border: '1px solid #fecaca',
                    fontFamily: LT.mono,
                  }}>
                    {entry.valore_precedente ?? '—'}
                  </div>
                  <Ico d={I.arrowRight} size={16} color={LT.n400} />
                  <div style={{
                    background: LT.verdeSoft, color: LT.verde,
                    padding: '5px 12px', borderRadius: LT.r.sm,
                    fontSize: 13, fontWeight: 600,
                    border: '1px solid #bbf7d0',
                    fontFamily: LT.mono,
                  }}>
                    {entry.valore_nuovo ?? '—'}
                  </div>
                </div>

                {/* Motivazione */}
                {entry.motivazione && (
                  <div style={{
                    background: LT.arancioSoft,
                    border: `1px solid ${LT.arancio}33`,
                    borderRadius: LT.r.sm,
                    padding: '8px 10px',
                    marginBottom: 8,
                    fontSize: 13, color: LT.n800, lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                  }}>
                    <div style={{
                      fontSize: 10.5, fontWeight: 700, color: LT.arancio,
                      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 3,
                    }}>
                      Motivazione
                    </div>
                    {entry.motivazione}
                  </div>
                )}

                {/* Footer autori */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                  fontSize: 12, color: LT.n500,
                  borderTop: `1px solid ${LT.n100}`, paddingTop: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Ico d={I.edit} size={12} color={LT.arancio} />
                    <span>Corretto da <strong style={{ color: LT.n700 }}>{entry.modificato_da}</strong></span>
                  </div>
                  {entry.operatore_monitoraggio && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Ico d={I.user} size={12} color={LT.n400} />
                      <span>Monitorato da <strong style={{ color: LT.n700 }}>{entry.operatore_monitoraggio}</strong></span>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </>
      )}

    </div>
  );
}

function Storico({ operatore, azienda, impostazioni = {}, onBack }) {
  const isAdmin = operatore.admin || operatore.master;

  // Correzione: admin/master sempre; l'operatore base solo per le PROPRIE
  // registrazioni, non ancora verificate, degli ultimi 7 giorni.
  // (Il backend applica le stesse regole sulla PUT /attivita/:id.)
  const GIORNI_FINESTRA_CORREZIONE = 7;
  const puoCorreggere = (r) => {
    if (isAdmin) return true;
    if (r.id_operatore !== operatore.id) return false;
    if (r.verificato) return false;
    const dataReg = new Date(r.data_effettuazione); dataReg.setHours(0, 0, 0, 0);
    const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
    return (oggi - dataReg) / 86400000 <= GIORNI_FINESTRA_CORREZIONE;
  };
  // Perche' non si puo' correggere. Prima il pulsante veniva semplicemente
  // nascosto: chi lo cercava non trovava niente e non aveva modo di capire.
  // Ora resta visibile e disabilitato, e questo testo finisce nel suggerimento.
  const motivoNoCorrezione = (r) => {
    if (isAdmin) return '';
    if (r.id_operatore !== operatore.id)
      return 'Puoi correggere solo le registrazioni fatte da te. Per questa serve un amministratore.';
    if (r.verificato)
      return 'Registrazione già verificata: la correzione è riservata agli amministratori.';
    return `Sono passati più di ${GIORNI_FINESTRA_CORREZIONE} giorni: per correggerla serve un amministratore.`;
  };
  const [ambiti,    setAmbiti]    = useState([]);
  const [tipi,      setTipi]      = useState([]);
  const [tipiFilt,  setTipiFilt]  = useState([]);
  const [oggetti,   setOggetti]   = useState([]);
  const [aree,      setAree]      = useState([]);
  const [esecutori, setEsecutori] = useState([]);
  // Responsabili a scadenziario: stessa fonte della verifica a blocchi, cosi'
  // le due tendine non possono divergere.
  const [responsabili, setResponsabili] = useState([]);
  const [lista,     setLista]     = useState([]);
  const [cercato,   setCercato]   = useState(false);
  const [daCorreggere, setDaCorreggere] = useState(null); // record aperto in ModalCorreggi
  const [allegatiRecord, setAllegatiRecord] = useState(null); // record per ModalAllegati
  const [filtri,    setFiltri]    = useState({
    idAmbito: '', idTipo: '', idOggetto: '', collocazione: '', idOperatore: '',
    idOpMonitoraggio: '',
    dal: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    al:  new Date().toISOString().split('T')[0]
  });
  // Fotografia dei filtri al momento della ricerca. `filtri` continua a cambiare
  // mentre l'utente tocca le tendine: se la stampa leggesse quello, il periodo e
  // gli ambiti scritti in testata potrebbero non corrispondere alle righe
  // stampate (che restano quelle dell'ultima ricerca).
  const [filtriUsati, setFiltriUsati] = useState(null);

useEffect(() => {
    const idAz = operatore.master ? '' : `?idAzienda=${operatore.id_azienda}`;
    Promise.all([
      apiFetch(`${API}/admin/ambiti`).then(r => r.json()),
      apiFetch(`${API}/admin/tipi-attivita`).then(r => r.json()),
      apiFetch(`${API}/admin/attrezzature${idAz}`).then(r => r.json()),
    ]).then(([a, t, o]) => { setAmbiti(a); setTipi(t); setOggetti(o); });

    // Elenco aree: stesso endpoint usato dalla verifica a blocchi, cosi' le due
    // sezioni mostrano sempre la stessa lista e non possono divergere.
    if (operatore.id_azienda) {
      apiFetch(`${API}/verifiche/filtri?id_azienda=${operatore.id_azienda}`)
        .then(r => r.json())
        .then(d => { setAree(d.aree || []); setResponsabili(d.responsabili || []); })
        .catch(() => {});
    }

    // Chi ha firmato almeno una registrazione. Endpoint dedicato invece
    // dell'organico completo: una tendina piena di nomi che danno sempre zero
    // risultati confonde chi cerca, e qui non servono le firme grafiche.
    apiFetch(`${API}/attivita/esecutori${idAz}`)
      .then(r => r.json())
      .then(d => setEsecutori(Array.isArray(d) ? d : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filtri.idAmbito) {
      setTipiFilt(tipi.filter(t => t.id_ambito === parseInt(filtri.idAmbito)));
    } else {
      setTipiFilt(tipi);
    }
    setFiltri(f => ({ ...f, idTipo: '' }));
  }, [filtri.idAmbito, tipi]);

  const oggettiFiltrati = filtri.collocazione
    ? oggetti.filter(o => (o.collocazione || '').trim() === filtri.collocazione)
    : oggetti;

  const cerca = async () => {
    const params = new URLSearchParams();
    if (!operatore.master) params.append('idAzienda', operatore.id_azienda);
    if (filtri.idAmbito)  params.append('idAmbito',  filtri.idAmbito);
    if (filtri.idTipo)    params.append('idTipo',    filtri.idTipo);
    if (filtri.idOggetto) params.append('idOggetto', filtri.idOggetto);
    if (filtri.collocazione) params.append('collocazione', filtri.collocazione);
    if (filtri.idOperatore)  params.append('idOperatore',  filtri.idOperatore);
    if (filtri.idOpMonitoraggio) params.append('idOpMonitoraggio', filtri.idOpMonitoraggio);
    if (filtri.dal)       params.append('dal',       filtri.dal);
    if (filtri.al)        params.append('al',        filtri.al);
    const res  = await apiFetch(`${API}/attivita/storico?${params}`);
    const data = await res.json();
    setLista(data);
    setFiltriUsati({ ...filtri });
    setCercato(true);
  };

  // Mappa multi-verificatore del periodo: id_verificatore -> dati aggregati.
  // Serve sia per la stampa (apice + blocchi firma) sia per eventuale uso futuro.
  const verificatoriPeriodo = (() => {
    const m = new Map();
    lista.forEach(r => {
      if (!r.verificato || !r.id_verificatore) return;
      const id = r.id_verificatore;
      if (!m.has(id)) {
        m.set(id, {
          id,
          nome: r.verificatore,
          firma: r.firma_verificatore,
          ultimaData: r.data_verifica,
          righe: 1,
        });
      } else {
        const v = m.get(id);
        v.righe++;
        if (new Date(r.data_verifica) > new Date(v.ultimaData)) v.ultimaData = r.data_verifica;
      }
    });
    return [...m.values()].sort((a, b) => new Date(b.ultimaData) - new Date(a.ultimaData));
  })();
  // (back-compat ultimaVerifica rimossa: non più letta da nessuno)

  // ── STAMPA REPORT HACCP (finestra dedicata, layout professionale) ───────
  const stampaReport = () => {
    const w = window.open('', '_blank', 'width=1200,height=800');
    if (!w) { toast('Il browser ha bloccato la finestra di stampa', 'errore'); return; }

    const esc = (s) => (s === null || s === undefined || s === '') ? '—' : String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const fmtD = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
    const fmtO = (o) => o ? String(o).substring(0, 5) : '—';

    // Filtri attivi — quelli con cui è stata fatta la ricerca, non quelli
    // attualmente selezionati nelle tendine (vedi filtriUsati).
    const fUsati = filtriUsati || {};
    const ambitoLabel  = fUsati.idAmbito  ? (ambiti.find(a  => a.id === parseInt(fUsati.idAmbito))?.descrizione  || '') : '';
    const tipoLabel    = fUsati.idTipo    ? (tipi.find(t    => t.id === parseInt(fUsati.idTipo))?.descrizione    || '') : '';
    const oggettoLabel = fUsati.idOggetto ? (oggetti.find(o => o.id === parseInt(fUsati.idOggetto))?.descrizione || '') : '';
    const areaLabel    = fUsati.collocazione || '';
    const operatoreLabel = fUsati.idOperatore
      ? (() => { const o = esecutori.find(x => x.id === parseInt(fUsati.idOperatore)); return o ? `${o.nome} ${o.cognome}` : ''; })()
      : '';
    const responsabileLabel = fUsati.idOpMonitoraggio
      ? (() => { const o = responsabili.find(x => x.id === parseInt(fUsati.idOpMonitoraggio)); return o ? `${o.nome} ${o.cognome}` : ''; })()
      : '';

    // Totali
    const tot        = lista.length;
    const conformi   = lista.filter(r => r.esito_conforme === true).length;
    const naCount    = lista.filter(r => r.saltata === true).length;
    const ncCount    = lista.filter(r => r.esito_conforme === false && r.saltata !== true).length;
    const verificate = lista.filter(r => r.verificato).length;

    // Indicizzazione apici: solo se nel periodo ci sono 2+ verificatori distinti.
    // Con 1 solo (o 0) la stampa resta come oggi (✓ "nudo" + un blocco firma).
    const usaApici = verificatoriPeriodo.length >= 2;
    const apiceById = new Map();
    verificatoriPeriodo.forEach((v, i) => apiceById.set(v.id, i + 1));

    // Raggruppamento per ambito
    const gruppi = {};
    lista.forEach(r => {
      const k = r.ambito || '—';
      if (!gruppi[k]) gruppi[k] = [];
      gruppi[k].push(r);
    });
    // Ordino ogni gruppo per data decrescente all'interno
    Object.keys(gruppi).forEach(k => {
      gruppi[k].sort((a, b) => new Date(b.data_effettuazione) - new Date(a.data_effettuazione));
    });
    const nomiAmbiti = Object.keys(gruppi).sort();

    // Riga HTML per un record
    const rigaHtml = (r) => {
      const valore = (r.esito_valore !== null && r.esito_valore !== undefined && r.esito_valore !== '')
        ? esc(r.esito_valore) : '—';
      const soglia = (r.valore_min !== null && r.valore_min !== undefined) || (r.valore_max !== null && r.valore_max !== undefined)
        ? `<span class="sub">${r.valore_min !== null && r.valore_min !== undefined ? `min ${esc(r.valore_min)}` : ''}${r.valore_max !== null && r.valore_max !== undefined ? ` max ${esc(r.valore_max)}` : ''}</span>`
        : '';
      return `
        <tr>
          <td class="mono">${fmtD(r.data_effettuazione)}</td>
          <td class="mono c">${fmtO(r.ora_effettuazione)}</td>
          <td>
            <div class="b">${esc(r.oggetto)}</div>
            ${r.collocazione ? `<span class="sub">${esc(r.collocazione)}</span>` : ''}
          </td>
          <td>
            ${esc(r.tipo_attivita)}
            ${r.sub_attivita ? `<div class="sub">${esc(r.sub_attivita)}</div>` : ''}
            ${r.registro_speciale ? `<span class="chipReg">${esc(r.registro_speciale)}</span>` : ''}
          </td>
          <td class="c">
            <span class="chip ${r.saltata ? 'chipNA' : (r.esito_conforme ? 'chipOK' : 'chipNC')}">${r.saltata ? 'NA' : (r.esito_conforme ? 'C' : 'NC')}</span>
          </td>
          <td class="c mono">${valore}${soglia}</td>
          <td>
            <div>${esc(r.operatore)}</div>
            ${r.firma_operatore ? `<img class="firmaOp" src="${BACKEND}${r.firma_operatore}" alt="firma" />` : ''}
          </td>
          <td class="c">${
            r.verificato
              ? `<span class="vCheck">✓${usaApici && r.id_verificatore && apiceById.has(r.id_verificatore)
                  ? `<sup class="vApice">(${apiceById.get(r.id_verificatore)})</sup>`
                  : ''}</span>`
              : '<span class="vNo">—</span>'
          }</td>
          <td class="note">${r.note ? esc(r.note) : ''}</td>
        </tr>`;
    };

    // Tabella con gruppi per ambito
    const corpoTabella = nomiAmbiti.map(nome => {
      const righe = gruppi[nome];
      const ncg = righe.filter(r => !r.esito_conforme && !r.saltata).length;
      return `
        <tbody class="gruppo">
          <tr class="grpHdr">
            <td colspan="9">
              <span class="grpNome">${esc(nome)}</span>
              <span class="grpMeta">${righe.length} ${righe.length === 1 ? 'record' : 'record'}${ncg > 0 ? ` · <span class="grpNc">${ncg} NC</span>` : ''}</span>
            </td>
          </tr>
          ${righe.map(rigaHtml).join('')}
        </tbody>`;
    }).join('');

    // Calce con firma verificatori (uno o più) + totali
    const blocchiFirma = verificatoriPeriodo.length === 0
      ? `
          <div class="firmaLabel">Verifica</div>
          <div class="firmaNome sub">Nessuna verifica effettuata nel periodo</div>
        `
      : `
          <div class="firmaLabel">${verificatoriPeriodo.length === 1 ? 'Ultima verifica' : 'Verificatori del periodo'}</div>
          ${usaApici ? `
            <div class="firmaLegenda">${verificatoriPeriodo.map((v, i) => `<span>(${i + 1}) ${esc(v.nome)}</span>`).join(' · ')}</div>
          ` : ''}
          <div class="firmaBlocchi">
            ${verificatoriPeriodo.map((v, i) => `
              <div class="firmaBlocco">
                <div class="firmaNome">
                  ${usaApici ? `<sup class="vApice">(${i + 1})</sup> ` : ''}<strong>${esc(v.nome)}</strong> — ${fmtD(v.ultimaData)}
                </div>
                ${v.firma ? `<img class="firmaImg" src="${BACKEND}${v.firma}" alt="firma" />` : '<div class="firmaPlaceholder">(nessuna firma digitale)</div>'}
                <div class="firmaRighe">${v.righe} ${v.righe === 1 ? 'riga verificata' : 'righe verificate'}</div>
              </div>
            `).join('')}
          </div>
        `;

    const calceHtml = `
      <div class="calce">
        <div class="firmaBox">
          ${blocchiFirma}
        </div>
        <div class="totali">
          <div class="totTitle">Totali del periodo</div>
          <div class="totGrid">
            <div class="totCell"><div class="totNum">${tot}</div><div class="totLab">Record</div></div>
            <div class="totCell ok"><div class="totNum">${conformi}</div><div class="totLab">Conformi</div></div>
            <div class="totCell ${ncCount > 0 ? 'nc' : ''}"><div class="totNum">${ncCount}</div><div class="totLab">Non conformi</div></div>
            <div class="totCell ${naCount > 0 ? 'na' : ''}"><div class="totNum">${naCount}</div><div class="totLab">N.A.</div></div>
            <div class="totCell ver"><div class="totNum">${verificate}</div><div class="totLab">Verificate</div></div>
          </div>
        </div>
      </div>
      ${impostazioni.fornitore_nome ? `
        <div class="fornitore">
          ${impostazioni.fornitore_logo ? `<img src="${BACKEND}${impostazioni.fornitore_logo}" alt="fornitore" />` : ''}
          <span>${esc(impostazioni.fornitore_nome)}${impostazioni.fornitore_telefono ? ` · ${esc(impostazioni.fornitore_telefono)}` : ''}</span>
        </div>
      ` : ''}
    `;

    const html = `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8">
  <title>Registro monitoraggi HACCP — ${esc(azienda?.ragione_sociale || '')}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm 12mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif; color: #0F172A; font-size: 10pt; background: #F7F8FC; }
    .toolbar { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #E2E8F0; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
    .toolbar .titolo { font-weight: 700; color: #2563EB; font-size: 14px; }
    .toolbar .btns button { padding: 8px 16px; font-size: 13px; font-weight: 600; margin-left: 8px; border-radius: 8px; cursor: pointer; border: 1.5px solid; font-family: inherit; }
    .toolbar .primary { background: #2563EB; color: #fff; border-color: #2563EB; }
    .toolbar .ghost { background: #fff; color: #475569; border-color: #CBD5E1; }
    .sheet { max-width: 297mm; margin: 12px auto; padding: 12mm; background: #fff; box-shadow: 0 4px 16px rgba(15,23,42,.08); }
    .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; border-bottom: 2px solid #2563EB; padding-bottom: 10px; margin-bottom: 14px; }
    .header .azienda { display: flex; gap: 14px; align-items: center; }
    .header .azLogo { max-height: 64px; max-width: 180px; object-fit: contain; }
    .header .azDati .rs { font-weight: 700; font-size: 14pt; color: #0F172A; letter-spacing: -.01em; }
    .header .azDati .sub { color: #475569; font-size: 9pt; margin-top: 1px; }
    .header .report { text-align: right; }
    .header .report .tit { font-weight: 700; font-size: 13pt; color: #2563EB; }
    .header .report .periodo { margin-top: 3px; color: #334155; font-size: 10pt; font-family: 'JetBrains Mono', monospace; }
    .header .report .filtri { color: #64748B; font-size: 9pt; margin-top: 3px; }
    .header .report .stampa { color: #94A3B8; font-size: 8pt; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    thead th { background: #EFF6FF; color: #2563EB; padding: 7px 8px; text-align: left; font-weight: 700; border-bottom: 1.5px solid #CBD5E1; font-size: 7.5pt; text-transform: uppercase; letter-spacing: .04em; }
    thead th.c { text-align: center; }
    tbody td { padding: 5px 8px; border-bottom: 1px solid #E2E8F0; vertical-align: top; color: #334155; }
    tbody td.c { text-align: center; }
    tbody td.mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 8.5pt; }
    tbody td.note { color: #64748B; font-size: 8pt; font-style: italic; max-width: 180px; }
    .b { font-weight: 600; color: #0F172A; }
    .sub { display: block; color: #94A3B8; font-size: 7.5pt; margin-top: 1px; }
    .chip { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 8pt; font-weight: 700; border: 1px solid; }
    .chipOK { background: #ECFDF5; color: #16a34a; border-color: #bbf7d0; }
    .chipNC { background: #FEF2F2; color: #DC2626; border-color: #fecaca; }
    .chipReg { display: inline-block; background: #F3EFFE; color: #7C5AED; border: 1px solid #e0d5fa; padding: 0 6px; border-radius: 8px; font-size: 7pt; font-weight: 700; margin-top: 2px; }
    .firmaOp { max-height: 24px; max-width: 70px; object-fit: contain; display: block; margin-top: 2px; }
    .vCheck { color: #16a34a; font-weight: 700; }
    .vNo { color: #CBD5E1; }
    .gruppo { page-break-inside: auto; }
    .grpHdr td { background: #F1F5F9; padding: 6px 10px; border-top: 1.5px solid #CBD5E1; border-bottom: 1px solid #CBD5E1; }
    .grpNome { font-weight: 700; color: #0F172A; font-size: 10pt; letter-spacing: -.01em; }
    .grpMeta { color: #64748B; font-size: 8pt; margin-left: 12px; }
    .grpNc { color: #DC2626; font-weight: 700; }
    .empty { text-align: center; padding: 40px; color: #94A3B8; font-size: 11pt; }
    .calce { margin-top: 22px; padding-top: 12px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; page-break-inside: avoid; }
    .firmaBox { min-width: 200px; }
    .firmaLabel { font-size: 8pt; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; }
    .firmaNome { font-size: 10pt; color: #334155; margin-top: 3px; }
    .firmaNome.sub { color: #94A3B8; font-style: italic; display: block; }
    .firmaImg { max-height: 50px; max-width: 180px; object-fit: contain; margin-top: 4px; display: block; }
    .firmaPlaceholder { color: #CBD5E1; font-size: 9pt; font-style: italic; margin-top: 4px; }
    .firmaLegenda { font-size: 8pt; color: #64748B; margin: 4px 0 8px; }
    .firmaLegenda span { margin-right: 6px; }
    .firmaBlocchi { display: flex; flex-wrap: wrap; gap: 16px 24px; }
    .firmaBlocco { min-width: 180px; }
    .firmaRighe { font-size: 7.5pt; color: #94A3B8; margin-top: 3px; text-transform: uppercase; letter-spacing: .04em; }
    .vApice { font-size: 7pt; color: #475569; font-weight: 700; margin-left: 1px; }
    .totali { text-align: right; }
    .totTitle { font-size: 8pt; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; margin-bottom: 6px; }
    .totGrid { display: flex; gap: 8px; }
    .totCell { padding: 6px 12px; border-radius: 6px; background: #F8FAFC; border: 1px solid #E2E8F0; min-width: 60px; text-align: center; }
    .totCell.ok { background: #ECFDF5; border-color: #bbf7d0; }
    .totCell.nc { background: #FEF2F2; border-color: #fecaca; }
    .totCell.na { background: #F8FAFC; border-color: #CBD5E1; }
    .totCell.ver { background: #EFF6FF; border-color: #bfdbfe; }
    .totNum { font-size: 15pt; font-weight: 700; font-family: 'JetBrains Mono', monospace; color: #0F172A; line-height: 1; }
    .totCell.ok .totNum { color: #16a34a; }
    .totCell.nc .totNum { color: #DC2626; }
    .totCell.na .totNum { color: #475569; }
    .totCell.ver .totNum { color: #2563EB; }
    .totLab { font-size: 7.5pt; color: #64748B; text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
    .fornitore { margin-top: 14px; padding-top: 8px; border-top: 1px dashed #E2E8F0; display: flex; align-items: center; justify-content: flex-end; gap: 8px; color: #94A3B8; font-size: 7.5pt; }
    .fornitore img { max-height: 24px; max-width: 60px; object-fit: contain; }
    @media print {
      body { background: #fff; }
      .toolbar { display: none !important; }
      .sheet { box-shadow: none; margin: 0; padding: 0; max-width: none; }
    }
  </style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
</head>
<body>
  <script>
    function scaricaPDF(btn){
      var el = document.querySelector('.sheet');
      if (!el || !window.html2pdf) { window.print(); return; }
      var titEl = document.querySelector('.sheet .tit');
      var base = (titEl ? titEl.textContent : (document.title || 'report'));
      base = base.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'report';
      var d = new Date();
      var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
      var nome = base + '-' + ymd + '.pdf';
      var orig = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Generazione...'; }
      var ripristina = function(){ if (btn) { btn.disabled = false; btn.textContent = orig; } };
      window.html2pdf().set({
        margin: 8,
        filename: nome,
        image: { type: 'jpeg', quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'] }
      }).from(el).save().then(ripristina).catch(function(){ ripristina(); window.print(); });
    }
  </script>
  <div class="toolbar">
    <div class="titolo">Anteprima registro monitoraggi HACCP</div>
    <div class="btns">
      <button class="ghost" onclick="window.close()">Chiudi</button>
      <button class="ghost" onclick="scaricaPDF(this)">⬇ Scarica PDF</button>
      <button class="primary" onclick="window.print()">🖨 Stampa</button>
    </div>
  </div>

  <div class="sheet">
    <!-- Intestazione -->
    <div class="header">
      <div class="azienda">
        ${azienda?.logo ? `<img class="azLogo" src="${BACKEND}${azienda.logo}" alt="logo" />` : ''}
        <div class="azDati">
          <div class="rs">${esc(azienda?.ragione_sociale || '')}</div>
          ${azienda?.indirizzo ? `<div class="sub">${esc(azienda.indirizzo)}</div>` : ''}
          ${azienda?.citta ? `<div class="sub">${esc(azienda.citta)}</div>` : ''}
          ${azienda?.telefono ? `<div class="sub">${esc(azienda.telefono)}</div>` : ''}
        </div>
      </div>
      <div class="report">
        <div class="tit">Registro monitoraggi HACCP</div>
        <div class="periodo">
          ${fUsati.dal ? fmtD(fUsati.dal) : '—'} — ${fUsati.al ? fmtD(fUsati.al) : '—'}
        </div>
        ${ambitoLabel ? `<div class="filtri">Ambito: <strong>${esc(ambitoLabel)}</strong></div>` : ''}
        ${tipoLabel ? `<div class="filtri">Tipo: <strong>${esc(tipoLabel)}</strong></div>` : ''}
        ${areaLabel ? `<div class="filtri">Area / reparto: <strong>${esc(areaLabel)}</strong></div>` : ''}
        ${operatoreLabel ? `<div class="filtri">Eseguito da: <strong>${esc(operatoreLabel)}</strong></div>` : ''}
        ${responsabileLabel ? `<div class="filtri">Responsabile assegnato: <strong>${esc(responsabileLabel)}</strong></div>` : ''}
        ${oggettoLabel ? `<div class="filtri">Oggetto: <strong>${esc(oggettoLabel)}</strong></div>` : ''}
        <div class="stampa">Stampato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>

    <!-- Tabella -->
    ${tot === 0 ? `
      <div class="empty">Nessun record nel periodo selezionato</div>
    ` : `
      <table>
        <thead>
          <tr>
            <th style="width: 8%;">Data</th>
            <th class="c" style="width: 5%;">Ora</th>
            <th style="width: 18%;">Oggetto / Collocazione</th>
            <th style="width: 16%;">Tipo attività</th>
            <th class="c" style="width: 6%;">Esito</th>
            <th class="c" style="width: 10%;">Valore</th>
            <th style="width: 13%;">Operatore</th>
            <th class="c" style="width: 6%;">Verif.</th>
            <th style="width: 18%;">Note</th>
          </tr>
        </thead>
        ${corpoTabella}
      </table>
    `}

    <!-- Calce -->
    ${calceHtml}
  </div>

  <script>
    // Autofocus per facilitare la stampa via Ctrl+P
    window.focus();
  </script>
</body>
</html>`;

    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div style={styles.container} className="storico-container">

      {daCorreggere && (
        <ModalCorreggi
          record={daCorreggere}
          operatore={operatore}
          onConferma={() => { setDaCorreggere(null); cerca(); }}
          onAnnulla={() => setDaCorreggere(null)}
        />
      )}

      {allegatiRecord && (
        <ModalAllegati
          idAttivita={allegatiRecord.id}
          operatore={operatore}
          tipoLabel={allegatiRecord.tipo_attivita}
          onChiudi={() => { setAllegatiRecord(null); cerca(); }}
        />
      )}

      {/* Header D2 */}
      <div className="storico-header no-print" style={{ padding: '24px 24px 0', maxWidth: 960, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <div style={{
            fontSize: 22, fontWeight: 700, color: LT.n900,
            letterSpacing: '-.01em', lineHeight: 1.2,
          }}>
            Storico monitoraggi
          </div>
          {azienda?.ragione_sociale && (
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {azienda.ragione_sociale}
            </div>
          )}
        </div>
      </div>

      {/* Filtri — Card D2 */}
      <div className="storico-filtri" style={{ padding: '0 24px', maxWidth: 960, margin: '0 auto' }}>
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 36, height: 36, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.filter} size={18} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Filtri ricerca</div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>Ambito, tipo e periodo</div>
            </div>
          </div>

          {/* Ambito */}
          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
            }}>Ambito</label>
            <select
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
              }}
              value={filtri.idAmbito}
              onChange={e => setFiltri({ ...filtri, idAmbito: e.target.value })}
            >
              <option value=''>— tutti gli ambiti —</option>
              {ambiti.map(a => <option key={a.id} value={a.id}>{a.descrizione}</option>)}
            </select>
          </div>

          {/* Tipo attività */}
          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
            }}>Tipo attività</label>
            <select
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
              }}
              value={filtri.idTipo}
              onChange={e => setFiltri({ ...filtri, idTipo: e.target.value })}
            >
              <option value=''>— tutti i tipi —</option>
              {tipiFilt.map(t => <option key={t.id} value={t.id}>{t.descrizione}</option>)}
            </select>
          </div>

          {/* Area / reparto — compare solo se l'anagrafica ha delle collocazioni */}
          {aree.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
              }}>Area / reparto</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                  color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                  borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
                }}
                value={filtri.collocazione}
                onChange={e => setFiltri({ ...filtri, collocazione: e.target.value, idOggetto: '' })}
              >
                <option value=''>— tutte le aree —</option>
                {aree.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}

          {/* Oggetto */}
          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
            }}>Oggetto</label>
            <select
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
              }}
              value={filtri.idOggetto}
              onChange={e => setFiltri({ ...filtri, idOggetto: e.target.value })}
            >
              <option value=''>— tutti gli oggetti —</option>
              {oggettiFiltrati.map(o => (
                <option key={o.id} value={o.id}>
                  {o.descrizione}{o.collocazione ? ` — ${o.collocazione}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Eseguito da — chi ha materialmente firmato la registrazione.
              Volutamente diverso dal "Responsabile assegnato" della verifica a
              blocchi: qui conta la firma, perche' e' quella che l'ispettore
              controlla. */}
          {esecutori.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
              }}>Eseguito da</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                  color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                  borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
                }}
                value={filtri.idOperatore}
                onChange={e => setFiltri({ ...filtri, idOperatore: e.target.value })}
              >
                <option value=''>— tutti gli operatori —</option>
                {esecutori.map(o => (
                  <option key={o.id} value={o.id}>{o.cognome} {o.nome}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: LT.n500, marginTop: 4, lineHeight: 1.35 }}>
                Chi ha firmato la registrazione, non il responsabile assegnato
                a scadenziario.
              </div>
            </div>
          )}

          {/* Responsabile assegnato — l'altra lente: la SCHEDA del titolare,
              come quella cartacea. Richiesta cliente (ago 2026): isola tutte
              le attivita' che lo scadenziario intesta a una persona, compresi
              i giorni firmati dai supplenti. Complementare a "Eseguito da". */}
          {responsabili.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
              }}>Responsabile assegnato</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                  color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                  borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
                }}
                value={filtri.idOpMonitoraggio}
                onChange={e => setFiltri({ ...filtri, idOpMonitoraggio: e.target.value })}
              >
                <option value=''>— tutti i responsabili —</option>
                {responsabili.map(o => (
                  <option key={o.id} value={o.id}>{o.cognome} {o.nome}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: LT.n500, marginTop: 4, lineHeight: 1.35 }}>
                La scheda del titolare a scadenziario: include anche i giorni
                firmati dai supplenti.
              </div>
            </div>
          )}

          {/* Periodo */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
              }}>Dal</label>
              <input
                type="date"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                  color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                  borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
                }}
                value={filtri.dal}
                onChange={e => setFiltri({ ...filtri, dal: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
              }}>Al</label>
              <input
                type="date"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
                  color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
                  borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
                }}
                value={filtri.al}
                onChange={e => setFiltri({ ...filtri, al: e.target.value })}
              />
            </div>
          </div>

          <Btn kind="primary" block icon="search" onClick={cerca}>
            Cerca
          </Btn>
        </Card>
      </div>

      {/* Risultati */}
      {cercato && (
        <div style={{ padding: '0 24px 24px', maxWidth: 960, margin: '0 auto' }}>
          <div className="storico-contatore no-print" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12,
          }}>
            <Chip kind="neutro" icon="list" size="sm">
              {lista.length} {lista.length === 1 ? 'record trovato' : 'record trovati'}
            </Chip>
            {lista.length > 0 && (
              <Btn kind="ghost" size="sm" icon="download" onClick={stampaReport}>
                Stampa
              </Btn>
            )}
          </div>

          {lista.length === 0 && (
            <Card pad={36} style={{ textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: 999, margin: '0 auto 12px',
                background: LT.n100, color: LT.n400,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.search} size={26} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: LT.n700 }}>
                Nessun record trovato
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
                Prova a modificare i filtri di ricerca.
              </div>
            </Card>
          )}

          {lista.length > 0 && (
            <div>
              {/* Visualizzazione a schermo — card D2 (la stampa è gestita da stampaReport()) */}
              <div>
                {lista.map((r, i) => {
                  const conforme = r.esito_conforme;
                  const saltata  = r.saltata;
                  const regIcon = r.registro_speciale === 'TARATURA'     ? 'wrench'
                               : r.registro_speciale === 'MANUTENZIONE' ? 'wrench'
                               : r.registro_speciale === 'ANALISI'      ? 'flask'
                               : r.registro_speciale === 'VERIFICA'     ? 'checkCircle'
                               : 'list';
                  const bordoColore = saltata ? LT.n400 : (conforme ? LT.verde : LT.rosso);
                  return (
                    <div key={r.id} style={{
                      background: '#fff',
                      border: `1px solid ${LT.n150}`,
                      borderLeft: `4px solid ${bordoColore}`,
                      borderRadius: LT.r.lg,
                      padding: 16,
                      marginBottom: 10,
                      boxShadow: LT.sh1,
                    }}>
                      {/* Riga badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        {saltata ? (
                          <Chip kind="neutro" size="sm" soft={false}>
                            N.A.
                          </Chip>
                        ) : (
                          <Chip kind={conforme ? 'verde' : 'rosso'} icon={conforme ? 'check' : 'alert'} size="sm" soft={false}>
                            {conforme ? 'Conforme' : 'Non conforme'}
                          </Chip>
                        )}
                        <Chip kind="neutro" icon="calendar" size="sm">
                          {new Date(r.data_effettuazione).toLocaleDateString('it-IT')}
                          {r.ora_effettuazione ? ` · ${String(r.ora_effettuazione).substring(0, 5)}` : ''}
                        </Chip>
                        {r.verificato ? (
                          <Chip kind="blu" icon="checkCircle" size="sm" soft>Verificato</Chip>
                        ) : (
                          <Chip kind="neutro" size="sm" soft>Non verificato</Chip>
                        )}
                        {r.registro_speciale && (
                          <Chip kind="viola" icon={regIcon} size="sm" soft>
                            {r.registro_speciale}
                          </Chip>
                        )}
                      </div>

                      {/* Titolo + dettagli */}
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <CatTile cat={r.ambito} size={40} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>
                            {r.oggetto}
                          </div>
                          <div style={{ fontSize: 13, color: LT.n600, marginTop: 3 }}>
                            {r.collocazione}
                            {r.tipo_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {r.tipo_attivita}</>}
                            {r.sub_attivita && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {r.sub_attivita}</>}
                          </div>
                        </div>
                      </div>

                      {/* Valore */}
                      {r.esito_valore !== null && r.esito_valore !== undefined && (
                        <div style={{
                          marginTop: 10,
                          background: conforme ? LT.bluSoft : LT.rossoSoft,
                          border: `1px solid ${conforme ? '#bfdbfe' : '#fecaca'}`,
                          borderRadius: LT.r.md,
                          padding: '8px 12px',
                          fontSize: 13,
                          color: conforme ? LT.blu : LT.rosso,
                          fontFamily: LT.mono,
                        }}>
                          Valore: <strong>{r.esito_valore}</strong>
                          {r.valore_min !== null && r.valore_min !== undefined && <> · min {r.valore_min}</>}
                          {r.valore_max !== null && r.valore_max !== undefined && <> · max {r.valore_max}</>}
                        </div>
                      )}

                      {/* Footer: operatore + azioni */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginTop: 12, gap: 8, flexWrap: 'wrap',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: LT.n600 }}>
                          <Ico d={I.user} size={14} color={LT.n500} />
                          <span style={{ fontWeight: 500 }}>{r.operatore}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {r.registro_speciale && (
                            <Btn kind="ghost" size="sm" icon="folder" onClick={() => setAllegatiRecord(r)}>
                              Allegati{r.num_allegati > 0 ? ` (${r.num_allegati})` : ''}
                            </Btn>
                          )}
                          {(() => {
                            const ok = puoCorreggere(r);
                            return (
                              <Btn kind="soft" size="sm" icon="edit"
                                   disabled={!ok}
                                   title={ok ? 'Correggi questa registrazione' : motivoNoCorrezione(r)}
                                   onClick={ok ? () => setDaCorreggere(r) : undefined}>
                                Correggi
                              </Btn>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Impostazioni({ onBack }) {
  const [dati,     setDati]     = useState({});
  const [salvando, setSalvando] = useState(false);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    apiFetch(`${API}/uploads/impostazioni`)
      .then(r => r.json())
      .then(d => { setDati(d); setCaricato(true); });
  }, []);

  const handleSalva = async () => {
    setSalvando(true);
    try {
      const res = await apiFetch(`${API}/uploads/impostazioni`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(dati)
      });
      if (!res.ok) throw new Error();
      toast('✓ Impostazioni salvate!');
    } catch { toast('Errore nel salvataggio', 'errore'); }
    setSalvando(false);
  };

  const handleLogoFornitore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    const res = await apiFetch(`${API}/uploads/logo-fornitore`, {
      method: 'POST',
      body: fd
    });
    const data = await res.json();
    if (data.logo) setDati({ ...dati, fornitore_logo: data.logo });
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };

  if (!caricato) return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <Card pad={28} style={{ textAlign: 'center' }}>
        <div style={{
          width: 40, height: 40, borderRadius: LT.r.full,
          background: LT.bluSoft, color: LT.blu,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 10,
        }}>
          <Ico d={I.settings} size={20} />
        </div>
        <div style={{ fontSize: 14, color: LT.n600 }}>Caricamento impostazioni…</div>
      </Card>
    </div>
  );

  const oggiData = new Date();
  const dataStr = oggiData.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>

      {/* Hero compatto */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {dataStr}
        </div>
        <div style={{
          fontSize: 22, fontWeight: 700, color: LT.n900,
          letterSpacing: '-.01em', lineHeight: 1.2,
        }}>
          Impostazioni fornitore
        </div>
        <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
          Dati che compaiono in stampe, bolle e intestazioni documentali
        </div>
      </div>

      {/* Dati fornitore */}
      <Card pad={20} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.building} size={16} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Dati fornitore</div>
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Identità dell'azienda fornitrice del servizio</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Nome / Ragione sociale</label>
          <input style={inputStyle} value={dati.fornitore_nome || ''}
            onChange={e => setDati({ ...dati, fornitore_nome: e.target.value })}
            placeholder="Es. LabThree S.r.l." />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Indirizzo</label>
          <input style={inputStyle} value={dati.fornitore_indirizzo || ''}
            onChange={e => setDati({ ...dati, fornitore_indirizzo: e.target.value })}
            placeholder="Via e numero civico, CAP città" />
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Telefono</label>
            <input style={inputStyle} value={dati.fornitore_telefono || ''}
              onChange={e => setDati({ ...dati, fornitore_telefono: e.target.value })}
              placeholder="+39 ..." />
          </div>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={dati.fornitore_email || ''}
              onChange={e => setDati({ ...dati, fornitore_email: e.target.value })}
              placeholder="info@fornitore.it" />
          </div>
        </div>
      </Card>

      {/* Logo fornitore */}
      <Card pad={20} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.arancioSoft, color: LT.arancio,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.camera} size={16} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Logo fornitore</div>
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Compare in cima a stampe e report</div>
          </div>
        </div>

        {dati.fornitore_logo && (
          <div style={{
            padding: 12, background: LT.n50, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.md, marginBottom: 10, textAlign: 'center',
          }}>
            <img src={`${BACKEND}${dati.fornitore_logo}`} alt="logo fornitore"
              style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }} />
          </div>
        )}

        <input type="file" accept="image/*" onChange={handleLogoFornitore}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 10px', fontSize: 13, fontFamily: LT.sans,
            border: `1px dashed ${LT.n200}`, borderRadius: LT.r.md,
            background: LT.n50,
          }}
        />
      </Card>

      <Btn kind="primary" icon="check" block onClick={handleSalva} disabled={salvando}>
        {salvando ? 'Salvataggio…' : 'Salva impostazioni'}
      </Btn>
    </div>
  );
}

// ─── Documenti HACCP ────────────────────────────────────────────────────────

function BadgeStato({ stato }) {
  const cfg = {
    bozza:      { kind: 'arancio', label: 'Bozza',      icon: 'edit' },
    in_vigore:  { kind: 'verde',   label: 'In vigore',  icon: 'checkCircle' },
    archiviato: { kind: 'neutro',  label: 'Archiviato', icon: 'folder' },
  }[stato] || { kind: 'neutro', label: stato || '—', icon: undefined };
  return <Chip kind={cfg.kind} icon={cfg.icon} size="sm" soft>{cfg.label}</Chip>;
}

function CardDocumento({ doc, isAdmin, onApri, onModifica, onElimina }) {
  const dataFmt = doc.data_caricamento
    ? new Date(doc.data_caricamento).toLocaleDateString('it-IT')
    : '';
  const archiviato = doc.stato === 'archiviato';
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${LT.n150}`,
      borderRadius: LT.r.lg,
      padding: 14,
      marginBottom: 8,
      boxShadow: LT.sh1,
      opacity: archiviato ? 0.65 : 1,
      transition: 'box-shadow .15s',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* Mini-tile PDF */}
        <div style={{
          width: 40, height: 40, borderRadius: LT.r.md,
          background: LT.rossoSoft, color: LT.rosso,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Ico d={I.book} size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900, lineHeight: 1.3 }}>
              {doc.nome}
            </span>
            {doc.revisione && (
              <span style={{ fontSize: 11.5, fontWeight: 500, color: LT.n400, fontFamily: LT.mono }}>
                {doc.revisione}
              </span>
            )}
          </div>
          {doc.descrizione && (
            <div style={{ fontSize: 13, color: LT.n600, marginTop: 3, lineHeight: 1.4 }}>
              {doc.descrizione}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
            {isAdmin && <BadgeStato stato={doc.stato} />}
            {doc.caricato_da && (
              <span style={{ fontSize: 11.5, color: LT.n500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Ico d={I.user} size={11} color={LT.n400} />
                {doc.caricato_da}{dataFmt ? ` · ${dataFmt}` : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, alignItems: 'flex-end' }}>
          <Btn kind="primary" size="sm" icon="eye" onClick={() => onApri(doc)}>
            Apri
          </Btn>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn kind="ghost" size="sm" icon="edit" onClick={() => onModifica(doc)}>Modifica</Btn>
              <Btn kind="danger" size="sm" icon="x" onClick={() => onElimina(doc)}>Elimina</Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GestioneDocumenti({ operatore, onBack }) {
  const isAdmin = operatore.admin || operatore.master;

  const [documenti,       setDocumenti]       = useState([]);
  const [allegatiTecnici, setAllegatiTecnici] = useState([]);
  const [caricamento,     setCaricamento]      = useState(true);
  const [filtroCategoria, setFiltroCategoria]  = useState('');
  const [filtroStato,     setFiltroStato]      = useState('');
  const [cerca,           setCerca]            = useState('');
  const [form,            setForm]             = useState(null);
  const [confermaElimina, setConfermaElimina]  = useState(null);
  const [salvando,        setSalvando]         = useState(false);
  const [erroreForm,      setErroreForm]       = useState('');
  const [storageInfo,     setStorageInfo]      = useState(null);

  const carica = async () => {
    setCaricamento(true);
    try {
      const soloVigenti = isAdmin ? 'false' : 'true';
      const [resDoc, resAll, resStorage] = await Promise.all([
        apiFetch(`${API}/documenti?idAzienda=${operatore.id_azienda}&soloVigenti=${soloVigenti}`),
        apiFetch(`${API}/allegati${operatore.master ? '' : `?idAzienda=${operatore.id_azienda}`}`),
        apiFetch(`${API}/documenti/storage-usage?idAzienda=${operatore.id_azienda}`),
      ]);
      const docs    = await resDoc.json();
      const alls    = await resAll.json();
      const storage = await resStorage.json();
      setDocumenti(Array.isArray(docs) ? docs : []);
      setAllegatiTecnici(Array.isArray(alls) ? alls : []);
      if (storage && typeof storage.usato === 'number') setStorageInfo(storage);
    } catch {
      setDocumenti([]);
      setAllegatiTecnici([]);
    }
    setCaricamento(false);
  };

  useEffect(() => { carica(); }, []); // eslint-disable-line

  const documentiFiltrati = documenti.filter(d => {
    if (filtroCategoria && d.categoria !== filtroCategoria) return false;
    if (isAdmin && filtroStato && d.stato !== filtroStato)  return false;
    if (cerca) {
      const q = cerca.toLowerCase();
      if (!`${d.nome || ''} ${d.descrizione || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const allegatiTecniciFiltrati = (filtroCategoria === '' || filtroCategoria === 'Documenti tecnici')
    ? allegatiTecnici.filter(al => {
        if (cerca) {
          const q = cerca.toLowerCase();
          if (!`${al.nome || ''} ${al.tipo_attivita || ''} ${al.oggetto || ''}`.toLowerCase().includes(q)) return false;
        }
        return true;
      })
    : [];

  const categoriePopolate = CATEGORIE_DOC.filter(cat => {
    if (cat === 'Documenti tecnici') return allegatiTecniciFiltrati.length > 0 || documentiFiltrati.some(d => d.categoria === cat);
    return documentiFiltrati.some(d => d.categoria === cat);
  });

  const apriNuovoForm = () => setForm({
    mode: 'nuovo',
    doc:  { nome: '', descrizione: '', categoria: '', revisione: '', stato: 'bozza', file: null },
  });

  const apriModificaForm = (doc) => setForm({
    mode: 'modifica',
    doc:  { ...doc, file: null },
  });

  const aggiornaForm = (campo, valore) =>
    setForm(f => ({ ...f, doc: { ...f.doc, [campo]: valore } }));

  const handleSalva = async () => {
    setErroreForm('');
    const { doc, mode } = form;
    if (!doc.nome.trim()) return setErroreForm('Il nome è obbligatorio');
    if (!doc.categoria)   return setErroreForm('Seleziona una categoria');
    if (!doc.stato)       return setErroreForm('Seleziona uno stato');
    if (mode === 'nuovo' && !doc.file) return setErroreForm('Seleziona un file PDF');

    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append('nome',        doc.nome.trim());
      fd.append('descrizione', doc.descrizione || '');
      fd.append('categoria',   doc.categoria);
      fd.append('revisione',   doc.revisione || '');
      fd.append('stato',       doc.stato);
      fd.append('idAzienda',   operatore.id_azienda);
      fd.append('idOperatore', operatore.id);
      if (doc.file) fd.append('file', doc.file);

      const url    = mode === 'nuovo' ? `${API}/documenti` : `${API}/documenti/${doc.id}`;
      const method = mode === 'nuovo' ? 'POST' : 'PUT';
      const res    = await apiFetch(url, { method, body: fd });

      if (!res.ok) {
        const err = await res.json();
        setErroreForm(err.error || 'Errore nel salvataggio');
        setSalvando(false);
        return;
      }
      setForm(null);
      setCerca('');
      await carica();
    } catch {
      setErroreForm('Errore di connessione');
    }
    setSalvando(false);
  };

  const handleElimina = async (doc) => {
    try {
      await apiFetch(`${API}/documenti/${doc.id}`, { method: 'DELETE' });
      setConfermaElimina(null);
      await carica();
    } catch {}
  };

  // Stili locali coerenti con D2
  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>

      {/* Hero compatto */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: LT.n500,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
        }}>
          {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: LT.n900,
              letterSpacing: '-.01em', lineHeight: 1.2,
            }}>
              Documenti
            </div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {isAdmin ? 'Gestione documenti aziendali' : 'Consultazione documenti in vigore'}
            </div>
          </div>
          {isAdmin && (
            <Btn kind="primary" icon="plus" onClick={apriNuovoForm}>
              Nuovo documento
            </Btn>
          )}
        </div>
      </div>

      {/* Barra storage */}
      {storageInfo && (() => {
        const pct = Math.min(100, (storageInfo.usato / storageInfo.limite) * 100);
        const fmtSize = b =>
          b >= 1073741824 ? `${(b / 1073741824).toFixed(2)} GB`
          : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB`
          : `${Math.round(b / 1024)} KB`;
        const livello = pct >= 90 ? 'alert' : pct >= 70 ? 'warn' : 'ok';
        const colore  = livello === 'alert' ? LT.rosso : livello === 'warn' ? LT.arancio : LT.blu;
        return (
          <Card pad={14} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: LT.r.sm,
                  background: LT.bluSoft, color: LT.blu,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.layers} size={15} />
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: LT.n800 }}>
                    Spazio utilizzato
                  </div>
                  <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                    Piano {storageInfo.piano}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: LT.n700, fontFamily: LT.mono }}>
                <strong style={{ color: colore }}>{fmtSize(storageInfo.usato)}</strong> / {fmtSize(storageInfo.limite)}
                <span style={{ marginLeft: 8, fontSize: 11, color: LT.n500 }}>({pct.toFixed(0)}%)</span>
              </div>
            </div>
            <div style={{ height: 8, background: LT.n150, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: colore, borderRadius: 4, transition: 'width .4s' }} />
            </div>
            {pct >= 90 && (
              <div style={{
                marginTop: 8, padding: '6px 10px', borderRadius: LT.r.sm,
                background: LT.rossoSoft, color: LT.rosso, fontSize: 12, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Ico d={I.alert} size={13} /> Spazio quasi esaurito — contatta il supporto per aggiornare il piano.
              </div>
            )}
          </Card>
        );
      })()}

      {/* Card filtri */}
      <Card pad={16} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.filter} size={16} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Filtri</div>
            <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 1 }}>Categoria, stato e ricerca</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Categoria</label>
            <select style={inputStyle} value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}>
              <option value="">Tutte le categorie</option>
              {CATEGORIE_DOC.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {isAdmin && (
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Stato</label>
              <select style={inputStyle} value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
                <option value="">Tutti gli stati</option>
                <option value="bozza">Bozza</option>
                <option value="in_vigore">In vigore</option>
                <option value="archiviato">Archiviato</option>
              </select>
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, pointerEvents: 'none',
          }}>
            <Ico d={I.search} size={16} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per nome o descrizione..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>
      </Card>

      {/* Lista */}
      {caricamento ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: LT.n500, fontSize: 13 }}>
          Caricamento…
        </div>
      ) : documentiFiltrati.length === 0 && allegatiTecniciFiltrati.length === 0 ? (
        <Card pad={40} style={{ textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 999, margin: '0 auto 14px',
            background: LT.n100, color: LT.n400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={cerca ? I.search : I.book} size={30} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: LT.n800 }}>
            {cerca ? `Nessun risultato per "${cerca}"` : 'Nessun documento disponibile'}
          </div>
          {!cerca && isAdmin && (
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
              Clicca "Nuovo documento" per iniziare.
            </div>
          )}
        </Card>
      ) : (
        categoriePopolate.map(cat => (
          <div key={cat} style={{ marginBottom: 22 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: LT.n500,
              letterSpacing: '.08em', textTransform: 'uppercase',
              marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Ico d={I.folder} size={13} color={LT.n400} />
              {cat}
            </div>
            {documentiFiltrati
              .filter(d => d.categoria === cat)
              .map(doc => (
                <CardDocumento
                  key={doc.id}
                  doc={doc}
                  isAdmin={isAdmin}
                  onApri={d => window.open(`${BACKEND}${d.file_path}`, '_blank')}
                  onModifica={apriModificaForm}
                  onElimina={setConfermaElimina}
                />
              ))
            }
            {cat === 'Documenti tecnici' && allegatiTecniciFiltrati.map(al => {
              const isPdf = /\.pdf$/i.test(al.nome);
              const isImg = /\.(jpg|jpeg|png|gif)$/i.test(al.nome);
              const ico   = isPdf ? I.book : isImg ? I.camera : I.folder;
              return (
                <div key={`al-${al.id}`} style={{
                  background: '#fff',
                  border: `1px solid ${LT.n150}`,
                  borderLeft: `3px solid ${LT.blu}`,
                  borderRadius: LT.r.lg,
                  padding: 12,
                  marginBottom: 8,
                  boxShadow: LT.sh1,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.md,
                    background: LT.bluSoft, color: LT.blu,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Ico d={ico} size={17} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={`${BACKEND}${al.path_file}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 13.5, fontWeight: 600, color: LT.blu, textDecoration: 'none', wordBreak: 'break-all' }}>
                      {al.nome}
                    </a>
                    <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {al.registro_speciale && (
                        <Chip kind="viola" icon="wrench" size="sm" soft>{al.registro_speciale}</Chip>
                      )}
                      {al.oggetto && <span>{al.oggetto}</span>}
                      {al.data_effettuazione && (
                        <span style={{ fontFamily: LT.mono }}>
                          {new Date(al.data_effettuazione).toLocaleDateString('it-IT')}
                        </span>
                      )}
                      {al.caricato_da && <span>— {al.caricato_da}</span>}
                    </div>
                  </div>
                  <Chip kind="ciano" size="sm" soft>Allegato tecnico</Chip>
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Modal: nuovo / modifica */}
      {form && (() => {
        const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(3px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
        const box = { background: '#fff', borderRadius: LT.r.xl, padding: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(15,23,42,0.35)' };
        return (
          <div style={overlay} onClick={() => { setForm(null); setErroreForm(''); }}>
            <div style={box} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.md,
                    background: LT.bluSoft, color: LT.blu,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Ico d={form.mode === 'nuovo' ? I.plus : I.edit} size={18} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>
                    {form.mode === 'nuovo' ? 'Nuovo documento' : 'Modifica documento'}
                  </div>
                </div>
                <button
                  onClick={() => { setForm(null); setErroreForm(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: LT.n500, padding: 6, display: 'flex' }}
                  aria-label="Chiudi"
                >
                  <Ico d={I.x} size={18} />
                </button>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Nome *</label>
                <input style={inputStyle} value={form.doc.nome}
                  onChange={e => aggiornaForm('nome', e.target.value)}
                  placeholder="es. Piano HACCP Stabilimento A" />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Categoria *</label>
                <select style={inputStyle} value={form.doc.categoria}
                  onChange={e => aggiornaForm('categoria', e.target.value)}>
                  <option value="">Seleziona...</option>
                  {CATEGORIE_DOC.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Revisione</label>
                  <input style={inputStyle} value={form.doc.revisione}
                    onChange={e => aggiornaForm('revisione', e.target.value)}
                    placeholder="es. Ed.2 Rev. 01" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Stato *</label>
                  <select style={inputStyle} value={form.doc.stato}
                    onChange={e => aggiornaForm('stato', e.target.value)}>
                    <option value="bozza">Bozza</option>
                    <option value="in_vigore">In vigore</option>
                    <option value="archiviato">Archiviato</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Descrizione</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 72, resize: 'vertical', fontFamily: LT.sans }}
                  value={form.doc.descrizione}
                  onChange={e => aggiornaForm('descrizione', e.target.value)}
                  placeholder="Descrizione opzionale..." />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  File PDF{form.mode === 'modifica' ? ' (lascia vuoto per mantenere il file attuale)' : ' *'}
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => aggiornaForm('file', e.target.files[0] || null)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '8px 10px', fontSize: 13, fontFamily: LT.sans,
                    border: `1px dashed ${LT.n200}`, borderRadius: LT.r.md,
                    background: LT.n50 || '#F8FAFC',
                  }} />
                {/* Il limite viveva solo nel backend: l'utente lo scopriva
                    sbattendoci contro. Va detto prima, come per gli allegati
                    delle non conformita'. */}
                <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 6 }}>
                  Solo PDF, massimo 20 MB.
                </div>
                {form.mode === 'modifica' && form.doc.nome_file_originale && !form.doc.file && (
                  <div style={{ fontSize: 11.5, color: LT.n500, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Ico d={I.book} size={12} color={LT.n400} />
                    File attuale: <strong style={{ color: LT.n700 }}>{form.doc.nome_file_originale}</strong>
                  </div>
                )}
              </div>

              {erroreForm && (
                <div style={{
                  background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
                  padding: '8px 12px', marginBottom: 10, fontSize: 13, fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
                }}>
                  <Ico d={I.alert} size={14} /> {erroreForm}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Btn kind="outline" onClick={() => { setForm(null); setErroreForm(''); }}>
                  Annulla
                </Btn>
                <div style={{ flex: 1 }}>
                  <Btn kind="primary" block icon="check" onClick={handleSalva} disabled={salvando}>
                    {salvando ? 'Salvataggio…' : 'Salva'}
                  </Btn>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal: conferma eliminazione */}
      {confermaElimina && (() => {
        const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(3px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
        const box = { background: '#fff', borderRadius: LT.r.xl, padding: 22, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(15,23,42,0.35)' };
        return (
          <div style={overlay} onClick={() => setConfermaElimina(null)}>
            <div style={box} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: LT.r.md,
                  background: LT.rossoSoft, color: LT.rosso,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.alert} size={20} />
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>
                  Elimina documento
                </div>
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: 14, color: LT.n700 }}>
                Stai per eliminare definitivamente <strong style={{ color: LT.n900 }}>{confermaElimina.nome}</strong>.
              </p>
              <p style={{ margin: '0 0 18px 0', fontSize: 13, color: LT.n500 }}>
                Il file PDF verrà rimosso dal server. L'operazione non è reversibile.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn kind="outline" onClick={() => setConfermaElimina(null)}>Annulla</Btn>
                <div style={{ flex: 1 }}>
                  <Btn
                    kind="primary"
                    block
                    icon="x"
                    style={{ background: LT.rosso, boxShadow: '0 4px 12px rgba(220,38,38,.25)' }}
                    onClick={() => handleElimina(confermaElimina)}
                  >
                    Elimina definitivamente
                  </Btn>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════
// STATISTICHE NC (dashboard dentro il Registro NC)
// Dati aggregati via /api/nc/statistiche. Grafici fatti in casa con
// SVG e flex per non introdurre dipendenze di charting.
// ═══════════════════════════════════════════════════════════════

function StatisticheNc({ operatore, onApriDettaglio }) {
  const [dati,      setDati]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [errore,    setErrore]    = useState('');
  const [periodo,   setPeriodo]   = useState('12mesi'); // '3mesi' | '12mesi' | 'tutto' | 'personalizzato'

  // Formato data locale YYYY-MM-DD (evita lo shift di fuso di toISOString)
  const fmtData = (d) => {
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, '0');
    const gg = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${gg}`;
  };

  // Default per il periodo Personalizzato = mese corrente,
  // così appena lo apri vedi subito qualcosa di utile.
  const _oggiIniz   = new Date();
  const _primoMese  = new Date(_oggiIniz.getFullYear(), _oggiIniz.getMonth(),     1);
  const _ultimoMese = new Date(_oggiIniz.getFullYear(), _oggiIniz.getMonth() + 1, 0);
  const [dalCustom, setDalCustom] = useState(fmtData(_primoMese));
  const [alCustom,  setAlCustom]  = useState(fmtData(_ultimoMese));

  const calcolaRange = (p) => {
    if (p === 'tutto')          return { dal: '', al: '' };
    if (p === 'personalizzato') return { dal: dalCustom || '', al: alCustom || '' };
    const oggi = new Date();
    const al   = fmtData(oggi);
    const d    = new Date(oggi);
    d.setMonth(d.getMonth() - (p === '3mesi' ? 3 : 12));
    return { dal: fmtData(d), al };
  };

  useEffect(() => {
    const carica = async () => {
      setLoading(true);
      setErrore('');
      try {
        const { dal, al } = calcolaRange(periodo);
        const params = new URLSearchParams();
        if (!operatore.master) params.append('idAzienda', operatore.id_azienda);
        else                   params.append('idAzienda', operatore.id_azienda); // master usa la sua azienda come default (nessun cross-tenant qui)
        if (dal) params.append('dal', dal);
        if (al)  params.append('al',  al);
        const res  = await apiFetch(`${API}/nc/statistiche?${params}`);
        if (!res.ok) throw new Error('http ' + res.status);
        const data = await res.json();
        setDati(data);
      } catch (e) {
        setErrore('Errore nel caricamento statistiche');
      }
      setLoading(false);
    };
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, dalCustom, alCustom]);

  // ─── Scorciatoie del periodo personalizzato ─────────────────────────────
  const setPeriodoMese = (offsetMesi = 0) => {
    const o = new Date();
    const primo  = new Date(o.getFullYear(), o.getMonth() + offsetMesi,     1);
    const ultimo = new Date(o.getFullYear(), o.getMonth() + offsetMesi + 1, 0);
    setDalCustom(fmtData(primo));
    setAlCustom(fmtData(ultimo));
  };
  const setAnnoCorrente = () => {
    const o = new Date();
    setDalCustom(`${o.getFullYear()}-01-01`);
    setAlCustom(fmtData(o));
  };
  const setMeseSpecifico = (yyyyMm) => {
    if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return;
    const [y, m]  = yyyyMm.split('-').map(Number);
    const primo   = new Date(y, m - 1, 1);
    const ultimo  = new Date(y, m,     0);
    setDalCustom(fmtData(primo));
    setAlCustom(fmtData(ultimo));
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const KpiCard = ({ label, valore, kind, suffix }) => {
    const colMap = {
      neutro: { c: LT.n900, bg: '#fff',          bd: LT.n200    },
      rosso:  { c: LT.rosso, bg: LT.rossoSoft,   bd: '#fecaca'  },
      verde:  { c: LT.verde, bg: LT.verdeSoft,   bd: '#bbf7d0'  },
      blu:    { c: LT.blu,   bg: LT.bluSoft,     bd: '#bfdbfe'  },
    };
    const st = colMap[kind || 'neutro'];
    return (
      <div style={{
        flex: '1 1 140px', minWidth: 140,
        padding: 14, borderRadius: LT.r.md,
        background: st.bg, border: `1px solid ${st.bd}`,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500,
                      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
          {label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: st.c,
                        fontFamily: LT.mono, letterSpacing: '-.02em', lineHeight: 1 }}>
            {valore}
          </div>
          {suffix && (
            <div style={{ fontSize: 13, color: LT.n500, fontWeight: 600 }}>{suffix}</div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Card pad={24} style={{ marginBottom: 14, textAlign: 'center', color: LT.n500 }}>
        Caricamento statistiche…
      </Card>
    );
  }
  if (errore) {
    return (
      <Card pad={24} style={{ marginBottom: 14, textAlign: 'center', color: LT.rosso }}>
        <Ico d={I.alert} size={16} /> {errore}
      </Card>
    );
  }
  if (!dati) return null;

  const maxCat  = Math.max(1, ...dati.per_categoria.map(c => c.totale));
  const maxOrig = Math.max(1, ...(dati.per_origine || []).map(o => o.totale));
  const maxMese = Math.max(1, ...dati.per_mese.map(m => m.totale));

  return (
    <div style={{ marginBottom: 14 }}>

      {/* Segmented periodo */}
      <Card pad={14} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500,
                        textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Periodo
          </div>
          <div style={{
            display: 'flex', gap: 0, padding: 3, borderRadius: LT.r.md,
            background: LT.n100, border: `1px solid ${LT.n150}`,
          }}>
            {[
              { key: '3mesi',          label: 'Ultimi 3 mesi' },
              { key: '12mesi',         label: 'Ultimi 12 mesi' },
              { key: 'tutto',          label: 'Tutto' },
              { key: 'personalizzato', label: 'Personalizzato' },
            ].map(opt => {
              const on = periodo === opt.key;
              return (
                <button key={opt.key} onClick={() => setPeriodo(opt.key)} style={{
                  padding: '6px 12px', borderRadius: LT.r.sm, border: 'none',
                  background: on ? '#fff' : 'transparent',
                  color: on ? LT.n900 : LT.n500,
                  fontSize: 12.5, fontWeight: on ? 700 : 500,
                  cursor: 'pointer', fontFamily: LT.sans,
                  boxShadow: on ? LT.sh1 : 'none', transition: 'all .15s',
                }}>{opt.label}</button>
              );
            })}
          </div>
        </div>

        {periodo === 'personalizzato' && (
          <div style={{
            marginTop: 12, paddingTop: 12,
            borderTop: `1px solid ${LT.n150}`,
            display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 12, color: LT.n600, fontWeight: 600 }}>Dal</label>
              <input
                type="date"
                value={dalCustom}
                onChange={(e) => setDalCustom(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: LT.r.sm,
                  border: `1px solid ${LT.n200}`, background: '#fff',
                  fontSize: 13, fontFamily: LT.sans, color: LT.n900,
                }}
              />
              <label style={{ fontSize: 12, color: LT.n600, fontWeight: 600 }}>Al</label>
              <input
                type="date"
                value={alCustom}
                onChange={(e) => setAlCustom(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: LT.r.sm,
                  border: `1px solid ${LT.n200}`, background: '#fff',
                  fontSize: 13, fontFamily: LT.sans, color: LT.n900,
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11, color: LT.n500, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '.06em',
              }}>Rapido</span>
              {[
                { label: 'Questo mese',   onClick: () => setPeriodoMese(0)  },
                { label: 'Mese scorso',   onClick: () => setPeriodoMese(-1) },
                { label: 'Anno corrente', onClick: setAnnoCorrente          },
              ].map(b => (
                <button key={b.label} onClick={b.onClick} style={{
                  padding: '5px 10px', borderRadius: LT.r.sm,
                  border: `1px solid ${LT.n200}`, background: '#fff',
                  color: LT.n700, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: LT.sans,
                }}>{b.label}</button>
              ))}
              <label style={{ fontSize: 12, color: LT.n600, fontWeight: 600, marginLeft: 4 }}>
                Mese
              </label>
              <input
                type="month"
                onChange={(e) => setMeseSpecifico(e.target.value)}
                style={{
                  padding: '5px 8px', borderRadius: LT.r.sm,
                  border: `1px solid ${LT.n200}`, background: '#fff',
                  color: LT.n900, fontSize: 12.5, fontFamily: LT.sans,
                }}
                title="Imposta dal/al sul mese scelto"
              />
            </div>
          </div>
        )}
      </Card>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <KpiCard label="Totale NC"           valore={dati.totale}                  kind="neutro" />
        <KpiCard label="Aperte"              valore={dati.aperte}                  kind={dati.aperte > 0 ? 'rosso' : 'neutro'} />
        <KpiCard label="Chiuse"              valore={dati.chiuse}                  kind="verde" />
        <KpiCard label="Tempo medio chiusura" valore={dati.tempo_medio_chiusura_gg} suffix="gg" kind="blu" />
      </div>

      {/* Per categoria */}
      <Card pad={18} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.violaSoft, color: LT.viola,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.layers} size={18} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Distribuzione per categoria</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
              Aperte (rosso) e chiuse (verde)
            </div>
          </div>
        </div>
        {dati.per_categoria.length === 0 ? (
          <div style={{ color: LT.n400, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
            Nessuna NC nel periodo selezionato
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dati.per_categoria.map(c => {
              const pctAp = (c.aperte / maxCat) * 100;
              const pctCh = (c.chiuse / maxCat) * 100;
              return (
                <div key={c.categoria}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 12.5, marginBottom: 4,
                  }}>
                    <div style={{ color: LT.n800, fontWeight: 600 }}>{c.categoria}</div>
                    <div style={{ color: LT.n500 }}>
                      <span style={{ color: LT.rosso, fontWeight: 700 }}>{c.aperte}</span>
                      <span style={{ margin: '0 4px', color: LT.n300 }}>·</span>
                      <span style={{ color: LT.verde, fontWeight: 700 }}>{c.chiuse}</span>
                      <span style={{ margin: '0 6px', color: LT.n300 }}>=</span>
                      <span style={{ color: LT.n900, fontWeight: 700 }}>{c.totale}</span>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', height: 10, borderRadius: 5,
                    background: LT.n100, overflow: 'hidden',
                  }}>
                    {pctAp > 0 && (
                      <div style={{ width: `${pctAp}%`, background: LT.rosso, transition: 'width .3s' }} />
                    )}
                    {pctCh > 0 && (
                      <div style={{ width: `${pctCh}%`, background: LT.verde, transition: 'width .3s' }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Per origine */}
      <Card pad={18} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.arancioSoft, color: LT.arancio,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.gitFork} size={18} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Distribuzione per origine</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
              Da dove emerge la NC (autocontrollo, audit, reclamo, …)
            </div>
          </div>
        </div>
        {(!dati.per_origine || dati.per_origine.length === 0) ? (
          <div style={{ color: LT.n400, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
            Nessuna NC nel periodo selezionato
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dati.per_origine.map(o => {
              const pctAp = (o.aperte / maxOrig) * 100;
              const pctCh = (o.chiuse / maxOrig) * 100;
              return (
                <div key={o.origine}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 12.5, marginBottom: 4,
                  }}>
                    <div style={{ color: LT.n800, fontWeight: 600 }}>{o.origine}</div>
                    <div style={{ color: LT.n500 }}>
                      <span style={{ color: LT.rosso, fontWeight: 700 }}>{o.aperte}</span>
                      <span style={{ margin: '0 4px', color: LT.n300 }}>·</span>
                      <span style={{ color: LT.verde, fontWeight: 700 }}>{o.chiuse}</span>
                      <span style={{ margin: '0 6px', color: LT.n300 }}>=</span>
                      <span style={{ color: LT.n900, fontWeight: 700 }}>{o.totale}</span>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', height: 10, borderRadius: 5,
                    background: LT.n100, overflow: 'hidden',
                  }}>
                    {pctAp > 0 && (
                      <div style={{ width: `${pctAp}%`, background: LT.rosso, transition: 'width .3s' }} />
                    )}
                    {pctCh > 0 && (
                      <div style={{ width: `${pctCh}%`, background: LT.verde, transition: 'width .3s' }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Trend mensile */}
      <Card pad={18} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.trend} size={18} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>Trend mensile</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
              NC registrate per mese nel periodo
            </div>
          </div>
        </div>
        {dati.per_mese.length === 0 ? (
          <div style={{ color: LT.n400, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
            Nessun dato per il trend
          </div>
        ) : (() => {
          const mesi = dati.per_mese;
          // Dimensioni SVG responsive via viewBox
          const W       = 720;
          const H       = 200;
          const PAD_L   = 30;
          const PAD_B   = 30;
          const PAD_T   = 10;
          const plotW   = W - PAD_L - 10;
          const plotH   = H - PAD_T - PAD_B;
          const barGap  = 4;
          const barW    = Math.max(8, (plotW / mesi.length) - barGap);
          const fmtLabel = (am) => {
            const [y, m] = am.split('-');
            return `${m}/${y.slice(2)}`;
          };
          // Tick della griglia Y (max arrotondato a multiplo di 5)
          const maxY = Math.max(5, Math.ceil(maxMese / 5) * 5);
          return (
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
              {/* Griglia orizzontale (4 step) */}
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                const y = PAD_T + plotH * (1 - f);
                return (
                  <g key={i}>
                    <line x1={PAD_L} y1={y} x2={W - 10} y2={y}
                      stroke={LT.n150} strokeWidth={1} strokeDasharray={i === 0 ? '' : '3,3'} />
                    <text x={PAD_L - 6} y={y + 3} fontSize="9" fill={LT.n500} textAnchor="end"
                      fontFamily={LT.mono}>
                      {Math.round(maxY * f)}
                    </text>
                  </g>
                );
              })}
              {/* Barre stacked per mese */}
              {mesi.map((m, i) => {
                const x = PAD_L + i * (plotW / mesi.length) + barGap / 2;
                const hAp = plotH * (m.aperte / maxY);
                const hCh = plotH * (m.chiuse / maxY);
                return (
                  <g key={m.anno_mese}>
                    {/* Chiuse sotto (base) */}
                    {hCh > 0 && (
                      <rect x={x} y={PAD_T + plotH - hCh}
                        width={barW} height={hCh}
                        fill={LT.verde} rx={2}>
                        <title>{`${m.anno_mese}: ${m.chiuse} chiuse`}</title>
                      </rect>
                    )}
                    {/* Aperte sopra */}
                    {hAp > 0 && (
                      <rect x={x} y={PAD_T + plotH - hCh - hAp}
                        width={barW} height={hAp}
                        fill={LT.rosso} rx={2}>
                        <title>{`${m.anno_mese}: ${m.aperte} aperte`}</title>
                      </rect>
                    )}
                    {/* Etichetta mese */}
                    <text x={x + barW / 2} y={H - PAD_B + 14}
                      fontSize="9" fill={LT.n500} textAnchor="middle" fontFamily={LT.mono}>
                      {fmtLabel(m.anno_mese)}
                    </text>
                  </g>
                );
              })}
              {/* Legenda */}
              <g transform={`translate(${W - 170}, ${PAD_T})`}>
                <rect x="0" y="0" width="10" height="10" fill={LT.rosso} rx="2" />
                <text x="14" y="9" fontSize="10" fill={LT.n600}>Aperte</text>
                <rect x="70" y="0" width="10" height="10" fill={LT.verde} rx="2" />
                <text x="84" y="9" fontSize="10" fill={LT.n600}>Chiuse</text>
              </g>
            </svg>
          );
        })()}
      </Card>

      {/* Top aperte da più tempo */}
      <Card pad={18} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: LT.rossoSoft, color: LT.rosso,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.clock} size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: LT.n900 }}>NC aperte da più tempo</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
              Top 10 aperte · {dati.aperte_oltre_60gg} oltre i 60 giorni
            </div>
          </div>
        </div>
        {dati.top_aperte.length === 0 ? (
          <div style={{ color: LT.n500, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
            Nessuna NC aperta — complimenti!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dati.top_aperte.map(nc => {
              const urgente = nc.giorni_aperta > 60;
              return (
                <div key={nc.id}
                  onClick={() => onApriDettaglio && onApriDettaglio(nc.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: LT.r.md,
                    background: '#fff', border: `1px solid ${LT.n150}`,
                    borderLeft: `4px solid ${urgente ? LT.rosso : LT.n300}`,
                    cursor: 'pointer', transition: 'background .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = LT.n50; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                >
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: LT.n700,
                    fontFamily: LT.mono, minWidth: 66, flexShrink: 0,
                  }}>
                    #{nc.progressivo}/{nc.anno}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: LT.n800, lineHeight: 1.35,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nc.evento_breve || <span style={{ fontStyle: 'italic', color: LT.n400 }}>(senza descrizione)</span>}
                    </div>
                    <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>
                      {nc.categoria}
                    </div>
                  </div>
                  <Chip kind={urgente ? 'rosso' : 'neutro'} icon="clock" size="sm">
                    {nc.giorni_aperta} gg
                  </Chip>
                  <Ico d={I.chevronR} size={14} color={LT.n300} />
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function CardNC({ nc, onApri }) {
  const chiusa = !!nc.data_risoluzione;
  const anno   = nc.data_nc ? new Date(nc.data_nc).getFullYear() : '—';
  const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  return (
    <div
      onClick={() => onApri(nc)}
      style={{
        background: '#fff',
        border: `1px solid ${LT.n150}`,
        borderLeft: `4px solid ${chiusa ? LT.verde : LT.rosso}`,
        borderRadius: LT.r.lg,
        padding: 14,
        marginBottom: 10,
        boxShadow: LT.sh1,
        cursor: 'pointer',
        transition: 'transform .1s, box-shadow .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = LT.sh2; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = LT.sh1; }}
    >
      {/* Riga badge: progressivo, data, stato */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 8, marginBottom: 8, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 13, fontWeight: 700, color: LT.n700,
            fontFamily: LT.mono, letterSpacing: '-.02em',
          }}>
            #{nc.progressivo ?? '—'}/{anno}
          </span>
          <Chip kind="neutro" icon="calendar" size="sm">
            {fmtData(nc.data_nc)}
          </Chip>
        </div>
        <Chip kind={chiusa ? 'verde' : 'rosso'} icon={chiusa ? 'checkCircle' : 'alert'} size="sm" soft={false}>
          {chiusa ? 'Chiusa' : 'Aperta'}
        </Chip>
      </div>

      {/* Categoria (se presente) */}
      {nc.categoria && (
        <div style={{ marginBottom: 4 }}>
          <Chip kind="viola" icon="layers" size="sm">
            {nc.categoria}
          </Chip>
        </div>
      )}

      {/* Oggetto/collocazione */}
      {nc.oggetto && (
        <div style={{ fontSize: 13, color: LT.n600, marginBottom: 4, fontWeight: 500 }}>
          {nc.oggetto}
          {nc.collocazione && <> <span style={{ color: LT.n300, margin: '0 4px' }}>·</span> {nc.collocazione}</>}
        </div>
      )}

      {/* Evento (preview) */}
      <div style={{ fontSize: 14, color: LT.n800, lineHeight: 1.4 }}>
        {nc.evento
          ? (nc.evento.length > 140 ? nc.evento.substring(0, 140) + '…' : nc.evento)
          : <span style={{ color: LT.n400, fontStyle: 'italic' }}>Nessun evento descritto</span>}
      </div>

      {/* Footer compilatore */}
      {nc.compilatore_nc && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: LT.n500, marginTop: 8,
          borderTop: `1px solid ${LT.n100}`, paddingTop: 8,
        }}>
          <Ico d={I.user} size={13} color={LT.n400} />
          <span>{nc.compilatore_nc}</span>
          {nc.num_allegati > 0 && (
            <>
              <span style={{ color: LT.n300, margin: '0 4px' }}>·</span>
              <Ico d={I.folder} size={13} color={LT.n400} />
              <span>{nc.num_allegati} {nc.num_allegati === 1 ? 'allegato' : 'allegati'}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RegistroNC({ operatore, onBack, azienda = {}, impostazioni = {} }) {
  const isAdmin = operatore.admin || operatore.master;

  const [vista,         setVista]         = useState('lista');
  const [lista,         setLista]         = useState([]);
  const [caricando,     setCaricando]     = useState(false);
  const [filtroStato,   setFiltroStato]   = useState('tutte');
  const [filtroDal,     setFiltroDal]     = useState('');
  const [filtroAl,      setFiltroAl]      = useState('');
  const [ncCorrente,    setNcCorrente]    = useState(null);
  const [salvando,      setSalvando]      = useState(false);
  const [erroreCarica,  setErroreCarica]  = useState('');
  const [erroreSalva,   setErroreSalva]   = useState('');
  const [erroreNuova,   setErroreNuova]   = useState('');
  const [cerca,         setCerca]         = useState('');

  // Campi form dettaglio
  const [fDataNc,           setFDataNc]           = useState('');
  const [fEvento,           setFEvento]           = useState('');
  const [fCausa,            setFCausa]            = useState('');
  const [fTrattamento,      setFTrattamento]      = useState('');
  const [fAzioneCorrettiva, setFAzioneCorrettiva] = useState('');
  const [fDataRisoluzione,  setFDataRisoluzione]  = useState('');
  const [fEsitoRisoluzione, setFEsitoRisoluzione] = useState('');
  const [fNoteRisoluzione,  setFNoteRisoluzione]  = useState('');
  const [fIdCategoria,      setFIdCategoria]      = useState('');
  const [fIdOrigine,        setFIdOrigine]        = useState('');

  // Campi form nuova NC
  const [nData,            setNData]            = useState('');
  const [nEvento,          setNEvento]          = useState('');
  const [nCausa,           setNCausa]           = useState('');
  const [nTrattamento,     setNTrattamento]     = useState('');
  const [nAzioneCorrettiva,setNAzioneCorrettiva]= useState('');
  const [nIdCategoria,     setNIdCategoria]     = useState('');
  const [nIdOrigine,       setNIdOrigine]       = useState('');

  // Tassonomia categorie (globale, gestita dal master)
  const [categorie,     setCategorie]     = useState([]);
  const [origini,       setOrigini]       = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState(''); // '' | 'null' | '<id>'
  const [filtroOrigine,   setFiltroOrigine]   = useState(''); // '' | 'null' | '<id>'

  // Allegati della NC corrente (caricati quando si apre il dettaglio)
  const [allegatiNc,          setAllegatiNc]          = useState([]);
  const [allegatiNcLoading,   setAllegatiNcLoading]   = useState(false);
  const [allegatiNcUploading, setAllegatiNcUploading] = useState(false);

  // Modalità vista lista: 'lista' (default) | 'statistiche' (solo admin/master)
  const [modalitaLista, setModalitaLista] = useState('lista');

  useEffect(() => {
    apiFetch(`${API}/admin/categorie-nc`)
      .then(r => r.ok ? r.json() : [])
      .then(setCategorie)
      .catch(() => setCategorie([]));
    apiFetch(`${API}/admin/origini-nc`)
      .then(r => r.ok ? r.json() : [])
      .then(setOrigini)
      .catch(() => setOrigini([]));
  }, []);

  useEffect(() => {
    if (vista === 'lista') caricaLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, filtroStato, filtroDal, filtroAl, filtroCategoria, filtroOrigine]);

  const caricaLista = async () => {
    setCaricando(true);
    setErroreCarica('');
    try {
      const params = new URLSearchParams({ stato: filtroStato });
      if (operatore.master) {
        params.append('master', '1');
      } else {
        params.append('idAzienda', operatore.id_azienda);
      }
      if (filtroDal)        params.append('dal',         filtroDal);
      if (filtroAl)         params.append('al',          filtroAl);
      if (!isAdmin)         params.append('idOperatore', operatore.id);
      if (filtroCategoria)  params.append('idCategoria', filtroCategoria);
      if (filtroOrigine)    params.append('idOrigine',   filtroOrigine);
      const res  = await apiFetch(`${API}/nc?${params}`);
      const data = await res.json();
      setLista(data);
    } catch {
      setErroreCarica('Errore nel caricamento NC');
    }
    setCaricando(false);
  };

  const apriDettaglio = (nc) => {
    setNcCorrente(nc);
    setFDataNc(nc.data_nc ? nc.data_nc.substring(0, 10) : '');
    setFEvento(nc.evento || '');
    setFCausa(nc.causa || '');
    setFTrattamento(nc.trattamento || '');
    setFAzioneCorrettiva(nc.azione_correttiva || '');
    setFDataRisoluzione(nc.data_risoluzione ? nc.data_risoluzione.substring(0, 10) : '');
    setFEsitoRisoluzione(nc.esito_risoluzione || '');
    setFNoteRisoluzione(nc.note_risoluzione || '');
    setFIdCategoria(nc.id_categoria ? String(nc.id_categoria) : '');
    setFIdOrigine(nc.id_origine ? String(nc.id_origine) : '');
    setAllegatiNc([]);
    caricaAllegatiNc(nc.id);
    setVista('dettaglio');
  };

  // ── Allegati NC ──────────────────────────────────────────────────────────
  const caricaAllegatiNc = async (idNc) => {
    if (!idNc) return;
    setAllegatiNcLoading(true);
    try {
      const res  = await apiFetch(`${API}/nc-allegati/${idNc}`);
      const data = await res.json().catch(() => []);
      setAllegatiNc(Array.isArray(data) ? data : []);
    } catch {
      setAllegatiNc([]);
    }
    setAllegatiNcLoading(false);
  };

  const uploadAllegatoNc = async (file) => {
    if (!file || !ncCorrente) return;
    setAllegatiNcUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('idOperatore', operatore.id);
      const res = await apiFetch(`${API}/nc-allegati/${ncCorrente.id}`, {
        method: 'POST', body: fd,
      });
      if (res.status === 413) {
        const d = await res.json().catch(() => ({}));
        toast(d.errore || 'Spazio di archiviazione esaurito', 'errore');
      } else if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast(d.errore || 'Errore nel caricamento', 'errore');
      } else {
        toast('✓ Allegato caricato!');
        await caricaAllegatiNc(ncCorrente.id);
      }
    } catch {
      toast('Errore nel caricamento', 'errore');
    }
    setAllegatiNcUploading(false);
  };

  const eliminaAllegatoNc = async (id, nome) => {
    const ok = await chiediConferma('Eliminare allegato?', nome);
    if (!ok) return;
    const res = await apiFetch(`${API}/nc-allegati/${id}`, { method: 'DELETE' });
    if (res.ok) {
      toast('✓ Allegato eliminato');
      await caricaAllegatiNc(ncCorrente?.id);
    } else {
      toast("Errore nell'eliminazione", 'errore');
    }
  };

  const salvaNc = async () => {
    setSalvando(true);
    setErroreSalva('');
    try {
      const res = await apiFetch(`${API}/nc/${ncCorrente.id}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dataNc:                 fDataNc           || null,
          evento:                 fEvento,
          causa:                  fCausa,
          trattamento:            fTrattamento,
          azioneCorrettiva:       fAzioneCorrettiva,
          dataRisoluzione:        fDataRisoluzione  || null,
          esitoRisoluzione:       fEsitoRisoluzione || null,
          noteRisoluzione:        fNoteRisoluzione  || null,
          idCategoria:            fIdCategoria ? parseInt(fIdCategoria) : null,
          idOrigine:              fIdOrigine ? parseInt(fIdOrigine) : null,
          idCompilatoreRisoluzione: fDataRisoluzione
            ? (ncCorrente.id_compilatore_risoluzione || operatore.id)
            : null,
        }),
      });
      if (!res.ok) throw new Error();
      toast('✓ NC aggiornata!');
      setVista('lista');
    } catch {
      setErroreSalva('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const salvaNuovaNc = async () => {
    if (!nEvento) { setErroreNuova("Inserisci almeno la descrizione dell'evento"); return; }
    setErroreNuova('');
    setSalvando(true);
    try {
      const res = await apiFetch(`${API}/nc`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:       operatore.id_azienda,
          idOperatore:     operatore.id,
          dataNc:          nData             || null,
          evento:          nEvento,
          causa:           nCausa            || null,
          trattamento:     nTrattamento      || null,
          azioneCorrettiva: nAzioneCorrettiva || null,
          idCategoria:     nIdCategoria ? parseInt(nIdCategoria) : null,
          idOrigine:       nIdOrigine ? parseInt(nIdOrigine) : null,
        }),
      });
      if (!res.ok) throw new Error();
      setNData(''); setNEvento(''); setNCausa(''); setNTrattamento(''); setNAzioneCorrettiva(''); setNIdCategoria(''); setNIdOrigine('');
      toast('✓ NC registrata!');
      setVista('lista');
    } catch {
      setErroreNuova('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const puoModificare = (nc) => isAdmin || nc.id_compilatore_nc === operatore.id;
  const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const rinumera = async () => {
    const ok = await chiediConferma(
      'Rinumerare le NC?',
      "Ricalcola i progressivi di tutte le NC dell'azienda in ordine cronologico per anno. L'operazione è irreversibile."
    );
    if (!ok) return;
    try {
      const res = await apiFetch(`${API}/nc/rinumera`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ idAzienda: operatore.id_azienda }),
      });
      if (!res.ok) throw new Error();
      await caricaLista();
      toast('✓ Rinumerazione completata!');
    } catch {
      toast('Errore nella rinumerazione', 'errore');
    }
  };

  // ── STAMPA REGISTRO NC (finestra dedicata, layout professionale) ─────────
  const stampaNc = (listaFiltrata) => {
    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) { toast('Il browser ha bloccato la finestra di stampa', 'errore'); return; }

    const esc = (s) => (s === null || s === undefined || s === '') ? '—' : String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const fmtD = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

    // Periodo e filtri attivi
    const periodoLabel = (() => {
      if (filtroDal && filtroAl) return `${fmtD(filtroDal)} — ${fmtD(filtroAl)}`;
      if (filtroDal)             return `Dal ${fmtD(filtroDal)}`;
      if (filtroAl)              return `Fino al ${fmtD(filtroAl)}`;
      return 'Tutti i periodi';
    })();
    const statoLabel = { tutte: 'Tutte', aperta: 'Solo aperte', chiusa: 'Solo chiuse' }[filtroStato] || '';
    const categoriaLabel = (() => {
      if (!filtroCategoria) return '';
      if (filtroCategoria === 'null') return 'Non categorizzate';
      const c = categorie.find(x => String(x.id) === String(filtroCategoria));
      return c ? c.descrizione : '';
    })();

    // Raggruppamento per stato
    const aperte = listaFiltrata.filter(nc => !nc.data_risoluzione);
    const chiuse = listaFiltrata.filter(nc =>  nc.data_risoluzione);
    // Ordino per data NC decrescente
    aperte.sort((a, b) => new Date(b.data_nc || 0) - new Date(a.data_nc || 0));
    chiuse.sort((a, b) => new Date(b.data_nc || 0) - new Date(a.data_nc || 0));

    const tot       = listaFiltrata.length;
    const totAperte = aperte.length;
    const totChiuse = chiuse.length;

    // Conteggi per categoria (ordina per count desc, poi per descrizione)
    const perCategoria = (() => {
      const map = new Map();
      listaFiltrata.forEach(nc => {
        const k = nc.categoria || '(Non categorizzate)';
        map.set(k, (map.get(k) || 0) + 1);
      });
      return Array.from(map.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'it'));
    })();

    // Riga campo (etichetta + valore prosa)
    const rigaCampo = (label, val) => {
      if (!val) return '';
      return `
        <div class="campo">
          <div class="campoLbl">${label}</div>
          <div class="campoVal">${esc(val)}</div>
        </div>`;
    };

    // Card singola NC
    const cardNc = (nc) => {
      const anno    = nc.data_nc ? new Date(nc.data_nc).getFullYear() : '—';
      const chiusa  = !!nc.data_risoluzione;
      const oggetto = [nc.oggetto, nc.collocazione].filter(Boolean).join(' — ');
      const ambito  = [nc.ambito, nc.tipo_attivita].filter(Boolean).join(' / ');

      // Footer: compilatore + risoluzione + conteggio allegati
      let footer = `<span class="footLbl">Compilatore NC:</span> <strong>${esc(nc.compilatore_nc)}</strong>`;
      if (chiusa) {
        footer += ` <span class="footSep">·</span> <span class="footLbl">Chiusa il</span> <strong class="mono">${fmtD(nc.data_risoluzione)}</strong>`;
        if (nc.compilatore_risoluzione) {
          footer += ` <span class="footLbl">da</span> <strong>${esc(nc.compilatore_risoluzione)}</strong>`;
        }
      }
      if (nc.num_allegati > 0) {
        footer += ` <span class="footSep">·</span> <span class="footLbl">Allegati:</span> <strong>${nc.num_allegati}</strong>`;
      }

      return `
        <div class="nc ${chiusa ? 'chiusa' : 'aperta'}">
          <div class="ncHead">
            <div class="ncId">
              <span class="ncNum">#${nc.progressivo ?? '—'}/${anno}</span>
              <span class="ncData mono">${fmtD(nc.data_nc)}</span>
            </div>
            <span class="chip ${chiusa ? 'chipOK' : 'chipNC'}">${chiusa ? 'CHIUSA' : 'APERTA'}</span>
          </div>
          ${oggetto || ambito || nc.categoria ? `
            <div class="ncContesto">
              ${nc.categoria ? `<div><span class="ctxLbl">Categoria:</span> ${esc(nc.categoria)}</div>` : ''}
              ${oggetto ? `<div><span class="ctxLbl">Oggetto:</span> ${esc(oggetto)}</div>` : ''}
              ${ambito ? `<div><span class="ctxLbl">Ambito:</span> ${esc(ambito)}</div>` : ''}
            </div>
          ` : ''}
          <div class="ncCorpo">
            ${rigaCampo('Descrizione Non Conformità / Reclamo', nc.evento)}
            ${rigaCampo('Causa', nc.causa)}
            ${rigaCampo('Trattamento immediato', nc.trattamento)}
            ${rigaCampo('Azione correttiva', nc.azione_correttiva)}
            ${chiusa && nc.esito_risoluzione ? rigaCampo('Esito risoluzione', nc.esito_risoluzione) : ''}
            ${chiusa && nc.note_risoluzione  ? rigaCampo('Note risoluzione e valutazione efficacia', nc.note_risoluzione) : ''}
          </div>
          <div class="ncFoot">${footer}</div>
        </div>`;
    };

    // Sezione raggruppamento
    const sezione = (titolo, items, tipo) => {
      if (items.length === 0) return '';
      return `
        <div class="sez">
          <div class="sezHdr ${tipo}">
            <span class="sezNome">${titolo}</span>
            <span class="sezConta">${items.length} ${items.length === 1 ? 'record' : 'record'}</span>
          </div>
          ${items.map(cardNc).join('')}
        </div>`;
    };

    // Calce con categorie + totali + fornitore
    const perCatHtml = perCategoria.length === 0 ? '' : `
        <div class="perCat">
          <div class="perCatTitle">Per categoria</div>
          <div class="perCatList">
            ${perCategoria.map(([nome, n]) => `
              <span class="perCatItem${nome === '(Non categorizzate)' ? ' nocat' : ''}">
                ${esc(nome)} <span class="perCatNum">${n}</span>
              </span>
            `).join('')}
          </div>
        </div>`;

    const calceHtml = `
      <div class="calce">
        ${perCatHtml}
        <div class="totali">
          <div class="totTitle">Totali del periodo</div>
          <div class="totGrid">
            <div class="totCell"><div class="totNum">${tot}</div><div class="totLab">Totale</div></div>
            <div class="totCell ${totAperte > 0 ? 'nc' : ''}"><div class="totNum">${totAperte}</div><div class="totLab">Aperte</div></div>
            <div class="totCell ok"><div class="totNum">${totChiuse}</div><div class="totLab">Chiuse</div></div>
          </div>
        </div>
      </div>
      ${impostazioni.fornitore_nome ? `
        <div class="fornitore">
          ${impostazioni.fornitore_logo ? `<img src="${BACKEND}${impostazioni.fornitore_logo}" alt="fornitore" />` : ''}
          <span>${esc(impostazioni.fornitore_nome)}${impostazioni.fornitore_telefono ? ` · ${esc(impostazioni.fornitore_telefono)}` : ''}</span>
        </div>
      ` : ''}
    `;

    // Decido cosa mostrare in base al filtro stato
    const mostraAperte = filtroStato === 'tutte' || filtroStato === 'aperta';
    const mostraChiuse = filtroStato === 'tutte' || filtroStato === 'chiusa';

    const html = `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8">
  <title>Registro NC — ${esc(azienda?.ragione_sociale || '')}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 12mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif; color: #0F172A; font-size: 10pt; background: #F7F8FC; }
    .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
    .toolbar { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #E2E8F0; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
    .toolbar .titolo { font-weight: 700; color: #2563EB; font-size: 14px; }
    .toolbar .btns button { padding: 8px 16px; font-size: 13px; font-weight: 600; margin-left: 8px; border-radius: 8px; cursor: pointer; border: 1.5px solid; font-family: inherit; }
    .toolbar .primary { background: #2563EB; color: #fff; border-color: #2563EB; }
    .toolbar .ghost { background: #fff; color: #475569; border-color: #CBD5E1; }
    .sheet { max-width: 210mm; margin: 12px auto; padding: 12mm; background: #fff; box-shadow: 0 4px 16px rgba(15,23,42,.08); }
    .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; border-bottom: 2px solid #2563EB; padding-bottom: 10px; margin-bottom: 14px; }
    .header .azienda { display: flex; gap: 14px; align-items: center; }
    .header .azLogo { max-height: 64px; max-width: 180px; object-fit: contain; }
    .header .azDati .rs { font-weight: 700; font-size: 14pt; color: #0F172A; letter-spacing: -.01em; }
    .header .azDati .sub { color: #475569; font-size: 9pt; margin-top: 1px; }
    .header .report { text-align: right; }
    .header .report .tit { font-weight: 700; font-size: 13pt; color: #2563EB; }
    .header .report .periodo { margin-top: 3px; color: #334155; font-size: 10pt; font-family: 'JetBrains Mono', monospace; }
    .header .report .filtri { color: #64748B; font-size: 9pt; margin-top: 3px; }
    .header .report .stampa { color: #94A3B8; font-size: 8pt; margin-top: 4px; }
    .empty { text-align: center; padding: 40px; color: #94A3B8; font-size: 11pt; }

    .sez { margin-bottom: 18px; }
    .sezHdr { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; border-radius: 6px 6px 0 0; font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
    .sezHdr.aperte { background: #FEF2F2; color: #DC2626; border: 1.5px solid #fecaca; border-bottom: 0; }
    .sezHdr.chiuse { background: #ECFDF5; color: #16a34a; border: 1.5px solid #bbf7d0; border-bottom: 0; }
    .sezHdr .sezConta { font-size: 8.5pt; font-weight: 600; opacity: .85; }

    .nc { border: 1px solid #E2E8F0; border-top: 0; margin-bottom: 0; page-break-inside: avoid; font-size: 9.5pt; }
    .nc.aperta { border-left: 3px solid #DC2626; }
    .nc.chiusa { border-left: 3px solid #16a34a; }
    .nc + .nc { margin-top: 6px; border-top: 1px solid #E2E8F0; }
    .sez .nc:first-of-type { border-top: 0; }
    .sez .nc:last-child { border-radius: 0 0 6px 6px; }

    .ncHead { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; }
    .ncId { display: flex; align-items: baseline; gap: 10px; }
    .ncNum { font-weight: 700; font-size: 10.5pt; color: #0F172A; letter-spacing: -.01em; }
    .ncData { font-size: 9pt; color: #475569; }
    .chip { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 8pt; font-weight: 700; border: 1px solid; letter-spacing: .04em; }
    .chipOK { background: #ECFDF5; color: #16a34a; border-color: #bbf7d0; }
    .chipNC { background: #FEF2F2; color: #DC2626; border-color: #fecaca; }

    .ncContesto { padding: 5px 10px; background: #FAFBFC; border-bottom: 1px solid #EEF2F7; color: #475569; font-size: 8.5pt; }
    .ncContesto .ctxLbl { color: #94A3B8; text-transform: uppercase; font-size: 7.5pt; font-weight: 700; letter-spacing: .06em; margin-right: 4px; }

    .ncCorpo { padding: 8px 10px; }
    .campo { margin-bottom: 6px; page-break-inside: avoid; }
    .campo:last-child { margin-bottom: 0; }
    .campoLbl { font-size: 7.5pt; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 2px; }
    .campoVal { color: #0F172A; font-size: 9.5pt; line-height: 1.4; white-space: pre-wrap; }

    .ncFoot { padding: 5px 10px; background: #FAFBFC; border-top: 1px solid #EEF2F7; color: #475569; font-size: 8.5pt; }
    .ncFoot .footLbl { color: #94A3B8; }
    .ncFoot .footSep { color: #CBD5E1; margin: 0 4px; }

    .calce { margin-top: 22px; padding-top: 12px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; flex-wrap: wrap; page-break-inside: avoid; }
    .totali { text-align: right; }
    .totTitle { font-size: 8pt; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; margin-bottom: 6px; }
    .totGrid { display: flex; gap: 8px; }
    .totCell { padding: 6px 12px; border-radius: 6px; background: #F8FAFC; border: 1px solid #E2E8F0; min-width: 70px; text-align: center; }
    .totCell.ok { background: #ECFDF5; border-color: #bbf7d0; }
    .totCell.nc { background: #FEF2F2; border-color: #fecaca; }
    .totNum { font-size: 15pt; font-weight: 700; font-family: 'JetBrains Mono', monospace; color: #0F172A; line-height: 1; }
    .totCell.ok .totNum { color: #16a34a; }
    .totCell.nc .totNum { color: #DC2626; }
    .totLab { font-size: 7.5pt; color: #64748B; text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }

    .perCat { flex: 1; min-width: 240px; }
    .perCatTitle { font-size: 8pt; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; margin-bottom: 6px; }
    .perCatList { display: flex; flex-wrap: wrap; gap: 6px; }
    .perCatItem { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 14px; background: #F1F5F9; border: 1px solid #E2E8F0; font-size: 9pt; color: #334155; }
    .perCatItem .perCatNum { font-weight: 700; color: #0F172A; font-family: 'JetBrains Mono', monospace; }
    .perCatItem.nocat { background: #FAFBFC; color: #64748B; }
    .fornitore { margin-top: 14px; padding-top: 8px; border-top: 1px dashed #E2E8F0; display: flex; align-items: center; justify-content: flex-end; gap: 8px; color: #94A3B8; font-size: 7.5pt; }
    .fornitore img { max-height: 24px; max-width: 60px; object-fit: contain; }

    @media print {
      body { background: #fff; }
      .toolbar { display: none !important; }
      .sheet { box-shadow: none; margin: 0; padding: 0; max-width: none; }
    }
  </style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
</head>
<body>
  <script>
    function scaricaPDF(btn){
      var el = document.querySelector('.sheet');
      if (!el || !window.html2pdf) { window.print(); return; }
      var titEl = document.querySelector('.sheet .tit');
      var base = (titEl ? titEl.textContent : (document.title || 'report'));
      base = base.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'report';
      var d = new Date();
      var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
      var nome = base + '-' + ymd + '.pdf';
      var orig = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Generazione...'; }
      var ripristina = function(){ if (btn) { btn.disabled = false; btn.textContent = orig; } };
      window.html2pdf().set({
        margin: 8,
        filename: nome,
        image: { type: 'jpeg', quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      }).from(el).save().then(ripristina).catch(function(){ ripristina(); window.print(); });
    }
  </script>
  <div class="toolbar">
    <div class="titolo">Anteprima registro Non Conformità</div>
    <div class="btns">
      <button class="ghost" onclick="window.close()">Chiudi</button>
      <button class="ghost" onclick="scaricaPDF(this)">⬇ Scarica PDF</button>
      <button class="primary" onclick="window.print()">🖨 Stampa</button>
    </div>
  </div>

  <div class="sheet">
    <!-- Intestazione -->
    <div class="header">
      <div class="azienda">
        ${azienda?.logo ? `<img class="azLogo" src="${BACKEND}${azienda.logo}" alt="logo" />` : ''}
        <div class="azDati">
          <div class="rs">${esc(azienda?.ragione_sociale || '')}</div>
          ${azienda?.indirizzo ? `<div class="sub">${esc(azienda.indirizzo)}</div>` : ''}
          ${azienda?.citta ? `<div class="sub">${esc(azienda.citta)}</div>` : ''}
          ${azienda?.telefono ? `<div class="sub">${esc(azienda.telefono)}</div>` : ''}
        </div>
      </div>
      <div class="report">
        <div class="tit">Registro Non Conformità</div>
        <div class="periodo">${periodoLabel}</div>
        ${statoLabel ? `<div class="filtri">Stato: <strong>${esc(statoLabel)}</strong></div>` : ''}
        ${categoriaLabel ? `<div class="filtri">Categoria: <strong>${esc(categoriaLabel)}</strong></div>` : ''}
        <div class="stampa">Stampato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>

    <!-- Sezioni -->
    ${tot === 0 ? `
      <div class="empty">Nessuna non conformità nel periodo selezionato</div>
    ` : `
      ${mostraAperte ? sezione('Aperte', aperte, 'aperte') : ''}
      ${mostraChiuse ? sezione('Chiuse', chiuse, 'chiuse') : ''}
    `}

    <!-- Calce -->
    ${calceHtml}
  </div>

  <script>
    window.focus();
  </script>
</body>
</html>`;

    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // ─── LISTA (D2) ───────────────────────────────────────────────────────────
  if (vista === 'lista') {
    // Stili locali condivisi
    const inputStyle = {
      width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
      color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
      borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
    };
    const labelStyle = {
      display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
    };
    const segPill = (on, kind) => ({
      flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600,
      border: `1px solid ${on ? (kind === 'rosso' ? LT.rosso : kind === 'verde' ? LT.verde : LT.blu) : LT.n200}`,
      background: on ? (kind === 'rosso' ? LT.rosso : kind === 'verde' ? LT.verde : LT.blu) : '#fff',
      color: on ? '#fff' : LT.n600,
      borderRadius: LT.r.md, cursor: 'pointer',
      transition: 'all .15s', fontFamily: LT.sans,
    });

    return (
      <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
        {/* Header D2 */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
          }}>
            {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <div style={{
            fontSize: 22, fontWeight: 700, color: LT.n900,
            letterSpacing: '-.01em', lineHeight: 1.2,
          }}>
            Registro Non Conformità
          </div>
          {azienda?.ragione_sociale && (
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {azienda.ragione_sociale}
            </div>
          )}
        </div>

        {/* Tab switcher Lista/Statistiche — solo admin/master */}
        {isAdmin && (
          <div style={{
            display: 'flex', gap: 0, marginBottom: 14,
            padding: 3, borderRadius: LT.r.md,
            background: LT.n100, border: `1px solid ${LT.n150}`,
            maxWidth: 340,
          }}>
            {[
              { key: 'lista',       label: 'Lista',       icon: 'list' },
              { key: 'statistiche', label: 'Statistiche', icon: 'chart' },
            ].map(opt => {
              const on = modalitaLista === opt.key;
              return (
                <button key={opt.key} onClick={() => setModalitaLista(opt.key)} style={{
                  flex: 1, padding: '8px 12px', borderRadius: LT.r.sm,
                  border: 'none',
                  background: on ? '#fff' : 'transparent',
                  color: on ? LT.n900 : LT.n500,
                  fontSize: 13, fontWeight: on ? 700 : 500,
                  cursor: 'pointer', fontFamily: LT.sans,
                  boxShadow: on ? LT.sh1 : 'none',
                  transition: 'all .15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <Ico d={I[opt.icon]} size={14} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Pannello Statistiche (solo admin/master) */}
        {isAdmin && modalitaLista === 'statistiche' && (
          <StatisticheNc
            operatore={operatore}
            onApriDettaglio={async (idNc) => {
              const trova = lista.find(x => x.id === idNc);
              if (trova) apriDettaglio(trova);
              else {
                await caricaLista();
                const trova2 = lista.find(x => x.id === idNc);
                if (trova2) apriDettaglio(trova2);
              }
            }}
          />
        )}

        {/* ──────────────────────────────────────────────────────────────── */}
        {/* Sotto questa riga: contenuto lista (filtri, azioni, record).    */}
        {/* Nascosto quando siamo in modalità Statistiche.                   */}
        {/* ──────────────────────────────────────────────────────────────── */}
        {modalitaLista === 'lista' && <>

        {/* Card Filtri */}
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 36, height: 36, borderRadius: LT.r.md,
              background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.filter} size={18} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Filtri</div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>Stato, categoria e periodo</div>
            </div>
          </div>

          {/* Segmented stato */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Stato</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setFiltroStato('tutte')} style={segPill(filtroStato === 'tutte', 'blu')}>
                Tutte
              </button>
              <button onClick={() => setFiltroStato('aperta')} style={segPill(filtroStato === 'aperta', 'rosso')}>
                Aperte
              </button>
              <button onClick={() => setFiltroStato('chiusa')} style={segPill(filtroStato === 'chiusa', 'verde')}>
                Chiuse
              </button>
            </div>
          </div>

          {/* Categoria */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Categoria</label>
            <select
              style={inputStyle}
              value={filtroCategoria}
              onChange={e => setFiltroCategoria(e.target.value)}
            >
              <option value=''>— tutte le categorie —</option>
              <option value='null'>Non categorizzate</option>
              {categorie.map(c => (
                <option key={c.id} value={c.id}>
                  {c.descrizione}{c.attivo === false ? ' (archiviata)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Origine */}
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Origine</label>
            <select
              style={inputStyle}
              value={filtroOrigine}
              onChange={e => setFiltroOrigine(e.target.value)}
            >
              <option value=''>— tutte le origini —</option>
              <option value='null'>Non specificate</option>
              {origini.map(o => (
                <option key={o.id} value={o.id}>
                  {o.descrizione}{o.attivo === false ? ' (archiviata)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Periodo */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Dal</label>
              <input type="date" style={inputStyle} value={filtroDal} onChange={e => setFiltroDal(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Al</label>
              <input type="date" style={inputStyle} value={filtroAl} onChange={e => setFiltroAl(e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Azioni primarie */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Btn kind="primary" block icon="plus" onClick={() => {
              setNData(''); setNEvento(''); setNCausa(''); setNTrattamento(''); setNAzioneCorrettiva('');
              setCerca(''); setVista('nuova');
            }}>
              Nuova NC manuale
            </Btn>
          </div>
          {isAdmin && (
            <Btn kind="outline" icon="repeat" onClick={rinumera} title="Ricalcola i progressivi in ordine cronologico">
              Rinumera
            </Btn>
          )}
        </div>

        {/* Errore caricamento */}
        {erroreCarica && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={16} /> {erroreCarica}
          </div>
        )}

        {/* Barra di ricerca */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: LT.n400, pointerEvents: 'none',
          }}>
            <Ico d={I.search} size={16} />
          </div>
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Cerca per evento o oggetto..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
        </div>

        {caricando && (
          <div style={{ color: LT.n500, fontSize: 13, padding: 12, textAlign: 'center' }}>Caricamento…</div>
        )}

        {(() => {
          const q = cerca.toLowerCase();
          const listaFiltrata = lista.filter(nc =>
            `${nc.evento || ''} ${nc.oggetto || ''}`.toLowerCase().includes(q)
          );
          const aperteFiltrate = listaFiltrata.filter(nc => !nc.data_risoluzione);
          const chiuseFiltrate = listaFiltrata.filter(nc =>  nc.data_risoluzione);

          if (cerca && listaFiltrata.length === 0) {
            return (
              <Card pad={28} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 999, margin: '0 auto 10px',
                  background: LT.n100, color: LT.n400,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.search} size={24} />
                </div>
                <div style={{ fontSize: 14, color: LT.n600 }}>
                  Nessun risultato per <strong style={{ color: LT.n800 }}>"{cerca}"</strong>
                </div>
              </Card>
            );
          }

          return (
            <>
              {/* Conteggio + stampa */}
              {listaFiltrata.length > 0 && (
                <div className="no-print" style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 12,
                }}>
                  <Chip kind="neutro" icon="list" size="sm">
                    {listaFiltrata.length} {listaFiltrata.length === 1 ? 'risultato' : 'risultati'}
                  </Chip>
                  <Btn kind="ghost" size="sm" icon="download" onClick={() => stampaNc(listaFiltrata)}>
                    Stampa
                  </Btn>
                </div>
              )}

              {/* Aperte */}
              {(filtroStato === 'tutte' || filtroStato === 'aperta') && aperteFiltrate.length > 0 && (
                <>
                  {filtroStato === 'tutte' && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 11, fontWeight: 700, color: LT.rosso,
                      textTransform: 'uppercase', letterSpacing: '.08em',
                      marginBottom: 8,
                    }}>
                      <Ico d={I.alert} size={14} />
                      Aperte ({aperteFiltrate.length})
                    </div>
                  )}
                  {aperteFiltrate.map(nc => <CardNC key={nc.id} nc={nc} onApri={apriDettaglio} />)}
                </>
              )}

              {/* Chiuse */}
              {(filtroStato === 'tutte' || filtroStato === 'chiusa') && chiuseFiltrate.length > 0 && (
                <>
                  {filtroStato === 'tutte' && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 11, fontWeight: 700, color: LT.verde,
                      textTransform: 'uppercase', letterSpacing: '.08em',
                      marginBottom: 8, marginTop: aperteFiltrate.length > 0 ? 18 : 4,
                    }}>
                      <Ico d={I.checkCircle} size={14} />
                      Chiuse ({chiuseFiltrate.length})
                    </div>
                  )}
                  {chiuseFiltrate.map(nc => <CardNC key={nc.id} nc={nc} onApri={apriDettaglio} />)}
                </>
              )}

              {/* Empty state totale */}
              {!caricando && lista.length === 0 && (
                <Card pad={36} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 999, margin: '0 auto 14px',
                    background: LT.verdeSoft, color: LT.verde,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Ico d={I.checkCircle} size={32} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: LT.n800 }}>
                    Nessuna non conformità registrata
                  </div>
                  <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
                    Tutti i processi sono conformi.
                  </div>
                </Card>
              )}
            </>
          );
        })()}
        </>}
      </div>
    );
  }

  // ─── DETTAGLIO / MODIFICA ─────────────────────────────────────────────────
  // ─── DETTAGLIO (D2) ───────────────────────────────────────────────────────
  if (vista === 'dettaglio' && ncCorrente) {
    const chiusa          = !!ncCorrente.data_risoluzione;
    const possoModificare = puoModificare(ncCorrente);
    const anno            = ncCorrente.data_nc ? new Date(ncCorrente.data_nc).getFullYear() : '—';

    const inputD2 = {
      width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
      color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
      borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
    };
    const inputD2Disabled = {
      ...inputD2, background: LT.n50, color: LT.n600, cursor: 'not-allowed',
    };
    const labelD2 = {
      display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
    };
    const textareaStyle = (ro) => ({
      ...(ro ? inputD2Disabled : inputD2),
      minHeight: 56, resize: 'vertical', fontFamily: LT.sans,
    });
    const tile = (bg, fg, icon) => (
      <div style={{
        width: 36, height: 36, borderRadius: LT.r.md,
        background: bg, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Ico d={I[icon]} size={18} />
      </div>
    );

    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        {/* Hero */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setVista('lista')}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: LT.n500,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              marginBottom: 8, fontFamily: LT.sans,
            }}
          >
            <Ico d={I.chevronR} size={14} style={{ transform: 'rotate(180deg)' }} />
            Torna al registro
          </button>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 22, fontWeight: 700, color: LT.n900,
                letterSpacing: '-.01em', lineHeight: 1.2,
                fontFamily: LT.mono,
              }}>
                NC #{ncCorrente.progressivo ?? '—'}/{anno}
              </div>
              <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
                {ncCorrente.data_nc ? `Data NC: ${fmtData(ncCorrente.data_nc)}` : 'Data NC non impostata'}
                {!possoModificare && ' · Sola lettura'}
              </div>
            </div>
            <Chip kind={chiusa ? 'verde' : 'rosso'} icon={chiusa ? 'checkCircle' : 'alert'} size="md" soft={false}>
              {chiusa ? 'Chiusa' : 'Aperta'}
            </Chip>
          </div>
        </div>

        {/* Testata informativa */}
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            {tile(LT.bluSoft, LT.blu, 'user')}
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Testata</div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
                Riferimenti di compilazione e contesto
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5, color: LT.n800 }}>
            <div>
              <span style={{ color: LT.n500, fontWeight: 600 }}>Compilato da: </span>
              <strong style={{ color: LT.n900 }}>{ncCorrente.compilatore_nc || '—'}</strong>
            </div>
            {ncCorrente.oggetto && (
              <div>
                <span style={{ color: LT.n500, fontWeight: 600 }}>Oggetto: </span>
                <strong style={{ color: LT.n900 }}>
                  {ncCorrente.oggetto}
                  {ncCorrente.collocazione && <> <span style={{ color: LT.n300 }}>·</span> {ncCorrente.collocazione}</>}
                </strong>
              </div>
            )}
            {ncCorrente.ambito && (
              <div>
                <span style={{ color: LT.n500, fontWeight: 600 }}>Ambito: </span>
                <strong style={{ color: LT.n900 }}>{ncCorrente.ambito} / {ncCorrente.tipo_attivita}</strong>
              </div>
            )}
          </div>
        </Card>

        {/* Dati NC */}
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            {tile(LT.rossoSoft, LT.rosso, 'alert')}
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Dati NC</div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
                Descrizione, causa, trattamento e azione correttiva
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Data NC</label>
            <input
              style={possoModificare ? inputD2 : inputD2Disabled}
              type="date" value={fDataNc}
              onChange={e => setFDataNc(e.target.value)}
              disabled={!possoModificare}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Categoria</label>
            <select
              style={possoModificare ? inputD2 : inputD2Disabled}
              value={fIdCategoria}
              onChange={e => setFIdCategoria(e.target.value)}
              disabled={!possoModificare}
            >
              <option value=''>— Non categorizzata —</option>
              {categorie
                .filter(c => c.attivo !== false || String(c.id) === fIdCategoria)
                .map(c => (
                  <option key={c.id} value={c.id}>
                    {c.descrizione}{c.attivo === false ? ' (archiviata)' : ''}
                  </option>
                ))}
            </select>
            {(() => {
              const sel = categorie.find(c => String(c.id) === fIdCategoria);
              return sel?.descrizione_estesa ? (
                <div style={{
                  marginTop: 6, fontSize: 11.5, color: LT.n600, lineHeight: 1.4,
                  background: LT.n50, border: `1px solid ${LT.n150}`,
                  borderRadius: LT.r.sm, padding: '8px 10px',
                }}>
                  {sel.descrizione_estesa}
                </div>
              ) : null;
            })()}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Origine</label>
            <select
              style={possoModificare ? inputD2 : inputD2Disabled}
              value={fIdOrigine}
              onChange={e => setFIdOrigine(e.target.value)}
              disabled={!possoModificare}
            >
              <option value=''>— Non specificata —</option>
              {origini
                .filter(o => o.attivo !== false || String(o.id) === fIdOrigine)
                .map(o => (
                  <option key={o.id} value={o.id}>
                    {o.descrizione}{o.attivo === false ? ' (archiviata)' : ''}
                  </option>
                ))}
            </select>
            {(() => {
              const sel = origini.find(o => String(o.id) === fIdOrigine);
              return sel?.descrizione_estesa ? (
                <div style={{
                  marginTop: 6, fontSize: 11.5, color: LT.n600, lineHeight: 1.4,
                  background: LT.n50, border: `1px solid ${LT.n150}`,
                  borderRadius: LT.r.sm, padding: '8px 10px',
                }}>
                  {sel.descrizione_estesa}
                </div>
              ) : null;
            })()}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Descrizione Non Conformità / Reclamo</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={3} value={fEvento}
              onChange={e => setFEvento(e.target.value)}
              disabled={!possoModificare}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Causa</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={2} value={fCausa}
              onChange={e => setFCausa(e.target.value)}
              disabled={!possoModificare}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Trattamento immediato</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={2} value={fTrattamento}
              onChange={e => setFTrattamento(e.target.value)}
              disabled={!possoModificare}
            />
          </div>

          <div>
            <label style={labelD2}>Azione correttiva</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={2} value={fAzioneCorrettiva}
              onChange={e => setFAzioneCorrettiva(e.target.value)}
              disabled={!possoModificare}
            />
          </div>
        </Card>

        {/* Risoluzione */}
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            {chiusa
              ? tile(LT.verdeSoft, LT.verde, 'checkCircle')
              : tile(LT.bluSoft,   LT.blu,   'shield')}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Risoluzione</div>
              {chiusa && ncCorrente.compilatore_risoluzione ? (
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
                  Chiusa da <strong style={{ color: LT.n700 }}>{ncCorrente.compilatore_risoluzione}</strong>
                  {' '}il <strong style={{ color: LT.n700 }}>{fmtData(ncCorrente.data_risoluzione)}</strong>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
                  {chiusa ? 'NC chiusa' : 'Compilare per chiudere la NC'}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Data risoluzione</label>
            <input
              style={possoModificare ? inputD2 : inputD2Disabled}
              type="date" value={fDataRisoluzione}
              onChange={e => setFDataRisoluzione(e.target.value)}
              disabled={!possoModificare}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelD2}>Esito risoluzione</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={2} value={fEsitoRisoluzione}
              onChange={e => setFEsitoRisoluzione(e.target.value)}
              placeholder="Descrivere l'esito della risoluzione"
              disabled={!possoModificare}
            />
          </div>

          <div>
            <label style={labelD2}>Note risoluzione e valutazione efficacia</label>
            <textarea
              style={textareaStyle(!possoModificare)}
              rows={2} value={fNoteRisoluzione}
              onChange={e => setFNoteRisoluzione(e.target.value)}
              disabled={!possoModificare}
            />
          </div>
        </Card>

        {/* Allegati */}
        <Card pad={18} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            {tile(LT.bluSoft, LT.blu, 'folder')}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Allegati</div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>
                Documenti, foto, email di reclamo, evidenze di risoluzione
              </div>
            </div>
            <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600 }}>
              {allegatiNc.length === 0 ? 'nessuno' : `${allegatiNc.length} ${allegatiNc.length === 1 ? 'file' : 'file'}`}
            </div>
          </div>

          {/* Drop-zone + input file */}
          {possoModificare && (() => {
            const fileInputId = `nc-allegato-${ncCorrente.id}`;
            const onDrop = (e) => {
              e.preventDefault();
              e.currentTarget.style.background = LT.n50;
              e.currentTarget.style.borderColor = LT.n300;
              const file = e.dataTransfer?.files?.[0];
              if (file) uploadAllegatoNc(file);
            };
            return (
              <label htmlFor={fileInputId}
                onDragOver={e => {
                  e.preventDefault();
                  e.currentTarget.style.background = LT.bluSoft;
                  e.currentTarget.style.borderColor = LT.blu;
                }}
                onDragLeave={e => {
                  e.currentTarget.style.background = LT.n50;
                  e.currentTarget.style.borderColor = LT.n300;
                }}
                onDrop={onDrop}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  padding: 12, borderRadius: LT.r.md,
                  background: LT.n50, border: `1.5px dashed ${LT.n300}`,
                  cursor: 'pointer', transition: 'all .15s',
                }}
              >
                <Ico d={I.download} size={18} style={{ color: LT.n500, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13, color: LT.n600 }}>
                  {allegatiNcUploading ? (
                    <span style={{ color: LT.blu, fontWeight: 600 }}>Caricamento…</span>
                  ) : (
                    <>Trascina un file qui oppure <span style={{ color: LT.blu, fontWeight: 600 }}>sfoglia</span> (max 10 MB)</>
                  )}
                </div>
                <input
                  id={fileInputId}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.eml,.msg"
                  disabled={allegatiNcUploading}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) uploadAllegatoNc(f);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            );
          })()}

          {/* Lista allegati */}
          {allegatiNcLoading ? (
            <div style={{ color: LT.n400, textAlign: 'center', padding: '16px 0', fontSize: 13 }}>
              Caricamento…
            </div>
          ) : allegatiNc.length === 0 ? (
            <div style={{
              color: LT.n400, textAlign: 'center', padding: '20px 0', fontSize: 13,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
              <Ico d={I.folder} size={28} style={{ color: LT.n300 }} />
              <div>Nessun allegato</div>
            </div>
          ) : (() => {
            const fmtSize = (bytes) => {
              if (bytes < 1024) return `${bytes} B`;
              if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
              return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
            };
            const iconPerFile = (nome) => {
              if (/\.pdf$/i.test(nome))                return { c: LT.rosso, bg: LT.rossoSoft, ico: I.receipt };
              if (/\.(jpg|jpeg|png|gif)$/i.test(nome)) return { c: LT.viola, bg: LT.violaSoft, ico: I.camera };
              if (/\.(xls|xlsx|csv)$/i.test(nome))     return { c: LT.verde, bg: LT.verdeSoft, ico: I.chart };
              if (/\.(doc|docx|txt)$/i.test(nome))     return { c: LT.blu,   bg: LT.bluSoft,   ico: I.book };
              if (/\.(eml|msg)$/i.test(nome))          return { c: LT.n600,  bg: LT.n100,      ico: I.mail };
              return                                          { c: LT.n500, bg: LT.n100,      ico: I.folder };
            };
            const puoEliminare = (al) =>
              operatore.master || operatore.admin || al.id_caricato_da === operatore.id;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {allegatiNc.map(al => {
                  const ic = iconPerFile(al.nome);
                  return (
                    <div key={al.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: 10, borderRadius: LT.r.md,
                      background: '#fff', border: `1px solid ${LT.n150}`,
                    }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: LT.r.sm,
                        background: ic.bg, color: ic.c,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <Ico d={ic.ico} size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a href={`${BACKEND}${al.path_file}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 13, color: LT.blu, fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}>
                          {al.nome}
                        </a>
                        <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>
                          {fmtSize(al.dimensione)}
                          {al.caricato_da && ` · ${al.caricato_da}`}
                          {al.created_at && ` · ${new Date(al.created_at).toLocaleDateString('it-IT')}`}
                        </div>
                      </div>
                      {puoEliminare(al) && (
                        <Btn kind="ghost" size="sm" icon="trash"
                          onClick={() => eliminaAllegatoNc(al.id, al.nome)}
                          style={{ color: LT.rosso }}
                          title="Elimina" />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Card>

        {erroreSalva && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={16} /> {erroreSalva}
          </div>
        )}

        {possoModificare && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="ghost" onClick={() => setVista('lista')}>Annulla</Btn>
            <div style={{ flex: 1 }}>
              <Btn kind="primary" block icon="check" onClick={salvaNc} disabled={salvando}>
                {salvando ? 'Salvataggio…' : 'Salva'}
              </Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── NUOVA NC MANUALE (D2) ────────────────────────────────────────────────
  const inputNuova = {
    width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans,
    color: LT.n900, background: '#fff', border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, outline: 'none', boxSizing: 'border-box',
  };
  const labelNuova = {
    display: 'block', fontSize: 11, fontWeight: 700, color: LT.n600,
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6,
  };
  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      {/* Header D2 con back inline */}
      <div style={{ marginBottom: 16 }}>
        <button
          onClick={() => setVista('lista')}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: LT.n500,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            marginBottom: 8, fontFamily: LT.sans,
          }}
        >
          <Ico d={I.chevronR} size={14} style={{ transform: 'rotate(180deg)' }} />
          Torna al registro
        </button>
        <div style={{
          fontSize: 22, fontWeight: 700, color: LT.n900,
          letterSpacing: '-.01em', lineHeight: 1.2,
        }}>
          Nuova non conformità
        </div>
        <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
          Registrazione manuale (non collegata a un'attività)
        </div>
      </div>

      <Card pad={18}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: LT.r.md,
            background: LT.rossoSoft, color: LT.rosso,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.alert} size={18} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900 }}>Dettaglio NC</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 1 }}>Compila i campi principali</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Data NC</label>
          <input style={inputNuova} type="date" value={nData} onChange={e => setNData(e.target.value)} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Categoria <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
          <select style={inputNuova} value={nIdCategoria}
            onChange={e => setNIdCategoria(e.target.value)}>
            <option value=''>— Non categorizzata —</option>
            {categorie
              .filter(c => c.attivo !== false)
              .map(c => (
                <option key={c.id} value={c.id}>{c.descrizione}</option>
              ))}
          </select>
          {(() => {
            const sel = categorie.find(c => String(c.id) === nIdCategoria);
            return sel?.descrizione_estesa ? (
              <div style={{
                marginTop: 6, fontSize: 11.5, color: LT.n600, lineHeight: 1.4,
                background: LT.n50, border: `1px solid ${LT.n150}`,
                borderRadius: LT.r.sm, padding: '8px 10px',
              }}>
                {sel.descrizione_estesa}
              </div>
            ) : null;
          })()}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Origine <span style={{ color: LT.n400, fontWeight: 500 }}>(opzionale)</span></label>
          <select style={inputNuova} value={nIdOrigine}
            onChange={e => setNIdOrigine(e.target.value)}>
            <option value=''>— Non specificata —</option>
            {origini
              .filter(o => o.attivo !== false)
              .map(o => (
                <option key={o.id} value={o.id}>{o.descrizione}</option>
              ))}
          </select>
          {(() => {
            const sel = origini.find(o => String(o.id) === nIdOrigine);
            return sel?.descrizione_estesa ? (
              <div style={{
                marginTop: 6, fontSize: 11.5, color: LT.n600, lineHeight: 1.4,
                background: LT.n50, border: `1px solid ${LT.n150}`,
                borderRadius: LT.r.sm, padding: '8px 10px',
              }}>
                {sel.descrizione_estesa}
              </div>
            ) : null;
          })()}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Descrizione Non Conformità / Reclamo *</label>
          <textarea
            style={{ ...inputNuova, minHeight: 72, resize: 'vertical' }}
            rows={3}
            value={nEvento}
            onChange={e => setNEvento(e.target.value)}
            placeholder="Descrivere la non conformità o il reclamo"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Causa</label>
          <textarea
            style={{ ...inputNuova, minHeight: 52, resize: 'vertical' }}
            rows={2}
            value={nCausa}
            onChange={e => setNCausa(e.target.value)}
            placeholder="Causa del problema"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Trattamento immediato</label>
          <textarea
            style={{ ...inputNuova, minHeight: 52, resize: 'vertical' }}
            rows={2}
            value={nTrattamento}
            onChange={e => setNTrattamento(e.target.value)}
            placeholder="Azione immediata intrapresa"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelNuova}>Azione correttiva</label>
          <textarea
            style={{ ...inputNuova, minHeight: 52, resize: 'vertical' }}
            rows={2}
            value={nAzioneCorrettiva}
            onChange={e => setNAzioneCorrettiva(e.target.value)}
            placeholder="Azione correttiva pianificata"
          />
        </div>

        {erroreNuova && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
          }}>
            <Ico d={I.alert} size={16} /> {erroreNuova}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="ghost" onClick={() => { setVista('lista'); setErroreNuova(''); }}>
            Annulla
          </Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" block icon="check" onClick={salvaNuovaNc} disabled={salvando}>
              {salvando ? 'Salvataggio…' : 'Salva NC'}
            </Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function _MenuPersonale({ operatore, azienda, onSezione, onLogout }) {
  const isAdmin  = operatore.admin  || operatore.master;
  const ruolo    = operatore.master ? 'Master' : isAdmin ? 'Amministratore' : 'Operatore';

  const VoceMenu = ({ icona, etichetta, sottoetichetta, onClick, disabilitato }) => (
    disabilitato
      ? <div style={{
          padding: '14px 18px',
          background: '#f8f8f8', border: '1.5px solid #eee',
          borderRadius: 10, marginBottom: 8,
          fontSize: 14, color: '#bbb',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 20, opacity: 0.4 }}>{icona}</span>
          <span>{etichetta}</span>
        </div>
      : <button onClick={onClick} style={{
          width: '100%', padding: '14px 18px',
          background: '#fff', border: '1.5px solid #e2e8f0',
          borderRadius: 10, marginBottom: 8,
          fontSize: 15, fontWeight: 500, color: '#1a1a1a',
          cursor: 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'inherit',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,0.10)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}>
          <span style={{ fontSize: 20 }}>{icona}</span>
          <div>
            <div>{etichetta}</div>
            {sottoetichetta && <div style={{ fontSize: 12, color: '#999', fontWeight: 400, marginTop: 1 }}>{sottoetichetta}</div>}
          </div>
          <span style={{ marginLeft: 'auto', color: '#ccc', fontSize: 18 }}>›</span>
        </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', padding: '0 0 32px 0', fontFamily: 'inherit' }}>

      {/* Intestazione utente */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        padding: '20px 20px 16px',
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
              {operatore.nome} {operatore.cognome}
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{
                display: 'inline-block', fontSize: 11, fontWeight: 600,
                color: '#2563EB', background: '#eff6ff',
                padding: '2px 10px', borderRadius: 20, letterSpacing: '0.03em',
              }}>{ruolo}</span>
            </div>
          </div>
          <button onClick={onLogout} style={{
            padding: '7px 16px', fontSize: 13, fontWeight: 500,
            background: 'transparent', border: '1.5px solid #ddd',
            borderRadius: 8, cursor: 'pointer', color: '#666',
            fontFamily: 'inherit',
          }}>Esci</button>
        </div>
      </div>

      {/* Voci menu */}
      <div style={{ maxWidth: 480, margin: '24px auto 0', padding: '0 16px' }}>

        <div style={{ fontSize: 11, fontWeight: 600, color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
          Operatività
        </div>

        <VoceMenu
          icona="📋"
          etichetta="Le mie attività"
          sottoetichetta="Monitoraggi da eseguire"
          onClick={() => onSezione('attivita')}
          disabilitato={!(operatore.monitoratore === true || operatore.monitoratore === 1)}
        />
        <VoceMenu
          icona="✅"
          etichetta="Le mie verifiche"
          sottoetichetta="Attività da verificare"
          onClick={() => onSezione('verifiche')}
          disabilitato={!(operatore.verificatore === true || operatore.verificatore === 1)}
        />
        <VoceMenu
          icona="📊"
          etichetta="Storico monitoraggi"
          sottoetichetta="Registro storico attività"
          onClick={() => onSezione('storico')}
        />
        <VoceMenu
          icona="📁"
          etichetta="Documenti HACCP"
          sottoetichetta="Procedure, modulistica e documenti tecnici"
          onClick={() => onSezione('documenti')}
        />
        <VoceMenu
          icona="⚠️"
          etichetta="Non Conformità"
          sottoetichetta="Registro NC e azioni correttive"
          onClick={() => onSezione('registro-nc')}
        />

        {hasPiano(azienda, operatore, 'TRACE') ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '20px 0 10px' }}>
              Moduli
            </div>
            <VoceMenu
              icona="🔍"
              etichetta="Tracciabilità"
              sottoetichetta="Fornitori, ingredienti, prodotti, fasi"
              onClick={() => onSezione('tracciabilita')}
            />
          </>
        ) : null}

        {isAdmin && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '20px 0 10px' }}>
              Gestione
            </div>
            <VoceMenu
              icona="⚙️"
              etichetta="Amministrazione"
              sottoetichetta="Organico, oggetti, scadenziario"
              onClick={() => onSezione('gestione')}
            />
          </>
        )}
      </div>
    </div>
  );
}

function Dashboard({ operatore, azienda, onSezione }) {
  const [dati,         setDati]         = useState(null);
  const [caricando,    setCaricando]    = useState(true);
  const [errore,       setErrore]       = useState('');
  const [aziende,      setAziende]      = useState([]);
  const [idAzFiltro,   setIdAzFiltro]   = useState('');
  const [alertSicurezza, setAlertSicurezza] = useState([]);
  const [alertVisibile,  setAlertVisibile]  = useState(true);
  const [correzioniNuove, setCorrezioniNuove] = useState(null); // banner correzioni operatori

  useEffect(() => {
    if (operatore.master) {
      apiFetch(`${API}/admin/aziende`).then(r => r.json()).then(setAziende).catch(() => {});
      apiFetch(`${API}/organico/alert-sicurezza`)
        .then(r => r.json())
        .then(data => { if (Array.isArray(data) && data.length) setAlertSicurezza(data); })
        .catch(() => {});
    }
    if (operatore.admin || operatore.master) {
      apiFetch(`${API}/attivita/correzioni-nuove`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d && d.registrazioni > 0) setCorrezioniNuove(d); })
        .catch(() => {});
    }
    carica('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carica = async (idAz) => {
    setCaricando(true);
    setErrore('');
    try {
      const params = new URLSearchParams();
      if (operatore.master) {
        params.append('master', '1');
        if (idAz) params.append('idAzienda', idAz);
      } else {
        params.append('idAzienda', operatore.id_azienda);
      }
      const res  = await apiFetch(`${API}/dashboard?${params}`);
      const data = await res.json();
      setDati(data);
    } catch {
      setErrore('Errore nel caricamento dashboard');
    }
    setCaricando(false);
  };

  const handleFiltroAz = (id) => { setIdAzFiltro(id); carica(id); };

  // ── Logica semantica dei colori KPI (invariata) ──────────────────────────
  const coloreRitardo = () => {
    if (!dati || dati.kpi.attivita_in_ritardo === 0) return 'verde';
    if (dati.attivita_in_ritardo.some(a => a.giorni_ritardo > 7)) return 'rosso';
    return 'arancio';
  };
  const coloreNc = () => {
    if (!dati || dati.kpi.nc_aperte === 0) return 'verde';
    if (dati.nc_aperte.some(n => n.giorni_apertura > 30)) return 'rosso';
    return 'arancio';
  };
  const coloreOggi = () => (!dati || dati.kpi.scadenza_oggi === 0) ? 'verde' : 'arancio';

  const KPI_COLORI = {
    verde:   { c: LT.verde,   bg: LT.verdeSoft },
    arancio: { c: LT.arancio, bg: LT.arancioSoft },
    rosso:   { c: LT.rosso,   bg: LT.rossoSoft },
    blu:     { c: LT.blu,     bg: LT.bluSoft },
  };

  const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const limitiStr = (a) => {
    const min = a.valore_min != null ? a.valore_min : null;
    const max = a.valore_max != null ? a.valore_max : null;
    if (min !== null && max !== null) return `${min} – ${max}`;
    if (min !== null) return `≥ ${min}`;
    if (max !== null) return `≤ ${max}`;
    return null;
  };

  // ── Sub-componenti D2 (locali al Dashboard) ──────────────────────────────

  const KpiCard = ({ label, valore, iconKey, colore = 'blu', hint }) => {
    const s = KPI_COLORI[colore] || KPI_COLORI.blu;
    return (
      <div
        className="lt-kpi"
        style={{
          background: '#fff', border: `1px solid ${LT.n150}`,
          borderRadius: LT.r.lg, padding: 18, boxShadow: LT.sh2,
          display: 'flex', alignItems: 'flex-start', gap: 14,
          flex: '1 1 200px', minWidth: 180,
        }}
      >
        <div
          className="lt-kpi-icon"
          style={{
            width: 42, height: 42, borderRadius: LT.r.md,
            background: s.bg, color: s.c, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Ico d={I[iconKey]} size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lt-kpi-value" style={{ fontSize: 28, fontWeight: 800, color: LT.n900, lineHeight: 1, letterSpacing: '-.02em' }}>
            {valore}
          </div>
          <div className="lt-kpi-label" style={{ fontSize: 12, color: LT.n500, marginTop: 6, fontWeight: 500, lineHeight: 1.35 }}>
            {label}
          </div>
          {hint && (
            <div className="lt-kpi-hint" style={{ fontSize: 11, color: s.c, marginTop: 6, fontWeight: 600 }}>{hint}</div>
          )}
        </div>
      </div>
    );
  };

  const SectionBox = ({ titolo, iconKey, colore = 'blu', count, children }) => {
    const s = KPI_COLORI[colore] || KPI_COLORI.blu;
    return (
      <div
        style={{
          background: '#fff', border: `1px solid ${LT.n150}`,
          borderRadius: LT.r.lg, marginBottom: 16, overflow: 'hidden',
          boxShadow: LT.sh1,
        }}
      >
        <div
          style={{
            padding: '12px 16px', borderBottom: `1px solid ${LT.n150}`,
            display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
          }}
        >
          <div
            style={{
              width: 30, height: 30, borderRadius: LT.r.md,
              background: s.bg, color: s.c,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Ico d={I[iconKey]} size={16} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: LT.n800, flex: 1 }}>
            {titolo}
          </div>
          {count != null && (
            <span
              style={{
                fontSize: 12, fontWeight: 700, color: s.c, background: s.bg,
                padding: '3px 10px', borderRadius: 999,
              }}
            >
              {count}
            </span>
          )}
        </div>
        <div>{children}</div>
      </div>
    );
  };

  const Riga = ({ onClick, children }) => (
    <div
      onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.background = LT.n50)}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      style={{
        padding: '12px 16px', borderBottom: `1px solid ${LT.n100}`,
        cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 10, transition: 'background .12s',
      }}
    >
      {children}
    </div>
  );

  const GiornoBadge = ({ gg, prefix = '+', soglia = 7 }) => {
    const critico = gg > soglia;
    return (
      <span
        style={{
          background: critico ? LT.rossoSoft : LT.arancioSoft,
          color: critico ? LT.rosso : LT.arancio,
          fontSize: 12, fontWeight: 700, padding: '3px 10px',
          borderRadius: 999, whiteSpace: 'nowrap',
        }}
      >
        {prefix}{gg}gg
      </span>
    );
  };

  // Data formattata per il banner di benvenuto (coerente con la HomeOperatore)
  const oggi = new Date();
  const dataStr = oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  const ruoloLabel = operatore.master ? 'Pannello di controllo Master' : 'Pannello di controllo';

  return (
    <div className="lt-page" style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px 48px' }}>

      {/* ── Hero / banner di benvenuto ────────────────────────────────────── */}
      <Card
        className="lt-hero"
        pad={28}
        style={{
          background: LT.grad, color: '#fff', border: 'none',
          position: 'relative', overflow: 'hidden', marginBottom: 20,
        }}
      >
        <div
          style={{
            position: 'absolute', top: -40, right: -40,
            width: 200, height: 200, borderRadius: 999,
            background: 'rgba(255,255,255,.08)', pointerEvents: 'none',
          }}
        />
        <div style={{ fontSize: 12, fontWeight: 600, opacity: .85, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {dataStr}
        </div>
        <div className="lt-hero-title" style={{ fontSize: 26, fontWeight: 700, marginTop: 4, letterSpacing: '-.02em' }}>
          Ciao {operatore?.nome || ''}
        </div>
        <div className="lt-hero-sub" style={{ fontSize: 13, opacity: .9, marginTop: 4 }}>
          {ruoloLabel}
          {azienda?.ragione_sociale ? ` · ${azienda.ragione_sociale}` : ''}
        </div>
      </Card>

      {/* ── Banner sicurezza (solo master) ────────────────────────────────── */}
      {operatore.master && alertSicurezza.length > 0 && alertVisibile && (
        <div
          style={{
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}33`,
            borderRadius: LT.r.md, padding: '12px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}
        >
          <div style={{ color: LT.rosso, flexShrink: 0, marginTop: 1 }}>
            <Ico d={I.alert} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 8, flexWrap: 'wrap', marginBottom: 6,
            }}>
              <div style={{ fontWeight: 700, color: LT.rosso, fontSize: 14 }}>
                {alertSicurezza.length} coppie (IP, azienda) bloccate nelle ultime 24 ore
              </div>
              <button
                onClick={async () => {
                  const ok = await chiediConferma(
                    'Sbloccare tutti?',
                    "Cancellerà i record 'bloccato' e 'fallito' di tutte le coppie bloccate. Da usare se la maggior parte dei blocchi è frutto di un falso positivo."
                  );
                  if (!ok) return;
                  const res = await apiFetch(`${API}/organico/sblocca`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                  });
                  if (res.ok) {
                    const d = await res.json();
                    toast(`✓ Sbloccati (${d.eliminati} record eliminati)`);
                    setAlertSicurezza([]);
                  } else {
                    toast('Errore sblocco', 'errore');
                  }
                }}
                style={{
                  fontSize: 11.5, fontWeight: 700, color: '#fff',
                  background: LT.rosso, border: 'none',
                  padding: '4px 10px', borderRadius: LT.r.sm, cursor: 'pointer',
                  fontFamily: LT.sans,
                }}
              >
                Sblocca tutti
              </button>
            </div>
            {alertSicurezza.map((a, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                fontSize: 12, color: '#7f1d1d', lineHeight: 1.7,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  IP: <strong>{a.ip}</strong> — Azienda: <strong>{a.codice_azienda || '—'}</strong>
                  {' — '}{a.blocchi ?? a.tentativi} blocchi, {a.tentativi_falliti ?? '—'} falliti
                  {' — '}{new Date(a.ultimo_tentativo).toLocaleString('it-IT')}
                </div>
                <button
                  onClick={async () => {
                    const ok = await chiediConferma(
                      'Sbloccare questa coppia?',
                      `Verranno eliminati i blocchi e i tentativi falliti per IP ${a.ip} su azienda ${a.codice_azienda || '—'}.`
                    );
                    if (!ok) return;
                    const res = await apiFetch(`${API}/organico/sblocca`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ ip: a.ip, codiceAzienda: a.codice_azienda }),
                    });
                    if (res.ok) {
                      toast('✓ Coppia sbloccata');
                      setAlertSicurezza(prev => prev.filter((_, j) => j !== i));
                    } else {
                      toast('Errore sblocco', 'errore');
                    }
                  }}
                  style={{
                    fontSize: 11, fontWeight: 600, color: LT.rosso,
                    background: '#fff', border: `1px solid ${LT.rosso}66`,
                    padding: '2px 8px', borderRadius: LT.r.sm, cursor: 'pointer',
                    fontFamily: LT.sans, flexShrink: 0,
                  }}
                >
                  Sblocca
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setAlertVisibile(false)}
            aria-label="Chiudi avviso"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: LT.rosso, padding: 4, lineHeight: 1, flexShrink: 0,
            }}
          >
            <Ico d={I.x} size={16} />
          </button>
        </div>
      )}

      {/* ── Banner correzioni operatori (admin/master) ────────────────────── */}
      {correzioniNuove && (
        <div
          style={{
            background: LT.arancioSoft, border: `1px solid ${LT.arancio}33`,
            borderRadius: LT.r.md, padding: '12px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}
        >
          <div style={{ color: LT.arancio, flexShrink: 0 }}>
            <Ico d={I.edit} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: LT.n800 }}>
            <strong style={{ color: LT.arancio }}>
              {correzioniNuove.registrazioni === 1
                ? '1 registrazione corretta'
                : `${correzioniNuove.registrazioni} registrazioni corrette`}
            </strong>
            {' '}da operatori dall'ultimo controllo
            {correzioniNuove.ultima && (
              <span style={{ color: LT.n500 }}>
                {' — ultima: '}
                {new Date(correzioniNuove.ultima).toLocaleString('it-IT', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            )}
          </div>
          <button
            onClick={() => onSezione('correzioni')}
            style={{
              fontSize: 11.5, fontWeight: 700, color: '#fff',
              background: LT.arancio, border: 'none',
              padding: '5px 12px', borderRadius: LT.r.sm, cursor: 'pointer',
              fontFamily: LT.sans, flexShrink: 0,
            }}
          >
            Vedi correzioni
          </button>
        </div>
      )}

      {/* ── Filtro azienda (solo master) ──────────────────────────────────── */}
      {operatore.master && (
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: 'block', fontSize: 11, fontWeight: 700, color: LT.n500,
              textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6,
            }}
          >
            Filtro azienda
          </label>
          <select
            value={idAzFiltro}
            onChange={e => handleFiltroAz(e.target.value)}
            style={{
              width: '100%', maxWidth: 320,
              padding: '10px 12px',
              border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
              background: '#fff', fontSize: 14, color: LT.n800,
              fontFamily: LT.sans, cursor: 'pointer',
            }}
          >
            <option value="">Tutte le aziende</option>
            {aziende.map(az => (
              <option key={az.id} value={az.id}>{az.ragione_sociale}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── Errore ────────────────────────────────────────────────────────── */}
      {errore && (
        <div
          style={{
            background: LT.rossoSoft, color: LT.rosso, padding: '14px 16px',
            borderRadius: LT.r.md, fontSize: 14, fontWeight: 500, textAlign: 'center',
            marginBottom: 16, border: `1px solid ${LT.rosso}33`,
          }}
        >
          {errore}
        </div>
      )}

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {caricando && (
        <div style={{ textAlign: 'center', color: LT.n400, padding: 48, fontSize: 14 }}>
          Caricamento…
        </div>
      )}

      {dati && !caricando && (<>

        {/* Section label */}
        <div
          style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '.08em',
            margin: '8px 4px 12px',
          }}
        >
          Panoramica
        </div>

        {/* ── KPI cards ──────────────────────────────────────────────────── */}
        <div className="lt-kpi-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 24 }}>
          <KpiCard
            label="Attività in ritardo"
            valore={dati.kpi.attivita_in_ritardo}
            iconKey="clock"
            colore={coloreRitardo()}
          />
          <KpiCard
            label="In scadenza oggi"
            valore={dati.kpi.scadenza_oggi}
            iconKey="calendar"
            colore={coloreOggi()}
          />
          <KpiCard
            label="NC aperte"
            valore={dati.kpi.nc_aperte}
            iconKey="alert"
            colore={coloreNc()}
          />
          <KpiCard
            label={`Monitoraggi ${new Date().toLocaleString('it-IT', { month: 'long' })}`}
            valore={dati.kpi.monitoraggi_mese}
            iconKey="checkCircle"
            colore="blu"
          />
          {(() => {
            const future  = dati.kpi.scadenze_tecniche        ?? 0;
            const scadute = dati.kpi.scadenze_tecniche_scadute ?? 0;
            const tot     = future + scadute;
            const colore  = scadute > 0 ? 'rosso' : tot > 0 ? 'arancio' : 'verde';
            const hint    = scadute > 0 ? `${scadute} scadute · ${future} in scadenza` : null;
            return (
              <KpiCard
                label="Scadenze tecniche (15 gg)"
                valore={tot}
                iconKey="wrench"
                colore={colore}
                hint={hint}
              />
            );
          })()}
        </div>

        {/* ── Attività in ritardo ────────────────────────────────────────── */}
        {dati.attivita_in_ritardo.length > 0 && (
          <SectionBox
            titolo="Attività in ritardo"
            iconKey="clock"
            colore={coloreRitardo()}
            count={dati.kpi.attivita_in_ritardo}
          >
            {dati.attivita_in_ritardo.map((a, i) => (
              <Riga key={i} onClick={() => onSezione('storico')}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: LT.n800 }}>{a.oggetto}</div>
                  {a.collocazione && <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>{a.collocazione}</div>}
                  {a.tipo_attivita && <div style={{ fontSize: 12, color: LT.n600, marginTop: 2 }}>{a.tipo_attivita}</div>}
                  {limitiStr(a) && <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>Limiti: {limitiStr(a)}</div>}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: LT.n500, marginTop: 4 }}>
                    <Ico d={I.user} size={12} />
                    {a.operatore || '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <GiornoBadge gg={a.giorni_ritardo} />
                  <div style={{ fontSize: 11, color: LT.n400, marginTop: 4 }}>{fmtData(a.data_scadenza)}</div>
                </div>
              </Riga>
            ))}
          </SectionBox>
        )}

        {/* ── In scadenza oggi ───────────────────────────────────────────── */}
        {dati.scadenza_oggi.length > 0 && (
          <SectionBox
            titolo="In scadenza oggi"
            iconKey="calendar"
            colore={coloreOggi()}
            count={dati.kpi.scadenza_oggi}
          >
            {dati.scadenza_oggi.map((a, i) => (
              <Riga key={i} onClick={() => onSezione('attivita')}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: LT.n800 }}>{a.oggetto}</div>
                  {a.collocazione && <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>{a.collocazione}</div>}
                  {a.tipo_attivita && <div style={{ fontSize: 12, color: LT.n600, marginTop: 2 }}>{a.tipo_attivita}</div>}
                  {limitiStr(a) && <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>Limiti: {limitiStr(a)}</div>}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: LT.n500, flexShrink: 0 }}>
                  <Ico d={I.user} size={12} />
                  {a.operatore || '—'}
                </div>
              </Riga>
            ))}
          </SectionBox>
        )}

        {/* ── NC aperte ──────────────────────────────────────────────────── */}
        {dati.nc_aperte.length > 0 && (
          <SectionBox
            titolo={`NC aperte — ultime ${dati.nc_aperte.length}`}
            iconKey="alert"
            colore={coloreNc()}
            count={dati.kpi.nc_aperte}
          >
            {dati.nc_aperte.map((nc, i) => (
              <Riga key={i} onClick={() => onSezione('registro-nc')}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: LT.n800 }}>
                    NC #{nc.progressivo} — {fmtData(nc.data_nc)}
                  </div>
                  {nc.evento && (
                    <div
                      style={{
                        fontSize: 12, color: LT.n600, marginTop: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420,
                      }}
                    >
                      {nc.evento}
                    </div>
                  )}
                  {nc.oggetto && <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>{nc.oggetto}</div>}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: LT.n500, marginTop: 4 }}>
                    <Ico d={I.edit} size={12} />
                    {nc.compilatore || '—'}
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <GiornoBadge gg={nc.giorni_apertura} prefix="" soglia={30} />
                </div>
              </Riga>
            ))}
          </SectionBox>
        )}

        {/* ── Stato "tutto ok" ───────────────────────────────────────────── */}
        {dati.kpi.attivita_in_ritardo === 0 && dati.kpi.nc_aperte === 0 && dati.kpi.scadenza_oggi === 0 && (
          <div
            style={{
              background: '#fff', border: `1px solid ${LT.n150}`,
              borderRadius: LT.r.lg, padding: '40px 24px',
              textAlign: 'center', marginTop: 8, boxShadow: LT.sh1,
            }}
          >
            <div
              style={{
                width: 56, height: 56, borderRadius: 999,
                background: LT.verdeSoft, color: LT.verde, margin: '0 auto 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Ico d={I.checkCircle} size={28} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: LT.n800 }}>Tutto in regola</div>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 6 }}>
              Nessuna attività in ritardo, nessuna NC aperta, nessuna scadenza oggi.
            </div>
          </div>
        )}

      </>)}
    </div>
  );
}

function _MenuAdmin({ operatore, azienda, onSezione, onLogout, onHome }) {
  const ruolo = operatore.master ? 'Master' : 'Amministratore';

  const VoceMenu = ({ icona, etichetta, sottoetichetta, onClick }) => (
    <button onClick={onClick} style={{
      width: '100%', padding: '14px 18px',
      background: '#fff', border: '1.5px solid #e2e8f0',
      borderRadius: 10, marginBottom: 8,
      fontSize: 15, fontWeight: 500, color: '#1a1a1a',
      cursor: 'pointer', textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 12,
      fontFamily: 'inherit',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,0.10)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}>
      <span style={{ fontSize: 20 }}>{icona}</span>
      <div>
        <div>{etichetta}</div>
        {sottoetichetta && <div style={{ fontSize: 12, color: '#999', fontWeight: 400, marginTop: 1 }}>{sottoetichetta}</div>}
      </div>
      <span style={{ marginLeft: 'auto', color: '#ccc', fontSize: 18 }}>›</span>
    </button>
  );

  const SectionLabel = ({ testo }) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '20px 0 10px' }}>
      {testo}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', padding: '0 0 32px 0', fontFamily: 'inherit' }}>

      {/* Intestazione */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '20px 20px 16px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
              {operatore.nome} {operatore.cognome}
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{
                display: 'inline-block', fontSize: 11, fontWeight: 600,
                color: '#2563EB', background: '#eff6ff',
                padding: '2px 10px', borderRadius: 20, letterSpacing: '0.03em',
              }}>{ruolo}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onHome} style={{
              padding: '7px 14px', fontSize: 13, background: 'transparent',
              border: '1.5px solid #ddd', borderRadius: 8, cursor: 'pointer',
              color: '#666', fontFamily: 'inherit',
            }}>🏠</button>
            <button onClick={onLogout} style={{
              padding: '7px 16px', fontSize: 13, fontWeight: 500,
              background: 'transparent', border: '1.5px solid #ddd',
              borderRadius: 8, cursor: 'pointer', color: '#666', fontFamily: 'inherit',
            }}>Esci</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: '24px auto 0', padding: '0 16px' }}>

        <VoceMenu icona="📊" etichetta="Dashboard" sottoetichetta="Panoramica rapida dello stato azienda" onClick={() => onSezione('dashboard')} />

        <SectionLabel testo="Operatività" />
        <VoceMenu icona="📋" etichetta="Le mie attività"  sottoetichetta="Monitoraggi da eseguire"                    onClick={() => onSezione('attivita')} />
        <VoceMenu icona="📁" etichetta="Documenti HACCP"  sottoetichetta="Procedure, modulistica e documenti tecnici"  onClick={() => onSezione('documenti')} />
        <VoceMenu icona="⚠️" etichetta="Non Conformità"   sottoetichetta="Registro NC e azioni correttive"            onClick={() => onSezione('registro-nc')} />
        <VoceMenu icona="✏️" etichetta="Storico correzioni" sottoetichetta="Log modifiche monitoraggi"                onClick={() => onSezione('correzioni')} />

        {hasPiano(azienda, operatore, 'TRACE') ? (
          <>
            <SectionLabel testo="Moduli" />
            <VoceMenu icona="🔍" etichetta="Tracciabilità" sottoetichetta="Fornitori, ingredienti, prodotti, fasi" onClick={() => onSezione('tracciabilita')} />
          </>
        ) : null}

        <SectionLabel testo="Gestione" />
        <VoceMenu icona="🏢" etichetta="La mia azienda"        sottoetichetta="Dati anagrafici e logo"     onClick={() => onSezione('mia-azienda')} />
        <VoceMenu icona="👥" etichetta="Organico"              sottoetichetta="Operatori e accessi"        onClick={() => onSezione('organico')} />
        <VoceMenu icona="🏭" etichetta="Oggetti da monitorare" sottoetichetta="Attrezzature e collocazioni" onClick={() => onSezione('oggetti')} />
        <VoceMenu icona="📅" etichetta="Scadenziario"          sottoetichetta="Programma attività HACCP"   onClick={() => onSezione('scadenziario')} />

        {operatore.master && (
          <>
            <SectionLabel testo="Solo Master" />
            <VoceMenu icona="🗂️" etichetta="Ambiti e tipi attività" sottoetichetta="Categorie di monitoraggio"  onClick={() => onSezione('ambiti')} />
            <VoceMenu icona="🏷️" etichetta="Categorie NC"           sottoetichetta="Tassonomia non conformità"   onClick={() => onSezione('categorie-nc')} />
            <VoceMenu icona="🔀" etichetta="Origini NC"             sottoetichetta="Da dove emerge la NC"        onClick={() => onSezione('origini-nc')} />
            <VoceMenu icona="🏢" etichetta="Aziende"                sottoetichetta="Gestione multi-azienda"      onClick={() => onSezione('aziende')} />
            <VoceMenu icona="✅" etichetta="Verifiche da eseguire"  sottoetichetta="Tutte le aziende"            onClick={() => onSezione('verifiche')} />
            <VoceMenu icona="⚙️" etichetta="Impostazioni fornitore" sottoetichetta="Logo e dati fornitore"       onClick={() => onSezione('impostazioni')} />
          </>
        )}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// PROFILO AZIENDA
// ═══════════════════════════════════════════════════════════════

function ProfiloAzienda({ operatore, onBack }) {
  const [form,      setForm]     = useState({ ragione_sociale: '', indirizzo: '', citta: '', telefono: '', email: '', logo: '', codice: '' });
  const [loading,   setLoading]  = useState(true);
  const [saving,    setSaving]   = useState(false);
  const [messaggio, setMessaggio]= useState('');
  const [errore,    setErrore]   = useState('');
  const [mostraQR,  setMostraQR] = useState(false);

  useEffect(() => {
    apiFetch(`${API}/admin/mia-azienda`)
      .then(r => r.json())
      .then(d => { setForm(d); setLoading(false); })
      .catch(() => { setErrore('Errore nel caricamento dei dati'); setLoading(false); });
  }, []);

  const handleSave = async () => {
    setErrore(''); setMessaggio('');
    if (!form.ragione_sociale?.trim()) return setErrore('La ragione sociale è obbligatoria');
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/admin/mia-azienda`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ragioneSociale: form.ragione_sociale,
          indirizzo:      form.indirizzo || null,
          citta:          form.citta     || null,
          telefono:       form.telefono  || null,
          email:          form.email     || null,
          // dati fiscali per fattura elettronica SDI
          partitaIva:             form.partita_iva || null,
          codiceFiscale:          form.codice_fiscale || null,
          cap:                    form.cap || null,
          provincia:              form.provincia || null,
          nazione:                form.nazione || 'IT',
          codiceDestinatarioSdi:  form.codice_destinatario_sdi || null,
          pec:                    form.pec || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) setErrore(data.errore || 'Errore durante il salvataggio');
      else setMessaggio('Dati salvati con successo');
    } catch { setErrore('Errore di connessione. Riprova.'); }
    setSaving(false);
  };

  const handleLogo = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setMessaggio(''); setErrore('');
    const fd = new FormData();
    fd.append('logo', file);
    try {
      const res = await apiFetch(`${API}/uploads/logo-azienda/${operatore.id_azienda}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.logo) { setForm(f => ({ ...f, logo: data.logo })); setMessaggio('Logo aggiornato'); }
      else setErrore(data.errore || 'Errore upload logo');
    } catch { setErrore('Errore durante il caricamento del logo'); }
  };

  // ── Abbonamento: Stripe customer portal ───────────────────────────
  const apriPortaleAbbonamento = async () => {
    setErrore(''); setMessaggio('');
    try {
      const res = await apiFetch(`${API}/stripe/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codice: form.codice }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setErrore(data.errore || 'Impossibile aprire il portale clienti.');
        return;
      }
      window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      setErrore('Connessione non riuscita.');
    }
  };

  const apriAttivazione = () => {
    const url = `https://www.labthree.it/attiva.html?codice=${encodeURIComponent(form.codice || '')}&piano=${form.piano || 'START'}&billing=monthly`;
    window.open(url, '_blank', 'noopener');
  };

  const statoLabel = (s) => ({
    attivo:       'Attivo',
    prova:        'Prova gratuita',
    sola_lettura: 'Sola lettura',
    cancellato:   'Cancellato',
  }[s] || s || '—');

  const statoChipKind = (s) => ({
    attivo:       'verde',
    prova:        'blu',
    sola_lettura: 'arancio',
    cancellato:   'rosso',
  }[s] || 'neutro');

  // ── D2 Evoluto local styles ───────────────────────────────────────
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: LT.n700, marginBottom: 6, letterSpacing: 0.2 };
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '11px 13px', fontSize: 14, color: LT.navy,
    background: LT.n0, border: `1.5px solid ${LT.n200}`, borderRadius: LT.r.md,
    outline: 'none', fontFamily: LT.sans, transition: 'border-color .15s, box-shadow .15s',
  };
  const sectionHead = (bg, c, iconName, titolo, sotto) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
      <div style={{
        width: 34, height: 34, borderRadius: LT.r.md, background: bg, color: c,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
      }}>
        <Ico d={I[iconName]} size={18} stroke={c} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>{titolo}</div>
        {sotto && <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>{sotto}</div>}
      </div>
    </div>
  );

  // ── Loading ───────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ fontSize: 16, fontWeight: 800, color: LT.navy }}>La mia azienda</div>
      </div>
      <div style={{ maxWidth: 720, margin: '24px auto', padding: '0 16px' }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 4px' }}>
            <div style={{
              width: 40, height: 40, borderRadius: LT.r.md, background: LT.bluSoft, color: LT.blu,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.building} size={20} stroke={LT.blu} />
            </div>
            <div style={{ fontSize: 14, color: LT.n600, fontWeight: 500 }}>Caricamento dati azienda…</div>
          </div>
        </Card>
      </div>
    </div>
  );

  // ── Main ──────────────────────────────────────────────────────────
  const oggi = new Date();
  const weekday = oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        {/* Hero compatto */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>
            {weekday}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2, marginBottom: 4 }}>
            La mia azienda
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Dati anagrafici, contatti e logo della tua struttura
          </div>
        </div>

        {/* Toast messaggi */}
        {errore && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}22`,
            borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: LT.rosso, fontWeight: 600,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.rosso} />
            {errore}
          </div>
        )}
        {messaggio && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: LT.verdeSoft, border: `1px solid ${LT.verde}33`,
            borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: '#166534', fontWeight: 600,
          }}>
            <Ico d={I.check} size={16} stroke={LT.verde} />
            {messaggio}
          </div>
        )}

        {/* Card: Dati anagrafici */}
        <Card style={{ marginBottom: 14 }}>
          {sectionHead(LT.bluSoft, LT.blu, 'building', 'Dati anagrafici', 'Ragione sociale e indirizzo')}

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Ragione sociale *</label>
            <input
              style={inputStyle}
              value={form.ragione_sociale || ''}
              onChange={e => setForm(f => ({ ...f, ragione_sociale: e.target.value }))}
              onFocus={e => { e.target.style.borderColor = LT.blu; e.target.style.boxShadow = `0 0 0 3px ${LT.blu}22`; }}
              onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 240px', minWidth: 200 }}>
              <label style={labelStyle}>Indirizzo</label>
              <input
                style={inputStyle}
                value={form.indirizzo || ''}
                onChange={e => setForm(f => ({ ...f, indirizzo: e.target.value }))}
                onFocus={e => { e.target.style.borderColor = LT.blu; e.target.style.boxShadow = `0 0 0 3px ${LT.blu}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <label style={labelStyle}>Città</label>
              <input
                style={inputStyle}
                value={form.citta || ''}
                onChange={e => setForm(f => ({ ...f, citta: e.target.value }))}
                onFocus={e => { e.target.style.borderColor = LT.blu; e.target.style.boxShadow = `0 0 0 3px ${LT.blu}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
        </Card>

        {/* Card: Contatti */}
        <Card style={{ marginBottom: 14 }}>
          {sectionHead(LT.violaSoft, LT.viola, 'mail', 'Contatti', 'Recapiti usati in bolle e documenti')}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 200 }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                style={inputStyle}
                value={form.email || ''}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                onFocus={e => { e.target.style.borderColor = LT.viola; e.target.style.boxShadow = `0 0 0 3px ${LT.viola}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '1 1 180px', minWidth: 160 }}>
              <label style={labelStyle}>Telefono</label>
              <input
                style={inputStyle}
                value={form.telefono || ''}
                onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                onFocus={e => { e.target.style.borderColor = LT.viola; e.target.style.boxShadow = `0 0 0 3px ${LT.viola}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
        </Card>

        {/* Card: Dati fiscali per fattura SDI */}
        <Card style={{ marginBottom: 14 }}>
          {sectionHead(LT.cianoSoft || '#ECFEFF', LT.ciano, 'receipt', 'Dati fiscali',
            'Necessari per emettere la fattura elettronica via Sistema di Interscambio (SDI).')}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <label style={labelStyle}>Partita IVA</label>
              <input
                style={inputStyle}
                value={form.partita_iva || ''}
                placeholder="11 cifre"
                inputMode="numeric"
                maxLength={11}
                onChange={e => setForm(f => ({ ...f, partita_iva: e.target.value.replace(/\D/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 180 }}>
              <label style={labelStyle}>Codice fiscale</label>
              <input
                style={inputStyle}
                value={form.codice_fiscale || ''}
                placeholder="16 caratteri (o 11 cifre)"
                maxLength={16}
                onChange={e => setForm(f => ({ ...f, codice_fiscale: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: '1 1 100px', minWidth: 100, maxWidth: 140 }}>
              <label style={labelStyle}>CAP</label>
              <input
                style={inputStyle}
                value={form.cap || ''}
                placeholder="5 cifre"
                inputMode="numeric"
                maxLength={5}
                onChange={e => setForm(f => ({ ...f, cap: e.target.value.replace(/\D/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '1 1 100px', minWidth: 100, maxWidth: 140 }}>
              <label style={labelStyle}>Provincia</label>
              <input
                style={inputStyle}
                value={form.provincia || ''}
                placeholder="es. MO"
                maxLength={2}
                onChange={e => setForm(f => ({ ...f, provincia: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '1 1 120px', minWidth: 100, maxWidth: 160 }}>
              <label style={labelStyle}>Nazione</label>
              <input
                style={inputStyle}
                value={form.nazione || 'IT'}
                placeholder="IT"
                maxLength={2}
                onChange={e => setForm(f => ({ ...f, nazione: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          <div style={{
            padding: '10px 14px',
            background: LT.bluSoft,
            border: `1px solid ${LT.blu}22`,
            borderRadius: LT.r.md,
            fontSize: 12.5,
            color: LT.n700,
            lineHeight: 1.5,
            marginBottom: 12,
          }}>
            <strong style={{ color: LT.navy }}>Recapito fattura elettronica:</strong> compila almeno
            uno tra <em>Codice destinatario SDI</em> (7 caratteri) e <em>PEC</em>.
            Se non hai un codice destinatario, lascia vuoto e indica solo la PEC.
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <label style={labelStyle}>Codice destinatario SDI</label>
              <input
                style={inputStyle}
                value={form.codice_destinatario_sdi || ''}
                placeholder="7 caratteri"
                maxLength={7}
                onChange={e => setForm(f => ({ ...f, codice_destinatario_sdi: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <div style={{ flex: '2 1 260px', minWidth: 220 }}>
              <label style={labelStyle}>PEC</label>
              <input
                type="email"
                style={inputStyle}
                value={form.pec || ''}
                placeholder="esempio@pec.it"
                onChange={e => setForm(f => ({ ...f, pec: e.target.value.trim() }))}
                onFocus={e => { e.target.style.borderColor = LT.ciano; e.target.style.boxShadow = `0 0 0 3px ${LT.ciano}22`; }}
                onBlur={e => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
        </Card>

        {/* Card: Logo */}
        <Card style={{ marginBottom: 18 }}>
          {sectionHead(LT.arancioSoft, LT.arancio, 'camera', 'Logo aziendale', 'Appare su stampe, intestazioni e bolle')}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
            padding: 16, marginBottom: 12,
          }}>
            {form.logo ? (
              <img
                src={`${BACKEND}${form.logo}`}
                alt="logo"
                style={{
                  maxHeight: 88, maxWidth: 220, objectFit: 'contain',
                  background: LT.n0, borderRadius: LT.r.md,
                  border: `1px solid ${LT.n200}`, padding: 10,
                }}
              />
            ) : (
              <div style={{
                width: 88, height: 88, borderRadius: LT.r.lg,
                background: LT.n0, border: `1px dashed ${LT.n300}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: LT.n400,
              }}>
                <Ico d={I.building} size={32} stroke={LT.n400} />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
                {form.logo ? 'Logo caricato' : 'Nessun logo caricato'}
              </div>
              <div style={{ fontSize: 12, color: LT.n500, lineHeight: 1.4 }}>
                Formati consigliati: PNG o SVG su sfondo trasparente, max 1 MB.
              </div>
            </div>
          </div>

          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', background: LT.n0,
            border: `1.5px dashed ${LT.arancio}66`, color: LT.arancio,
            borderRadius: LT.r.md, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', transition: 'background .15s',
          }}
          onMouseOver={e => e.currentTarget.style.background = LT.arancioSoft}
          onMouseOut={e => e.currentTarget.style.background = LT.n0}>
            <Ico d={I.camera} size={16} stroke={LT.arancio} />
            {form.logo ? 'Cambia logo' : 'Carica logo'}
            <input type="file" accept="image/*" onChange={handleLogo} style={{ display: 'none' }} />
          </label>
        </Card>

        {/* Abbonamento e fatturazione */}
        <Card style={{ marginBottom: 14 }}>
          {sectionHead(LT.verdeSoft, LT.verde, 'package', 'Abbonamento e fatturazione',
            'Gestisci il tuo piano, vedi le fatture, aggiorna la carta o disdici quando vuoi.')}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: LT.n500, textTransform: 'uppercase', marginBottom: 6 }}>Piano</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: LT.navy }}>{form.piano || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: LT.n500, textTransform: 'uppercase', marginBottom: 6 }}>Stato</div>
              <Chip kind={statoChipKind(form.stato_abbonamento)} size="md">{statoLabel(form.stato_abbonamento)}</Chip>
            </div>
          </div>

          {form.stato_abbonamento === 'prova' && form.data_scadenza_prova && (
            <div style={{
              padding: '10px 14px', background: LT.bluSoft, border: `1px solid ${LT.blu}33`,
              borderRadius: LT.r.md, fontSize: 13, color: LT.navy, marginBottom: 14,
            }}>
              Prova gratuita in corso. Scade il <strong>{new Date(form.data_scadenza_prova).toLocaleDateString('it-IT')}</strong>.
              Puoi attivare l'abbonamento in qualsiasi momento per non interrompere il servizio.
            </div>
          )}
          {form.stato_abbonamento === 'sola_lettura' && (
            <div style={{
              padding: '10px 14px', background: LT.arancioSoft || '#FFFBEB',
              border: `1px solid ${LT.arancio}33`, borderRadius: LT.r.md,
              fontSize: 13, color: '#92400E', marginBottom: 14,
            }}>
              Account in sola lettura. Riattiva un piano per riprendere a registrare nuove attività.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {form.has_stripe_customer ? (
              <Btn kind="primary" icon="settings" onClick={apriPortaleAbbonamento}>
                Gestisci abbonamento
              </Btn>
            ) : (
              <Btn kind="primary" icon="play" onClick={apriAttivazione}>
                Attiva un abbonamento
              </Btn>
            )}
          </div>
        </Card>

        {/* Cartello QR stampabile */}
        <Card style={{ marginBottom: 14 }}>
          {sectionHead(LT.violaSoft, LT.viola, 'qr', 'Cartello QR per la produzione',
            'Stampa un A4 con il QR che porta direttamente alla pagina di accesso della tua azienda.')}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Btn kind="outline" icon="qr" onClick={() => setMostraQR(true)}>
              Anteprima cartello
            </Btn>
            <div style={{ fontSize: 12, color: LT.n500, alignSelf: 'center' }}>
              Da plastificare e appendere in produzione
            </div>
          </div>
        </Card>

        {/* Azione principale */}
        <Btn kind="primary" icon="check" block disabled={saving} onClick={handleSave}>
          {saving ? 'Salvataggio…' : 'Salva modifiche'}
        </Btn>
      </div>

      {mostraQR && (
        <ModaleStampaQR
          azienda={form}
          onClose={() => setMostraQR(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODULO TRACCIABILITÀ
// ═══════════════════════════════════════════════════════════════

const CAT_FORNITORE  = ['MATERIA_PRIMA', 'MOCA', 'PACKAGING', 'SERVIZI'];
const CAT_INGREDIENTE = ['MATERIA_PRIMA', 'INGREDIENTE', 'ADDITIVO', 'MOCA', 'PACKAGING'];
const CAT_PRODOTTO   = ['SEMILAVORATO', 'PRODOTTO_FINITO'];
const UNITA_MISURA   = ['kg', 'g', 'l', 'ml', 'pz', 'conf', 'sacchi', 'cartoni'];
const ALLERGENI = [
  { key: 'all_glutine',       label: 'Glutine (cereali)' },
  { key: 'all_crostacei',     label: 'Crostacei' },
  { key: 'all_uova',          label: 'Uova' },
  { key: 'all_pesce',         label: 'Pesce' },
  { key: 'all_arachidi',      label: 'Arachidi' },
  { key: 'all_soia',          label: 'Soia' },
  { key: 'all_latte',         label: 'Latte' },
  { key: 'all_frutta_guscio', label: 'Frutta a guscio' },
  { key: 'all_sedano',        label: 'Sedano' },
  { key: 'all_senape',        label: 'Senape' },
  { key: 'all_sesamo',        label: 'Sesamo' },
  { key: 'all_solfiti',       label: 'Solfiti / SO₂' },
  { key: 'all_lupini',        label: 'Lupini' },
  { key: 'all_molluschi',     label: 'Molluschi' },
];

const STATO_MAG_COLORI = {
  IN_MAGAZZINO: { bg: '#eaf4ea', color: '#16a34a' },
  IN_USO:       { bg: '#fff3e0', color: '#b45309' },
  ESAURITO:     { bg: '#fdecea', color: '#b00020' },
};
const STATO_MAG_LABEL = {
  IN_MAGAZZINO: 'In magazzino',
  IN_USO:       'In uso',
  ESAURITO:     'Esaurito',
};
const STATO_LAV_COLORI = {
  APERTA:    { bg: '#eaf4ea', color: '#16a34a' },
  CHIUSA:    { bg: '#e3f0ff', color: '#1a56b0' },
  ANNULLATA: { bg: '#fdecea', color: '#b00020' },
};
const STATO_LAV_LABEL = { APERTA: 'Aperta', CHIUSA: 'Chiusa', ANNULLATA: 'Annullata' };
const _STATO_PF_COLORI = {
  DISPONIBILE:  { bg: '#eaf4ea', color: '#16a34a' },
  IN_MAGAZZINO: { bg: '#eaf4ea', color: '#16a34a' },
  PARZIALE:     { bg: '#fff3e0', color: '#b45309' },
  ESAURITO:     { bg: '#f0f0f0', color: '#888' },
  IN_USO:       { bg: '#eaf4ea', color: '#16a34a' },
};
const STATO_PF_LABEL = { DISPONIBILE: 'Disponibile', IN_MAGAZZINO: 'In magazzino', PARZIALE: 'Parziale', ESAURITO: 'Esaurito', IN_USO: 'In uso' };
const _STATO_SL_COLORI = {
  DISPONIBILE:  { bg: '#e3f0ff', color: '#1a56b0' },
  IN_MAGAZZINO: { bg: '#e3f0ff', color: '#1a56b0' },
  IN_USO:       { bg: '#eaf4ea', color: '#16a34a' },
  ESAURITO:     { bg: '#f0f0f0', color: '#888' },
};
const STATO_SL_LABEL = { DISPONIBILE: 'Disponibile', IN_MAGAZZINO: 'In magazzino', IN_USO: 'In uso', ESAURITO: 'Esaurito' };

// ─── Menu hub Tracciabilità ────────────────────────────────────
// ─── Dashboard Tracciabilità ───────────────────────────────────
function DashboardTracciabilita({ operatore, onBack, onSezione }) {
  const idAzienda = operatore.id_azienda;
  const [dati,       setDati]       = useState(null);
  const [caricando,  setCaricando]  = useState(true);

  useEffect(() => {
    apiFetch(`${API}/tracciabilita/dashboard?idAzienda=${idAzienda}`)
      .then(r => r.json())
      .then(d => { setDati(d); setCaricando(false); })
      .catch(() => setCaricando(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chip stato lavorazione coerente con il resto del design system
  const statoLavChip = (stato) => {
    if (stato === 'APERTA')    return <Chip kind="arancio" icon="play">Aperta</Chip>;
    if (stato === 'CHIUSA')    return <Chip kind="verde"   icon="check">Chiusa</Chip>;
    if (stato === 'ANNULLATA') return <Chip kind="rosso"   icon="x">Annullata</Chip>;
    return <Chip kind="neutro">{stato}</Chip>;
  };

  // KPI card: tile icona + valore grosso + label + sotto-label opzionale
  const kpiCard = ({ iconName, titolo, valore, sotto, c, bg, onClick, alert = false }) => (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        textAlign: 'left', cursor: onClick ? 'pointer' : 'default',
        background: LT.n0, border: `1px solid ${alert ? c + '55' : LT.n150}`,
        borderRadius: LT.r.lg, padding: '14px 14px',
        display: 'flex', flexDirection: 'column', gap: 6,
        fontFamily: LT.sans, minWidth: 0,
        transition: 'border-color .15s, box-shadow .15s',
      }}
      onMouseOver={e => { if (onClick) { e.currentTarget.style.borderColor = c; e.currentTarget.style.boxShadow = LT.sh2; } }}
      onMouseOut={e => { if (onClick) { e.currentTarget.style.borderColor = alert ? c + '55' : LT.n150; e.currentTarget.style.boxShadow = 'none'; } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 32, height: 32, borderRadius: LT.r.md,
          background: bg, color: c,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Ico d={I[iconName]} size={16} stroke={c} />
        </div>
        {alert && <Ico d={I.alert} size={14} stroke={c} />}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: alert ? c : LT.navy, lineHeight: 1 }}>
        {valore}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: LT.n700, lineHeight: 1.3 }}>{titolo}</div>
      {sotto && <div style={{ fontSize: 11, color: LT.n500 }}>{sotto}</div>}
    </button>
  );

  const oggi = new Date();
  const weekday = oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Menu</Btn>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        {/* Hero compatto */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>
            {weekday}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2, marginBottom: 4 }}>
            Dashboard Tracciabilità
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Stato operativo del modulo: lavorazioni, scorte e qualifiche
          </div>
        </div>

        {/* Loading */}
        {caricando && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px' }}>
              <div style={{
                width: 34, height: 34, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.chart} size={18} stroke={LT.blu} />
              </div>
              <div style={{ fontSize: 13, color: LT.n600, fontWeight: 500 }}>Caricamento dashboard…</div>
            </div>
          </Card>
        )}

        {dati && (<>
          {/* Griglia KPI */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10, marginBottom: 22,
          }}>
            {kpiCard({
              iconName: 'chefHat', titolo: 'Lavorazioni aperte',
              valore: dati.lavorazioni_aperte,
              c: dati.lavorazioni_aperte > 0 ? LT.arancio : LT.n600,
              bg: dati.lavorazioni_aperte > 0 ? LT.arancioSoft : LT.n100,
              alert: dati.lavorazioni_aperte > 0,
              onClick: () => onSezione('tr-lavorazioni'),
            })}
            {kpiCard({
              iconName: 'check', titolo: 'Chiuse ultimi 30 gg',
              valore: dati.lavorazioni_recenti,
              c: LT.verde, bg: LT.verdeSoft,
              onClick: () => onSezione('tr-lavorazioni'),
            })}
            {kpiCard({
              iconName: 'pkg', titolo: 'Lotti PF in giacenza',
              valore: dati.giacenze_pf.n_lotti,
              sotto: `Totale: ${(dati.giacenze_pf.qtot || 0).toFixed(2)} kg`,
              c: LT.blu, bg: LT.bluSoft,
              onClick: () => onSezione('tr-magazzino-pf'),
            })}
            {kpiCard({
              iconName: 'calendar', titolo: 'MP in scadenza (30 gg)',
              valore: dati.mp_in_scadenza,
              c: dati.mp_in_scadenza > 0 ? LT.rosso : LT.n600,
              bg: dati.mp_in_scadenza > 0 ? LT.rossoSoft : LT.n100,
              alert: dati.mp_in_scadenza > 0,
              onClick: () => onSezione('tr-magazzino-mp'),
            })}
            {kpiCard({
              iconName: 'factory', titolo: 'Fornitori qualif. scad. (60 gg)',
              valore: dati.fornitori_qualif_scad,
              c: dati.fornitori_qualif_scad > 0 ? LT.arancio : LT.n600,
              bg: dati.fornitori_qualif_scad > 0 ? LT.arancioSoft : LT.n100,
              alert: dati.fornitori_qualif_scad > 0,
              onClick: () => onSezione('tr-fornitori'),
            })}
          </div>

          {/* Ultime lavorazioni */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: LT.r.md,
              background: LT.verdeSoft, color: LT.verde,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.history} size={15} stroke={LT.verde} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: LT.navy }}>Ultime lavorazioni</div>
          </div>

          {(dati.ultime_lavorazioni || []).length === 0 ? (
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px' }}>
                <div style={{
                  width: 34, height: 34, borderRadius: LT.r.md,
                  background: LT.n100, color: LT.n500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.chefHat} size={18} stroke={LT.n500} />
                </div>
                <div style={{ fontSize: 13, color: LT.n500 }}>Nessuna lavorazione registrata</div>
              </div>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(dati.ultime_lavorazioni || []).map(lav => (
                <button
                  key={lav.id}
                  onClick={() => onSezione('tr-lavorazioni')}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: LT.n0, border: `1px solid ${LT.n150}`,
                    borderRadius: LT.r.lg, padding: '12px 14px',
                    fontFamily: LT.sans, color: LT.navy,
                    transition: 'border-color .15s, box-shadow .15s',
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = LT.verde; e.currentTarget.style.boxShadow = LT.sh2; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = LT.n150; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lav.prodotto || '—'}
                    </div>
                    {statoLavChip(lav.stato)}
                  </div>
                  <div style={{ fontSize: 12, color: LT.n500, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    {lav.lotto_prodotto && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.barcode} size={13} stroke={LT.n500} />
                        <span style={{ fontFamily: LT.mono, fontSize: 11, color: LT.n700 }}>{lav.lotto_prodotto}</span>
                      </span>
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Ico d={I.calendar} size={13} stroke={LT.n500} />
                      {lav.data_inizio}
                    </span>
                    {lav.quantita_reale && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.package} size={13} stroke={LT.n500} />
                        {lav.quantita_reale} {lav.unita_misura}
                      </span>
                    )}
                    {lav.committente_nome && (
                      <Chip kind="arancio" icon="users">{lav.committente_nome}</Chip>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

function MenuTracciabilita({ operatore, onSezione, onBack }) {
  // Ogni gruppo ha un colore D2 che colora il chip titolo + il tile icona delle voci.
  // Un criterio solo: prima quello che si usa ogni giorno, nell'ordine in cui il
  // prodotto attraversa l'azienda; poi le verifiche; in fondo, tutto insieme,
  // quello che si imposta una volta e non si tocca piu'. Prima i gruppi
  // mescolavano flusso e tipo di dato, e le anagrafiche finivano meta' qui e
  // meta' la'.
  const gruppi = [
    {
      // La Dashboard non ha un chip di gruppo: era un titolo speso per una voce
      // sola. In cima, da sola, si capisce meglio.
      id: 'apertura', titolo: null,
      c: LT.grigio, bg: LT.n100, cText: LT.n600,
      voci: [
        { icon: 'chart',    etichetta: 'Dashboard',
          sotto: 'KPI e stato operativo del modulo', sez: 'tr-dashboard' },
      ],
    },
    {
      id: 'entrate', titolo: 'Entrate', icon: 'arrowDown',
      c: LT.blu, bg: LT.bluSoft, cText: LT.blu,
      voci: [
        { icon: 'receipt',  etichetta: 'Bolle di ingresso',
          sotto: 'Ricezione merci e documenti di trasporto', sez: 'tr-bolle-ingresso' },
        { icon: 'stamp',    etichetta: 'Ricevimento / Accettazione',
          sotto: 'Registro dei controlli alla consegna', sez: 'tr-ricevimento' },
        { icon: 'package',  etichetta: 'Magazzino materie prime',
          sotto: 'Lotti di materia prima, stati e scadenze', sez: 'tr-magazzino-mp' },
      ],
    },
    {
      id: 'produzione', titolo: 'Produzione', icon: 'chefHat',
      c: LT.verde, bg: LT.verdeSoft, cText: '#166534',
      voci: [
        { icon: 'chefHat',  etichetta: 'Lavorazioni',
          sotto: 'Produzione e tracciabilità dei lotti', sez: 'tr-lavorazioni' },
        { icon: 'grid',     etichetta: 'Giacenze per ubicazione',
          sotto: 'Dove si trova adesso il prodotto, e come spostarlo', sez: 'tr-giacenze' },
      ],
    },
    {
      id: 'uscite', titolo: 'Uscite', icon: 'route',
      c: LT.viola, bg: LT.violaSoft, cText: LT.viola,
      voci: [
        { icon: 'pkg',      etichetta: 'Magazzino prodotti finiti',
          sotto: 'Lotti di prodotto finito e movimentazioni', sez: 'tr-magazzino-pf' },
        { icon: 'truck',    etichetta: 'Bolle di uscita',
          sotto: 'Spedizioni e documenti di trasporto', sez: 'tr-bolle-uscita' },
      ],
    },
    {
      id: 'verifiche', titolo: 'Verifiche e controlli', icon: 'shield',
      c: LT.ciano, bg: LT.cianoSoft, cText: '#155E75',
      voci: [
        { icon: 'search',   etichetta: 'Test di tracciabilità',
          sotto: 'Da un numero di lotto, la catena a monte e a valle', sez: 'tr-test-tracciabilita' },
        { icon: 'trend',    etichetta: 'Bilancio di massa',
          sotto: 'Rese, scarti e verifica delle lavorazioni chiuse', sez: 'tr-bilancio-massa' },
      ],
    },
    {
      id: 'configurazione', titolo: 'Configurazione', icon: 'settings',
      nota: 'si imposta una volta',
      c: LT.arancio, bg: LT.arancioSoft, cText: '#92400E',
      voci: [
        { icon: 'factory',  etichetta: 'Fornitori',
          sotto: 'Anagrafica fornitori, categorie e qualifica', sez: 'tr-fornitori' },
        { icon: 'users',    etichetta: 'Clienti',
          sotto: 'Anagrafica clienti e contatti per il richiamo', sez: 'tr-clienti' },
        { icon: 'drop',     etichetta: 'Ingredienti e materie prime',
          sotto: 'Materie prime, ingredienti, MOCA e allergeni', sez: 'tr-ingredienti' },
        { icon: 'barcode',  etichetta: 'Prodotti e semilavorati',
          sotto: 'Prodotti finiti e semilavorati', sez: 'tr-prodotti' },
        { icon: 'layers',   etichetta: 'Fasi produttive',
          sotto: 'Fasi e parametri di processo', sez: 'tr-fasi' },
        { icon: 'list',     etichetta: 'Ricette',
          sotto: 'Composizione e processo produttivo', sez: 'tr-ricette' },
        { icon: 'pin',      etichetta: 'Ubicazioni',
          sotto: 'Aree fisiche dello stabilimento', sez: 'tr-ubicazioni' },
        { icon: 'checkCircle', etichetta: 'Modelli di accettazione',
          sotto: 'La checklist usata dal Ricevimento', sez: 'tr-accettazione-modelli' },
      ],
    },
  ];

  const oggi = new Date();
  const weekday = oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        {/* Hero compatto */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>
            {weekday}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 32, height: 32, borderRadius: LT.r.md,
              background: LT.gradSoft,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: LT.blu,
            }}>
              <Ico d={I.qr} size={18} stroke={LT.blu} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>
              Tracciabilità
            </div>
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Gestione della filiera: entrate, produzione, uscite e ricerca lotti
          </div>
        </div>

        {/* Gruppi */}
        {gruppi.map(g => (
          <div key={g.id} style={{ marginBottom: 22 }}>
            {/* Titolo gruppo — chip colorato soft. Senza titolo il chip
                non si disegna: serve alla Dashboard, che sta in cima da sola. */}
            {g.titolo && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: g.bg, color: g.cText,
              padding: '8px 12px', borderRadius: LT.r.md,
              marginBottom: 10,
              fontWeight: 800, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase',
            }}>
              <Ico d={I[g.icon]} size={14} stroke={g.cText} />
              <span>{g.titolo}</span>
              {g.nota && (
                <span style={{ fontWeight: 600, opacity: 0.75, textTransform: 'none', letterSpacing: 0 }}>
                  · {g.nota}
                </span>
              )}
            </div>
            )}

            {/* Voci — card con tile colorato a sinistra */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.voci.map(v => (
                <button
                  key={v.sez}
                  onClick={() => onSezione(v.sez)}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: LT.n0, border: `1px solid ${LT.n150}`,
                    borderRadius: LT.r.lg, padding: '12px 14px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    fontFamily: LT.sans, color: LT.navy,
                    transition: 'border-color .15s, box-shadow .15s, transform .05s',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.borderColor = g.c;
                    e.currentTarget.style.boxShadow = LT.sh2;
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.borderColor = LT.n150;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseDown={e => { e.currentTarget.style.transform = 'scale(.995)'; }}
                  onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: LT.r.md,
                    background: g.bg, color: g.c,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flex: '0 0 auto',
                  }}>
                    <Ico d={I[v.icon]} size={20} stroke={g.c} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, lineHeight: 1.25 }}>
                      {v.etichetta}
                    </div>
                    <div style={{ fontSize: 12, color: LT.n500, marginTop: 2, lineHeight: 1.35 }}>
                      {v.sotto}
                    </div>
                  </div>
                  <div style={{ color: LT.n400, flex: '0 0 auto' }}>
                    <Ico d={I.chevronR} size={18} stroke={LT.n400} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Gestione Fornitori ───────────────────────────────────────
const STATI_QUALIFICA = [
  { val: 'NON_QUALIFICATO', label: 'Non qualificato', colore: '#777',    bg: '#f0f0f0' },
  { val: 'IN_VALUTAZIONE',  label: 'In valutazione',  colore: '#7a4500', bg: '#fff3e0' },
  { val: 'QUALIFICATO',     label: 'Qualificato',      colore: '#16a34a', bg: '#eaf4ea' },
  { val: 'SOSPESO',         label: 'Sospeso',          colore: '#b45309', bg: '#fef3c7' },
  { val: 'REVOCATO',        label: 'Revocato',         colore: '#b00020', bg: '#ffeaea' },
];

function BadgeQualifica({ stato }) {
  // Mappa stato qualifica → kind del Chip D2
  const map = {
    NON_QUALIFICATO: { kind: 'neutro',  icon: 'dot',    label: 'Non qualificato' },
    IN_VALUTAZIONE:  { kind: 'arancio', icon: 'clock',  label: 'In valutazione' },
    QUALIFICATO:     { kind: 'verde',   icon: 'check',  label: 'Qualificato' },
    SOSPESO:         { kind: 'arancio', icon: 'alert',  label: 'Sospeso' },
    REVOCATO:        { kind: 'rosso',   icon: 'x',      label: 'Revocato' },
  };
  const cfg = map[stato] || map.NON_QUALIFICATO;
  return <Chip kind={cfg.kind} icon={cfg.icon}>{cfg.label}</Chip>;
}

// Sezione form in stile D2: tile colorato + titolo + sottotitolo opzionale.
// Manteniamo la vecchia firma ({titolo, children}) ma aggiungiamo opzioni:
//   color ('blu'|'verde'|'viola'|'arancio'|'ciano'|'rosso'), icon, sotto
function SezioneForm({ titolo, children, color = 'blu', icon = 'folder', sotto }) {
  const palette = {
    blu:     { c: LT.blu,     bg: LT.bluSoft },
    verde:   { c: LT.verde,   bg: LT.verdeSoft },
    viola:   { c: LT.viola,   bg: LT.violaSoft },
    arancio: { c: LT.arancio, bg: LT.arancioSoft },
    ciano:   { c: LT.ciano,   bg: LT.cianoSoft },
    rosso:   { c: LT.rosso,   bg: LT.rossoSoft },
    neutro:  { c: LT.n600,    bg: LT.n100 },
  };
  const p = palette[color] || palette.blu;
  return (
    <div style={{ marginTop: 18, marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: LT.r.md, background: p.bg, color: p.c,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
        }}>
          <Ico d={I[icon] || I.folder} size={17} stroke={p.c} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>{titolo}</div>
          {sotto && <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>{sotto}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

// Helper globali per tutte le schermate Tracciabilità anagrafiche
const TR_LABEL = { display: 'block', fontSize: 12, fontWeight: 700, color: LT.n700, marginBottom: 6, marginTop: 10, letterSpacing: 0.2 };
const TR_INPUT = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 12px', fontSize: 14, color: LT.navy,
  background: LT.n0, border: `1.5px solid ${LT.n200}`, borderRadius: LT.r.md,
  outline: 'none', fontFamily: LT.sans, transition: 'border-color .15s, box-shadow .15s',
};
const trFocus = (e, col = LT.blu) => { e.target.style.borderColor = col; e.target.style.boxShadow = `0 0 0 3px ${col}22`; };
const trBlur  = (e) => { e.target.style.borderColor = LT.n200; e.target.style.boxShadow = 'none'; };

// ─── Conto lavorazione: di chi e' la merce ───────────────────────────────────
// La merce di un committente non e' dell'azienda: non e' stata comprata e non
// verra' venduta, viene lavorata e restituita. Ovunque compaia un lotto va
// detto a chi appartiene, altrimenti in magazzino le due merci si confondono.
// Il contrassegno compare SOLO sulla merce di terzi: chi non lavora conto terzi
// non deve vedere niente di nuovo.
// Normalizza il separatore decimale all'ingresso: "2,4" -> "2.4".
// PERCHE': i campi quantita usavano <input type="number">, e con quel tipo il
// browser SCARTA la virgola mentre si digita — "2,4" diventa "24" senza nessun
// avviso (verificato in Chromium, lingua italiana e inglese). I campi decimali
// sono quindi type="text" inputMode="decimal" (tastierino numerico sul
// telefono) e questa funzione converte la virgola nel punto DENTRO lo stato
// del form: tutto il codice a valle (parseFloat, JSON, backend) resta uguale.
const decVal = v => String(v ?? '').replace(',', '.');

// ── ModalAllegatiBolla ───────────────────────────────────────────────────────
// Allegati di una bolla (ingresso o uscita): DDT scansionato, foto della
// merce, referti. Si puo' aprire in qualsiasi momento, anche a bolla vecchia
// ("anche postumi"). Il server comprime da solo immagini e PDF: qui si mostra
// quanto spazio ha risparmiato, cosi' la compressione non e' un mistero.
function ModalAllegatiBolla({ tipo, idBolla, titolo, operatore, onChiudi }) {
  const [allegati,  setAllegati]  = useState([]);
  const [caricando, setCaricando] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const carica = async () => {
    setCaricando(true);
    try {
      const res  = await apiFetch(`${API}/bolle-allegati/${tipo}/${idBolla}`);
      const data = await res.json();
      setAllegati(Array.isArray(data) ? data : []);
    } catch { setAllegati([]); }
    setCaricando(false);
  };
  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiFetch(`${API}/bolle-allegati/${tipo}/${idBolla}`, { method: 'POST', body: fd });
    if (res.ok) {
      const d = await res.json().catch(() => ({}));
      const risp = d.dimensione_originale && d.dimensione < d.dimensione_originale
        ? ` (compresso: −${Math.round(100 - d.dimensione / d.dimensione_originale * 100)}%)`
        : '';
      toast('✓ Allegato caricato' + risp);
      await carica();
    } else {
      const d = await res.json().catch(() => ({}));
      toast(d.errore || 'Errore nel caricamento', 'errore');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleElimina = async (id, nome) => {
    const ok = await chiediConferma('Eliminare allegato?', nome);
    if (!ok) return;
    const res = await apiFetch(`${API}/bolle-allegati/${id}`, { method: 'DELETE' });
    if (res.ok) { toast('✓ Allegato eliminato'); await carica(); }
    else toast('Errore eliminazione', 'errore');
  };

  const fmtSize = (b) => {
    if (b == null) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div style={lt_overlay}>
      <div style={{ ...lt_modal, maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.folder} size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>Allegati della bolla</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {titolo}
            </div>
          </div>
          <Btn kind="ghost" size="sm" icon="x" onClick={onChiudi} title="Chiudi" />
        </div>

        {caricando ? (
          <div style={{ color: LT.n500, fontSize: 13, padding: 12 }}>Caricamento…</div>
        ) : allegati.length === 0 ? (
          <div style={{
            border: `1.5px dashed ${LT.n300}`, borderRadius: LT.r.md,
            padding: '22px 14px', textAlign: 'center', fontSize: 13, color: LT.n500,
          }}>
            Nessun documento allegato a questa bolla.
          </div>
        ) : (
          <div>
            {allegati.map(al => (
              <div key={al.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 10px', border: `1px solid ${LT.n150}`,
                borderRadius: LT.r.md, marginBottom: 8, background: LT.n50,
              }}>
                <Ico d={/\.pdf$/i.test(al.path_file) ? I.receipt : I.camera} size={18} stroke={LT.n500} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={`${BACKEND}${al.path_file}`} target="_blank" rel="noreferrer"
                     style={{ fontSize: 13.5, fontWeight: 600, color: LT.blu, textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {al.nome}
                  </a>
                  <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                    {fmtSize(al.dimensione)}
                    {al.dimensione_originale && al.dimensione < al.dimensione_originale && (
                      <span style={{ color: LT.verde }}>
                        {' '}· compresso da {fmtSize(al.dimensione_originale)}
                      </span>
                    )}
                    {al.caricato_da ? ` · ${al.caricato_da}` : ''}
                    {al.created_at ? ` · ${new Date(al.created_at).toLocaleDateString('it-IT')}` : ''}
                  </div>
                </div>
                {(operatore.admin || operatore.master) && (
                  <Btn kind="ghost" size="sm" icon="trash"
                    onClick={() => handleElimina(al.id, al.nome)}
                    style={{ color: LT.rosso }} title="Elimina" />
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.eml,.msg"
            onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
          <Btn kind="primary" block icon="plus" disabled={uploading}
            onClick={() => fileRef.current && fileRef.current.click()}>
            {uploading ? 'Caricamento…' : 'Aggiungi documento'}
          </Btn>
          <div style={{ fontSize: 11, color: LT.n500, marginTop: 6, textAlign: 'center' }}>
            PDF e immagini vengono compressi automaticamente dal server.
          </div>
        </div>
      </div>
    </div>
  );
}

const ChipContoTerzi = ({ committente, size = 'sm' }) => (
  committente
    ? <Chip kind="arancio" size={size} icon="users">Conto terzi · {committente}</Chip>
    : null
);

// Il filtro compare solo se l'azienda ha davvero merce di terzi: per tutti gli
// altri sarebbe un campo in piu' che non serve.
const FiltroProprieta = ({ valore, onChange, colore = LT.blu }) => (
  <div>
    <label style={TR_LABEL}>Proprietà</label>
    <select
      style={TR_INPUT}
      value={valore}
      onChange={e => onChange(e.target.value)}
      onFocus={e => trFocus(e, colore)}
      onBlur={trBlur}
    >
      <option value="">Tutta la merce</option>
      <option value="PROPRIA">Solo merce mia</option>
      <option value="TERZI">Solo merce di terzi</option>
    </select>
  </div>
);

function GestioneFornitori({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null);
  const [ricerca,  setRicerca]  = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/fornitori?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const apriForm = (item = {}) => {
    setForm({ attivo: true, stato_qualifica: 'NON_QUALIFICATO', ...item });
    setErrore('');
  };

  const handleSalva = async () => {
    if (!form.ragione_sociale) { setErrore('Ragione sociale obbligatoria'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/fornitori/${form.id}` : `${API}/tracciabilita/fornitori`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:              idAzienda,
          ragioneSociale:         form.ragione_sociale,
          indirizzo:              form.indirizzo              || null,
          citta:                  form.citta                  || null,
          telefono:               form.telefono               || null,
          email:                  form.email                  || null,
          categoria:              form.categoria              || null,
          attivo:                 form.attivo !== false,
          piva:                   form.piva                   || null,
          referenteQualita:       form.referente_qualita      || null,
          telQualita:             form.tel_qualita            || null,
          emailQualita:           form.email_qualita          || null,
          referenteRichiamo:      form.referente_richiamo     || null,
          telRichiamo:            form.tel_richiamo           || null,
          emailRichiamo:          form.email_richiamo         || null,
          statoQualifica:         form.stato_qualifica        || 'NON_QUALIFICATO',
          dataQualifica:          form.data_qualifica         || null,
          dataScadenzaQualifica:  form.data_scadenza_qualifica || null,
          noteQualifica:          form.note_qualifica         || null,
        }),
      });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Fornitore salvato!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(f =>
    !ricerca || f.ragione_sociale?.toLowerCase().includes(ricerca.toLowerCase())
  );

  // ── Calcolo scadenza qualifica (rosso se ≤60 gg, arancio se ≤180) ─────
  const scadenzaStato = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0)   return { c: LT.rosso,   bg: LT.rossoSoft,  label: 'Scaduta',              kind: 'rosso'   };
    if (diff <= 60) return { c: LT.rosso,   bg: LT.rossoSoft,  label: `Scade in ${diff} gg`,  kind: 'rosso'   };
    if (diff <= 180)return { c: LT.arancio, bg: LT.arancioSoft,label: `Scade in ${diff} gg`,  kind: 'arancio' };
    return { c: LT.verde, bg: LT.verdeSoft, label: `In corso`, kind: 'verde' };
  };

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2, marginBottom: 4 }}>
            {form.id ? 'Modifica fornitore' : 'Nuovo fornitore'}
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Dati anagrafici, qualifica e contatti per ritiro prodotti
          </div>
        </div>

        {errore && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}22`,
            borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: LT.rosso, fontWeight: 600,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.rosso} />
            {errore}
          </div>
        )}

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Dati anagrafici" sotto="Identificativi e recapiti principali" color="blu" icon="factory">
            <label style={TR_LABEL}>Ragione sociale *</label>
            <input style={TR_INPUT} value={form.ragione_sociale || ''}
              onChange={e => setForm({ ...form, ragione_sociale: e.target.value })}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>P.IVA</label>
                <input style={TR_INPUT} value={form.piva || ''}
                  onChange={e => setForm({ ...form, piva: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                <label style={TR_LABEL}>Categoria</label>
                <select style={TR_INPUT} value={form.categoria || ''}
                  onChange={e => setForm({ ...form, categoria: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}>
                  <option value="">— seleziona —</option>
                  {CAT_FORNITORE.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 240px', minWidth: 200 }}>
                <label style={TR_LABEL}>Indirizzo</label>
                <input style={TR_INPUT} value={form.indirizzo || ''}
                  onChange={e => setForm({ ...form, indirizzo: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Città</label>
                <input style={TR_INPUT} value={form.citta || ''}
                  onChange={e => setForm({ ...form, citta: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                <label style={TR_LABEL}>Telefono</label>
                <input style={TR_INPUT} value={form.telefono || ''}
                  onChange={e => setForm({ ...form, telefono: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email</label>
                <input style={TR_INPUT} type="email" value={form.email || ''}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
            </div>
          </SezioneForm>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Qualifica fornitore" sotto="Stato di valutazione, scadenze e referente qualità" color="verde" icon="shield">
            <label style={TR_LABEL}>Stato qualifica</label>
            <select style={TR_INPUT} value={form.stato_qualifica || 'NON_QUALIFICATO'}
              onChange={e => setForm({ ...form, stato_qualifica: e.target.value })}
              onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}>
              {STATI_QUALIFICA.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
            </select>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Data qualifica</label>
                <input style={TR_INPUT} type="date" value={form.data_qualifica || ''}
                  onChange={e => setForm({ ...form, data_qualifica: e.target.value })}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Scadenza qualifica</label>
                <input style={TR_INPUT} type="date" value={form.data_scadenza_qualifica || ''}
                  onChange={e => setForm({ ...form, data_scadenza_qualifica: e.target.value })}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />
              </div>
            </div>

            <label style={TR_LABEL}>Referente qualità</label>
            <input style={TR_INPUT} value={form.referente_qualita || ''}
              onChange={e => setForm({ ...form, referente_qualita: e.target.value })}
              onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Telefono qualità</label>
                <input style={TR_INPUT} value={form.tel_qualita || ''}
                  onChange={e => setForm({ ...form, tel_qualita: e.target.value })}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email qualità</label>
                <input style={TR_INPUT} type="email" value={form.email_qualita || ''}
                  onChange={e => setForm({ ...form, email_qualita: e.target.value })}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />
              </div>
            </div>

            <label style={TR_LABEL}>Note qualifica</label>
            <textarea style={{ ...TR_INPUT, height: 80, resize: 'vertical' }}
              value={form.note_qualifica || ''}
              onChange={e => setForm({ ...form, note_qualifica: e.target.value })}
              onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur} />
          </SezioneForm>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Contatti per ritiro / richiamo prodotti" sotto="Usati in caso di richiamo urgente o ritiro merce" color="arancio" icon="phone">
            <label style={TR_LABEL}>Referente richiamo</label>
            <input style={TR_INPUT} value={form.referente_richiamo || ''}
              onChange={e => setForm({ ...form, referente_richiamo: e.target.value })}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Tel. richiamo (urgente)</label>
                <input style={TR_INPUT} value={form.tel_richiamo || ''}
                  onChange={e => setForm({ ...form, tel_richiamo: e.target.value })}
                  onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email richiamo</label>
                <input style={TR_INPUT} type="email" value={form.email_richiamo || ''}
                  onChange={e => setForm({ ...form, email_richiamo: e.target.value })}
                  onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
              </div>
            </div>
          </SezioneForm>
        </Card>

        {form.id && (
          <Card style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: LT.verde, cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>Fornitore attivo</div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Se disattivato non compare nei nuovi documenti di ingresso
                </div>
              </div>
            </label>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" icon="check" block disabled={salvando} onClick={handleSalva}>
              {salvando ? 'Salvataggio…' : (form.id ? 'Salva modifiche' : 'Crea fornitore')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Lista fornitori ─────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Menu</Btn>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        {/* Hero compatto */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.factory} size={18} stroke={LT.blu} />
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>Fornitori</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {lista.length} {lista.length === 1 ? 'fornitore' : 'fornitori'} in anagrafica
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuovo fornitore</Btn>
        </div>

        {/* Ricerca */}
        <Card style={{ marginBottom: 14, padding: 12 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
              <Ico d={I.search} size={16} stroke={LT.n400} />
            </div>
            <input
              style={{ ...TR_INPUT, paddingLeft: 36 }}
              placeholder="Cerca fornitore per ragione sociale…"
              value={ricerca}
              onChange={e => setRicerca(e.target.value)}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
            />
          </div>
        </Card>

        {/* Empty state */}
        {filtrati.length === 0 && (
          <Card>
            <div style={{ padding: '20px 4px', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: LT.r.lg,
                background: LT.n100, color: LT.n500, margin: '0 auto 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.factory} size={24} stroke={LT.n500} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
                {ricerca ? 'Nessun fornitore corrisponde alla ricerca' : 'Nessun fornitore in anagrafica'}
              </div>
              <div style={{ fontSize: 12, color: LT.n500 }}>
                {ricerca ? 'Prova con un altro nome' : 'Aggiungi il primo fornitore con il bottone in alto'}
              </div>
            </div>
          </Card>
        )}

        {/* Lista */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrati.map(f => {
            const scad = scadenzaStato(f.data_scadenza_qualifica);
            return (
              <Card key={f.id} style={{ padding: 0, opacity: f.attivo ? 1 : 0.65 }}>
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>
                        {f.ragione_sociale}
                      </div>
                      {f.categoria && (
                        <div style={{ fontSize: 11, color: LT.n500, fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                          {f.categoria.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                    <BadgeQualifica stato={f.stato_qualifica} />
                  </div>

                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: LT.n500, marginTop: 8 }}>
                    {f.citta && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.pin} size={13} stroke={LT.n500} />{f.citta}
                      </span>
                    )}
                    {f.telefono && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.phone} size={13} stroke={LT.n500} />{f.telefono}
                      </span>
                    )}
                    {f.email && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Ico d={I.mail} size={13} stroke={LT.n500} />{f.email}
                      </span>
                    )}
                  </div>

                  {scad && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: scad.bg, color: scad.c,
                      padding: '4px 10px', borderRadius: LT.r.full,
                      fontSize: 11, fontWeight: 700, marginTop: 10,
                    }}>
                      <Ico d={I.calendar} size={12} stroke={scad.c} />
                      Qualifica: {scad.label}
                      {f.data_scadenza_qualifica && (
                        <span style={{ opacity: 0.75, fontWeight: 500 }}>
                          · {new Date(f.data_scadenza_qualifica).toLocaleDateString('it-IT')}
                        </span>
                      )}
                    </div>
                  )}

                  {!f.attivo && (
                    <div style={{ marginTop: 8 }}>
                      <Chip kind="rosso" icon="x">Disattivo</Chip>
                    </div>
                  )}
                </div>

                <div style={{ borderTop: `1px solid ${LT.n150}`, padding: '8px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn kind="ghost" size="sm" icon="edit" onClick={() => apriForm(f)}>Modifica</Btn>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Gestione Clienti ────────────────────────────────────────
function GestioneClienti({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null);
  const [ricerca,  setRicerca]  = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/clienti?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const apriForm = (item = {}) => { setForm({ attivo: true, ...item }); setErrore(''); };

  const handleSalva = async () => {
    if (!form.ragione_sociale) { setErrore('Ragione sociale obbligatoria'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/clienti/${form.id}` : `${API}/tracciabilita/clienti`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:         idAzienda,
          ragioneSociale:    form.ragione_sociale,
          piva:              form.piva              || null,
          codiceFiscale:     form.codice_fiscale    || null,
          indirizzo:         form.indirizzo         || null,
          citta:             form.citta             || null,
          telefono:          form.telefono          || null,
          email:             form.email             || null,
          referente:         form.referente         || null,
          telReferente:      form.tel_referente     || null,
          emailReferente:    form.email_referente   || null,
          referenteRichiamo: form.referente_richiamo || null,
          telRichiamo:       form.tel_richiamo      || null,
          emailRichiamo:     form.email_richiamo    || null,
          note:              form.note              || null,
          attivo:            form.attivo !== false,
        }),
      });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Cliente salvato!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(c =>
    !ricerca || c.ragione_sociale?.toLowerCase().includes(ricerca.toLowerCase())
  );

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2, marginBottom: 4 }}>
            {form.id ? 'Modifica cliente' : 'Nuovo cliente'}
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Anagrafica e contatti per richiamo prodotti
          </div>
        </div>

        {errore && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}22`,
            borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: LT.rosso, fontWeight: 600,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.rosso} />{errore}
          </div>
        )}

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Dati anagrafici" sotto="Ragione sociale, dati fiscali e indirizzo" color="viola" icon="users">
            <label style={TR_LABEL}>Ragione sociale *</label>
            <input style={TR_INPUT} value={form.ragione_sociale || ''}
              onChange={e => setForm({ ...form, ragione_sociale: e.target.value })}
              onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>P.IVA</label>
                <input style={TR_INPUT} value={form.piva || ''}
                  onChange={e => setForm({ ...form, piva: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                <label style={TR_LABEL}>Codice fiscale</label>
                <input style={TR_INPUT} value={form.codice_fiscale || ''}
                  onChange={e => setForm({ ...form, codice_fiscale: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 240px', minWidth: 200 }}>
                <label style={TR_LABEL}>Indirizzo</label>
                <input style={TR_INPUT} value={form.indirizzo || ''}
                  onChange={e => setForm({ ...form, indirizzo: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Città</label>
                <input style={TR_INPUT} value={form.citta || ''}
                  onChange={e => setForm({ ...form, citta: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Telefono</label>
                <input style={TR_INPUT} value={form.telefono || ''}
                  onChange={e => setForm({ ...form, telefono: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email</label>
                <input style={TR_INPUT} type="email" value={form.email || ''}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
            </div>
          </SezioneForm>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Referente principale" sotto="Persona di riferimento per ordini e comunicazioni" color="blu" icon="user">
            <label style={TR_LABEL}>Nome referente</label>
            <input style={TR_INPUT} value={form.referente || ''}
              onChange={e => setForm({ ...form, referente: e.target.value })}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Telefono</label>
                <input style={TR_INPUT} value={form.tel_referente || ''}
                  onChange={e => setForm({ ...form, tel_referente: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email</label>
                <input style={TR_INPUT} type="email" value={form.email_referente || ''}
                  onChange={e => setForm({ ...form, email_referente: e.target.value })}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur} />
              </div>
            </div>
          </SezioneForm>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Contatti per ritiro / richiamo prodotti" sotto="Usati in caso di richiamo urgente" color="arancio" icon="phone">
            <label style={TR_LABEL}>Referente richiamo</label>
            <input style={TR_INPUT} value={form.referente_richiamo || ''}
              onChange={e => setForm({ ...form, referente_richiamo: e.target.value })}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <label style={TR_LABEL}>Tel. richiamo (urgente)</label>
                <input style={TR_INPUT} value={form.tel_richiamo || ''}
                  onChange={e => setForm({ ...form, tel_richiamo: e.target.value })}
                  onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                <label style={TR_LABEL}>Email richiamo</label>
                <input style={TR_INPUT} type="email" value={form.email_richiamo || ''}
                  onChange={e => setForm({ ...form, email_richiamo: e.target.value })}
                  onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
              </div>
            </div>
          </SezioneForm>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Note" sotto="Annotazioni libere sul cliente" color="ciano" icon="edit">
            <textarea style={{ ...TR_INPUT, height: 80, resize: 'vertical' }}
              value={form.note || ''}
              onChange={e => setForm({ ...form, note: e.target.value })}
              onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur} />
          </SezioneForm>
        </Card>

        {form.id && (
          <Card style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: LT.verde, cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>Cliente attivo</div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Se disattivato non compare nelle nuove bolle di uscita
                </div>
              </div>
            </label>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" icon="check" block disabled={salvando} onClick={handleSalva}>
              {salvando ? 'Salvataggio…' : (form.id ? 'Salva modifiche' : 'Crea cliente')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Lista clienti ────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Menu</Btn>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: LT.violaSoft, color: LT.viola,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.users} size={18} stroke={LT.viola} />
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>Clienti</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {lista.length} {lista.length === 1 ? 'cliente' : 'clienti'} in anagrafica
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuovo cliente</Btn>
        </div>

        <Card style={{ marginBottom: 14, padding: 12 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
              <Ico d={I.search} size={16} stroke={LT.n400} />
            </div>
            <input style={{ ...TR_INPUT, paddingLeft: 36 }}
              placeholder="Cerca cliente per ragione sociale…"
              value={ricerca} onChange={e => setRicerca(e.target.value)}
              onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
          </div>
        </Card>

        {filtrati.length === 0 && (
          <Card>
            <div style={{ padding: '20px 4px', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: LT.r.lg,
                background: LT.n100, color: LT.n500, margin: '0 auto 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.users} size={24} stroke={LT.n500} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
                {ricerca ? 'Nessun cliente corrisponde alla ricerca' : 'Nessun cliente in anagrafica'}
              </div>
              <div style={{ fontSize: 12, color: LT.n500 }}>
                {ricerca ? 'Prova con un altro nome' : 'Aggiungi il primo cliente con il bottone in alto'}
              </div>
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrati.map(c => (
            <Card key={c.id} style={{ padding: 0, opacity: c.attivo ? 1 : 0.65 }}>
              <div style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>
                    {c.ragione_sociale}
                  </div>
                  {c.piva && (
                    <div style={{ fontSize: 11, color: LT.n500, fontFamily: LT.mono }}>
                      P.IVA {c.piva}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: LT.n500, marginTop: 6 }}>
                  {c.citta && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Ico d={I.pin} size={13} stroke={LT.n500} />{c.citta}
                    </span>
                  )}
                  {c.telefono && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Ico d={I.phone} size={13} stroke={LT.n500} />{c.telefono}
                    </span>
                  )}
                  {c.email && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Ico d={I.mail} size={13} stroke={LT.n500} />{c.email}
                    </span>
                  )}
                </div>

                {c.referente && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: LT.bluSoft, color: LT.blu,
                    padding: '4px 10px', borderRadius: LT.r.full,
                    fontSize: 11, fontWeight: 700, marginTop: 10,
                  }}>
                    <Ico d={I.user} size={12} stroke={LT.blu} />
                    {c.referente}{c.tel_referente ? ` · ${c.tel_referente}` : ''}
                  </div>
                )}

                {c.tel_richiamo && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: LT.rossoSoft, color: LT.rosso,
                    padding: '4px 10px', borderRadius: LT.r.full,
                    fontSize: 11, fontWeight: 700, marginTop: 8, marginLeft: c.referente ? 6 : 0,
                  }}>
                    <Ico d={I.alert} size={12} stroke={LT.rosso} />
                    Richiamo: {c.referente_richiamo || ''} {c.tel_richiamo}
                  </div>
                )}

                {!c.attivo && (
                  <div style={{ marginTop: 8 }}>
                    <Chip kind="rosso" icon="x">Disattivo</Chip>
                  </div>
                )}
              </div>

              <div style={{ borderTop: `1px solid ${LT.n150}`, padding: '8px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                <Btn kind="ghost" size="sm" icon="edit" onClick={() => apriForm(c)}>Modifica</Btn>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Gestione Ubicazioni ──────────────────────────────────────
function GestioneUbicazioni({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null);
  const [ricerca,  setRicerca]  = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/ubicazioni?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const apriForm = (item = {}) => { setForm({ attivo: true, ...item }); setErrore(''); };

  const handleSalva = async () => {
    if (!form.nome) { setErrore('Nome obbligatorio'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/ubicazioni/${form.id}` : `${API}/tracciabilita/ubicazioni`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:   idAzienda,
          nome:        form.nome,
          descrizione: form.descrizione || null,
          attivo:      form.attivo !== false,
        }),
      });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Ubicazione salvata!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(u =>
    !ricerca || u.nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
      </div>
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2, marginBottom: 4 }}>
            {form.id ? 'Modifica ubicazione' : 'Nuova ubicazione'}
          </div>
          <div style={{ fontSize: 13, color: LT.n500 }}>
            Area fisica dove vengono eseguite le fasi di lavorazione
          </div>
        </div>

        {errore && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: LT.rossoSoft, border: `1px solid ${LT.rosso}22`,
            borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 14,
            fontSize: 13, color: LT.rosso, fontWeight: 600,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.rosso} />{errore}
          </div>
        )}

        <Card style={{ marginBottom: 14 }}>
          <SezioneForm titolo="Dati ubicazione" color="arancio" icon="pin">
            <label style={TR_LABEL}>Nome *</label>
            <input style={TR_INPUT} value={form.nome || ''}
              onChange={e => setForm({ ...form, nome: e.target.value })}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />

            <label style={TR_LABEL}>Descrizione</label>
            <input style={TR_INPUT} value={form.descrizione || ''}
              onChange={e => setForm({ ...form, descrizione: e.target.value })}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
          </SezioneForm>
        </Card>

        {form.id && (
          <Card style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: LT.verde, cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>Ubicazione attiva</div>
                <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                  Se disattivata non è selezionabile nelle nuove lavorazioni
                </div>
              </div>
            </label>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn kind="outline" icon="x" onClick={() => { setForm(null); setErrore(''); }}>Annulla</Btn>
          <div style={{ flex: 1 }}>
            <Btn kind="primary" icon="check" block disabled={salvando} onClick={handleSalva}>
              {salvando ? 'Salvataggio…' : (form.id ? 'Salva modifiche' : 'Crea ubicazione')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Lista ubicazioni ─────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: LT.n0, borderBottom: `1px solid ${LT.n150}`,
        padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Menu</Btn>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: LT.arancioSoft, color: LT.arancio,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.pin} size={18} stroke={LT.arancio} />
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>Ubicazioni</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {lista.length} {lista.length === 1 ? 'ubicazione' : 'ubicazioni'} · aree fisiche di lavorazione
            </div>
          </div>
          <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuova ubicazione</Btn>
        </div>

        <Card style={{ marginBottom: 14, padding: 12 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
              <Ico d={I.search} size={16} stroke={LT.n400} />
            </div>
            <input style={{ ...TR_INPUT, paddingLeft: 36 }}
              placeholder="Cerca ubicazione per nome…"
              value={ricerca} onChange={e => setRicerca(e.target.value)}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur} />
          </div>
        </Card>

        {filtrati.length === 0 && (
          <Card>
            <div style={{ padding: '20px 4px', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: LT.r.lg,
                background: LT.n100, color: LT.n500, margin: '0 auto 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.pin} size={24} stroke={LT.n500} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
                {ricerca ? 'Nessuna ubicazione corrisponde alla ricerca' : 'Nessuna ubicazione in anagrafica'}
              </div>
              <div style={{ fontSize: 12, color: LT.n500 }}>
                {ricerca ? 'Prova con un altro nome' : 'Aggiungi la prima ubicazione con il bottone in alto'}
              </div>
            </div>
          </Card>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
          {filtrati.map(u => (
            <Card key={u.id} style={{ padding: 0, opacity: u.attivo ? 1 : 0.6 }}>
              <div style={{ padding: '14px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.md,
                    background: LT.arancioSoft, color: LT.arancio, flex: '0 0 auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Ico d={I.pin} size={18} stroke={LT.arancio} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>{u.nome}</div>
                    {u.descrizione && (
                      <div style={{ fontSize: 12, color: LT.n500, marginTop: 3, lineHeight: 1.35 }}>{u.descrizione}</div>
                    )}
                  </div>
                </div>
                {!u.attivo && <div style={{ marginTop: 4 }}><Chip kind="rosso" icon="x">Disattiva</Chip></div>}
              </div>
              <div style={{ borderTop: `1px solid ${LT.n150}`, padding: '8px 10px', display: 'flex', justifyContent: 'flex-end' }}>
                <Btn kind="ghost" size="sm" icon="edit" onClick={() => apriForm(u)}>Modifica</Btn>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Giacenze Ubicazioni ─────────────────────────────────────
function GiacenzeUbicazioni({ operatore, onBack }) {
  const [giacenze,       setGiacenze]       = useState([]);
  const [ubicazioni,     setUbicazioni]     = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [filtroProdotto, setFiltroProdotto] = useState('');
  const [filtroUbicaz,   setFiltroUbicaz]   = useState('');
  const [filtroPropr,    setFiltroPropr]    = useState('');
  const [modalSposta,    setModalSposta]    = useState(null);
  const [datiSposta,     setDatiSposta]     = useState({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: '' });
  const [spostando,      setSpostando]      = useState(false);

  const caricaGiacenze = () => {
    if (!operatore?.id_azienda) return Promise.resolve();
    setLoading(true);
    return apiFetch(`${API}/tracciabilita/ubicazioni/giacenze?idAzienda=${operatore.id_azienda}`)
      .then(r => r.json())
      .then(r => setGiacenze(Array.isArray(r) ? r : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!operatore?.id_azienda) return;
    caricaGiacenze();
    apiFetch(`${API}/tracciabilita/ubicazioni?idAzienda=${operatore.id_azienda}`)
      .then(r => r.json())
      .then(r => setUbicazioni(Array.isArray(r) ? r.filter(u => u.attivo !== false) : []))
      .catch(() => {});
  }, [operatore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ora locale nel formato di un campo datetime-local.
  const oraLocaleInput = () => {
    const now = new Date();
    return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  // La data che arriva dal backend ha lo spazio fra data e ora: Safari su quel
  // formato restituisce Invalid Date, quindi serve la T.
  const perInputDataOra = (v) => (v ? String(v).replace(' ', 'T').slice(0, 16) : undefined);

  const apriSposta = (g) => {
    setModalSposta(g);
    setDatiSposta({
      idUbicazioneA: '',
      quantitaPz: parseFloat(g.pz_attuali)   > 0 ? String(Math.round(parseFloat(g.pz_attuali)   * 1000) / 1000) : '',
      pesoKg:     parseFloat(g.peso_attuale) > 0 ? String(Math.round(parseFloat(g.peso_attuale) * 1000) / 1000) : '',
      dataMovimento: oraLocaleInput(),
    });
  };

  const chiudiSposta = () => {
    setModalSposta(null);
    setDatiSposta({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: '' });
  };

  const confermaSposta = async () => {
    if (!modalSposta) return;
    if (!datiSposta.idUbicazioneA) { window.alert('Scegli l\'ubicazione di destinazione.'); return; }
    const pz = parseFloat(datiSposta.quantitaPz) || 0;
    const kg = parseFloat(datiSposta.pesoKg)     || 0;
    if (pz <= 0 && kg <= 0) { window.alert('Indica quanto stai spostando: pezzi, chili, o entrambi.'); return; }
    setSpostando(true);
    try {
      const res = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${modalSposta.id_fase}/sposta`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idUbicazioneDa: modalSposta.id_ubicazione,
          idUbicazioneA:  parseInt(datiSposta.idUbicazioneA),
          quantitaPz:     pz > 0 ? pz : null,
          pesoKg:         kg > 0 ? kg : null,
          dataMovimento:  datiSposta.dataMovimento || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Il modal resta aperto: l'errore del server dice esattamente cosa
        // correggere (giacenza insufficiente, data troppo indietro).
        toast(body.errore || 'Errore nello spostamento', 'errore');
        return;
      }
      chiudiSposta();
      await caricaGiacenze();
      toast('Spostamento registrato');
    } finally {
      setSpostando(false);
    }
  };

  const giacenzeFiltrate = giacenze.filter(g => {
    const nomeProdotto = `${g.prodotto || ''}${g.ricetta ? ` (${g.ricetta})` : ''}`.toLowerCase();
    const nomeUbicaz   = (g.ubicazione || '').toLowerCase();
    if (filtroProdotto && !nomeProdotto.includes(filtroProdotto.toLowerCase())) return false;
    if (filtroUbicaz   && !nomeUbicaz.includes(filtroUbicaz.toLowerCase()))   return false;
    if (filtroPropr === 'TERZI'   && !g.id_committente) return false;
    if (filtroPropr === 'PROPRIA' &&  g.id_committente) return false;
    return true;
  });

  // Il filtro si basa sull'elenco completo, non su quello gia' filtrato:
  // altrimenti scegliendo "solo merce mia" il filtro sparirebbe da solo.
  const haContoTerzi = giacenze.some(g => g.id_committente);

  // Totali aggregati
  const totPz   = giacenzeFiltrate.reduce((s, g) => s + (parseFloat(g.pz_attuali)   || 0), 0);
  const totPeso = giacenzeFiltrate.reduce((s, g) => s + (parseFloat(g.peso_attuale) || 0), 0);
  const nUbicaz = new Set(giacenzeFiltrate.map(g => g.ubicazione)).size;

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      {/* Top bar sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      {/* Hero compatto */}
      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.cianoSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Ico d={I.package} size={28} stroke={LT.ciano} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Produzione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Giacenze per ubicazione
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Dove si trova adesso il prodotto, in lavorazione e finito. Da qui lo puoi spostare.
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {/* KPI cards */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10, marginBottom: 14,
        }}>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.cianoSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.pin} size={18} stroke={LT.ciano} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n500, fontWeight: 600 }}>Ubicazioni</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy }}>{nUbicaz}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.bluSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.package} size={18} stroke={LT.blu} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n500, fontWeight: 600 }}>Righe</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy }}>{giacenzeFiltrate.length}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.verdeSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.layers} size={18} stroke={LT.verde} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n500, fontWeight: 600 }}>Pezzi totali</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy }}>{totPz > 0 ? totPz.toFixed(0) : '—'}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.arancioSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.chart} size={18} stroke={LT.arancio} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n500, fontWeight: 600 }}>Peso totale</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy }}>
                  {totPeso > 0 ? `${totPeso.toFixed(2)} kg` : '—'}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Ico d={I.filter} size={16} stroke={LT.n500} />
            <span style={{ fontSize: 12, fontWeight: 700, color: LT.n700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Filtri
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <div>
              <label style={TR_LABEL}>Prodotto</label>
              <input
                style={TR_INPUT}
                placeholder="Filtra per prodotto..."
                value={filtroProdotto}
                onChange={e => setFiltroProdotto(e.target.value)}
                onFocus={e => trFocus(e, LT.ciano)}
                onBlur={trBlur}
              />
            </div>
            <div>
              <label style={TR_LABEL}>Collocazione</label>
              <input
                style={TR_INPUT}
                placeholder="Filtra per ubicazione..."
                value={filtroUbicaz}
                onChange={e => setFiltroUbicaz(e.target.value)}
                onFocus={e => trFocus(e, LT.ciano)}
                onBlur={trBlur}
              />
            </div>
            {haContoTerzi && (
              <FiltroProprieta valore={filtroPropr} onChange={setFiltroPropr} colore={LT.ciano} />
            )}
          </div>
        </Card>

        {/* Loading / Empty / Tabella */}
        {loading && (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{ color: LT.n500, fontSize: 14 }}>Caricamento giacenze...</div>
          </Card>
        )}

        {!loading && giacenzeFiltrate.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              Nessuna giacenza attiva
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {filtroProdotto || filtroUbicaz
                ? 'Prova a rimuovere o modificare i filtri'
                : 'Non ci sono ancora giacenze da visualizzare'}
            </div>
          </Card>
        )}

        {!loading && giacenzeFiltrate.length > 0 && (
          <Card padding="none">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: LT.n50 }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Ubicazione
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Prodotto
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Lotto
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Fase
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Pz
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Peso (kg)
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700,
                      color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}` }}>
                      Sposta
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {giacenzeFiltrate.map((g, i) => (
                    <tr key={i} style={{
                      borderBottom: i < giacenzeFiltrate.length - 1 ? `1px solid ${LT.n100}` : 'none',
                    }}>
                      <td style={{ padding: '10px 12px', color: LT.navy, fontWeight: 600 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Ico d={I.pin} size={14} stroke={LT.arancio} />
                          {g.ubicazione}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: LT.navy }}>
                        {g.prodotto}
                        {g.ricetta && (
                          <span style={{ color: LT.n500, fontSize: 12 }}> ({g.ricetta})</span>
                        )}
                        {g.committente_nome && (
                          <div style={{ marginTop: 4 }}>
                            <ChipContoTerzi committente={g.committente_nome} />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: LT.mono, fontSize: 12, color: LT.n700 }}>
                        {g.lotto_prodotto}
                        {/* Il lotto e' derivato dalla data: due lavorazioni avviate lo
                            stesso giorno lo condividono. Senza il numero della
                            lavorazione le righe sono indistinguibili. */}
                        {g.id_lavorazione && (
                          <div style={{ fontFamily: LT.sans, fontSize: 11, color: LT.n500, marginTop: 2 }}>
                            lav. #{g.id_lavorazione}
                            {g.data_lavorazione ? ` del ${g.data_lavorazione}` : ''}
                            {g.stato_lavorazione === 'CHIUSA' ? ' · chiusa' : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {g.fase_corrente && (
                          <Chip kind="verde" size="sm">{g.fase_corrente}</Chip>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: LT.navy, fontWeight: 600 }}>
                        {parseFloat(g.pz_attuali) > 0 ? Number(g.pz_attuali).toFixed(0) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: LT.navy, fontWeight: 600 }}>
                        {parseFloat(g.peso_attuale) > 0 ? Number(g.peso_attuale).toFixed(2) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <button
                          onClick={() => apriSposta(g)}
                          title="Sposta questo prodotto in un'altra ubicazione"
                          style={{
                            padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            color: LT.blu, background: LT.n0,
                            border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
                          }}>
                          Sposta
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Spostamento da un'ubicazione all'altra. Usa lo stesso endpoint dello
          Sposta interno alla lavorazione, quindi valgono gli stessi controlli:
          non si sposta piu' di quello che c'e' e non si datano movimenti
          precedenti all'inizio della fase. */}
      {modalSposta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ background: LT.n0, borderRadius: LT.r.lg, padding: 20, width: 430, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 800, color: LT.navy }}>
              Sposta prodotto
            </h3>

            <div style={{ fontSize: 13, color: LT.n700, background: LT.n50, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, padding: '9px 11px', marginBottom: 14, lineHeight: 1.5 }}>
              <div><strong>{modalSposta.prodotto}</strong>{modalSposta.ricetta ? ` (${modalSposta.ricetta})` : ''}</div>
              <div style={{ color: LT.n500 }}>
                lotto {modalSposta.lotto_prodotto} · lav. #{modalSposta.id_lavorazione}
                {modalSposta.fase_corrente ? ` · ${modalSposta.fase_corrente}` : ''}
              </div>
              <div style={{ marginTop: 4 }}>
                Da <strong>{modalSposta.ubicazione}</strong> · disponibili{' '}
                {parseFloat(modalSposta.pz_attuali)   > 0 ? `${Number(modalSposta.pz_attuali).toFixed(0)} pz` : ''}
                {parseFloat(modalSposta.pz_attuali)   > 0 && parseFloat(modalSposta.peso_attuale) > 0 ? ' / ' : ''}
                {parseFloat(modalSposta.peso_attuale) > 0 ? `${Number(modalSposta.peso_attuale).toFixed(2)} kg` : ''}
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={TR_LABEL}>A quale ubicazione</label>
              <select
                value={datiSposta.idUbicazioneA}
                onChange={e => setDatiSposta(p => ({ ...p, idUbicazioneA: e.target.value }))}
                style={TR_INPUT}>
                <option value="">Scegli...</option>
                {ubicazioni
                  .filter(u => String(u.id) !== String(modalSposta.id_ubicazione))
                  .map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={TR_LABEL}>Pezzi</label>
                <input
                  type="text" inputMode="decimal"
                  value={datiSposta.quantitaPz}
                  onChange={e => setDatiSposta(p => ({ ...p, quantitaPz: decVal(e.target.value) }))}
                  onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur}
                  style={TR_INPUT} />
              </div>
              <div>
                <label style={TR_LABEL}>Peso (kg)</label>
                <input
                  type="text" inputMode="decimal"
                  value={datiSposta.pesoKg}
                  onChange={e => setDatiSposta(p => ({ ...p, pesoKg: decVal(e.target.value) }))}
                  onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur}
                  style={TR_INPUT} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: LT.n500, marginTop: -4, marginBottom: 10 }}>
              Precompilati con tutto quello che c'è nell'ubicazione. Abbassali se stai
              spostando solo una parte.
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={TR_LABEL}>Data e ora dello spostamento</label>
              <input
                type="datetime-local"
                value={datiSposta.dataMovimento}
                min={perInputDataOra(modalSposta.data_inizio_fase)}
                onChange={e => setDatiSposta(p => ({ ...p, dataMovimento: e.target.value }))}
                onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur}
                style={TR_INPUT} />
              <div style={{ fontSize: 11, color: LT.n500, marginTop: 3 }}>
                Precompilata con l'ora attuale. Si può correggere, ma non prima
                dell'inizio della fase.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn kind="ghost" onClick={chiudiSposta} disabled={spostando}>Annulla</Btn>
              <Btn kind="primary" onClick={confermaSposta} disabled={spostando}>
                {spostando ? 'Registro...' : 'Conferma spostamento'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Gestione Ingredienti ─────────────────────────────────────
function GestioneIngredienti({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,       setLista]       = useState([]);
  const [fornitori,   setFornitori]   = useState([]);
  const [form,        setForm]        = useState(null);
  const [ricerca,     setRicerca]     = useState('');
  const [salvando,    setSalvando]    = useState(false);
  const [errore,      setErrore]      = useState('');
  const [nutriSearch,  setNutriSearch]  = useState('');
  const [nutriResults, setNutriResults] = useState(null); // null=pannello chiuso
  const [nutriLoading, setNutriLoading] = useState(false);

  useEffect(() => {
    carica();
    apiFetch(`${API}/tracciabilita/fornitori?idAzienda=${idAzienda}`)
      .then(r => r.json()).then(d => setFornitori(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/ingredienti?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const allergeniBoolInit = () => Object.fromEntries(ALLERGENI.map(a => [a.key, false]));

  const apriForm = (item = {}) => {
    const allergeni = item.id ? Object.fromEntries(ALLERGENI.map(a => [a.key, !!item[a.key]])) : allergeniBoolInit();
    setForm({ attivo: true, ...item, ...allergeni });
    setErrore('');
  };

  const apriRicercaNutrizione = () => {
    setNutriSearch(form.nome || '');
    setNutriResults([]);
  };

  const eseguiRicercaNutrizione = async (q) => {
    if (!q.trim()) return;
    setNutriLoading(true);
    try {
      const res = await apiFetch(`${API}/nutrition/search?q=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setNutriResults(data.results || []);
    } catch (e) {
      toast('Errore ricerca online', 'errore');
      setNutriResults([]);
    }
    setNutriLoading(false);
  };

  const applicaRisultatoNutrizione = (r) => {
    setForm(prev => ({
      ...prev,
      kcal_100g: r.kcal_100g, grassi_100g: r.grassi_100g, grassi_saturi_100g: r.grassi_saturi_100g,
      carboidrati_100g: r.carboidrati_100g, zuccheri_100g: r.zuccheri_100g, proteine_100g: r.proteine_100g,
      sale_100g: r.sale_100g, fibre_100g: r.fibre_100g
    }));
    setNutriResults(null);
    setNutriSearch('');
  };

  const handleSalva = async () => {
    if (!form.nome) { setErrore('Nome obbligatorio'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/ingredienti/${form.id}` : `${API}/tracciabilita/ingredienti`;
      const method = form.id ? 'PUT' : 'POST';
      const body   = {
        idAzienda:              idAzienda,
        nome:                   form.nome,
        categoria:              form.categoria              || null,
        idFornitorePrincipale:  form.id_fornitore_principale || null,
        unitaMisura:            form.unita_misura           || null,
        note:                   form.note                   || null,
        attivo:                 form.attivo !== false,
        kcal_100g:              form.kcal_100g || 0,
        grassi_100g:            form.grassi_100g || 0,
        grassi_saturi_100g:     form.grassi_saturi_100g || 0,
        carboidrati_100g:       form.carboidrati_100g || 0,
        zuccheri_100g:          form.zuccheri_100g || 0,
        proteine_100g:          form.proteine_100g || 0,
        sale_100g:              form.sale_100g || 0,
        fibre_100g:             form.fibre_100g || 0,
        lista_sotto_ingredienti:form.lista_sotto_ingredienti || null,
      };
      ALLERGENI.forEach(a => { body[a.key] = !!form[a.key]; });
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Ingrediente salvato!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(i =>
    !ricerca || i.nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  const _allergenoPresente = item => ALLERGENI.some(a => item[a.key]);

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      {/* Top bar sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      {/* Hero compatto */}
      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.arancioSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Ico d={I.package} size={28} stroke={LT.arancio} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              {form.id ? 'Modifica ingrediente' : 'Nuovo ingrediente'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {form.id ? form.nome || 'Anagrafica ingrediente' : 'Compila i campi per registrare un nuovo ingrediente o materia prima'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Dati anagrafici */}
        <SezioneForm color="arancio" icon="package" titolo="Dati anagrafici" sotto="Nome, categoria, fornitore, unità di misura">
          <label style={TR_LABEL}>Nome *</label>
          <input
            style={TR_INPUT}
            value={form.nome || ''}
            onChange={e => setForm({ ...form, nome: e.target.value })}
            onFocus={e => trFocus(e, LT.arancio)}
            onBlur={trBlur}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label style={TR_LABEL}>Categoria</label>
              <select
                style={TR_INPUT}
                value={form.categoria || ''}
                onChange={e => setForm({ ...form, categoria: e.target.value })}
                onFocus={e => trFocus(e, LT.arancio)}
                onBlur={trBlur}
              >
                <option value="">— seleziona —</option>
                {CAT_INGREDIENTE.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={TR_LABEL}>Unità di misura</label>
              <select
                style={TR_INPUT}
                value={form.unita_misura || ''}
                onChange={e => setForm({ ...form, unita_misura: e.target.value })}
                onFocus={e => trFocus(e, LT.arancio)}
                onBlur={trBlur}
              >
                <option value="">— seleziona —</option>
                {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <label style={TR_LABEL}>Fornitore principale</label>
          <select
            style={TR_INPUT}
            value={form.id_fornitore_principale || ''}
            onChange={e => setForm({ ...form, id_fornitore_principale: e.target.value || null })}
            onFocus={e => trFocus(e, LT.arancio)}
            onBlur={trBlur}
          >
            <option value="">— nessuno —</option>
            {fornitori.filter(f => f.attivo).map(f => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
          </select>

          <label style={TR_LABEL}>Note</label>
          <textarea
            style={{ ...TR_INPUT, resize: 'vertical', minHeight: 60 }}
            rows={2}
            value={form.note || ''}
            onChange={e => setForm({ ...form, note: e.target.value })}
            onFocus={e => trFocus(e, LT.arancio)}
            onBlur={trBlur}
          />
        </SezioneForm>

        {/* Valori Nutrizionali */}
        <SezioneForm color="verde" icon="flask" titolo="Valori nutrizionali" sotto="Etichettatura per 100 g di prodotto · Reg. UE 1169/2011">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <Btn kind="secondary" size="sm" icon="search" onClick={apriRicercaNutrizione}>
              Cerca in DB/Online
            </Btn>
          </div>

          {/* Pannello ricerca inline */}
          {nutriResults !== null && (
            <div style={{
              marginBottom: 12, background: LT.cianoSoft,
              border: `1px solid ${LT.ciano}40`, borderRadius: LT.r.md, padding: 12,
            }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input
                  value={nutriSearch}
                  onChange={e => setNutriSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && eseguiRicercaNutrizione(nutriSearch)}
                  placeholder="Nome prodotto o codice EAN (es. 8000500310427)…"
                  style={{ ...TR_INPUT, flex: 1 }}
                  onFocus={e => trFocus(e, LT.ciano)}
                  onBlur={trBlur}
                  autoFocus
                />
                <Btn kind="primary" size="sm" onClick={() => eseguiRicercaNutrizione(nutriSearch)}>Cerca</Btn>
                <Btn kind="ghost" size="sm" icon="x" onClick={() => { setNutriResults(null); setNutriSearch(''); }} />
              </div>
              {nutriLoading && <div style={{ fontSize: 12, color: LT.n500, margin: 0 }}>Ricerca in corso…</div>}
              {!nutriLoading && nutriResults.length === 0 && nutriSearch.trim() && (
                <div style={{ fontSize: 12, color: LT.n500, margin: 0 }}>Nessun risultato. Prova un termine diverso o un codice EAN.</div>
              )}
              {!nutriLoading && nutriResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => applicaRisultatoNutrizione(r)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    marginBottom: 4,
                    background: LT.n0, border: `1px solid ${LT.n200}`,
                    transition: 'background .12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = LT.cianoSoft}
                  onMouseLeave={e => e.currentTarget.style.background = LT.n0}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: LT.navy }}>{r.nome}</div>
                    <div style={{ color: LT.n500, fontSize: 11 }}>{r.source}</div>
                  </div>
                  <Chip kind="ciano" size="sm">{r.kcal_100g} kcal</Chip>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {['kcal_100g', 'grassi_100g', 'grassi_saturi_100g', 'carboidrati_100g', 'zuccheri_100g', 'proteine_100g', 'sale_100g', 'fibre_100g'].map(nf => (
              <div key={nf}>
                <label style={{ ...TR_LABEL, fontSize: 11, marginTop: 4 }}>{nf.replace('_100g', '').toUpperCase()}</label>
                <input
                  type="text" inputMode="decimal"
                  style={TR_INPUT}
                  value={form[nf] === undefined ? '' : form[nf]}
                  onChange={e => setForm({ ...form, [nf]: parseFloat(decVal(e.target.value)) || 0 })}
                  onFocus={e => trFocus(e, LT.verde)}
                  onBlur={trBlur}
                />
              </div>
            ))}
          </div>
        </SezioneForm>

        {/* Sotto-ingredienti */}
        <SezioneForm color="viola" icon="layers" titolo="Sotto-ingredienti" sotto="Per miscele o semilavorati acquistati">
          <textarea
            style={{ ...TR_INPUT, resize: 'vertical', minHeight: 60 }}
            rows={2}
            placeholder="es. farina, acqua, lievito, sale..."
            value={form.lista_sotto_ingredienti || ''}
            onChange={e => setForm({ ...form, lista_sotto_ingredienti: e.target.value })}
            onFocus={e => trFocus(e, LT.viola)}
            onBlur={trBlur}
          />
        </SezioneForm>

        {/* Allergeni */}
        <SezioneForm color="rosso" icon="alert" titolo="Allergeni (Reg. UE 1169/2011)" sotto="Seleziona tutti gli allergeni presenti nell'ingrediente">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6 }}>
            {ALLERGENI.map(a => (
              <label key={a.key} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                background: form[a.key] ? LT.rossoSoft : LT.n0,
                border: `1.5px solid ${form[a.key] ? LT.rosso + '60' : LT.n200}`,
                borderRadius: LT.r.sm, fontSize: 13, color: form[a.key] ? LT.rosso : LT.navy,
                fontWeight: form[a.key] ? 600 : 400,
                cursor: 'pointer', userSelect: 'none',
                transition: 'background .12s, border-color .12s, color .12s',
              }}>
                <input
                  type="checkbox"
                  checked={!!form[a.key]}
                  onChange={e => setForm({ ...form, [a.key]: e.target.checked })}
                  style={{ accentColor: LT.rosso, width: 15, height: 15, flexShrink: 0 }}
                />
                {a.label}
              </label>
            ))}
          </div>
        </SezioneForm>

        {/* Attivo */}
        {form.id && (
          <Card padding="md">
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              cursor: 'pointer', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ accentColor: LT.verde, width: 16, height: 16 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: LT.navy }}>Ingrediente attivo</div>
                <div style={{ fontSize: 12, color: LT.n500 }}>Gli ingredienti disattivati non compaiono nelle nuove ricette</div>
              </div>
            </label>
          </Card>
        )}

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />
              {errore}
            </div>
          </Card>
        )}

        {/* CTA */}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>
            Annulla
          </Btn>
          <Btn
            kind="primary"
            icon="check"
            onClick={handleSalva}
            disabled={salvando}
            style={{ flex: 2 }}
          >
            {salvando ? 'Salvataggio…' : 'Salva ingrediente'}
          </Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      {/* Top bar sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuovo ingrediente</Btn>
      </div>

      {/* Hero compatto */}
      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.arancioSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Ico d={I.package} size={28} stroke={LT.arancio} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Ingredienti e materie prime
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} ingredient{lista.length === 1 ? 'e' : 'i'} in anagrafica
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Ricerca */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Ico d={I.search} size={18} stroke={LT.n500} />
            <input
              style={{ ...TR_INPUT, border: 'none', padding: 0, fontSize: 14 }}
              placeholder="Cerca ingrediente per nome…"
              value={ricerca}
              onChange={e => setRicerca(e.target.value)}
            />
          </div>
        </Card>

        {/* Lista */}
        {filtrati.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              {ricerca ? 'Nessun ingrediente trovato' : 'Nessun ingrediente in anagrafica'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {ricerca ? 'Prova un termine di ricerca diverso' : 'Clicca "Nuovo ingrediente" per iniziare'}
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10,
          }}>
            {filtrati.map(i => {
              const allergeniList = ALLERGENI.filter(a => i[a.key]).map(a => a.label);
              return (
                <Card
                  key={i.id}
                  padding="md"
                  style={{
                    opacity: i.attivo ? 1 : 0.55,
                    cursor: 'pointer',
                    transition: 'transform .12s, box-shadow .12s',
                  }}
                  onClick={() => apriForm(i)}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = LT.shadow.md; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: LT.r.md,
                      background: LT.arancioSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I.package} size={18} stroke={LT.arancio} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy, lineHeight: 1.3 }}>{i.nome}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {i.categoria && <Chip kind="arancio" size="sm">{i.categoria.replace(/_/g, ' ')}</Chip>}
                        {i.unita_misura && <Chip kind="neutro" size="sm">{i.unita_misura}</Chip>}
                        {!i.attivo && <Chip kind="rosso" size="sm" icon="x">Disattivo</Chip>}
                      </div>
                      {i.fornitore_nome && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 12, color: LT.n500 }}>
                          <Ico d={I.factory} size={12} stroke={LT.n500} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {i.fornitore_nome}
                          </span>
                        </div>
                      )}
                      {allergeniList.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: LT.rosso, fontWeight: 600 }}>
                          <Ico d={I.alert} size={12} stroke={LT.rosso} />
                          <span>{allergeniList.join(', ')}</span>
                        </div>
                      )}
                    </div>
                    <Ico d={I.chevronR} size={16} stroke={LT.n400} />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gestione Prodotti ────────────────────────────────────────
function GestioneProdotti({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null);
  const [ricerca,  setRicerca]  = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/prodotti?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const apriForm = (item = {}) => { setForm({ attivo: true, ...item }); setErrore(''); };

  const handleSalva = async () => {
    if (!form.nome) { setErrore('Nome obbligatorio'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/prodotti/${form.id}` : `${API}/tracciabilita/prodotti`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:   idAzienda,
          nome:        form.nome,
          codice:      form.codice       || null,
          categoria:   form.categoria    || null,
          unitaMisura: form.unita_misura || null,
          note:        form.note         || null,
          attivo:      form.attivo !== false,
        }),
      });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Prodotto salvato!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(p =>
    !ricerca || p.nome?.toLowerCase().includes(ricerca.toLowerCase()) || p.codice?.toLowerCase().includes(ricerca.toLowerCase())
  );

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      {/* Top bar sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      {/* Hero compatto */}
      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.violaSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Ico d={I.pkg} size={28} stroke={LT.viola} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              {form.id ? 'Modifica prodotto' : 'Nuovo prodotto'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {form.id ? form.nome || 'Anagrafica prodotto' : 'Compila i campi per registrare un nuovo prodotto o semilavorato'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Dati anagrafici */}
        <SezioneForm color="viola" icon="pkg" titolo="Dati anagrafici" sotto="Nome, codice, categoria e unità di misura">
          <label style={TR_LABEL}>Nome *</label>
          <input
            style={TR_INPUT}
            value={form.nome || ''}
            onChange={e => setForm({ ...form, nome: e.target.value })}
            onFocus={e => trFocus(e, LT.viola)}
            onBlur={trBlur}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label style={TR_LABEL}>Codice</label>
              <input
                style={TR_INPUT}
                value={form.codice || ''}
                onChange={e => setForm({ ...form, codice: e.target.value })}
                onFocus={e => trFocus(e, LT.viola)}
                onBlur={trBlur}
              />
            </div>
            <div>
              <label style={TR_LABEL}>Unità di misura</label>
              <select
                style={TR_INPUT}
                value={form.unita_misura || ''}
                onChange={e => setForm({ ...form, unita_misura: e.target.value })}
                onFocus={e => trFocus(e, LT.viola)}
                onBlur={trBlur}
              >
                <option value="">— seleziona —</option>
                {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <label style={TR_LABEL}>Categoria</label>
          <select
            style={TR_INPUT}
            value={form.categoria || ''}
            onChange={e => setForm({ ...form, categoria: e.target.value })}
            onFocus={e => trFocus(e, LT.viola)}
            onBlur={trBlur}
          >
            <option value="">— seleziona —</option>
            {CAT_PRODOTTO.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>

          <label style={TR_LABEL}>Note</label>
          <textarea
            style={{ ...TR_INPUT, resize: 'vertical', minHeight: 60 }}
            rows={2}
            value={form.note || ''}
            onChange={e => setForm({ ...form, note: e.target.value })}
            onFocus={e => trFocus(e, LT.viola)}
            onBlur={trBlur}
          />
        </SezioneForm>

        {/* Attivo */}
        {form.id && (
          <Card padding="md">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ accentColor: LT.verde, width: 16, height: 16 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: LT.navy }}>Prodotto attivo</div>
                <div style={{ fontSize: 12, color: LT.n500 }}>I prodotti disattivati non compaiono nelle nuove bolle</div>
              </div>
            </label>
          </Card>
        )}

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />
              {errore}
            </div>
          </Card>
        )}

        {/* CTA */}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>
            Annulla
          </Btn>
          <Btn
            kind="primary"
            icon="check"
            onClick={handleSalva}
            disabled={salvando}
            style={{ flex: 2 }}
          >
            {salvando ? 'Salvataggio…' : 'Salva prodotto'}
          </Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      {/* Top bar sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuovo prodotto</Btn>
      </div>

      {/* Hero compatto */}
      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.violaSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Ico d={I.pkg} size={28} stroke={LT.viola} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Prodotti e semilavorati
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} prodott{lista.length === 1 ? 'o' : 'i'} in anagrafica
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Ricerca */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Ico d={I.search} size={18} stroke={LT.n500} />
            <input
              style={{ ...TR_INPUT, border: 'none', padding: 0, fontSize: 14 }}
              placeholder="Cerca per nome o codice…"
              value={ricerca}
              onChange={e => setRicerca(e.target.value)}
            />
          </div>
        </Card>

        {/* Lista */}
        {filtrati.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.pkg} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              {ricerca ? 'Nessun prodotto trovato' : 'Nessun prodotto in anagrafica'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {ricerca ? 'Prova un termine di ricerca diverso' : 'Clicca "Nuovo prodotto" per iniziare'}
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10,
          }}>
            {filtrati.map(p => {
              const isFinito = p.categoria === 'PRODOTTO_FINITO';
              return (
                <Card
                  key={p.id}
                  padding="md"
                  style={{
                    opacity: p.attivo ? 1 : 0.55,
                    cursor: 'pointer',
                    transition: 'transform .12s, box-shadow .12s',
                  }}
                  onClick={() => apriForm(p)}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = LT.shadow.md; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: LT.r.md,
                      background: isFinito ? LT.bluSoft : LT.violaSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I.pkg} size={18} stroke={isFinito ? LT.blu : LT.viola} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy, lineHeight: 1.3 }}>{p.nome}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {p.categoria && (
                          <Chip kind={isFinito ? 'blu' : 'viola'} size="sm">
                            {p.categoria.replace(/_/g, ' ')}
                          </Chip>
                        )}
                        {p.unita_misura && <Chip kind="neutro" size="sm">{p.unita_misura}</Chip>}
                        {!p.attivo && <Chip kind="rosso" size="sm" icon="x">Disattivo</Chip>}
                      </div>
                      {p.codice && (
                        <div style={{ marginTop: 6, fontSize: 12, color: LT.n500, fontFamily: LT.mono }}>
                          {p.codice}
                        </div>
                      )}
                    </div>
                    <Ico d={I.chevronR} size={16} stroke={LT.n400} />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gestione Fasi produttive ─────────────────────────────────
function GestioneFasi({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null);
  const [ricerca,  setRicerca]  = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/fasi?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const parseParametri = (parametri) => {
    if (!parametri) return [];
    if (Array.isArray(parametri)) return parametri;
    try {
      const parsed = typeof parametri === 'string' ? JSON.parse(parametri) : parametri;
      if (Array.isArray(parsed)) return parsed;
      if (parsed.parametri && Array.isArray(parsed.parametri)) return parsed.parametri;
      return [];
    } catch {
      return [];
    }
  };

  const apriForm = (item = {}) => {
    setForm({ attivo: true, ...item, parametri: parseParametri(item.parametri) });
    setErrore('');
  };

  const aggiungiParametro = () => setForm(f => ({ ...f, parametri: [...f.parametri, { nome: '', unita: '', min: '', max: '' }] }));
  const rimuoviParametro  = (i) => setForm(f => ({ ...f, parametri: f.parametri.filter((_, idx) => idx !== i) }));
  const aggiornaParametro = (i, campo, valore) =>
    setForm(f => ({ ...f, parametri: f.parametri.map((p, idx) => idx === i ? { ...p, [campo]: valore } : p) }));

  const handleSalva = async () => {
    if (!form.nome) { setErrore('Nome obbligatorio'); return; }
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/fasi/${form.id}` : `${API}/tracciabilita/fasi`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda:   idAzienda,
          nome:        form.nome,
          descrizione: form.descrizione || null,
          parametri:   form.parametri.filter(p => p.nome.trim()),
          attivo:      form.attivo !== false,
        }),
      });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Fase salvata!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const filtrati = lista.filter(f =>
    !ricerca || f.nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.verdeSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.wrench} size={28} stroke={LT.verde} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              {form.id ? 'Modifica fase' : 'Nuova fase'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {form.id ? form.nome || 'Fase produttiva' : 'Configura una nuova fase produttiva con parametri misurabili'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SezioneForm color="verde" icon="wrench" titolo="Dati fase" sotto="Nome e descrizione della fase produttiva">
          <label style={TR_LABEL}>Nome *</label>
          <input
            style={TR_INPUT}
            value={form.nome || ''}
            onChange={e => setForm({ ...form, nome: e.target.value })}
            onFocus={e => trFocus(e, LT.verde)}
            onBlur={trBlur}
          />
          <label style={TR_LABEL}>Descrizione</label>
          <input
            style={TR_INPUT}
            value={form.descrizione || ''}
            onChange={e => setForm({ ...form, descrizione: e.target.value })}
            onFocus={e => trFocus(e, LT.verde)}
            onBlur={trBlur}
            placeholder="Descrizione estesa (opzionale)"
          />
        </SezioneForm>

        <SezioneForm color="blu" icon="chart" titolo="Parametri misurabili" sotto="Aggiungi i parametri da registrare per questa fase (temperature, pH, tempi…)">
          {form.parametri.length === 0 && (
            <div style={{
              padding: '12px 14px', background: LT.n50,
              border: `1px dashed ${LT.n200}`, borderRadius: LT.r.sm,
              fontSize: 13, color: LT.n500, textAlign: 'center',
            }}>
              Nessun parametro configurato. Aggiungine uno per iniziare.
            </div>
          )}
          {form.parametri.map((p, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(130px, 2fr) 90px 70px 70px auto',
              gap: 6, marginBottom: 8, alignItems: 'center',
            }}>
              <input
                style={TR_INPUT}
                placeholder="Nome (es. Temperatura)"
                value={p.nome}
                onChange={e => aggiornaParametro(i, 'nome', e.target.value)}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
              <select
                style={TR_INPUT}
                value={p.unita}
                onChange={e => aggiornaParametro(i, 'unita', e.target.value)}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              >
                <option value="">UM</option>
                {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
                {['°C', '%', 'pH', 'bar', 'min', 'h'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <input
                type="text" inputMode="decimal"
                style={TR_INPUT}
                placeholder="Min"
                value={p.min ?? ''}
                onChange={e => aggiornaParametro(i, 'min', decVal(e.target.value) === '' ? '' : parseFloat(decVal(e.target.value)))}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
              <input
                type="text" inputMode="decimal"
                style={TR_INPUT}
                placeholder="Max"
                value={p.max ?? ''}
                onChange={e => aggiornaParametro(i, 'max', decVal(e.target.value) === '' ? '' : parseFloat(decVal(e.target.value)))}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
              <Btn kind="ghost" size="sm" icon="trash" onClick={() => rimuoviParametro(i)} />
            </div>
          ))}
          <Btn kind="secondary" size="sm" icon="plus" onClick={aggiungiParametro}>
            Aggiungi parametro
          </Btn>
        </SezioneForm>

        {form.id && (
          <Card padding="md">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ accentColor: LT.verde, width: 16, height: 16 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: LT.navy }}>Fase attiva</div>
                <div style={{ fontSize: 12, color: LT.n500 }}>Le fasi disattivate non sono disponibili per nuove lavorazioni</div>
              </div>
            </label>
          </Card>
        )}

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />
              {errore}
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>
            Annulla
          </Btn>
          <Btn kind="primary" icon="check" onClick={handleSalva} disabled={salvando} style={{ flex: 2 }}>
            {salvando ? 'Salvataggio…' : 'Salva fase'}
          </Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={() => apriForm()}>Nuova fase</Btn>
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.verdeSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.wrench} size={28} stroke={LT.verde} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Fasi produttive
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} fas{lista.length === 1 ? 'e' : 'i'} in anagrafica
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Ico d={I.search} size={18} stroke={LT.n500} />
            <input
              style={{ ...TR_INPUT, border: 'none', padding: 0, fontSize: 14 }}
              placeholder="Cerca fase…"
              value={ricerca}
              onChange={e => setRicerca(e.target.value)}
            />
          </div>
        </Card>

        {filtrati.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.wrench} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              {ricerca ? 'Nessuna fase trovata' : 'Nessuna fase in anagrafica'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {ricerca ? 'Prova un termine di ricerca diverso' : 'Clicca "Nuova fase" per iniziare'}
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10,
          }}>
            {filtrati.map(f => {
              const params = parseParametri(f.parametri);
              return (
                <Card
                  key={f.id}
                  padding="md"
                  style={{
                    opacity: f.attivo ? 1 : 0.55,
                    cursor: 'pointer',
                    transition: 'transform .12s, box-shadow .12s',
                  }}
                  onClick={() => apriForm(f)}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = LT.shadow.md; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: LT.r.md,
                      background: LT.verdeSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I.wrench} size={18} stroke={LT.verde} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy, lineHeight: 1.3 }}>{f.nome}</div>
                      {f.descrizione && (
                        <div style={{ fontSize: 12, color: LT.n500, marginTop: 2, lineHeight: 1.4 }}>
                          {f.descrizione}
                        </div>
                      )}
                      {params.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {params.map((p, i) => {
                            const um     = p.unita || p.um || '';
                            const hasMin = p.min != null && p.min !== '';
                            const hasMax = p.max != null && p.max !== '';
                            const range  = hasMin && hasMax ? ` ${p.min}–${p.max}`
                                         : hasMin           ? ` ≥${p.min}`
                                         : hasMax           ? ` ≤${p.max}`
                                         : '';
                            return (
                              <Chip key={i} kind="blu" size="sm">
                                {p.nome}{um ? ` (${um})` : ''}{range}
                              </Chip>
                            );
                          })}
                        </div>
                      )}
                      {!f.attivo && (
                        <div style={{ marginTop: 6 }}>
                          <Chip kind="rosso" size="sm" icon="x">Disattivo</Chip>
                        </div>
                      )}
                    </div>
                    <Ico d={I.chevronR} size={16} stroke={LT.n400} />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TechDataTab({ form, setForm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!form.id) return;
    setLoading(true);
    apiFetch(`${API}/tracciabilita/ricette/${form.id}/tech-data`)
      .then(r => r.json())
      .then(d => { if (!d.errore) setData(d); })
      .finally(() => setLoading(false));
  }, [form.id]);

  if (!form.id) return (
    <div style={{ fontSize: 13, color: LT.n500, padding: 12, textAlign: 'center',
      background: LT.n50, border: `1px dashed ${LT.n200}`, borderRadius: LT.r.sm }}>
      Salva la ricetta prima di poter elaborare i dati tecnici.
    </div>
  );
  if (loading) return (
    <div style={{ fontSize: 13, color: LT.n500, padding: 12, textAlign: 'center' }}>
      Calcolo nutrizionali in corso...
    </div>
  );
  if (!data) return (
    <div style={{ fontSize: 13, color: LT.rosso, padding: 12, textAlign: 'center',
      background: LT.rossoSoft, border: `1px solid ${LT.rosso}40`, borderRadius: LT.r.sm }}>
      Errore durante il calcolo.
    </div>
  );

  const n = data.nutrizionali_100g || {};
  const strIngr = data.lista_ingredienti_generata || '';

  const kjVal   = n.kj_100g   ? `${n.kj_100g} kJ` : (n.kcal_100g ? `${Math.round(Number(n.kcal_100g) * 4.184)} kJ` : '—');
  const kcalVal = n.kcal_100g ? `${n.kcal_100g} kcal` : '—';
  const vociNutrizione = {
    'Energia':          `${kjVal} / ${kcalVal}`,
    'Grassi':           (n.grassi_100g       ?? '—') + ' g',
    ' di cui saturi':   (n.grassi_saturi_100g ?? '—') + ' g',
    'Carboidrati':      (n.carboidrati_100g   ?? '—') + ' g',
    ' di cui zuccheri': (n.zuccheri_100g      ?? '—') + ' g',
    'Fibre':            (n.fibre_100g         ?? '—') + ' g',
    'Proteine':         (n.proteine_100g      ?? '—') + ' g',
    'Sale':             (n.sale_100g          ?? '—') + ' g',
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontWeight: 700, color: LT.verde, marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            <Ico d={I.flask} size={14} stroke={LT.verde} />
            Valori nutrizionali (per 100 g)
          </div>
          <div style={{ background: LT.n0, border: `1px solid ${LT.n200}`, borderRadius: LT.r.sm, overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {Object.entries(vociNutrizione).map(([k, v], i, arr) => (
                  <tr key={k} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${LT.n100}` : 'none' }}>
                    <td style={{ padding: '8px 12px', color: LT.navy }}>
                      {k.startsWith(' ')
                        ? <span style={{ marginLeft: 12, color: LT.n500, fontSize: 12 }}>{k.trim()}</span>
                        : <strong>{k}</strong>}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: LT.navy, fontWeight: 600, fontFamily: LT.mono }}>
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontWeight: 700, color: LT.arancio, marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            <Ico d={I.list} size={14} stroke={LT.arancio} />
            Ingredienti (ordine decrescente)
          </div>
          <div style={{
            fontSize: 13, lineHeight: 1.5, color: LT.navy,
            background: LT.arancioSoft, border: `1px solid ${LT.arancio}30`,
            padding: 10, borderRadius: LT.r.sm, margin: '0 0 12px 0',
          }}>
            {strIngr}
          </div>
          <label style={TR_LABEL}>Testo personalizzato etichetta</label>
          <textarea
            style={{ ...TR_INPUT, resize: 'vertical', minHeight: 70, fontSize: 13 }}
            value={form.bozza_ingredienti_custom || ''}
            onChange={e => setForm({ ...form, bozza_ingredienti_custom: e.target.value })}
            onFocus={e => trFocus(e, LT.arancio)}
            onBlur={trBlur}
            placeholder="Se compilato, avrà priorità sulla lista calcolata in fase di stampa…"
          />
        </div>
      </div>

      {/* Scheda tecnica PDF — SOSPESA.
          Il documento generato non è ancora allo standard richiesto da
          un'etichetta reg. UE 1169/2011, quindi il download è disattivato per
          non mettere in mano al cliente un file da correggere a mano.
          I dati qui sopra (valori nutrizionali e lista ingredienti) restano
          calcolati, visibili e copiabili.
          Per riattivarla: rimuovere questo blocco, ripristinare il
          PDFDownloadLink con SchedaTecnicaDocument e riattivare i due import
          commentati in cima al file. */}
      <div style={{
        marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 10,
        background: LT.n50 || '#F8FAFC', border: `1px solid ${LT.n150}`,
        borderRadius: LT.r.sm, padding: '12px 14px',
      }}>
        <Ico d={I.info} size={16} stroke={LT.n500} />
        <div style={{ fontSize: 13, color: LT.n700, lineHeight: 1.5 }}>
          <strong>Scheda tecnica in PDF: funzione in corso di sviluppo.</strong>
          <div style={{ color: LT.n500, marginTop: 2 }}>
            Stiamo rifacendo il documento per allinearlo ai requisiti di
            etichettatura. Nel frattempo i valori nutrizionali e la lista
            ingredienti qui sopra sono già calcolati e si possono copiare.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Gestione Ricette ─────────────────────────────────────────
function GestioneRicette({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,              setLista]             = useState([]);
  const [form,               setForm]              = useState(null);
  const [ricerca,            setRicerca]           = useState('');
  const [filtroCat,          setFiltroCat]         = useState('');
  const [salvando,           setSalvando]          = useState(false);
  const [errore,             setErrore]            = useState('');
  const [caricando,          setCaricando]         = useState(false);
  const [mostraGenerali,     setMostraGenerali]    = useState(true);
  const [dettaglioId,        setDettaglioId]       = useState(null);
  const [dettaglioData,      setDettaglioData]     = useState(null);
  const [dettaglioCaricando, setDettaglioCaricando]= useState(false);
  // Anagrafiche per i select del form
  const [prodotti,    setProdotti]    = useState([]);
  const [ingredienti, setIngredienti] = useState([]);
  const [semilavorati,setSemilavorati]= useState([]);
  const [fasiDisp,    setFasiDisp]    = useState([]);

  useEffect(() => { caricaLista(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaLista = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/ricette?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const caricaAnagrafiche = async () => {
    const [rProd, rIngr, rFasi] = await Promise.all([
      apiFetch(`${API}/tracciabilita/prodotti?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/ingredienti?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/fasi?idAzienda=${idAzienda}`).then(r => r.json()),
    ]);
    const prod = Array.isArray(rProd) ? rProd : [];
    setProdotti(prod);
    setIngredienti(Array.isArray(rIngr) ? rIngr : []);
    setSemilavorati(prod.filter(p => p.categoria === 'SEMILAVORATO'));
    setFasiDisp(Array.isArray(rFasi) ? rFasi : []);
  };

  const parseFaseParametri = (json) => {
    if (!json) return [];
    try {
      const p = typeof json === 'string' ? JSON.parse(json) : json;
      if (Array.isArray(p)) return p;
      if (p.parametri && Array.isArray(p.parametri)) return p.parametri;
      return [];
    } catch { return []; }
  };

  const nuovaRiga = () => ({ tipo: 'INGREDIENTE', _id_rif: '', quantita: '', unita_misura: '', note: '' });
  const nuovaFase = () => ({ id_fase: '', durata_prevista: '', note: '', ingredienti: [] });

  const mapIngr = i => ({ ...i, _dbId: i.id, _id_rif: i.tipo === 'INGREDIENTE' ? i.id_ingrediente : i.id_semilavorato });

  const apriNuova = async () => {
    setCaricando(true);
    await caricaAnagrafiche();
    setForm({ attivo: true, fasi_form: [], ingredienti_generali: [] });
    setErrore('');
    setCaricando(false);
  };

  const apriModifica = async (item) => {
    setCaricando(true);
    await caricaAnagrafiche();
    const res = await apiFetch(`${API}/tracciabilita/ricette/${item.id}`);
    const det = await res.json();
    setForm({
      ...det,
      fasi_form: (det.fasi || []).map(f => ({
        ...f,
        _dbId:       f.id,
        ingredienti: (f.ingredienti || []).map(mapIngr),
      })),
      ingredienti_generali: (det.ingredienti_senza_fase || []).map(mapIngr),
    });
    setErrore('');
    setCaricando(false);
  };

  // ── Gestione fasi ──
  const aggiungiFase          = () => setForm(f => ({ ...f, fasi_form: [...f.fasi_form, nuovaFase()] }));
  const rimuoviFase           = (i) => setForm(f => ({ ...f, fasi_form: f.fasi_form.filter((_, idx) => idx !== i) }));
  const aggiornaFase          = (i, campo, val) => setForm(f => ({ ...f, fasi_form: f.fasi_form.map((fase, idx) => idx !== i ? fase : { ...fase, [campo]: val }) }));
  const spostaFase            = (i, dir) => setForm(f => {
    const arr = [...f.fasi_form];
    const altro = i + dir;
    if (altro < 0 || altro >= arr.length) return f;
    [arr[i], arr[altro]] = [arr[altro], arr[i]];
    return { ...f, fasi_form: arr };
  });

  // ── Gestione ingredienti per fase ──
  const aggiungiIngFase       = (iFase) => setForm(f => ({ ...f, fasi_form: f.fasi_form.map((fase, i) => i !== iFase ? fase : { ...fase, ingredienti: [...fase.ingredienti, nuovaRiga()] }) }));
  const rimuoviIngFase        = (iFase, iIng) => setForm(f => ({ ...f, fasi_form: f.fasi_form.map((fase, i) => i !== iFase ? fase : { ...fase, ingredienti: fase.ingredienti.filter((_, j) => j !== iIng) }) }));
  const aggiornaIngFase       = (iFase, iIng, campo, val) => setForm(f => ({ ...f, fasi_form: f.fasi_form.map((fase, i) => i !== iFase ? fase : { ...fase, ingredienti: fase.ingredienti.map((ing, j) => j !== iIng ? ing : { ...ing, [campo]: val }) }) }));

  // ── Gestione ingredienti generali ──
  const aggiungiIngGenerale   = () => setForm(f => ({ ...f, ingredienti_generali: [...f.ingredienti_generali, nuovaRiga()] }));
  const rimuoviIngGenerale    = (i) => setForm(f => ({ ...f, ingredienti_generali: f.ingredienti_generali.filter((_, idx) => idx !== i) }));
  const aggiornaIngGenerale   = (i, campo, val) => setForm(f => ({ ...f, ingredienti_generali: f.ingredienti_generali.map((ing, idx) => idx !== i ? ing : { ...ing, [campo]: val }) }));

  const handleSalva = async () => {
    if (!form.id_prodotto || !form.nome) { setErrore('Prodotto collegato e nome ricetta sono obbligatori'); return; }
    setErrore(''); setSalvando(true);
    try {
      const bodyRicetta = {
        idAzienda,
        idProdotto:       form.id_prodotto,
        nome:             form.nome,
        quantitaPrevista: form.quantita_prevista || null,
        unitaMisura:      form.unita_misura      || null,
        note:             form.note              || null,
        attivo:           form.attivo !== false,
        pesoPerPezzoG:    form.unita_misura === 'pz' ? (form.peso_per_pezzo_g || null) : null,
        bozza_ingredienti_custom: form.bozza_ingredienti_custom || null,
      };

      let idRicetta = form.id;
      if (idRicetta) {
        await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(bodyRicetta),
        });
      } else {
        const res  = await apiFetch(`${API}/tracciabilita/ricette`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(bodyRicetta),
        });
        const data = await res.json();
        idRicetta  = data.id;
        if (!idRicetta) throw new Error('ID ricetta non ricevuto');
      }

      // Cancella tutto e reinserisce (ingredienti prima per FK)
      await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}/ingredienti`, { method: 'DELETE' });
      await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}/fasi`,        { method: 'DELETE' });

      // Inserisce fasi + ingredienti annidati
      for (const [idx, fase] of form.fasi_form.entries()) {
        if (!fase.id_fase) continue;
        const resFase = await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}/fasi`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idFase: fase.id_fase, ordine: idx + 1, durataPrevista: fase.durata_prevista || null, note: fase.note || null }),
        });
        const dataFase = await resFase.json();
        const newFaseId = dataFase.id;
        for (const ing of fase.ingredienti) {
          if (!ing._id_rif) continue;
          await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}/ingredienti`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tipo:           ing.tipo,
              idIngrediente:  ing.tipo === 'INGREDIENTE'  ? ing._id_rif : null,
              idSemilavorato: ing.tipo === 'SEMILAVORATO' ? ing._id_rif : null,
              quantita:       ing.quantita    || null,
              unitaMisura:    ing.unita_misura || null,
              idRicettaFase:  newFaseId,
              note:           ing.note        || null,
            }),
          });
        }
      }

      // Inserisce ingredienti generali (senza fase)
      for (const ing of (form.ingredienti_generali || [])) {
        if (!ing._id_rif) continue;
        await apiFetch(`${API}/tracciabilita/ricette/${idRicetta}/ingredienti`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo:           ing.tipo,
            idIngrediente:  ing.tipo === 'INGREDIENTE'  ? ing._id_rif : null,
            idSemilavorato: ing.tipo === 'SEMILAVORATO' ? ing._id_rif : null,
            quantita:       ing.quantita    || null,
            unitaMisura:    ing.unita_misura || null,
            idRicettaFase:  null,
            note:           ing.note        || null,
          }),
        });
      }

      setForm(null);
      await caricaLista();
      toast('✓ Ricetta salvata!');
    } catch (e) {
      console.error(e);
      setErrore('Errore nel salvataggio. Riprova.');
    }
    setSalvando(false);
  };

  const toggleDettaglio = async (id) => {
    if (dettaglioId === id) { setDettaglioId(null); setDettaglioData(null); return; }
    setDettaglioId(id);
    setDettaglioData(null);
    setDettaglioCaricando(true);
    try {
      const res = await apiFetch(`${API}/tracciabilita/ricette/${id}`);
      setDettaglioData(await res.json());
    } catch {}
    setDettaglioCaricando(false);
  };

  const filtrati = lista.filter(r => {
    const okCat = !filtroCat || r.prodotto_categoria === filtroCat;
    const okRic = !ricerca  || r.nome?.toLowerCase().includes(ricerca.toLowerCase()) || r.prodotto_nome?.toLowerCase().includes(ricerca.toLowerCase());
    return okCat && okRic;
  });

  // ── Stili condivisi form ──
  const inputSm = { ...TR_INPUT, padding: '7px 10px', fontSize: 13 };

  // ── Riga ingrediente riutilizzabile ──
  const renderIngRow = (ing, onChange, onRemove, focusColor = LT.arancio) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
      <select
        value={ing.tipo}
        onChange={e => onChange('tipo', e.target.value)}
        style={{ ...inputSm, flex: '0 0 120px' }}
        onFocus={e => trFocus(e, focusColor)}
        onBlur={trBlur}
      >
        <option value="INGREDIENTE">Ingrediente</option>
        <option value="SEMILAVORATO">Semilavorato</option>
      </select>
      <select
        value={ing._id_rif || ''}
        onChange={e => onChange('_id_rif', e.target.value)}
        style={{ ...inputSm, flex: '1 1 150px' }}
        onFocus={e => trFocus(e, focusColor)}
        onBlur={trBlur}
      >
        <option value="">— seleziona —</option>
        {(ing.tipo === 'INGREDIENTE' ? ingredienti : semilavorati)
          .filter(i => i.attivo !== false && i.attivo !== 0)
          .map(i => <option key={i.id} value={i.id}>{i.nome}{i.codice ? ` [${i.codice}]` : ''}</option>)}
      </select>
      <input
        type="text" inputMode="decimal" placeholder="Qtà"
        value={ing.quantita || ''}
        onChange={e => onChange('quantita', decVal(e.target.value))}
        style={{ ...inputSm, flex: '0 0 72px' }}
        onFocus={e => trFocus(e, focusColor)}
        onBlur={trBlur}
      />
      <select
        value={ing.unita_misura || ''}
        onChange={e => onChange('unita_misura', e.target.value)}
        style={{ ...inputSm, flex: '0 0 64px' }}
        onFocus={e => trFocus(e, focusColor)}
        onBlur={trBlur}
      >
        <option value="">UM</option>
        {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
      <input
        placeholder="Note"
        value={ing.note || ''}
        onChange={e => onChange('note', e.target.value)}
        style={{ ...inputSm, flex: '1 1 90px' }}
        onFocus={e => trFocus(e, focusColor)}
        onBlur={trBlur}
      />
      <Btn kind="ghost" size="sm" icon="x" onClick={onRemove} />
    </div>
  );

  // ── FORM ──
  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.cianoSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.book} size={28} stroke={LT.ciano} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              {form.id ? 'Modifica ricetta' : 'Nuova ricetta'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {form.id ? form.nome || 'Ricetta' : 'Configura la ricetta con fasi, ingredienti e dati tecnici'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Sezione 1: Dati generali */}
        <SezioneForm color="blu" icon="edit" titolo="Dati generali" sotto="Prodotto collegato, nome e quantità prevista">
          <label style={TR_LABEL}>Prodotto collegato *</label>
          <select
            style={TR_INPUT}
            value={form.id_prodotto || ''}
            onChange={e => setForm({ ...form, id_prodotto: e.target.value })}
            onFocus={e => trFocus(e, LT.blu)}
            onBlur={trBlur}
          >
            <option value="">— seleziona prodotto —</option>
            {['SEMILAVORATO', 'PRODOTTO_FINITO'].map(cat => (
              <optgroup key={cat} label={cat.replace(/_/g, ' ')}>
                {prodotti.filter(p => p.categoria === cat && p.attivo).map(p => (
                  <option key={p.id} value={p.id}>{p.nome}{p.codice ? ` [${p.codice}]` : ''}</option>
                ))}
              </optgroup>
            ))}
          </select>

          <label style={TR_LABEL}>Nome ricetta *</label>
          <input
            style={TR_INPUT}
            value={form.nome || ''}
            onChange={e => setForm({ ...form, nome: e.target.value })}
            placeholder="Es. Ricetta base, Variante estiva…"
            onFocus={e => trFocus(e, LT.blu)}
            onBlur={trBlur}
          />

          <div style={{ display: 'grid', gridTemplateColumns: form.unita_misura === 'pz' ? '2fr 1fr 1fr' : '2fr 1fr', gap: 10 }}>
            <div>
              <label style={TR_LABEL}>Quantità prevista</label>
              <input
                style={TR_INPUT}
                type="text" inputMode="decimal"
                value={form.quantita_prevista || ''}
                onChange={e => setForm({ ...form, quantita_prevista: decVal(e.target.value) })}
                placeholder="0.000"
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
            </div>
            <div>
              <label style={TR_LABEL}>Unità</label>
              <select
                style={TR_INPUT}
                value={form.unita_misura || ''}
                onChange={e => setForm({ ...form, unita_misura: e.target.value })}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              >
                <option value="">—</option>
                {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            {form.unita_misura === 'pz' && (
              <div>
                <label style={TR_LABEL}>Peso/pz (g)</label>
                <input
                  style={TR_INPUT}
                  type="text" inputMode="decimal"
                  value={form.peso_per_pezzo_g || ''}
                  onChange={e => setForm({ ...form, peso_per_pezzo_g: parseFloat(decVal(e.target.value)) || 0 })}
                  placeholder="es. 100"
                  onFocus={e => trFocus(e, LT.blu)}
                  onBlur={trBlur}
                />
              </div>
            )}
          </div>

          <label style={TR_LABEL}>Note</label>
          <textarea
            style={{ ...TR_INPUT, resize: 'vertical', minHeight: 60 }}
            rows={2}
            value={form.note || ''}
            onChange={e => setForm({ ...form, note: e.target.value })}
            onFocus={e => trFocus(e, LT.blu)}
            onBlur={trBlur}
          />

          {form.id && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', userSelect: 'none', marginTop: 10,
              fontSize: 13, color: LT.navy,
            }}>
              <input
                type="checkbox"
                checked={form.attivo !== false}
                onChange={e => setForm({ ...form, attivo: e.target.checked })}
                style={{ accentColor: LT.verde, width: 16, height: 16 }}
              />
              Ricetta attiva
            </label>
          )}
        </SezioneForm>

        {/* Sezione 2: Fasi e ingredienti */}
        <SezioneForm color="verde" icon="wrench" titolo="Fasi e ingredienti" sotto="Configura l'ordine di lavorazione e gli ingredienti per ogni fase">
          {form.fasi_form.length === 0 && (
            <div style={{
              padding: '12px 14px', background: LT.n50,
              border: `1px dashed ${LT.n200}`, borderRadius: LT.r.sm,
              fontSize: 13, color: LT.n500, textAlign: 'center', marginBottom: 10,
            }}>
              Nessuna fase aggiunta. Clicca "Aggiungi fase" per iniziare.
            </div>
          )}

          {form.fasi_form.map((fase, iFase) => (
            <div key={iFase} style={{
              background: LT.verdeSoft,
              border: `1.5px solid ${LT.verde}40`,
              borderRadius: LT.r.md, marginBottom: 10, overflow: 'hidden',
            }}>
              {/* Header fase */}
              <div style={{
                background: `${LT.verde}18`,
                padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                borderBottom: `1px solid ${LT.verde}30`,
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: LT.verde, color: LT.n0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, flexShrink: 0,
                }}>
                  {iFase + 1}
                </div>
                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                  <Btn kind="ghost" size="sm" onClick={() => spostaFase(iFase, -1)} disabled={iFase === 0} title="Sposta su">↑</Btn>
                  <Btn kind="ghost" size="sm" onClick={() => spostaFase(iFase,  1)} disabled={iFase === form.fasi_form.length - 1} title="Sposta giù">↓</Btn>
                </div>
                <select
                  value={fase.id_fase || ''}
                  onChange={e => aggiornaFase(iFase, 'id_fase', e.target.value)}
                  style={{ ...inputSm, flex: '1 1 160px' }}
                  onFocus={e => trFocus(e, LT.verde)}
                  onBlur={trBlur}
                >
                  <option value="">— seleziona fase —</option>
                  {fasiDisp.filter(f => f.attivo !== false && f.attivo !== 0).map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
                <input
                  type="number" min="0" placeholder="Durata min"
                  value={fase.durata_prevista || ''}
                  onChange={e => aggiornaFase(iFase, 'durata_prevista', e.target.value)}
                  style={{ ...inputSm, flex: '0 0 100px' }}
                  title="Durata prevista in minuti"
                  onFocus={e => trFocus(e, LT.verde)}
                  onBlur={trBlur}
                />
                <input
                  placeholder="Note fase"
                  value={fase.note || ''}
                  onChange={e => aggiornaFase(iFase, 'note', e.target.value)}
                  style={{ ...inputSm, flex: '1 1 110px' }}
                  onFocus={e => trFocus(e, LT.verde)}
                  onBlur={trBlur}
                />
                <Btn kind="ghost" size="sm" icon="trash" onClick={() => rimuoviFase(iFase)} style={{ marginLeft: 'auto' }} title="Rimuovi fase" />
              </div>

              {/* Ingredienti di questa fase */}
              <div style={{ padding: '10px', background: LT.n0 }}>
                {fase.ingredienti.length === 0 && (
                  <div style={{ color: LT.n400, fontSize: 12, margin: '0 0 6px', fontStyle: 'italic' }}>
                    Nessun ingrediente per questa fase.
                  </div>
                )}
                {fase.ingredienti.map((ing, iIng) =>
                  renderIngRow(ing, (campo, val) => aggiornaIngFase(iFase, iIng, campo, val), () => rimuoviIngFase(iFase, iIng), LT.verde)
                )}
                <Btn kind="secondary" size="sm" icon="plus" onClick={() => aggiungiIngFase(iFase)}>
                  Aggiungi ingrediente
                </Btn>
              </div>
            </div>
          ))}

          <Btn kind="primary" icon="plus" onClick={aggiungiFase}>
            Aggiungi fase
          </Btn>
        </SezioneForm>

        {/* Sezione 3: Ingredienti generali */}
        <SezioneForm color="arancio" icon="package" titolo={`Ingredienti generali${form.ingredienti_generali?.length > 0 ? ` (${form.ingredienti_generali.length})` : ''}`} sotto="Ingredienti non legati a una fase specifica">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <Btn kind="ghost" size="sm" onClick={() => setMostraGenerali(v => !v)}>
              {mostraGenerali ? '▲ nascondi' : '▼ mostra'}
            </Btn>
          </div>
          {mostraGenerali && (
            <>
              {(form.ingredienti_generali || []).length === 0 && (
                <div style={{
                  padding: '10px 14px', background: LT.n50,
                  border: `1px dashed ${LT.n200}`, borderRadius: LT.r.sm,
                  fontSize: 12, color: LT.n500, textAlign: 'center', marginBottom: 8,
                }}>
                  Nessun ingrediente generale.
                </div>
              )}
              {(form.ingredienti_generali || []).map((ing, i) =>
                renderIngRow(ing, (campo, val) => aggiornaIngGenerale(i, campo, val), () => rimuoviIngGenerale(i), LT.arancio)
              )}
              <Btn kind="secondary" size="sm" icon="plus" onClick={aggiungiIngGenerale} style={{ marginTop: 4 }}>
                Aggiungi ingrediente generale
              </Btn>
            </>
          )}
        </SezioneForm>

        {/* Sezione 4: Dati Tecnici & Nutrizionali */}
        <SezioneForm color="ciano" icon="flask" titolo="Dati tecnici & Nutrizionali" sotto="Valori nutrizionali calcolati e lista ingredienti per etichetta">
          <TechDataTab form={form} setForm={setForm} />
        </SezioneForm>

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />
              {errore}
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>
            Annulla
          </Btn>
          <Btn kind="primary" icon="check" onClick={handleSalva} disabled={salvando} style={{ flex: 2 }}>
            {salvando ? 'Salvataggio…' : 'Salva ricetta'}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── LISTA ──
  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={apriNuova} disabled={caricando}>
          {caricando ? 'Caricamento…' : 'Nuova ricetta'}
        </Btn>
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.cianoSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.book} size={28} stroke={LT.ciano} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Configurazione
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Ricette
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} ricett{lista.length === 1 ? 'a' : 'e'} in anagrafica
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <div>
              <label style={TR_LABEL}>Ricerca</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: LT.n0,
                border: `1.5px solid ${LT.n200}`, borderRadius: LT.r.md, padding: '8px 12px' }}>
                <Ico d={I.search} size={16} stroke={LT.n500} />
                <input
                  style={{ ...TR_INPUT, border: 'none', padding: 0, fontSize: 14, flex: 1 }}
                  placeholder="Nome ricetta o prodotto…"
                  value={ricerca}
                  onChange={e => setRicerca(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label style={TR_LABEL}>Categoria</label>
              <select
                style={TR_INPUT}
                value={filtroCat}
                onChange={e => setFiltroCat(e.target.value)}
                onFocus={e => trFocus(e, LT.ciano)}
                onBlur={trBlur}
              >
                <option value="">Tutte</option>
                <option value="SEMILAVORATO">Semilavorati</option>
                <option value="PRODOTTO_FINITO">Prodotti finiti</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Lista */}
        {filtrati.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.book} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              {ricerca || filtroCat ? 'Nessuna ricetta trovata' : 'Nessuna ricetta in anagrafica'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {ricerca || filtroCat ? 'Prova a modificare i filtri' : 'Clicca "Nuova ricetta" per iniziare'}
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtrati.map(r => {
              const isFinito = r.prodotto_categoria === 'PRODOTTO_FINITO';
              const isOpen   = dettaglioId === r.id;
              return (
                <Card
                  key={r.id}
                  padding="md"
                  style={{ opacity: r.attivo ? 1 : 0.55 }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: LT.r.md,
                      background: LT.cianoSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I.book} size={20} stroke={LT.ciano} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: LT.navy, lineHeight: 1.3 }}>{r.nome}</div>
                        {r.prodotto_categoria && (
                          <Chip kind={isFinito ? 'blu' : 'viola'} size="sm">
                            {r.prodotto_categoria.replace(/_/g, ' ')}
                          </Chip>
                        )}
                        {!r.attivo && <Chip kind="rosso" size="sm" icon="x">Disattiva</Chip>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 13, color: LT.n700 }}>
                        <Ico d={I.pkg} size={14} stroke={LT.n500} />
                        {r.prodotto_nome}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12, color: LT.n500, flexWrap: 'wrap', marginTop: 4 }}>
                        {r.quantita_prevista && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <Ico d={I.chart} size={12} stroke={LT.n500} />
                            {r.quantita_prevista} {r.unita_misura}
                          </span>
                        )}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Ico d={I.wrench} size={12} stroke={LT.verde} />
                          {r.n_fasi} fas{r.n_fasi === 1 ? 'e' : 'i'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Ico d={I.package} size={12} stroke={LT.arancio} />
                          {r.n_ingredienti} ingredient{r.n_ingredienti === 1 ? 'e' : 'i'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Dettaglio annidato */}
                  {isOpen && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${LT.n200}` }}>
                      {dettaglioCaricando && (
                        <div style={{ fontSize: 12, color: LT.n500, margin: 0 }}>Caricamento…</div>
                      )}
                      {dettaglioData && (
                        <>
                          {(dettaglioData.fasi || []).map(fase => {
                            const params = parseFaseParametri(fase.fase_parametri);
                            return (
                              <div key={fase.id} style={{
                                marginBottom: 10, padding: '8px 10px',
                                background: LT.verdeSoft, borderRadius: LT.r.sm,
                                border: `1px solid ${LT.verde}30`,
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: LT.verde }}>
                                  <div style={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    background: LT.verde, color: LT.n0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 700, fontSize: 11, flexShrink: 0,
                                  }}>{fase.ordine}</div>
                                  {fase.fase_nome}
                                  {fase.durata_prevista ? (
                                    <Chip kind="neutro" size="sm">{fase.durata_prevista} min</Chip>
                                  ) : null}
                                </div>
                                {params.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, paddingLeft: 26 }}>
                                    {params.map((p, i) => {
                                      const um     = p.um || p.unita || '';
                                      const hasMin = p.min != null && p.min !== '';
                                      const hasMax = p.max != null && p.max !== '';
                                      const range  = hasMin && hasMax ? ` ${p.min}–${p.max}`
                                                   : hasMin           ? ` ≥${p.min}`
                                                   : hasMax           ? ` ≤${p.max}`
                                                   : '';
                                      return (
                                        <Chip key={i} kind="blu" size="sm">
                                          {p.nome}{um ? ` (${um})` : ''}{range}
                                        </Chip>
                                      );
                                    })}
                                  </div>
                                )}
                                <div style={{ marginTop: 6, paddingLeft: 26 }}>
                                  {(fase.ingredienti || []).length === 0 ? (
                                    <div style={{ fontSize: 12, color: LT.n400, fontStyle: 'italic' }}>— nessun ingrediente</div>
                                  ) : (
                                    (fase.ingredienti || []).map(ing => (
                                      <div key={ing.id} style={{ fontSize: 12, color: LT.n700, lineHeight: 1.8 }}>
                                        • {ing.nome_riferimento}
                                        {ing.quantita ? <span style={{ color: LT.n500 }}> — {ing.quantita} {ing.unita_misura || ''}</span> : ''}
                                        {ing.tipo === 'SEMILAVORATO' && (
                                          <span style={{ fontSize: 10, color: LT.viola, marginLeft: 6, fontWeight: 700 }}>[SEMI]</span>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {(dettaglioData.ingredienti_senza_fase || []).length > 0 && (
                            <div style={{
                              padding: '8px 10px', background: LT.arancioSoft,
                              borderRadius: LT.r.sm, border: `1px solid ${LT.arancio}30`,
                            }}>
                              <div style={{ fontWeight: 700, fontSize: 12, color: LT.arancio, marginBottom: 4 }}>
                                Ingredienti generali
                              </div>
                              {dettaglioData.ingredienti_senza_fase.map(ing => (
                                <div key={ing.id} style={{ fontSize: 12, color: LT.n700, lineHeight: 1.8 }}>
                                  • {ing.nome_riferimento}
                                  {ing.quantita ? <span style={{ color: LT.n500 }}> — {ing.quantita} {ing.unita_misura || ''}</span> : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <Btn kind="secondary" size="sm" icon={isOpen ? 'chevronD' : 'list'} onClick={() => toggleDettaglio(r.id)}>
                      {isOpen ? 'Nascondi' : 'Dettaglio'}
                    </Btn>
                    <Btn kind="secondary" size="sm" icon="edit" onClick={() => apriModifica(r)} disabled={caricando}>
                      {caricando ? '…' : 'Modifica'}
                    </Btn>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gestione Bolle di Ingresso ───────────────────────────────
// ─── Modelli di accettazione (checklist controlli al ricevimento) ──────────
const ACC_TIPI = [
  { key: 'cn',     label: 'Conforme / Non Conforme' },
  { key: 'numero', label: 'Valore numerico (es. temperatura)' },
  { key: 'flag',   label: 'Sì / No (es. controcampione)' },
];

// Esito di una singola voce di controllo dato il valore compilato.
// 'C' | 'NC' | null (non valutabile / non compilata).
// Flag: se la voce è OBBLIGATORIA, il "No" (o il non compilato) è NC e il "Sì" è C —
// un requisito tipo "confezioni integre" lasciato a No deve pesare sull'esito.
// I flag facoltativi restano informativi (null).
function accEsitoVoce(voce, val) {
  if (!voce) return null;
  if (voce.tipo === 'cn') return val === 'NC' ? 'NC' : (val === 'C' ? 'C' : null);
  if (voce.tipo === 'numero') {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    if (Number.isNaN(n)) return null;
    const okMin = voce.min === null || voce.min === undefined || n >= Number(voce.min);
    const okMax = voce.max === null || voce.max === undefined || n <= Number(voce.max);
    return (okMin && okMax) ? 'C' : 'NC';
  }
  if (voce.tipo === 'flag' && voce.obbligatorio) {
    return (val === true || val === 'true' || val === 1) ? 'C' : 'NC';
  }
  return null;
}

// Costruisce l'array `controlli` da salvare partendo dal modello e dai valori.
function accBuildControlli(modello, valori) {
  return ((modello && modello.voci) || []).map(v => ({
    key: v.key, label: v.label, tipo: v.tipo,
    um: v.tipo === 'numero' ? (v.um || null) : null,
    valore: valori[v.key] ?? null,
    esito: accEsitoVoce(v, valori[v.key]),
  }));
}

// Esito complessivo: NC se almeno un controllo è NC, altrimenti C se almeno uno valutato.
function accEsitoComplessivo(controlli) {
  if (controlli.some(c => c.esito === 'NC')) return 'NC';
  if (controlli.some(c => c.esito === 'C'))  return 'C';
  return null;
}

function GestioneModelliAccettazione({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,    setLista]    = useState([]);
  const [form,     setForm]     = useState(null); // null=lista; {id?, nome, voci, attivo}
  const [salvando, setSalvando] = useState(false);
  const [errore,   setErrore]   = useState('');

  useEffect(() => { carica(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/accettazione-modelli?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const nuovaVoce = () => ({
    key: `k${Date.now()}${Math.floor(Math.random() * 1000)}`,
    label: '', tipo: 'cn', um: '', min: null, max: null, obbligatorio: true,
  });

  const apriNuovo    = () => { setForm({ nome: '', voci: [nuovaVoce()], attivo: true }); setErrore(''); };
  const apriModifica = (m) => {
    setForm({
      id: m.id, nome: m.nome, attivo: m.attivo,
      voci: Array.isArray(m.voci) ? m.voci.map(v => ({ ...v })) : [],
    });
    setErrore('');
  };

  const setVoce     = (i, campo, val) => setForm(f => ({ ...f, voci: f.voci.map((v, idx) => idx !== i ? v : { ...v, [campo]: val }) }));
  const aggiungiVoce = () => setForm(f => ({ ...f, voci: [...f.voci, nuovaVoce()] }));
  const rimuoviVoce  = (i) => setForm(f => ({ ...f, voci: f.voci.filter((_, idx) => idx !== i) }));
  const spostaVoce   = (i, dir) => setForm(f => {
    const j = i + dir;
    if (j < 0 || j >= f.voci.length) return f;
    const v = [...f.voci];
    [v[i], v[j]] = [v[j], v[i]];
    return { ...f, voci: v };
  });

  const handleSalva = async () => {
    if (!form.nome || !form.nome.trim()) { setErrore('Indica un nome per il modello'); return; }
    const vociPulite = (form.voci || []).filter(v => v.label && v.label.trim());
    if (!vociPulite.length) { setErrore('Aggiungi almeno un controllo con etichetta'); return; }
    // Normalizza min/max numerici
    const voci = vociPulite.map(v => ({
      key: v.key, label: v.label.trim(), tipo: v.tipo || 'cn',
      um: v.tipo === 'numero' ? (v.um || '') : undefined,
      min: v.tipo === 'numero' && v.min !== '' && v.min !== null && v.min !== undefined ? Number(v.min) : null,
      max: v.tipo === 'numero' && v.max !== '' && v.max !== null && v.max !== undefined ? Number(v.max) : null,
      obbligatorio: v.obbligatorio !== false,
    }));
    setErrore(''); setSalvando(true);
    try {
      const url    = form.id ? `${API}/tracciabilita/accettazione-modelli/${form.id}` : `${API}/tracciabilita/accettazione-modelli`;
      const method = form.id ? 'PUT' : 'POST';
      const body   = form.id
        ? { nome: form.nome.trim(), voci, attivo: form.attivo }
        : { idAzienda, nome: form.nome.trim(), voci };
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setForm(null);
      await carica();
      toast('✓ Modello salvato!');
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  // ── FORM ──
  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
      </div>

      <div style={{ padding: '20px 16px 24px', maxWidth: 820, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: LT.navy }}>
          {form.id ? 'Modifica modello' : 'Nuovo modello di accettazione'}
        </h1>

        <Card padding="md">
          <label style={TR_LABEL}>Nome modello *</label>
          <input
            style={TR_INPUT}
            value={form.nome || ''}
            onChange={e => setForm({ ...form, nome: e.target.value })}
            placeholder="Es. Carne, Altre MP e MOCA, Standard…"
          />
          {form.id && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: LT.n700, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.attivo !== false} onChange={e => setForm({ ...form, attivo: e.target.checked })} />
              Modello attivo (disattiva per archiviarlo senza eliminarlo)
            </label>
          )}
        </Card>

        <Card padding="md">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: LT.navy }}>Controlli della checklist</div>
            <Btn kind="secondary" size="sm" icon="plus" onClick={aggiungiVoce}>Aggiungi controllo</Btn>
          </div>

          {(form.voci || []).map((v, i) => (
            <div key={v.key} style={{ border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', background: LT.bluSoft, color: LT.blu,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0,
                }}>{i + 1}</span>
                <input
                  style={{ ...TR_INPUT, flex: 1 }}
                  value={v.label || ''}
                  onChange={e => setVoce(i, 'label', e.target.value)}
                  placeholder="Etichetta controllo (es. Conformità documenti / DDT)"
                />
                <Btn kind="ghost" size="sm" onClick={() => spostaVoce(i, -1)} disabled={i === 0} title="Sposta su">↑</Btn>
                <Btn kind="ghost" size="sm" onClick={() => spostaVoce(i, 1)} disabled={i === form.voci.length - 1} title="Sposta giù">↓</Btn>
                <Btn kind="ghost" size="sm" icon="trash" onClick={() => rimuoviVoce(i)} title="Rimuovi" />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <select style={{ ...TR_INPUT, flex: '1 1 220px' }} value={v.tipo || 'cn'} onChange={e => setVoce(i, 'tipo', e.target.value)}>
                  {ACC_TIPI.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                {v.tipo === 'numero' && (
                  <>
                    <input style={{ ...TR_INPUT, flex: '0 0 70px' }} value={v.um || ''} onChange={e => setVoce(i, 'um', e.target.value)} placeholder="UM" title="Unità di misura" />
                    <input style={{ ...TR_INPUT, flex: '0 0 90px' }} type="text" inputMode="decimal" value={v.min ?? ''} onChange={e => setVoce(i, 'min', decVal(e.target.value))} placeholder="min" title="Soglia minima (opzionale)" />
                    <input style={{ ...TR_INPUT, flex: '0 0 90px' }} type="text" inputMode="decimal" value={v.max ?? ''} onChange={e => setVoce(i, 'max', decVal(e.target.value))} placeholder="max" title="Soglia massima (opzionale)" />
                  </>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: LT.n600, whiteSpace: 'nowrap' }}
                  title={v.tipo === 'flag' ? 'Se obbligatorio, il "No" (o il non compilato) rende la voce Non Conforme' : undefined}>
                  <input type="checkbox" checked={v.obbligatorio !== false} onChange={e => setVoce(i, 'obbligatorio', e.target.checked)} />
                  Obbligatorio
                </label>
              </div>
            </div>
          ))}
        </Card>

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />{errore}
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>Annulla</Btn>
          <Btn kind="primary" icon="check" onClick={handleSalva} disabled={salvando} style={{ flex: 2 }}>
            {salvando ? 'Salvataggio…' : 'Salva modello'}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── LISTA ──
  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={apriNuovo}>Nuovo modello</Btn>
      </div>

      <div style={{ padding: '20px 16px 24px', maxWidth: 820, margin: '0 auto' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: LT.navy }}>Modelli di accettazione</h1>
        <div style={{ fontSize: 13, color: LT.n500, marginBottom: 16 }}>
          Checklist dei controlli al ricevimento materie prime. Puoi crearne più d'una (es. una per la carne, una per le altre MP).
        </div>

        {lista.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>Nessun modello</div>
            <div style={{ fontSize: 13, color: LT.n500 }}>Crea il primo modello di controlli al ricevimento.</div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lista.map(m => (
              <Card key={m.id} padding="md" style={{ cursor: 'pointer', opacity: m.attivo ? 1 : 0.6 }} onClick={() => apriModifica(m)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.md, background: LT.bluSoft,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Ico d={I.list} size={18} stroke={LT.blu} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy }}>{m.nome}</div>
                      {!m.attivo && <Chip kind="rosso" size="sm" icon="x">Archiviato</Chip>}
                    </div>
                    <div style={{ fontSize: 12.5, color: LT.n500, marginTop: 2 }}>
                      {(Array.isArray(m.voci) ? m.voci.length : 0)} controll{(Array.isArray(m.voci) ? m.voci.length : 0) === 1 ? 'o' : 'i'}
                    </div>
                  </div>
                  <Ico d={I.chevronR} size={16} stroke={LT.n400} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Registro Ricevimento / Accettazione MP (vista + stampa sulle bolle) ───
function GestioneRicevimentoMP({ operatore, azienda, onBack, onApriBolle, onApriBolla }) {
  const idAzienda = operatore.id_azienda;
  const [lista,     setLista]     = useState([]);
  const [caricando, setCaricando] = useState(false);
  const [dal,       setDal]       = useState('');
  const [al,        setAl]        = useState('');
  const [esito,     setEsito]     = useState('');   // '' | 'C' | 'NC'
  const [ricerca,   setRicerca]   = useState('');

  useEffect(() => { carica(); }, [dal, al, esito]); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    setCaricando(true);
    const qs = new URLSearchParams({ idAzienda: String(idAzienda) });
    if (dal)   qs.set('dal', dal);
    if (al)    qs.set('al', al);
    if (esito) qs.set('esito', esito);
    try {
      const res  = await apiFetch(`${API}/tracciabilita/bolle-ingresso?${qs.toString()}`);
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch { setLista([]); }
    setCaricando(false);
  };

  const filtrate = lista.filter(b => !ricerca ||
    (b.fornitore_nome  || '').toLowerCase().includes(ricerca.toLowerCase()) ||
    (b.committente_nome|| '').toLowerCase().includes(ricerca.toLowerCase()) ||
    (b.numero_bolla    || '').toLowerCase().includes(ricerca.toLowerCase()));

  const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const stampa = () => {
    const fmtIt = d => { if (!d) return ''; const [y, m, g] = String(d).slice(0, 10).split('-'); return (y && m && g) ? `${g}/${m}/${y}` : esc(d); };
    const fmtCtrl = c => {
      let v;
      if (c.tipo === 'flag') v = c.valore ? 'Sì' : 'No';
      else if (c.tipo === 'numero') {
        const raw = (c.valore === null || c.valore === undefined || c.valore === '') ? '—' : c.valore;
        v = `${raw}${c.um ? ' ' + c.um : ''}`;
      } else v = c.esito === 'NC' ? 'NC' : c.esito === 'C' ? 'C' : '—';
      return `<span class="ctrl${c.esito === 'NC' ? ' ncx' : ''}">${esc(c.label)}: <b>${esc(v)}</b></span>`;
    };
    const righe = filtrate.map((b, i) => {
      const esitoCode = b.esito_accettazione;
      const esitoCell = esitoCode === 'NC'
        ? '<td class="e nc">NON CONF.</td>'
        : esitoCode === 'C' ? '<td class="e ok">CONFORME</td>' : '<td class="e">—</td>';
      const ctrlHtml = (Array.isArray(b.controlli) && b.controlli.length)
        ? b.controlli.map(fmtCtrl).join(' ')
        : '<span style="color:#999">—</span>';
      return `<tr>
        <td class="c">${i + 1}</td>
        <td>${fmtIt(b.data_ricevimento || b.data_bolla)}</td>
        <td>${fmtIt(b.data_bolla)}</td>
        <td>${esc(b.fornitore_nome || '')}${b.committente_nome ? `<br><span style="color:#b45309;font-size:9px">conto terzi: ${esc(b.committente_nome)}</span>` : ''}</td>
        <td>${esc(b.numero_bolla || '')}</td>
        <td class="c">${b.n_righe || 0}</td>
        ${esitoCell}
        <td class="ctrls">${ctrlHtml}</td>
      </tr>`;
    }).join('');

    const tot  = filtrate.length;
    const conf = filtrate.filter(b => b.esito_accettazione === 'C').length;
    const nconf = filtrate.filter(b => b.esito_accettazione === 'NC').length;
    const periodo = (dal || al) ? `Periodo dal ${fmtIt(dal) || '—'} al ${fmtIt(al) || 'oggi'}` : 'Tutte le registrazioni';
    const oggi = new Date().toLocaleDateString('it-IT');
    const rs   = (azienda && azienda.ragione_sociale) ? esc(azienda.ragione_sociale) : '';
    const addr = azienda ? [azienda.indirizzo, [azienda.cap, azienda.citta].filter(Boolean).join(' ')].filter(Boolean).map(esc).join(' · ') : '';

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Registro accettazione materie prime</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:28px 26px}
        .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e40af;padding-bottom:10px;margin-bottom:6px}
        .az{font-size:17px;font-weight:800;color:#0f172a}
        .azsub{font-size:11px;color:#555;margin-top:2px}
        .docmeta{text-align:right;font-size:10.5px;color:#555;line-height:1.5}
        h1{font-size:15px;margin:12px 0 2px;color:#1e40af;text-transform:uppercase;letter-spacing:.4px}
        .sub{color:#555;font-size:11.5px;margin-bottom:12px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th,td{border:1px solid #cbd5e1;padding:5px 7px;vertical-align:top}
        th{background:#1e40af;color:#fff;text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:.3px}
        tbody tr:nth-child(even){background:#f1f5f9}
        td.c{text-align:center}
        td.e{text-align:center;font-weight:700;font-size:10px;white-space:nowrap}
        td.e.ok{background:#dcfce7;color:#166534}
        td.e.nc{background:#fee2e2;color:#991b1b}
        td.ctrls{font-size:9.5px;line-height:1.6}
        .ctrl{display:inline-block;margin:0 8px 1px 0;white-space:nowrap;color:#334155}
        .ctrl.ncx{color:#991b1b;font-weight:700}
        .tot{margin-top:14px;font-size:11.5px;color:#333}
        .tot b{color:#0f172a}
        .firma{margin-top:42px;font-size:11px;color:#333}
        .firma .line{display:inline-block;border-top:1px solid #333;width:260px;margin-left:8px}
        .foot{margin-top:18px;font-size:9.5px;color:#888;border-top:1px solid #e2e8f0;padding-top:6px}
        @page{size:A4 landscape;margin:12mm}
        @media print{body{padding:0}button{display:none}}
      </style></head><body>
      <div class="head">
        <div><div class="az">${rs || 'Registro di accettazione'}</div>${addr ? `<div class="azsub">${addr}</div>` : ''}</div>
        <div class="docmeta">Documento generato il ${esc(oggi)}<br/>Modulo: Registro accettazione MP</div>
      </div>
      <h1>Registro di accettazione materie prime</h1>
      <div class="sub">${esc(periodo)} · ${tot} registrazion${tot === 1 ? 'e' : 'i'}</div>
      <table><thead><tr>
        <th style="width:26px">N°</th><th>Data ric.</th><th>Data DDT</th><th>Fornitore</th>
        <th>N° DDT</th><th style="width:38px">Art.</th><th style="width:78px">Esito</th><th>Controlli eseguiti</th>
      </tr></thead><tbody>${righe || '<tr><td colspan="8" style="text-align:center;color:#888">Nessuna registrazione nel periodo</td></tr>'}</tbody></table>
      <div class="tot">Totale: <b>${tot}</b> &nbsp;·&nbsp; Conformi: <b style="color:#166534">${conf}</b> &nbsp;·&nbsp; Non conformi: <b style="color:#991b1b">${nconf}</b></div>
      <div class="firma">Firma del responsabile della verifica: <span class="line"></span></div>
      <div class="foot">Sistema HACCP LabThree · Registro di accettazione delle materie prime in ingresso</div>
      <button onclick="window.print()" style="margin-top:18px;padding:9px 18px;cursor:pointer;font-size:13px">Stampa</button>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: `1px solid ${LT.n200}`, whiteSpace: 'nowrap' };
  const tdStyle = { padding: '8px 10px', fontSize: 13, color: LT.navy, borderBottom: `1px solid ${LT.n100}`, verticalAlign: 'middle' };

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="secondary" size="sm" icon="download" onClick={stampa}>Stampa registro</Btn>
        {onApriBolle && <Btn kind="primary" size="sm" icon="plus" onClick={onApriBolle}>Nuova accettazione</Btn>}
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg, background: LT.arancioSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.stamp} size={28} stroke={LT.arancio} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Entrate
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Ricevimento materie prime
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              Registro dei controlli al ricevimento · {filtrate.length} registrazion{filtrate.length === 1 ? 'e' : 'i'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div>
              <label style={TR_LABEL}>Dal</label>
              <input style={TR_INPUT} type="date" value={dal} onChange={e => setDal(e.target.value)} />
            </div>
            <div>
              <label style={TR_LABEL}>Al</label>
              <input style={TR_INPUT} type="date" value={al} onChange={e => setAl(e.target.value)} />
            </div>
            <div>
              <label style={TR_LABEL}>Esito</label>
              <select style={TR_INPUT} value={esito} onChange={e => setEsito(e.target.value)}>
                <option value="">Tutti</option>
                <option value="C">Solo conformi</option>
                <option value="NC">Solo non conformi</option>
              </select>
            </div>
            <div>
              <label style={TR_LABEL}>Cerca</label>
              <input style={TR_INPUT} placeholder="Fornitore o n° DDT…" value={ricerca} onChange={e => setRicerca(e.target.value)} />
            </div>
          </div>
        </Card>

        {!caricando && filtrate.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>Nessuna registrazione</div>
            <div style={{ fontSize: 13, color: LT.n500 }}>Le accettazioni registrate dalle bolle di ingresso compaiono qui.</div>
          </Card>
        ) : (
          <Card padding="sm" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Data ric.</th>
                  <th style={thStyle}>Fornitore</th>
                  <th style={thStyle}>N° DDT</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Art.</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Esito</th>
                </tr>
              </thead>
              <tbody>
                {filtrate.map(b => (
                  <tr key={b.id}
                    onClick={() => onApriBolla && onApriBolla(b.id)}
                    style={{ cursor: onApriBolla ? 'pointer' : 'default' }}
                    onMouseEnter={e => { e.currentTarget.style.background = LT.n50; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={tdStyle}>{b.data_ricevimento || b.data_bolla || '—'}</td>
                    <td style={tdStyle}>
                      {b.fornitore_nome || '—'}
                      {b.committente_nome && (
                        <div style={{ fontSize: 11, color: LT.arancio, fontWeight: 600, marginTop: 2 }}>
                          conto terzi: {b.committente_nome}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: LT.mono }}>{b.numero_bolla || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{b.n_righe || 0}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {b.esito_accettazione
                        ? <Chip kind={b.esito_accettazione === 'NC' ? 'rosso' : 'verde'} size="sm" icon={b.esito_accettazione === 'NC' ? 'x' : 'check'}>
                            {b.esito_accettazione === 'NC' ? 'NC' : 'Conf.'}
                          </Chip>
                        : <span style={{ color: LT.n400 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}

function GestioneBolleIngresso({ operatore, onBack, initBollaId, onInitConsumed }) {
  const idAzienda = operatore.id_azienda;
  const [lista,      setLista]     = useState([]);
  const [form,       setForm]      = useState(null);   // null=lista; {id?}=form
  const [dettaglio,  setDettaglio] = useState(null);
  const [ricerca,    setRicerca]   = useState('');
  const [salvando,   setSalvando]  = useState(false);
  const [errore,     setErrore]    = useState('');
  const [caricando,  setCaricando] = useState(false);
  const [fornitori,  setFornitori] = useState([]);
  const [ingredienti,setIngredienti]= useState([]);
  const [clienti,    setClienti]   = useState([]);
  const [modelli,    setModelli]   = useState([]);
  const [origineRicId, setOrigineRicId] = useState(null);
  const [allegatiBolla, setAllegatiBolla] = useState(null); // bolla di cui si vede la cartella allegati
  const [quickForn,  setQuickForn]  = useState(null); // {ragione_sociale, categoria} | null
  const [quickSaving,setQuickSaving] = useState(false);

  useEffect(() => {
    (async () => {
      await carica();
      // Apertura diretta del dettaglio arrivando dal registro ricevimento.
      if (initBollaId) {
        await apriDettaglio(initBollaId);
        if (onInitConsumed) onInitConsumed();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carica = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/bolle-ingresso?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
  };

  const caricaAnagrafiche = async () => {
    const [rFor, rIngr, rCli, rMod, rOrig] = await Promise.all([
      apiFetch(`${API}/tracciabilita/fornitori?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/ingredienti?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/clienti?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/accettazione-modelli?idAzienda=${idAzienda}&soloAttivi=1`).then(r => r.json()),
      apiFetch(`${API}/admin/origini-nc?soloAttive=1`).then(r => r.json()).catch(() => []),
    ]);
    setFornitori(Array.isArray(rFor)  ? rFor.filter(f => f.attivo)  : []);
    setIngredienti(Array.isArray(rIngr) ? rIngr.filter(i => i.attivo) : []);
    setClienti(Array.isArray(rCli)  ? rCli.filter(c => c.attivo)  : []);
    const mods = Array.isArray(rMod) ? rMod : [];
    setModelli(mods);
    const orig = Array.isArray(rOrig)
      ? rOrig.find(o => (o.descrizione || '').toLowerCase().includes('ricevimento'))
      : null;
    setOrigineRicId(orig ? orig.id : null);
    return mods;
  };

  // Il lotto interno NON viene più pre-riempito nel form: se il campo resta
  // vuoto lo genera il backend (formato L.GGMMAA) con de-duplica automatica
  // per azienda+ingrediente. Prima il pre-riempimento (uguale per tutte le
  // righe) bypassava la de-duplica e produceva lotti interni duplicati.
  const nuovaRiga = (_dataBolla) => ({
    id_ingrediente: '', lotto_fornitore: '', lotto_interno: '',
    quantita: '', unita_misura: '', data_scadenza: '', note: '',
  });

  const apriNuovo = async () => {
    setCaricando(true);
    const mods = await caricaAnagrafiche();
    const oggi = new Date().toISOString().slice(0, 10);
    setForm({
      data_bolla: oggi, data_ricevimento: oggi, numero_bolla: '',
      id_fornitore: '', id_committente: '',
      id_modello_accettazione: mods[0] ? String(mods[0].id) : '',
      controlli: {},
      righe: [nuovaRiga(oggi)],
    });
    setErrore('');
    setCaricando(false);
  };

  const apriDettaglio = async (id) => {
    setCaricando(true);
    const res = await apiFetch(`${API}/tracciabilita/bolle-ingresso/${id}`);
    setDettaglio(await res.json());
    setCaricando(false);
  };

  const apriModifica = async (bolla) => {
    setCaricando(true);
    await caricaAnagrafiche();
    const controlliObj = {};
    if (Array.isArray(bolla.controlli)) bolla.controlli.forEach(c => { controlliObj[c.key] = c.valore; });
    setForm({
      id:             bolla.id,
      numero_bolla:   bolla.numero_bolla  || '',
      data_bolla:     bolla.data_bolla?.slice(0, 10) || '',
      data_ricevimento: bolla.data_ricevimento?.slice(0, 10) || bolla.data_bolla?.slice(0, 10) || '',
      id_fornitore:   bolla.id_fornitore   || '',
      id_committente: bolla.id_committente || '',
      id_modello_accettazione: bolla.id_modello_accettazione ? String(bolla.id_modello_accettazione) : '',
      controlli:      controlliObj,
    });
    setDettaglio(null);
    setErrore('');
    setCaricando(false);
  };

  const aggiungiRiga = () => setForm(f => ({ ...f, righe: [...f.righe, nuovaRiga(f.data_bolla)] }));
  const rimuoviRiga  = (i) => setForm(f => ({ ...f, righe: f.righe.filter((_, idx) => idx !== i) }));
  const aggiornaRiga = (i, campo, val) => setForm(f => ({ ...f, righe: f.righe.map((r, idx) => idx !== i ? r : { ...r, [campo]: val }) }));

  // Creazione rapida di un fornitore senza uscire dal form bolla.
  const creaFornitoreRapido = async () => {
    const rs = (quickForn.ragione_sociale || '').trim();
    if (!rs) return;
    setQuickSaving(true);
    try {
      const res = await apiFetch(`${API}/tracciabilita/fornitori`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idAzienda, ragioneSociale: rs, categoria: quickForn.categoria || 'MATERIA_PRIMA' }),
      });
      if (!res.ok) throw new Error();
      // Ricarica l'anagrafica fornitori e seleziona quello appena creato.
      const lst = await apiFetch(`${API}/tracciabilita/fornitori?idAzienda=${idAzienda}`).then(r => r.json());
      const attivi = Array.isArray(lst) ? lst.filter(f => f.attivo) : [];
      setFornitori(attivi);
      const nuovo = attivi.find(f => (f.ragione_sociale || '').trim().toLowerCase() === rs.toLowerCase());
      if (nuovo) setForm(f => ({ ...f, id_fornitore: String(nuovo.id) }));
      setQuickForn(null);
      toast('✓ Fornitore creato');
    } catch { setErrore('Errore nella creazione del fornitore'); }
    setQuickSaving(false);
  };

  // Se l'esito dei controlli è NC, propone di aprire una Non Conformità collegata alla bolla.
  const proponiNC = async (idBolla, controlliArr) => {
    const ok = await chiediConferma(
      'Esito NON CONFORME',
      'Sono presenti controlli non conformi al ricevimento. Vuoi aprire una Non Conformità nel registro?'
    );
    if (!ok) return;
    const falliti = controlliArr.filter(c => c.esito === 'NC').map(c => c.label);
    const forn = fornitori.find(f => String(f.id) === String(form.id_fornitore));
    const evento =
      `Accettazione materie prime non conforme` +
      (forn ? ` — fornitore ${forn.ragione_sociale}` : '') +
      (form.numero_bolla ? `, DDT ${form.numero_bolla}` : '') +
      `. Controlli non conformi: ${falliti.join(', ')}.`;
    try {
      const rNc = await apiFetch(`${API}/nc`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idAzienda, idOperatore: operatore.id,
          dataNc: form.data_ricevimento || form.data_bolla,
          evento, idOrigine: origineRicId,
        }),
      });
      if (rNc.ok) {
        const { id } = await rNc.json();
        await apiFetch(`${API}/tracciabilita/bolle-ingresso/${idBolla}/nc`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idNc: id }),
        });
        toast('✓ Non Conformità aperta');
      }
    } catch { /* la bolla resta salvata comunque */ }
  };

  const handleSalva = async () => {
    if (!form.data_bolla) { setErrore('Data bolla obbligatoria'); return; }
    // Chi consegna e di chi e' la merce sono due informazioni diverse e vanno
    // registrate tutte e due: il macello spedisce, ma le cosce possono essere di
    // un committente. Prima erano alternative e sceglierne una cancellava
    // l'altra, cosi' una bolla in conto lavorazione perdeva il fornitore.
    const idForn = form.id_fornitore   || null;
    const idComm = form.id_committente || null;
    if (!idForn) { setErrore('Indica il fornitore, cioè chi ti ha consegnato la merce'); return; }
    setErrore(''); setSalvando(true);
    try {
      const modello = modelli.find(m => String(m.id) === String(form.id_modello_accettazione)) || null;
      const controlliArr = modello ? accBuildControlli(modello, form.controlli || {}) : [];
      const esito = accEsitoComplessivo(controlliArr);
      const comune = {
        numeroBolla:   form.numero_bolla || null,
        dataBolla:     form.data_bolla,
        dataRicevimento: form.data_ricevimento || null,
        idFornitore:   idForn,
        idCommittente: idComm,
        idModelloAccettazione: modello ? modello.id : null,
        controlli:     modello ? controlliArr : null,
        esitoAccettazione: esito,
      };
      let idBolla = form.id;
      if (form.id) {
        const res = await apiFetch(`${API}/tracciabilita/bolle-ingresso/${form.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(comune),
        });
        if (!res.ok) throw new Error();
        toast('✓ Bolla aggiornata!');
      } else {
        const righeValide = (form.righe || []).filter(r => r.id_ingrediente);
        const res = await apiFetch(`${API}/tracciabilita/bolle-ingresso`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...comune,
            idAzienda,
            idOperatore: operatore.id,
            righe: righeValide.map(r => ({
              idIngrediente:  r.id_ingrediente,
              lottoFornitore: r.lotto_fornitore || null,
              lottoInterno:   r.lotto_interno   || null,
              quantita:       r.quantita        || null,
              unitaMisura:    r.unita_misura     || null,
              dataScadenza:   r.data_scadenza    || null,
              note:           r.note             || null,
            })),
          }),
        });
        if (!res.ok) throw new Error();
        const d = await res.json();
        idBolla = d.id;
        toast('✓ Bolla creata!');
      }
      if (esito === 'NC' && idBolla) await proponiNC(idBolla, controlliArr);
      setForm(null);
      await carica();
    } catch { setErrore('Errore nel salvataggio'); }
    setSalvando(false);
  };

  const inputSm = { ...TR_INPUT, padding: '7px 10px', fontSize: 13 };

  // ── FORM ──
  if (form !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => { setForm(null); setErrore(''); }}>Indietro</Btn>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.bluSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.truck} size={28} stroke={LT.blu} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Entrate
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              {form.id ? 'Modifica bolla' : 'Nuova bolla di ingresso'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {form.id ? `Bolla #${form.numero_bolla || '—'}` : 'Registra la merce in ingresso: chi te l\'ha consegnata e, se è conto lavorazione, di chi è'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Intestazione bolla */}
        <SezioneForm color="blu" icon="edit" titolo="Intestazione bolla" sotto="Numero documento, data, chi consegna e di chi è la merce">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <div>
              <label style={TR_LABEL}>N° bolla / DDT</label>
              <input
                style={TR_INPUT}
                value={form.numero_bolla || ''}
                onChange={e => setForm({ ...form, numero_bolla: e.target.value })}
                placeholder="Es. 2024/001"
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
            </div>
            <div>
              <label style={TR_LABEL}>Data documento (DDT) *</label>
              <input
                style={TR_INPUT}
                type="date"
                value={form.data_bolla || ''}
                onChange={e => setForm({ ...form, data_bolla: e.target.value })}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
            </div>
            <div>
              <label style={TR_LABEL}>Data ricevimento</label>
              <input
                style={TR_INPUT}
                type="date"
                value={form.data_ricevimento || ''}
                onChange={e => setForm({ ...form, data_ricevimento: e.target.value })}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              />
            </div>
          </div>

          {/* Chi consegna e di chi e' la merce sono due cose diverse: il
              macello spedisce, ma le cosce possono essere di un committente.
              Prima i due campi erano alternativi e sceglierne uno cancellava
              l'altro; ora convivono. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label style={TR_LABEL}>Fornitore — chi ti ha consegnato la merce *</label>
            {!quickForn && (
              <Btn kind="ghost" size="sm" icon="plus" onClick={() => setQuickForn({ ragione_sociale: '', categoria: 'MATERIA_PRIMA' })}>
                Nuovo
              </Btn>
            )}
          </div>
          {quickForn ? (
            <div style={{ border: `1px solid ${LT.blu}40`, background: LT.bluSoft, borderRadius: LT.r.md, padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: LT.blu, marginBottom: 8 }}>Nuovo fornitore</div>
              <input
                style={{ ...TR_INPUT, marginBottom: 8 }}
                value={quickForn.ragione_sociale}
                onChange={e => setQuickForn({ ...quickForn, ragione_sociale: e.target.value })}
                placeholder="Ragione sociale *"
                autoFocus
              />
              <select
                style={{ ...TR_INPUT, marginBottom: 8 }}
                value={quickForn.categoria}
                onChange={e => setQuickForn({ ...quickForn, categoria: e.target.value })}
              >
                {CAT_FORNITORE.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn kind="secondary" size="sm" onClick={() => setQuickForn(null)} style={{ flex: 1 }}>Annulla</Btn>
                <Btn kind="primary" size="sm" icon="check" onClick={creaFornitoreRapido}
                  disabled={quickSaving || !quickForn.ragione_sociale.trim()} style={{ flex: 1 }}>
                  {quickSaving ? '…' : 'Crea e seleziona'}
                </Btn>
              </div>
            </div>
          ) : (
            <select
              style={TR_INPUT}
              value={form.id_fornitore || ''}
              onChange={e => setForm({ ...form, id_fornitore: e.target.value })}
              onFocus={e => trFocus(e, LT.blu)}
              onBlur={trBlur}
            >
              <option value="">— seleziona fornitore —</option>
              {fornitori.map(f => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
            </select>
          )}

          <label style={TR_LABEL}>Committente — di chi è la merce</label>
          <select
            style={TR_INPUT}
            value={form.id_committente || ''}
            onChange={e => setForm({ ...form, id_committente: e.target.value })}
            onFocus={e => trFocus(e, LT.arancio)}
            onBlur={trBlur}
          >
            <option value="">— la merce è mia —</option>
            {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
          </select>
          <div style={{ fontSize: 12, color: LT.n500, marginTop: 6, lineHeight: 1.5 }}>
            Da compilare solo in conto lavorazione, quando la merce non è tua: la ricevi,
            la lavori e la restituisci. Il fornitore resta chi te l'ha materialmente
            consegnata, che può essere un soggetto diverso dal proprietario.
          </div>
          {form.id_committente && (
            <div style={{ marginTop: 8 }}>
              <ChipContoTerzi committente={(clienti.find(c => String(c.id) === String(form.id_committente)) || {}).ragione_sociale} />
            </div>
          )}
        </SezioneForm>

        {/* Controlli al ricevimento */}
        <SezioneForm color="arancio" icon="shield" titolo="Controlli al ricevimento" sotto="Esito dei controlli di accettazione secondo il modello scelto">
          {modelli.length === 0 ? (
            <div style={{ fontSize: 13, color: LT.n500 }}>
              Nessun modello di accettazione configurato. Puoi crearne uno in Tracciabilità · Configurazione · Modelli di accettazione.
            </div>
          ) : (
            <>
              {modelli.length > 1 && (
                <div style={{ marginBottom: 10 }}>
                  <label style={TR_LABEL}>Modello di controllo</label>
                  <select
                    style={TR_INPUT}
                    value={form.id_modello_accettazione || ''}
                    onChange={e => setForm({ ...form, id_modello_accettazione: e.target.value, controlli: {} })}
                  >
                    {modelli.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </select>
                </div>
              )}
              {(() => {
                const modello = modelli.find(m => String(m.id) === String(form.id_modello_accettazione));
                const voci = (modello && modello.voci) || [];
                const setVal = (k, v) => setForm(f => ({ ...f, controlli: { ...(f.controlli || {}), [k]: v } }));
                const arr = modello ? accBuildControlli(modello, form.controlli || {}) : [];
                const esito = accEsitoComplessivo(arr);
                return (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {voci.map(v => {
                        const val = (form.controlli || {})[v.key];
                        return (
                          <div key={v.key} style={{
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            padding: '8px 10px', borderRadius: LT.r.md, background: LT.n50, border: `1px solid ${LT.n150}`,
                          }}>
                            <div style={{ flex: '1 1 200px', fontSize: 13, color: LT.navy, fontWeight: 600 }}>
                              {v.label}{v.obbligatorio !== false ? ' *' : ''}
                            </div>
                            {v.tipo === 'cn' && (
                              <div style={{ display: 'flex', gap: 6 }}>
                                {[['C', 'Conforme', LT.verde], ['NC', 'Non conf.', LT.rosso]].map(([code, lab, col]) => {
                                  const on = val === code;
                                  return (
                                    <button key={code} type="button"
                                      onClick={() => setVal(v.key, on ? undefined : code)}
                                      style={{
                                        padding: '6px 12px', borderRadius: LT.r.md,
                                        border: `1.5px solid ${on ? col : LT.n200}`,
                                        background: on ? col : LT.n0, color: on ? LT.n0 : LT.navy,
                                        fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: LT.sans,
                                      }}>{lab}</button>
                                  );
                                })}
                              </div>
                            )}
                            {v.tipo === 'numero' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input type="text" inputMode="decimal" style={{ ...TR_INPUT, width: 90 }}
                                  value={val ?? ''} onChange={e => setVal(v.key, decVal(e.target.value))} placeholder="valore" />
                                {v.um && <span style={{ fontSize: 12.5, color: LT.n500 }}>{v.um}</span>}
                                {(v.min != null || v.max != null) && (
                                  <span style={{ fontSize: 11.5, color: LT.n400 }}>({v.min ?? '–'}/{v.max ?? '–'})</span>
                                )}
                                {accEsitoVoce(v, val) === 'NC' && <Chip kind="rosso" size="sm" icon="alert">fuori soglia</Chip>}
                              </div>
                            )}
                            {v.tipo === 'flag' && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: LT.n700, cursor: 'pointer' }}>
                                <input type="checkbox" checked={val === true} onChange={e => setVal(v.key, e.target.checked)} /> Sì
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {esito && (
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12.5, color: LT.n500, fontWeight: 600 }}>Esito complessivo:</span>
                        <Chip kind={esito === 'NC' ? 'rosso' : 'verde'} icon={esito === 'NC' ? 'x' : 'check'}>
                          {esito === 'NC' ? 'NON CONFORME' : 'CONFORME'}
                        </Chip>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </SezioneForm>

        {/* Articoli — solo per nuova bolla */}
        {!form.id && (
          <SezioneForm color="verde" icon="package" titolo="Articoli ricevuti" sotto="Aggiungi le righe della bolla con ingrediente, lotto, quantità e scadenza">
            {form.righe.map((r, i) => (
              <div key={i} style={{
                background: LT.verdeSoft, border: `1px solid ${LT.verde}30`,
                borderRadius: LT.r.md, padding: '10px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: LT.verde, color: LT.n0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 11, flexShrink: 0,
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: LT.verde, flex: 1 }}>Articolo {i + 1}</div>
                  <Btn kind="ghost" size="sm" icon="x" onClick={() => rimuoviRiga(i)} disabled={form.righe.length === 1} />
                </div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={r.id_ingrediente}
                    onChange={e => aggiornaRiga(i, 'id_ingrediente', e.target.value)}
                    style={{ ...inputSm, flex: '1 1 180px' }}
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  >
                    <option value="">— ingrediente —</option>
                    {ingredienti.map(ing => <option key={ing.id} value={ing.id}>{ing.nome}</option>)}
                  </select>
                  <input
                    placeholder="Lotto fornitore"
                    value={r.lotto_fornitore}
                    onChange={e => aggiornaRiga(i, 'lotto_fornitore', e.target.value)}
                    style={{ ...inputSm, flex: '1 1 120px' }}
                    title="Il lotto scritto dal fornitore sul DDT o sull'etichetta. Se la consegna non ne ha uno, a identificarla resta il numero di DDT."
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  />
                  <input
                    placeholder="Lotto interno (auto)"
                    value={r.lotto_interno}
                    onChange={e => aggiornaRiga(i, 'lotto_interno', e.target.value)}
                    style={{ ...inputSm, flex: '1 1 100px' }}
                    title="Lascia vuoto per generarlo automaticamente (L.GGMMAA, senza duplicati)"
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  />
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text" inputMode="decimal" placeholder="Qtà"
                    value={r.quantita}
                    onChange={e => aggiornaRiga(i, 'quantita', decVal(e.target.value))}
                    style={{ ...inputSm, flex: '0 0 78px' }}
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  />
                  <select
                    value={r.unita_misura}
                    onChange={e => aggiornaRiga(i, 'unita_misura', e.target.value)}
                    style={{ ...inputSm, flex: '0 0 64px' }}
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  >
                    <option value="">UM</option>
                    {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <span style={{ fontSize: 11, color: LT.n500, fontWeight: 600, whiteSpace: 'nowrap' }}>Scad.</span>
                  <input
                    type="date"
                    value={r.data_scadenza}
                    onChange={e => aggiornaRiga(i, 'data_scadenza', e.target.value)}
                    style={{ ...inputSm, flex: '0 0 140px' }}
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  />
                  <input
                    placeholder="Note"
                    value={r.note}
                    onChange={e => aggiornaRiga(i, 'note', e.target.value)}
                    style={{ ...inputSm, flex: '1 1 100px' }}
                    onFocus={e => trFocus(e, LT.verde)}
                    onBlur={trBlur}
                  />
                </div>
              </div>
            ))}
            <Btn kind="secondary" size="sm" icon="plus" onClick={aggiungiRiga}>
              Aggiungi riga
            </Btn>
          </SezioneForm>
        )}

        {errore && (
          <Card padding="md" style={{ background: LT.rossoSoft, border: `1px solid ${LT.rosso}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: LT.rosso, fontSize: 13, fontWeight: 600 }}>
              <Ico d={I.alert} size={16} stroke={LT.rosso} />
              {errore}
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn kind="secondary" onClick={() => { setForm(null); setErrore(''); }} style={{ flex: 1 }}>
            Annulla
          </Btn>
          <Btn kind="primary" icon="check" onClick={handleSalva} disabled={salvando} style={{ flex: 2 }}>
            {salvando ? 'Salvataggio…' : (form.id ? 'Salva modifiche' : 'Salva bolla')}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── DETTAGLIO BOLLA ──
  if (dettaglio !== null) return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={() => setDettaglio(null)}>Lista</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="ghost" size="sm" icon="folder" onClick={() => setAllegatiBolla(dettaglio)}
             title="DDT scansionato, foto della merce, referti — si possono aggiungere anche dopo">
          Allegati{dettaglio.num_allegati > 0 ? ` (${dettaglio.num_allegati})` : ''}
        </Btn>
        <Btn kind="secondary" size="sm" icon="edit" onClick={() => apriModifica(dettaglio)} disabled={caricando}>
          Modifica intestazione
        </Btn>
      </div>

      {allegatiBolla && (
        <ModalAllegatiBolla
          tipo="ingresso"
          idBolla={allegatiBolla.id}
          titolo={`${allegatiBolla.numero_bolla ? '#' + allegatiBolla.numero_bolla + ' · ' : ''}${allegatiBolla.fornitore_nome || ''}`}
          operatore={operatore}
          onChiudi={() => { setAllegatiBolla(null); carica(); }}
        />
      )}

      <div style={{ padding: '20px 16px 12px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.bluSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.receipt} size={28} stroke={LT.blu} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Entrate
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Bolla #{dettaglio.numero_bolla || '—'}
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {dettaglio.data_bolla}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 900, margin: '0 auto' }}>
        {/* Riepilogo intestazione */}
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3 }}>Data</div>
              <div style={{ fontSize: 14, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{dettaglio.data_bolla}</div>
            </div>
            {dettaglio.fornitore_nome && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3 }}>Fornitore</div>
                <div style={{ fontSize: 14, color: LT.navy, fontWeight: 600, marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Ico d={I.factory} size={14} stroke={LT.blu} />
                  {dettaglio.fornitore_nome}
                </div>
              </div>
            )}
            {dettaglio.committente_nome && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.3 }}>Conto terzi</div>
                <div style={{ marginTop: 4 }}>
                  <Chip kind="arancio" icon="users">{dettaglio.committente_nome}</Chip>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Controlli al ricevimento */}
        {(dettaglio.esito_accettazione || (Array.isArray(dettaglio.controlli) && dettaglio.controlli.length > 0)) && (
          <Card padding="md" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ico d={I.shield} size={16} stroke={LT.arancio} />
                <span style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Controlli al ricevimento</span>
                {dettaglio.modello_nome && <Chip kind="neutro" size="sm">{dettaglio.modello_nome}</Chip>}
              </div>
              {dettaglio.esito_accettazione && (
                <Chip kind={dettaglio.esito_accettazione === 'NC' ? 'rosso' : 'verde'} icon={dettaglio.esito_accettazione === 'NC' ? 'x' : 'check'}>
                  {dettaglio.esito_accettazione === 'NC' ? 'NON CONFORME' : 'CONFORME'}
                </Chip>
              )}
            </div>
            {Array.isArray(dettaglio.controlli) && dettaglio.controlli.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dettaglio.controlli.map(c => (
                  <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                    <span style={{ flex: '1 1 auto', color: LT.n700 }}>{c.label}</span>
                    {c.tipo === 'flag' ? (
                      <Chip kind={c.valore ? 'verde' : 'neutro'} size="sm">{c.valore ? 'Sì' : 'No'}</Chip>
                    ) : c.tipo === 'numero' ? (
                      <span style={{ fontWeight: 600, color: c.esito === 'NC' ? LT.rosso : LT.navy }}>
                        {c.valore ?? '—'}{c.um ? ` ${c.um}` : ''}{c.esito ? ` · ${c.esito}` : ''}
                      </span>
                    ) : (
                      <Chip kind={c.esito === 'NC' ? 'rosso' : c.esito === 'C' ? 'verde' : 'neutro'} size="sm" icon={c.esito === 'NC' ? 'x' : c.esito === 'C' ? 'check' : undefined}>
                        {c.esito === 'NC' ? 'NC' : c.esito === 'C' ? 'Conforme' : '—'}
                      </Chip>
                    )}
                  </div>
                ))}
              </div>
            )}
            {dettaglio.id_nc && (
              <div style={{ marginTop: 10, fontSize: 12, color: LT.rosso, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Ico d={I.alert} size={13} stroke={LT.rosso} /> Non Conformità collegata (registro NC)
              </div>
            )}
          </Card>
        )}

        {/* Articoli ricevuti */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
          fontSize: 12, fontWeight: 700, color: LT.n700, textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
          <Ico d={I.package} size={14} stroke={LT.verde} />
          Articoli ricevuti ({dettaglio.righe?.length || 0})
        </div>

        {(!dettaglio.righe || !dettaglio.righe.length) ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy }}>Nessun articolo in questa bolla</div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(dettaglio.righe || []).map(r => {
              const cfg = STATO_MAG_COLORI[r.stato] || {};
              return (
                <Card key={r.id} padding="md">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: LT.r.md,
                      background: LT.arancioSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ico d={I.package} size={18} stroke={LT.arancio} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy }}>{r.ingrediente_nome}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: LT.n500, marginTop: 4 }}>
                        <span style={{ fontFamily: LT.mono, color: LT.n700 }}>🏷 {r.lotto_interno}</span>
                        {r.lotto_fornitore && <span>Lot.forn.: <span style={{ fontFamily: LT.mono, color: LT.n700 }}>{r.lotto_fornitore}</span></span>}
                        {r.quantita && <span style={{ color: LT.navy, fontWeight: 600 }}>{r.quantita} {r.unita_misura}</span>}
                        {r.data_scadenza && <span>Scad.: {r.data_scadenza}</span>}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                          fontSize: 11, fontWeight: 700,
                          background: cfg.bg || LT.n100, color: cfg.color || LT.n700,
                        }}>
                          {STATO_MAG_LABEL[r.stato] || r.stato}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── LISTA BOLLE ──
  const filtrate = lista.filter(b =>
    !ricerca ||
    b.fornitore_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    b.committente_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    b.numero_bolla?.toLowerCase().includes(ricerca.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: LT.n0, borderBottom: `1px solid ${LT.n200}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" icon="arrowLeft" onClick={onBack}>Indietro</Btn>
        <div style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={apriNuovo} disabled={caricando}>
          {caricando ? 'Caricamento…' : 'Nuova bolla'}
        </Btn>
      </div>

      <div style={{ padding: '20px 16px 12px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: LT.bluSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.truck} size={28} stroke={LT.blu} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tracciabilità · Entrate
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 800, color: LT.navy, letterSpacing: -0.3 }}>
              Bolle di ingresso
            </h1>
            <div style={{ fontSize: 13, color: LT.n500, marginTop: 2 }}>
              {lista.length} boll{lista.length === 1 ? 'a' : 'e'} registrat{lista.length === 1 ? 'a' : 'e'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <Card padding="md" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Ico d={I.search} size={18} stroke={LT.n500} />
            <input
              style={{ ...TR_INPUT, border: 'none', padding: 0, fontSize: 14 }}
              placeholder="Cerca fornitore, committente o numero bolla…"
              value={ricerca}
              onChange={e => setRicerca(e.target.value)}
            />
          </div>
        </Card>

        {!caricando && filtrate.length === 0 ? (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
              background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.truck} size={28} stroke={LT.n400} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
              {ricerca ? 'Nessuna bolla trovata' : 'Nessuna bolla registrata'}
            </div>
            <div style={{ fontSize: 13, color: LT.n500 }}>
              {ricerca ? 'Prova un termine di ricerca diverso' : 'Clicca "Nuova bolla" per iniziare'}
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10,
          }}>
            {filtrate.map(b => (
              <Card
                key={b.id}
                padding="md"
                style={{ cursor: 'pointer', transition: 'transform .12s, box-shadow .12s' }}
                onClick={() => apriDettaglio(b.id)}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = LT.shadow.md; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.md,
                    background: LT.bluSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Ico d={I.truck} size={18} stroke={LT.blu} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: LT.navy }}>{b.data_bolla}</div>
                      {b.numero_bolla && <Chip kind="blu" size="sm">#{b.numero_bolla}</Chip>}
                    </div>
                    {b.fornitore_nome && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 13, color: LT.n700 }}>
                        <Ico d={I.factory} size={13} stroke={LT.n500} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.fornitore_nome}</span>
                      </div>
                    )}
                    {b.committente_nome && (
                      <div style={{ marginTop: 4 }}>
                        <ChipContoTerzi committente={b.committente_nome} />
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: LT.n500, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.list} size={12} stroke={LT.n500} />
                        {b.n_righe} articol{b.n_righe === 1 ? 'o' : 'i'}
                      </span>
                      {b.esito_accettazione && (
                        <Chip kind={b.esito_accettazione === 'NC' ? 'rosso' : 'verde'} size="sm" icon={b.esito_accettazione === 'NC' ? 'x' : 'check'}>
                          {b.esito_accettazione === 'NC' ? 'NC' : 'Conforme'}
                        </Chip>
                      )}
                      {b.num_allegati > 0 && (
                        <Chip kind="neutro" size="sm" icon="folder">{b.num_allegati}</Chip>
                      )}
                    </div>
                  </div>
                  <Ico d={I.chevronR} size={16} stroke={LT.n400} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gestione Magazzino MP ─────────────────────────────────────
function GestioneMagazzinoMP({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,          setLista]          = useState([]);
  const [ricerca,        setRicerca]        = useState('');
  const [filtroStato,    setFiltroStato]    = useState('');
  const [filtroPropr,    setFiltroPropr]    = useState('');
  const [haContoTerzi,   setHaContoTerzi]   = useState(false);
  const [caricando,      setCaricando]      = useState(false);
  const [consumoAperto,  setConsumoAperto]  = useState({});  // {idLotto: dati[] | 'loading' | null}
  const [modalCorreggi,  setModalCorreggi]  = useState(null);
  const [correggendo,    setCorreggendo]    = useState(false);
  const oggi = new Date().toISOString().slice(0, 10);

  useEffect(() => { carica(); }, [filtroStato, filtroPropr]); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    setCaricando(true);
    let url = `${API}/tracciabilita/magazzino-mp?idAzienda=${idAzienda}`;
    if (filtroStato) url += `&stato=${filtroStato}`;
    if (filtroPropr) url += `&proprieta=${filtroPropr}`;
    const res  = await apiFetch(url);
    const data = await res.json();
    const righe = Array.isArray(data) ? data : [];
    setLista(righe);
    // Una volta vista, la merce di terzi resta un fatto acquisito: il filtro non
    // deve sparire quando si sceglie "solo merce mia" e la lista torna vuota.
    if (righe.some(r => r.id_committente)) setHaContoTerzi(true);
    setCaricando(false);
  };

  const cambiaStato = async (lotto, nuovoStato) => {
    await apiFetch(`${API}/tracciabilita/magazzino-mp/${lotto.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        stato:        nuovoStato,
        note:         lotto.note         || null,
        dataScadenza: lotto.data_scadenza || null,
      }),
    });
    toast(`✓ ${STATO_MAG_LABEL[nuovoStato]}`);
    await carica();
  };

  // La sezione "Articoli ricevuti" del form bolla compare solo alla creazione:
  // senza questa correzione una quantita' sbagliata al ricevimento non era piu'
  // rimediabile da nessuna schermata.
  const apriCorreggi = (l) => setModalCorreggi({
    lotto:          l,
    quantita:       l.quantita       != null ? String(l.quantita) : '',
    unitaMisura:    l.unita_misura    || '',
    lottoFornitore: l.lotto_fornitore || '',
    dataScadenza:   l.data_scadenza   || '',
    note:           l.note            || '',
  });

  const confermaCorreggi = async () => {
    const m = modalCorreggi;
    if (m.quantita !== '' && (isNaN(parseFloat(m.quantita)) || parseFloat(m.quantita) < 0))
      return toast('La quantità deve essere un numero maggiore o uguale a zero', 'errore');
    setCorreggendo(true);
    try {
      const res = await apiFetch(`${API}/tracciabilita/magazzino-mp/${m.lotto.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stato:          m.lotto.stato,
          note:           m.note || null,
          dataScadenza:   m.dataScadenza   || null,
          quantita:       m.quantita       !== '' ? m.quantita : null,
          unitaMisura:    m.unitaMisura    || null,
          lottoFornitore: m.lottoFornitore || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast(d.errore || 'Errore nella correzione', 'errore');
        return;
      }
      setModalCorreggi(null);
      await carica();
      toast('✓ Lotto corretto');
    } finally {
      setCorreggendo(false);
    }
  };

  const toggleConsumo = async (id) => {
    if (consumoAperto[id]) {
      setConsumoAperto(s => ({ ...s, [id]: null }));
      return;
    }
    setConsumoAperto(s => ({ ...s, [id]: 'loading' }));
    const res  = await apiFetch(`${API}/tracciabilita/magazzino-mp/${id}/consumo`);
    const data = await res.json();
    setConsumoAperto(s => ({ ...s, [id]: Array.isArray(data) ? data : { errore: data?.errore || 'Errore sconosciuto' } }));
  };

  const filtrati = lista.filter(l =>
    !ricerca ||
    l.ingrediente_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.lotto_interno?.toLowerCase().includes(ricerca.toLowerCase())    ||
    l.lotto_fornitore?.toLowerCase().includes(ricerca.toLowerCase())  ||
    l.fornitore_nome?.toLowerCase().includes(ricerca.toLowerCase())   ||
    l.committente_nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  // — KPI aggregati —
  const kpi = lista.reduce((acc, l) => {
    if (l.stato === 'IN_MAGAZZINO') acc.inMag++;
    else if (l.stato === 'IN_USO') acc.inUso++;
    else if (l.stato === 'ESAURITO') acc.esauriti++;
    if (l.data_scadenza && l.data_scadenza < oggi && l.stato !== 'ESAURITO') acc.scaduti++;
    return acc;
  }, { inMag: 0, inUso: 0, esauriti: 0, scaduti: 0 });

  const statoChipKind = (stato) => ({
    IN_MAGAZZINO: 'blu',
    IN_USO:       'verde',
    ESAURITO:     'neutro',
  }[stato] || 'neutro');

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans, color: LT.navy }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon={<Ico d={I.arrowLeft} s={18} />} onClick={onBack}>Menu</Btn>
        <div style={{ fontSize: 13, fontWeight: 700, color: LT.n700, letterSpacing: 0.3 }}>
          TRACCIABILITÀ · MAGAZZINO MATERIE PRIME
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.blu} 0%, #1a56b0 100%)`,
          color: LT.n0, borderRadius: LT.r.lg, padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
          boxShadow: LT.shBlu,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.md,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.package} s={32} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Magazzino materie prime</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              Lotti ingredienti ricevuti · stato, scadenze e consumi stimati
            </div>
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.bluSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LT.blu }}>
                <Ico d={I.package} s={22} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>In magazzino</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: LT.navy }}>{kpi.inMag}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.verdeSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LT.verde }}>
                <Ico d={I.flask} s={22} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>In uso</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: LT.navy }}>{kpi.inUso}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.n100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LT.n600 }}>
                <Ico d={I.x} s={22} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Esauriti</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: LT.navy }}>{kpi.esauriti}</div>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.rossoSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LT.rosso }}>
                <Ico d={I.alert} s={22} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Scaduti</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: kpi.scaduti > 0 ? LT.rosso : LT.navy }}>{kpi.scaduti}</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: haContoTerzi ? '1fr 220px 220px' : '1fr 260px', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={TR_LABEL}>Cerca</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n500, pointerEvents: 'none' }}>
                  <Ico d={I.search} s={16} />
                </div>
                <input
                  style={{ ...TR_INPUT, paddingLeft: 36 }}
                  placeholder="Ingrediente, lotto interno o fornitore…"
                  value={ricerca}
                  onChange={e => setRicerca(e.target.value)}
                  onFocus={e => trFocus(e, LT.blu)}
                  onBlur={trBlur}
                />
              </div>
            </div>
            <div>
              <label style={TR_LABEL}>Stato</label>
              <select
                style={TR_INPUT}
                value={filtroStato}
                onChange={e => setFiltroStato(e.target.value)}
                onFocus={e => trFocus(e, LT.blu)}
                onBlur={trBlur}
              >
                <option value="">Tutti gli stati</option>
                <option value="IN_MAGAZZINO">In magazzino</option>
                <option value="IN_USO">In uso</option>
                <option value="ESAURITO">Esaurito</option>
              </select>
            </div>
            {haContoTerzi && (
              <FiltroProprieta valore={filtroPropr} onChange={setFiltroPropr} colore={LT.blu} />
            )}
          </div>
        </Card>

        {caricando && (
          <Card padding="md" style={{ textAlign: 'center', color: LT.n600 }}>Caricamento…</Card>
        )}

        {/* Lista lotti */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrati.map(l => {
            const scaduto = l.data_scadenza && l.data_scadenza < oggi;
            const open    = consumoAperto[l.id];
            return (
              <Card key={l.id} padding="none" style={{ overflow: 'hidden', opacity: l.stato === 'ESAURITO' ? 0.72 : 1 }}>
                {/* Fascia colorata sinistra */}
                <div style={{ display: 'flex' }}>
                  <div style={{
                    width: 6, alignSelf: 'stretch',
                    background: scaduto ? LT.rosso : (l.stato === 'IN_USO' ? LT.verde : l.stato === 'IN_MAGAZZINO' ? LT.blu : LT.n300),
                  }} />
                  <div style={{ flex: 1, padding: 16 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.bluSoft, color: LT.blu, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Ico d={I.package} s={22} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.ingrediente_nome}</div>
                          <div style={{ fontSize: 12, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>
                            {l.lotto_interno}
                            {l.lotto_fornitore && <span style={{ marginLeft: 8, opacity: 0.75 }}>· forn. {l.lotto_fornitore}</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <ChipContoTerzi committente={l.committente_nome} />
                        {scaduto && (
                          <Chip kind="rosso" size="sm" icon={<Ico d={I.alert} s={12} />}>Scaduto</Chip>
                        )}
                        <Chip kind={statoChipKind(l.stato)} size="sm">
                          {STATO_MAG_LABEL[l.stato] || l.stato}
                        </Chip>
                      </div>
                    </div>

                    {/* Griglia info */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: 10, padding: '10px 12px',
                      background: LT.n50, borderRadius: LT.r.md, marginBottom: 10,
                    }}>
                      {l.quantita != null && (
                        <div>
                          <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Quantità</div>
                          <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>
                            {l.quantita} <span style={{ color: LT.n600, fontWeight: 400 }}>{l.unita_misura}</span>
                          </div>
                        </div>
                      )}
                      {l.data_scadenza && (
                        <div>
                          <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Scadenza</div>
                          <div style={{ fontSize: 13, color: scaduto ? LT.rosso : LT.navy, fontWeight: scaduto ? 700 : 600, marginTop: 2 }}>
                            {l.data_scadenza}
                          </div>
                        </div>
                      )}
                      {l.fornitore_nome && (
                        <div>
                          <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Fornitore</div>
                          <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Ico d={I.factory} s={14} /> {l.fornitore_nome}
                          </div>
                        </div>
                      )}
                      {l.committente_nome && (
                        <div>
                          <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Proprietario</div>
                          <div style={{ fontSize: 13, color: LT.arancio, fontWeight: 700, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Ico d={I.users} s={14} /> {l.committente_nome}
                          </div>
                        </div>
                      )}
                      {l.numero_bolla && (
                        <div>
                          <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Bolla</div>
                          <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>
                            #{l.numero_bolla} <span style={{ color: LT.n600, fontWeight: 400 }}>· {l.data_bolla}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Azioni */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {l.stato === 'IN_MAGAZZINO' && (
                        <Btn kind="primary" size="sm" icon={<Ico d={I.check} s={14} />} onClick={() => cambiaStato(l, 'IN_USO')}>
                          Metti in uso
                        </Btn>
                      )}
                      {l.stato === 'IN_USO' && (
                        <>
                          <Btn kind="secondary" size="sm" icon={<Ico d={I.arrowLeft} s={14} />} onClick={() => cambiaStato(l, 'IN_MAGAZZINO')}>
                            Rimetti in magazzino
                          </Btn>
                          <Btn kind="secondary" size="sm" icon={<Ico d={I.x} s={14} />} onClick={() => cambiaStato(l, 'ESAURITO')}
                               style={{ color: LT.rosso, borderColor: LT.rossoSoft }}>
                            Segna esaurito
                          </Btn>
                        </>
                      )}
                      <Btn kind="ghost" size="sm" icon={<Ico d={I.edit} s={14} />} onClick={() => apriCorreggi(l)}
                           title="Correggi quantità, lotto del fornitore, scadenza o note di questo lotto">
                        Correggi
                      </Btn>
                      <div style={{ marginLeft: 'auto' }}>
                        <Btn kind="ghost" size="sm" icon={<Ico d={I.chart} s={14} />} onClick={() => toggleConsumo(l.id)}>
                          {open && open !== 'loading' ? 'Nascondi consumi' : 'Dettaglio consumi'}
                        </Btn>
                      </div>
                    </div>

                    {/* Pannello consumi */}
                    {open === 'loading' && (
                      <div style={{ marginTop: 12, padding: 10, background: LT.n50, borderRadius: LT.r.md, fontSize: 12, color: LT.n600, textAlign: 'center' }}>
                        Caricamento consumi…
                      </div>
                    )}
                    {open?.errore && (
                      <div style={{ marginTop: 12, padding: '10px 12px', background: LT.rossoSoft, borderRadius: LT.r.md, fontSize: 12, color: LT.rosso, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Ico d={I.alert} s={14} /> {open.errore}
                      </div>
                    )}
                    {Array.isArray(open) && (
                      <div style={{ marginTop: 12, borderTop: `1px solid ${LT.n200}`, paddingTop: 12 }}>
                        {open.length === 0 ? (
                          <div style={{ fontSize: 12, color: LT.n600, fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
                            Nessuna lavorazione associata a questo lotto.
                          </div>
                        ) : (() => {
                          const totaleConsumato = open.reduce((acc, r) => acc + parseFloat(r.consumo_stimato || 0), 0);
                          const giacenzaTeorica = parseFloat(l.quantita || 0) - totaleConsumato;
                          const neg = giacenzaTeorica < 0;

                          const gruppi = [];
                          const idx = {};
                          for (const r of open) {
                            if (!idx[r.id_lavorazione]) {
                              idx[r.id_lavorazione] = { ...r, fasi: [] };
                              gruppi.push(idx[r.id_lavorazione]);
                            }
                            if (r.fase_nome || r.consumo_stimato != null) {
                              idx[r.id_lavorazione].fasi.push({
                                fase_nome: r.fase_nome,
                                qta_ing_ricetta: r.qta_ing_ricetta,
                                consumo_stimato: r.consumo_stimato,
                                um_ingrediente: r.um_ingrediente,
                                note: r.note,
                              });
                            }
                          }
                          const statoKind = { APERTA: 'blu', CHIUSA: 'verde', ANNULLATA: 'neutro' };

                          return (
                            <>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 14px', marginBottom: 12,
                                background: neg ? LT.rossoSoft : LT.verdeSoft,
                                border: `1px solid ${neg ? LT.rosso : LT.verde}33`,
                                borderRadius: LT.r.md,
                              }}>
                                <Ico d={I.chart} s={18} />
                                <div style={{ fontSize: 13, color: neg ? LT.rosso : LT.verde, fontWeight: 600 }}>
                                  Giacenza teorica stimata:
                                  <span style={{ fontSize: 15, marginLeft: 6, fontWeight: 700 }}>
                                    {Number(giacenzaTeorica).toFixed(3).replace(/\.?0+$/, '')} {l.unita_misura}
                                  </span>
                                </div>
                                <div style={{ fontSize: 11, color: LT.n600, marginLeft: 'auto' }}>
                                  Residuo {l.quantita} {l.unita_misura} − prenotato {Number(totaleConsumato).toFixed(3).replace(/\.?0+$/, '')} {l.unita_misura}
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {gruppi.map(g => (
                                  <div key={g.id_lavorazione} style={{
                                    background: LT.bluSoft, borderRadius: LT.r.md,
                                    padding: '10px 12px', border: `1px solid ${LT.blu}22`,
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: LT.navy }}>
                                        <Ico d={I.factory} s={14} /> {g.prodotto}
                                        {g.ricetta && <span style={{ fontWeight: 400, color: LT.n600, fontSize: 11 }}>({g.ricetta})</span>}
                                      </div>
                                      <Chip kind={statoKind[g.stato_lavorazione] || 'neutro'} size="sm">{g.stato_lavorazione}</Chip>
                                    </div>
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: LT.n700, marginBottom: 6 }}>
                                      <span style={{ fontFamily: LT.mono }}>{g.lotto_prodotto || '—'}</span>
                                      {g.data_inizio && <span>{g.data_inizio}{g.data_fine ? ` → ${g.data_fine}` : ''}</span>}
                                      {g.qta_lav && <span>{g.qta_lav} {g.um_lav}</span>}
                                    </div>
                                    {g.fasi.length > 0 ? (
                                      <div style={{ overflowX: 'auto', background: LT.n0, borderRadius: LT.r.sm, border: `1px solid ${LT.n200}` }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                          <thead>
                                            <tr style={{ background: LT.n100 }}>
                                              <th style={{ padding: '6px 10px', textAlign: 'left', color: LT.n700, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>Fase</th>
                                              <th style={{ padding: '6px 10px', textAlign: 'right', color: LT.n700, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>Q.tà ricetta</th>
                                              <th style={{ padding: '6px 10px', textAlign: 'right', color: LT.n700, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>Consumo</th>
                                              <th style={{ padding: '6px 10px', textAlign: 'left', color: LT.n700, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>Note</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {g.fasi.map((f, i) => (
                                              <tr key={i} style={{ borderTop: `1px solid ${LT.n200}` }}>
                                                <td style={{ padding: '6px 10px', color: LT.navy }}>{f.fase_nome || <em style={{ color: LT.n500 }}>—</em>}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', color: LT.n700 }}>
                                                  {f.qta_ing_ricetta != null ? `${parseFloat(f.qta_ing_ricetta)} ${f.um_ingrediente || ''}` : '—'}
                                                </td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', color: LT.blu, fontWeight: 700 }}>
                                                  {f.consumo_stimato != null ? `${parseFloat(f.consumo_stimato)} ${f.um_ingrediente || ''}` : '—'}
                                                </td>
                                                <td style={{ padding: '6px 10px', color: LT.n600 }}>{f.note || ''}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 11, color: LT.n500, fontStyle: 'italic' }}>Quantità non ricavabile dalla ricetta</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {!caricando && filtrati.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center', marginTop: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: LT.n100, color: LT.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Ico d={I.package} s={28} />
            </div>
            <div style={{ fontSize: 14, color: LT.n600 }}>Nessun lotto trovato</div>
          </Card>
        )}
      </div>

      {/* Correzione dei dati di ricevimento */}
      {modalCorreggi && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: LT.n0, borderRadius: LT.r.lg, padding: 20, maxWidth: 460, width: '100%', boxShadow: LT.sh3, fontFamily: LT.sans, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.bluSoft, color: LT.blu, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.edit} s={20} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: LT.navy }}>Correggi il lotto</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n600, marginBottom: 6 }}>
              {modalCorreggi.lotto.ingrediente_nome} — <span style={{ fontFamily: LT.mono }}>{modalCorreggi.lotto.lotto_interno}</span>
            </div>
            <div style={{ fontSize: 12, color: LT.n500, marginBottom: 14, lineHeight: 1.5 }}>
              Serve a rimediare a un dato digitato male al ricevimento. Il lotto interno e
              l'ingrediente non si toccano: se sono sbagliati quelli, la riga va rifatta.
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={TR_LABEL}>Quantità</label>
                <input
                  style={TR_INPUT} type="text" inputMode="decimal"
                  value={modalCorreggi.quantita}
                  onChange={e => setModalCorreggi(m => ({ ...m, quantita: decVal(e.target.value) }))}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={TR_LABEL}>UM</label>
                <select
                  style={TR_INPUT}
                  value={modalCorreggi.unitaMisura}
                  onChange={e => setModalCorreggi(m => ({ ...m, unitaMisura: e.target.value }))}
                  onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
                >
                  <option value="">—</option>
                  {UNITA_MISURA.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <label style={TR_LABEL}>Lotto del fornitore</label>
            <input
              style={TR_INPUT}
              value={modalCorreggi.lottoFornitore}
              onChange={e => setModalCorreggi(m => ({ ...m, lottoFornitore: e.target.value }))}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
            />

            <label style={TR_LABEL}>Scadenza</label>
            <input
              style={TR_INPUT} type="date"
              value={modalCorreggi.dataScadenza}
              onChange={e => setModalCorreggi(m => ({ ...m, dataScadenza: e.target.value }))}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
            />

            <label style={TR_LABEL}>Note</label>
            <input
              style={TR_INPUT}
              value={modalCorreggi.note}
              onChange={e => setModalCorreggi(m => ({ ...m, note: e.target.value }))}
              onFocus={e => trFocus(e, LT.blu)} onBlur={trBlur}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <Btn kind="secondary" onClick={() => setModalCorreggi(null)} style={{ flex: 1 }}>Annulla</Btn>
              <Btn kind="primary" icon={<Ico d={I.check} s={16} />} onClick={confermaCorreggi} disabled={correggendo} style={{ flex: 1 }}>
                {correggendo ? 'Salvataggio…' : 'Salva correzione'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Gestione Lavorazioni di Produzione ───────────────────────
function GestioneLavorazioni({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const oggi = new Date().toISOString().slice(0, 10);

  const genLotto = (dateStr) => {
    const d = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
    const gg = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const aa = String(d.getFullYear()).slice(2);
    return `L.${gg}${mm}${aa}`;
  };

  const FORM_VUOTO = {
    idRicetta: '', idProdotto: '', lottoProdotto: genLotto(oggi),
    dataInizio: oggi, quantitaPrevista: '', unitaMisura: '', note: '', idCommittente: '',
  };

  const [vista,          setVista]          = useState('lista');
  const [lista,          setLista]          = useState([]);
  const [dettaglio,      setDettaglio]      = useState(null);
  const [ricettaDett,    setRicettaDett]    = useState(null);
  const [filtroStato,    setFiltroStato]    = useState('APERTA');
  const [ricerca,        setRicerca]        = useState('');
  const [caricando,      setCaricando]      = useState(false);
  const [form,           setForm]           = useState(FORM_VUOTO);
  const [ricette,        setRicette]        = useState([]);
  const [prodotti,       setProdotti]       = useState([]);
  const [clientiLav,     setClientiLav]     = useState([]);
  const [lottiMp,        setLottiMp]        = useState([]);
  const [mostraAddMp,    setMostraAddMp]    = useState(false);
  const [mpSel,          setMpSel]          = useState('');

  const [modalMpSuggeriti,       setModalMpSuggeriti]       = useState([]);
  const [mpSuggeriteSelezionate, setMpSuggeriteSelezionate] = useState(new Set());
  const [nuovaLavorazioneId,     setNuovaLavorazioneId]     = useState(null);
  const [mostraAddSl,   setMostraAddSl]   = useState(false);
  const [slSel,         setSlSel]         = useState('');
  const [lottiSl,       setLottiSl]       = useState([]);
  const [modalAvvio,    setModalAvvio]    = useState(null);
  const [modalChiusura, setModalChiusura] = useState(null);
  const [modalAnnulla,  setModalAnnulla]  = useState(null);
  const [disponibilita, setDisponibilita] = useState([]);
  const [ubicazioni,      setUbicazioni]      = useState([]);
  const [ubicazioniFase,  setUbicazioniFase]  = useState([]);
  const [modalSposta,     setModalSposta]     = useState(null);
  const [modalScarta,     setModalScarta]     = useState(null);
  const [modalAvanza,     setModalAvanza]     = useState(null); // {idFaseCorrente, idFaseProssima, nomeFase}
  const [avanziAvanza,    setAvanziAvanza]    = useState([]);   // [{idUbicazioneDa, idUbicazioneA, quantitaPz, pesoKg}]
  const [datiSposta,      setDatiSposta]      = useState({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: '' });
  const [datiScarta,      setDatiScarta]      = useState({ quantitaPz: '', pesoKg: '', motivazione: '', dataMovimento: '' });
  // Due elenchi di giacenze, volutamente separati.
  //   giacenzeInCorso → quello che si vede nel riquadro "Posizioni prodotto"
  //                     sotto la fase aperta. Segue sempre e solo la fase in corso.
  //   giacenzeFase    → copia di servizio caricata dalle finestre (Chiusura,
  //                     Avanza) per la fase su cui la finestra sta lavorando.
  // Prima erano un'unica variabile: aprendo "Avanza" su una fase gia' completata
  // il riquadro dell'elenco si svuotava, perche' le due cose si sovrascrivevano.
  const [giacenzeFase,    setGiacenzeFase]    = useState([]);
  const [giacenzeInCorso, setGiacenzeInCorso] = useState([]);
  const [giacenzeFasePrecedente, setGiacenzeFasePrecedente] = useState([]);
  const [ubicazioniUscita,   setUbicazioniUscita]   = useState([]); // "Dove va il prodotto?" in chiusura
  // Chiusura lavorazione: dove si trova il prodotto adesso (letto dal server)
  // e dove l'operatore dichiara che resta.
  const [giacenzeLavorazione,   setGiacenzeLavorazione]   = useState([]);
  const [destinazioniChiusura,  setDestinazioniChiusura]  = useState([]); // [{idFaseDa, idUbicazioneDa, idUbicazione, quantitaPz, pesoKg}]
  const [mostraAddUbFase,    setMostraAddUbFase]    = useState(false);
  const [formAddUbFase,      setFormAddUbFase]      = useState({ idUbicazione: '', quantitaPz: '', pesoKg: '' });

  useEffect(() => { caricaLista(); }, [filtroStato]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { caricaSupporto(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaLista = async () => {
    setCaricando(true);
    let url = `${API}/tracciabilita/lavorazioni?idAzienda=${idAzienda}`;
    if (filtroStato) url += `&stato=${filtroStato}`;
    const res  = await apiFetch(url);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
    setCaricando(false);
  };

  const caricaSupporto = async () => {
    const [rRic, rProd, rUb, rCli] = await Promise.all([
      apiFetch(`${API}/tracciabilita/ricette?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/prodotti?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/ubicazioni?idAzienda=${idAzienda}`).then(r => r.json()),
      apiFetch(`${API}/tracciabilita/clienti?idAzienda=${idAzienda}`).then(r => r.json()),
    ]);
    setRicette(Array.isArray(rRic)  ? rRic  : []);
    setProdotti(Array.isArray(rProd) ? rProd : []);
    setUbicazioni((Array.isArray(rUb) ? rUb : []).filter(u => u.attivo));
    setClientiLav(Array.isArray(rCli) ? rCli.filter(c => c.attivo) : []);
  };

  const apriDettaglio = async (lav) => {
    setCaricando(true);
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni/${lav.id}`);
    const data = await res.json();
    setDettaglio(data);
    setMostraAddMp(false);
    setMpSel('');
    setMostraAddSl(false);
    setMostraAddUbFase(false);
    setSlSel('');
    if (data.id_ricetta) {
      const rRes  = await apiFetch(`${API}/tracciabilita/ricette/${data.id_ricetta}`);
      const rData = await rRes.json();
      setRicettaDett(rData);
    } else {
      setRicettaDett(null);
    }
    const promises = [
      apiFetch(`${API}/tracciabilita/magazzino-mp?idAzienda=${idAzienda}&stato=IN_USO`),
      apiFetch(`${API}/tracciabilita/magazzino-pf?idAzienda=${idAzienda}&categoriaProdotto=SEMILAVORATO`),
    ];
    if (data.id_ricetta) promises.push(apiFetch(`${API}/tracciabilita/lavorazioni/${data.id}/disponibilita`));
    const [mpRes, slRes, dispRes] = await Promise.all(promises);
    const mpData   = await mpRes.json();
    const slData   = await slRes.json();
    const dispData = dispRes ? await dispRes.json() : [];
    setLottiMp(Array.isArray(mpData) ? mpData : []);
    setLottiSl((Array.isArray(slData) ? slData : []).filter(l => l.stato === 'IN_USO' || l.stato === 'PARZIALE' || l.stato === 'DISPONIBILE' || !l.stato));
    setDisponibilita(Array.isArray(dispData) ? dispData : []);
    const faseAperta = (data.fasi_eseguite || []).find(f => !f.data_fine);
    if (faseAperta?.id) await aggiornaGiacenzeInCorso(faseAperta.id);
    else setGiacenzeInCorso([]);
    setVista('dettaglio');
    setCaricando(false);
  };

  const aggiornaDettaglio = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}`);
    const data = await res.json();
    setDettaglio(data);
    const faseAperta = (data.fasi_eseguite || []).find(f => !f.data_fine);
    if (faseAperta?.id) await aggiornaGiacenzeInCorso(faseAperta.id);
    else setGiacenzeInCorso([]);
  };

  const caricaDisponibilita = async (idLavorazione) => {
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni/${idLavorazione}/disponibilita`);
    const data = await res.json();
    setDisponibilita(Array.isArray(data) ? data : []);
  };

  // Legge i movimenti di una fase e calcola quanto resta in ogni ubicazione.
  // Non scrive niente nello stato: restituisce l'elenco a chi lo ha chiesto,
  // cosi' lo stesso conto puo' servire sia l'elenco sia le finestre.
  const calcolaGiacenzeFase = async (idFase) => {
    if (!idFase) return [];
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${idFase}/ubicazioni`);
    const movimenti = await res.json();
    if (!Array.isArray(movimenti)) return null; // risposta inattesa: non tocchiamo niente
    const map = {};
    for (const m of movimenti) {
      if (!m.tipo) continue; // salta record vecchi senza tipo
      const k = m.id_ubicazione;
      if (!map[k]) map[k] = { id_ubicazione: k, nome_ubicazione: m.nome_ubicazione, pz_attuali: 0, peso_attuale: 0 };
      const sign = m.tipo === 'ENTRATA' ? 1 : -1;
      map[k].pz_attuali   += sign * parseFloat(m.quantita_pz  || 0);
      map[k].peso_attuale += sign * parseFloat(m.peso_kg || 0);
    }
    return Object.values(map).filter(g => g.pz_attuali > 0 || g.peso_attuale > 0);
  };

  // Carica le giacenze per la finestra aperta (Chiusura / Avanza).
  const aggiornaGiacenzeFase = async (idFase) => {
    if (!idFase) return;
    const g = await calcolaGiacenzeFase(idFase);
    if (g) setGiacenzeFase(g);
  };

  // Carica le giacenze del riquadro "Posizioni prodotto" della fase in corso.
  const aggiornaGiacenzeInCorso = async (idFase) => {
    if (!idFase) { setGiacenzeInCorso([]); return; }
    const g = await calcolaGiacenzeFase(idFase);
    if (g) setGiacenzeInCorso(g);
  };

  const onRicettaChange = (idRicetta) => {
    const ric = ricette.find(r => String(r.id) === String(idRicetta));
    setForm(f => ({
      ...f,
      idRicetta,
      idProdotto:       ric?.id_prodotto       ? String(ric.id_prodotto) : f.idProdotto,
      unitaMisura:      ric?.unita_misura       || f.unitaMisura,
      quantitaPrevista: ric?.quantita_prevista  ? String(ric.quantita_prevista) : f.quantitaPrevista,
    }));
  };

  const salvaLavorazione = async () => {
    if (!form.dataInizio) return;
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idRicetta:        form.idRicetta        || null,
        idProdotto:       form.idProdotto       || null,
        lottoProdotto:    form.lottoProdotto    || null,
        dataInizio:       form.dataInizio,
        quantitaPrevista: form.quantitaPrevista || null,
        unitaMisura:      form.unitaMisura      || null,
        note:             form.note             || null,
        idCommittente:    form.idCommittente    || null,
        idAzienda, idOperatore: operatore.id,
      }),
    });
    const data = await res.json();
    if (!res.ok) return toast('Errore: ' + (data.errore || 'sconosciuto'));
    toast('✓ Lavorazione creata');
    const newId = data.id;
    const idRicettaSalvata = form.idRicetta;
    setForm(FORM_VUOTO);

    // Se c'è una ricetta, cerca lotti MP in uso coerenti con gli ingredienti
    if (idRicettaSalvata) {
      const [rRic, rMp] = await Promise.all([
        apiFetch(`${API}/tracciabilita/ricette/${idRicettaSalvata}`).then(r => r.json()),
        apiFetch(`${API}/tracciabilita/magazzino-mp?idAzienda=${idAzienda}&stato=IN_USO`).then(r => r.json()),
      ]);
      const idIngrRicetta = new Set();
      if (rRic?.fasi) rRic.fasi.forEach(f => f.ingredienti?.forEach(i => i.id_ingrediente && idIngrRicetta.add(i.id_ingrediente)));
      if (rRic?.ingredienti_senza_fase) rRic.ingredienti_senza_fase.forEach(i => i.id_ingrediente && idIngrRicetta.add(i.id_ingrediente));
      const mpMatch = (Array.isArray(rMp) ? rMp : []).filter(mp => idIngrRicetta.has(mp.id_ingrediente));
      if (mpMatch.length > 0) {
        setNuovaLavorazioneId(newId);
        setModalMpSuggeriti(mpMatch);
        setMpSuggeriteSelezionate(new Set(mpMatch.map(m => m.id)));
        return; // attendi conferma modal prima di navigare
      }
    }

    setVista('lista');
    await caricaLista();
  };

  const confermaAssociazioneMp = async () => {
    for (const idMp of mpSuggeriteSelezionate) {
      await apiFetch(`${API}/tracciabilita/lavorazioni/${nuovaLavorazioneId}/mp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idMagazzinoMp: idMp }),
      });
    }
    if (mpSuggeriteSelezionate.size > 0) toast('✓ MP associate alla lavorazione');
    setModalMpSuggeriti([]);
    setNuovaLavorazioneId(null);
    setMpSuggeriteSelezionate(new Set());
    setVista('lista');
    await caricaLista();
  };

  const aggiungiMp = async () => {
    if (!mpSel) return;
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/mp`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idMagazzinoMp: mpSel }),
    });
    if (res.ok) {
      toast('✓ MP aggiunta');
      setMpSel('');
      setMostraAddMp(false);
      await aggiornaDettaglio();
    }
  };

  const rimuoviMp = async (idRiga) => {
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/mp/${idRiga}`, { method: 'DELETE' });
    if (res.ok) { toast('MP rimossa'); await aggiornaDettaglio(); }
  };

  const aggiungiSl = async () => {
    if (!slSel) return;
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/sl`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idMagazzinoPf: slSel }),
    });
    if (res.ok) {
      toast('✓ Semilavorato aggiunto');
      setSlSel('');
      setMostraAddSl(false);
      await aggiornaDettaglio();
    }
  };

  const rimuoviSl = async (idRiga) => {
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/sl/${idRiga}`, { method: 'DELETE' });
    if (res.ok) { toast('Semilavorato rimosso'); await aggiornaDettaglio(); }
  };

  const oraLocaleInput = () => {
    const now = new Date();
    return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  // Converte una data che arriva dal backend nel formato di un campo
  // datetime-local ('YYYY-MM-DDTHH:MM'), riportata in ora locale. Serve per il
  // limite minimo dei campi data dei movimenti: prima dell'inizio della fase non
  // si puo' andare, sarebbe un movimento avvenuto quando la fase non esisteva.
  // La sostituzione dello spazio con la T e' necessaria per Safari, che sulle
  // date con lo spazio restituisce Invalid Date.
  const perInputDataOra = (v) => {
    if (!v) return '';
    const d = new Date(String(v).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const parseParametriFase = (parametriJson) => {
    if (!parametriJson) return [];
    try {
      const parsed = typeof parametriJson === 'string' ? JSON.parse(parametriJson) : parametriJson;
      if (Array.isArray(parsed)) return parsed;
      if (parsed.parametri && Array.isArray(parsed.parametri)) return parsed.parametri;
      return [];
    } catch { return []; }
  };

  const apriModalAvvio = async (ricettaFase) => {
    // Aggiorna disponibilità per avere i lotti attuali (potrebbero essere cambiati da quando è stata aperta la lavorazione)
    let dispAttuali = disponibilita;
    try {
      const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/disponibilita`);
      const data = await res.json();
      if (Array.isArray(data)) { dispAttuali = data; setDisponibilita(data); }
    } catch {}
    // Raggruppa ingredienti di questa fase dalla disponibilita aggiornata
    const righe = dispAttuali.filter(d => d.id_ricetta_fase === ricettaFase.id);
    const ingMap = new Map();
    righe.forEach(row => {
      if (!ingMap.has(row.id)) {
        ingMap.set(row.id, {
          id: row.id,
          tipo: row.tipo,
          nome_ingrediente: row.nome_ingrediente,
          quantita_ricetta: row.quantita_ricetta,
          unita_misura: row.unita_misura,
          lotti: [],
        });
      }
      if (row.id_lotto) {
        ingMap.get(row.id).lotti.push({
          id: row.id_lotto,
          lotto_interno: row.lotto_interno,
          lotto_fornitore: row.lotto_fornitore,
          stato: row.stato,
          quantita: row.quantita,
          consumo_stimato: row.consumo_stimato,
          consumo_altre: row.consumo_altre,
        });
      }
    });
    const ingredienti = Array.from(ingMap.values());
    const lottiScelti = {};
    ingredienti.forEach(ing => {
      const primoDispo = ing.lotti.find(l => l.stato === 'IN_USO');
      lottiScelti[ing.id] = primoDispo ? String(primoDispo.id) : '';
    });
    setUbicazioniFase([]);
    setGiacenzeFasePrecedente([]);
    setModalAvvio({ ricettaFase, dataInizio: oraLocaleInput(), ingredienti, lottiScelti });
  };

  const mettiInUsoLotto = async (idLotto, idIngrediente, tipoIng) => {
    const endpoint = tipoIng === 'SEMILAVORATO'
      ? `${API}/tracciabilita/magazzino-pf/${idLotto}/in-uso`
      : `${API}/tracciabilita/magazzino-mp/${idLotto}/in-uso`;
    const res = await apiFetch(endpoint, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    });
    if (!res.ok) return toast('Errore aggiornamento lotto');
    toast('✓ Lotto messo in uso');
    setModalAvvio(m => ({
      ...m,
      lottiScelti: { ...m.lottiScelti, [idIngrediente]: String(idLotto) },
      ingredienti: m.ingredienti.map(ing =>
        ing.id === idIngrediente
          ? { ...ing, lotti: ing.lotti.map(l => l.id === idLotto ? { ...l, stato: 'IN_USO' } : l) }
          : ing
      ),
    }));
  };

  const apriModalChiusura = async (faseEseguita, rfCorrente, ricettaFaseProssima, dopoChiudeLavorazione = false) => {
    const parDef = parseParametriFase(rfCorrente?.fase_parametri);
    const parametri = {};
    parDef.forEach(p => { parametri[p.nome] = ''; });
    setUbicazioniFase([]);
    setUbicazioniUscita([]);
    setGiacenzeFase([]);
    // Carica ingredienti specifici della fase successiva (se presente)
    let ingredientiProssima = [];
    let lottiSceltiProssima = {};
    if (ricettaFaseProssima) {
      let dispAttuali = disponibilita;
      try {
        const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/disponibilita`);
        const data = await res.json();
        if (Array.isArray(data)) { dispAttuali = data; setDisponibilita(data); }
      } catch {}
      const righe = dispAttuali.filter(d => d.id_ricetta_fase === ricettaFaseProssima.id);
      const ingMap = new Map();
      righe.forEach(row => {
        if (!ingMap.has(row.id)) {
          ingMap.set(row.id, {
            id: row.id, tipo: row.tipo,
            nome_ingrediente: row.nome_ingrediente,
            quantita_ricetta: row.quantita_ricetta, unita_misura: row.unita_misura,
            lotti: [],
          });
        }
        if (row.id_lotto) {
          ingMap.get(row.id).lotti.push({
            id: row.id_lotto, lotto_interno: row.lotto_interno,
            lotto_fornitore: row.lotto_fornitore, stato: row.stato, quantita: row.quantita,
            consumo_stimato: row.consumo_stimato, consumo_altre: row.consumo_altre,
          });
        }
      });
      ingredientiProssima = Array.from(ingMap.values());
      ingredientiProssima.forEach(ing => {
        const primoDispo = ing.lotti.find(l => l.stato === 'IN_USO');
        lottiSceltiProssima[ing.id] = primoDispo ? String(primoDispo.id) : '';
      });
    }
    setModalChiusura({
      faseEseguita,
      ricettaFaseProssima,
      dopoChiudeLavorazione,
      nomeFase: rfCorrente?.fase_nome || faseEseguita?.fase_nome || '',
      dataInizio: faseEseguita?.data_inizio?.slice(0, 16) || oraLocaleInput(),
      dataFine: oraLocaleInput(),
      parametri,
      parDef,
      id: faseEseguita?.id,
      peso_totale_kg: faseEseguita?.peso_totale_kg,
      stato_avanzamento: faseEseguita?.stato_avanzamento,
      ingredientiProssima,
      lottiSceltiProssima,
      quantitaPzReale: '',
      pesoKgReale: '',
      pesoInizialeFase1: dettaglio?.fasi_eseguite?.[0]?.peso_totale_kg || null,
    });
    // Carica giacenze attuali della fase e della fase precedente come riferimento
    if (faseEseguita?.id) {
      await aggiornaGiacenzeFase(faseEseguita.id);
    }
    // Cerca giacenze della fase precedente (se esiste)
    const eseguite = dettaglio?.fasi_eseguite || [];
    const idxCorrente = eseguite.findIndex(f => f.id === faseEseguita?.id);
    if (idxCorrente > 0) {
      const fasePrecedente = eseguite[idxCorrente - 1];
      const resPrec = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${fasePrecedente.id}/ubicazioni`);
      const movPrec = await resPrec.json();
      if (Array.isArray(movPrec)) {
        const mapPrec = {};
        for (const m of movPrec) {
          if (!m.tipo) continue;
          const k = m.id_ubicazione;
          if (!mapPrec[k]) mapPrec[k] = { id_ubicazione: k, nome_ubicazione: m.nome_ubicazione, pz_attuali: 0, peso_attuale: 0 };
          const sign = m.tipo === 'ENTRATA' ? 1 : -1;
          mapPrec[k].pz_attuali   += sign * parseFloat(m.quantita_pz  || 0);
          mapPrec[k].peso_attuale += sign * parseFloat(m.peso_kg || 0);
        }
        setGiacenzeFasePrecedente(Object.values(mapPrec).filter(g => g.pz_attuali > 0 || g.peso_attuale > 0));
      }
    } else {
      setGiacenzeFasePrecedente([]);
    }
  };

  const avviaChiusuraLavorazione = async () => {
    // ── ITEM E — non si chiude saltando delle fasi ─────────────────────────
    // Il controllo vero sta nel backend, che e' l'unico posto dove non si puo'
    // aggirare. Questo qui serve a dirlo PRIMA: senza, l'operatore chiuderebbe
    // la fase in corso e solo dopo si sentirebbe rifiutare la chiusura, con una
    // fase chiusa per niente.
    const eseguiteIds = new Set((dettaglio?.fasi_eseguite || []).map(f => f.id_fase));
    const mancanti = (ricettaDett?.fasi || [])
      .filter(rf => rf.id_fase && !eseguiteIds.has(rf.id_fase))
      .map(rf => rf.fase_nome || rf.nome || 'fase senza nome');
    if (mancanti.length) {
      window.alert(
        (mancanti.length === 1
          ? `Non è possibile chiudere la lavorazione: la fase "${mancanti[0]}" non è mai stata avviata.`
          : `Non è possibile chiudere la lavorazione. Queste fasi non sono mai state avviate:\n\n• ${mancanti.join('\n• ')}`) +
        '\n\nOgni fase della ricetta va avviata e chiusa. Completale e riprova.'
      );
      return;
    }
    const aperte = (dettaglio?.fasi_eseguite || []).filter(f => !f.data_fine);
    if (aperte.length > 1) {
      window.alert(
        'Non è possibile chiudere la lavorazione: risultano aperte più fasi contemporaneamente (' +
        aperte.map(f => f.fase_nome).filter(Boolean).join(', ') + ').\n\n' +
        'Chiudi quelle precedenti e lascia aperta solo l\'ultima.'
      );
      return;
    }

    // ── ITEM B — dove si trova il prodotto in questo momento ───────────────
    // La proposta di partenza e' "resta dov'e'": si precompila una riga per
    // ogni giacenza con le quantita' che ci sono davvero. L'operatore corregge
    // solo se sposta qualcosa o se il peso finale e' calato.
    let dest = [];
    try {
      const rG = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/giacenze`);
      const gg = await rG.json();
      if (Array.isArray(gg)) {
        setGiacenzeLavorazione(gg);
        const arr = n => { const v = parseFloat(n || 0); return v > 0 ? String(Math.round(v * 1000) / 1000) : ''; };
        // Si propone soltanto quello che sta sull'ultima fase. Cio' che resta
        // sulle fasi gia' concluse e' la differenza fra quanto era entrato e
        // quanto e' stato mandato avanti, cioe' calo di lavorazione: non e'
        // prodotto da collocare. Resta comunque selezionabile a mano nel
        // menu "Da", per il caso in cui sia prodotto vero dimenticato li'.
        const ordMax = gg.reduce((m, g) => Math.max(m, parseInt(g.ordine) || 0), 0);
        const idFaseUltima = gg.reduce((m, g) =>
          (parseInt(g.ordine) || 0) === ordMax ? Math.max(m, parseInt(g.id_fase) || 0) : m, 0);
        dest = gg.filter(g => (parseInt(g.id_fase) || 0) === idFaseUltima).map(g => ({
          idFaseDa:       String(g.id_fase),
          idUbicazioneDa: String(g.id_ubicazione),
          idUbicazione:   String(g.id_ubicazione),
          quantitaPz:     arr(g.pz_attuali),
          pesoKg:         arr(g.peso_attuale),
        }));
      } else {
        setGiacenzeLavorazione([]);
      }
    } catch (e) {
      setGiacenzeLavorazione([]);
    }
    setDestinazioniChiusura(dest);

    const faseAperta = aperte[0];
    if (faseAperta) {
      const rfCorrente = ricettaDett?.fasi?.find(rf => rf.id_fase === faseAperta.id_fase);
      apriModalChiusura(faseAperta, rfCorrente, null, true);
    } else {
      apriModalChiusura(null, null, null, true);
    }
  };

  const confermaAvvio = async () => {
    const { ricettaFase, dataInizio, ingredienti = [], lottiScelti = {} } = modalAvvio;
    // Associa lotti scelti alla lavorazione (se non già presenti)
    const giàAssociati   = new Set((dettaglio.mp_utilizzate  || []).map(mp => mp.id_magazzino_mp));
    const giàAssociatiSl = new Set((dettaglio.sl_utilizzati  || []).map(sl => sl.id_magazzino_pf));
    for (const ing of ingredienti) {
      const idLotto = lottiScelti[ing.id] ? parseInt(lottiScelti[ing.id]) : null;
      if (!idLotto) continue;
      if (ing.tipo === 'SEMILAVORATO') {
        if (!giàAssociatiSl.has(idLotto)) {
          await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/sl`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idMagazzinoPf: idLotto }),
          });
        }
      } else {
        if (!giàAssociati.has(idLotto)) {
          await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/mp`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idMagazzinoMp: idLotto }),
          });
        }
      }
    }
    // Righe compilate a meta': quantita' indicate ma nessuna ubicazione scelta.
    // Prima venivano scartate in silenzio e la fase nasceva senza posizioni,
    // con la conseguenza che tutti i passaggi successivi restavano scollegati.
    const righeIncomplete = ubicazioniFase.filter(u =>
      !u.idUbicazione && ((parseFloat(u.quantitaPz) || 0) > 0 || (parseFloat(u.pesoKg) || 0) > 0)
    );
    if (righeIncomplete.length > 0) {
      toast('Hai indicato delle quantità senza scegliere l\'ubicazione: completa la riga o eliminala', 'errore');
      return;
    }
    const ubicazioniEntrata = ubicazioniFase
      .filter(u => u.idUbicazione)
      .map(u => ({ idUbicazione: parseInt(u.idUbicazione), quantitaPz: parseFloat(u.quantitaPz) || null, pesoKg: parseFloat(u.pesoKg) || null }));
    // Senza almeno un'ubicazione in entrata la fase parte "vuota": non si potra'
    // spostare o scartare niente e il passaggio alla fase successiva non lascera'
    // traccia dell'uscita. Meglio dirlo prima che dopo.
    if (ubicazioniEntrata.length === 0) {
      const proseguo = window.confirm(
        'Non hai indicato nessuna ubicazione in entrata.\n\n' +
        'La fase partirà senza posizioni: non potrai registrare spostamenti o scarti ' +
        'e il passaggio alla fase successiva non registrerà l\'uscita del prodotto.\n\n' +
        'Vuoi avviare la fase lo stesso?'
      );
      if (!proseguo) return;
    }
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/fasi`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idFase: ricettaFase.id_fase, ordine: ricettaFase.ordine,
        idOperatore: operatore.id, dataInizio: dataInizio ? dataInizio + ':00' : null,
        ubicazioni: ubicazioniEntrata.length > 0 ? ubicazioniEntrata : undefined,
      }),
    });
    if (!res.ok) {
      // Prima l'errore veniva ingoiato: la finestra restava aperta senza spiegazioni
      // e sembrava che il pulsante non funzionasse.
      const body = await res.json().catch(() => ({}));
      toast(body.errore || 'Errore nell\'avvio della fase', 'errore');
      return;
    }
    toast('✓ Fase avviata');
    setModalAvvio(null);
    setUbicazioniFase([]);
    await caricaDisponibilita(dettaglio.id);
    await aggiornaDettaglio();
  };

  const confermaChiusura = async () => {
    const { faseEseguita, ricettaFaseProssima, dopoChiudeLavorazione, nomeFase, dataInizio, dataFine, parametri, parDef, quantitaPzReale, pesoKgReale, pesoInizialeFase1 } = modalChiusura;

    // Le destinazioni si leggono adesso, prima di qualsiasi azzeramento di
    // stato piu' sotto.
    const destValide = destinazioniChiusura.filter(d => d.idUbicazione &&
      ((parseFloat(d.quantitaPz) || 0) > 0 || (parseFloat(d.pesoKg) || 0) > 0));

    if (dopoChiudeLavorazione) {
      if (!quantitaPzReale && !pesoKgReale) return toast('Inserire la quantità finale elaborata (Pz o Kg)');

      // Scostamento fra dati finali dichiarati e somma delle ubicazioni.
      // Blocca. Le due cose devono coincidere per forza: quello che dichiaro di
      // aver prodotto e' esattamente quello che resta fisicamente nelle celle.
      // Se una parte si e' persa, la si toglie dalle righe e il sistema la
      // manda a scarto da solo. Lasciar passare uno scostamento significa
      // scrivere in giacenza un numero diverso da quello dichiarato, e nessuno
      // se ne accorge piu'.
      if (destValide.length) {
        const somma = (k) => destValide.reduce((t, d) => t + (parseFloat(d[k]) || 0), 0);
        const pzDest = somma('quantitaPz'), kgDest = somma('pesoKg');
        const pzDich = parseFloat(quantitaPzReale || 0), kgDich = parseFloat(pesoKgReale || 0);
        const r3 = n => Math.round(n * 1000) / 1000;
        const scost = [];
        if (pzDich > 0 && Math.abs(pzDest - pzDich) > 0.0005)
          scost.push(`pezzi: dichiarati ${r3(pzDich)}, nelle ubicazioni ${r3(pzDest)}`);
        if (kgDich > 0 && Math.abs(kgDest - kgDich) > 0.0005)
          scost.push(`peso: dichiarato ${r3(kgDich)} kg, nelle ubicazioni ${r3(kgDest)} kg`);
        if (scost.length) {
          window.alert(
            'I dati finali e le ubicazioni non coincidono:\n\n• ' + scost.join('\n• ') +
            '\n\nI due valori devono corrispondere: quello che dichiari di aver prodotto è quello che resta fisicamente nelle ubicazioni.' +
            '\n\nSe una parte è andata persa, abbassa le righe fino ad arrivare ai dati finali: la differenza viene registrata da sola come calo. In alternativa usa "allinea ai dati finali".'
          );
          return;
        }
      }
    }

    const chiudiSingolaFase = async () => {
      const parametriRilevati = {};
      parDef.forEach(p => { if (parametri[p.nome]) parametriRilevati[p.nome] = parametri[p.nome]; });
      const pr = Object.keys(parametriRilevati).length > 0 ? parametriRilevati : null;

      const fuoriLimite = parDef.filter(p => {
        const val = parametri[p.nome];
        if (!val && val !== 0) return false;
        const v = parseFloat(val);
        if (isNaN(v)) return false;
        const hasMin = p.min != null && p.min !== '';
        const hasMax = p.max != null && p.max !== '';
        return (hasMin && v < parseFloat(p.min)) || (hasMax && v > parseFloat(p.max));
      });

      const avviaFaseSuccessiva = async () => {
        if (!ricettaFaseProssima) return;
        const usciteValide = ubicazioniUscita.filter(u => u.idUbicazione);
        const resNuova = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/fasi`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idFase: ricettaFaseProssima.id_fase, ordine: ricettaFaseProssima.ordine,
            idOperatore: operatore.id, dataInizio: dataFine ? dataFine + ':00' : null,
          }),
        });
        const nuovaFaseData = await resNuova.json();
        const idNuovaFase = nuovaFaseData.id;
        if (usciteValide.length > 0 && idNuovaFase) {
          const idUbSingola = giacenzeFase.length === 1 ? giacenzeFase[0].id_ubicazione : null;
          const resAv = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${faseEseguita.id}/avanza`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              idFaseProssima: idNuovaFase,
              // Stessa data che chiude la fase e apre la successiva: e' il senso
              // stesso del passaggio, il prodotto si e' mosso in quel momento.
              dataMovimento: dataFine ? dataFine + ':00' : null,
              avanzi: usciteValide.map(u => ({
                idUbicazioneDa: u.idUbicazioneDa ? parseInt(u.idUbicazioneDa) : (idUbSingola || null),
                idUbicazioneA:  parseInt(u.idUbicazione),
                quantitaPz:     parseFloat(u.quantitaPz) || null,
                pesoKg:         parseFloat(u.pesoKg)     || null,
              })),
            }),
          });
          if (!resAv.ok) {
            const bodyAv = await resAv.json().catch(() => ({}));
            // La fase successiva e' gia' stata creata qui sopra: se il trasferimento
            // fallisce la lavorazione resta a meta' (fase nuova aperta ma vuota, fase
            // precedente senza uscita). Un avviso che scompare da solo si perde: qui
            // serve qualcosa che l'operatore debba per forza leggere.
            window.alert(
              'ATTENZIONE: il prodotto NON è stato trasferito alla fase successiva.\n\n' +
              (bodyAv.errore || 'Errore nell\'avanzamento del prodotto') + '\n\n' +
              'La fase successiva è comunque stata creata ed è vuota. ' +
              'Aggiungi a mano le ubicazioni con "+ Aggiungi ubicazione", oppure contatta l\'assistenza.'
            );
            toast(bodyAv.errore || 'Errore nell\'avanzamento del prodotto', 'errore');
          } else {
            const bodyOk = await resAv.json().catch(() => ({}));
            if (bodyOk.avviso) toast(bodyOk.avviso, 'errore');
          }
        }
        const { ingredientiProssima = [], lottiSceltiProssima = {} } = modalChiusura;
        const giàAssociati   = new Set((dettaglio.mp_utilizzate  || []).map(mp => mp.id_magazzino_mp));
        const giàAssociatiSl = new Set((dettaglio.sl_utilizzati  || []).map(sl => sl.id_magazzino_pf));
        for (const ing of ingredientiProssima) {
          const idLotto = lottiSceltiProssima[ing.id] ? parseInt(lottiSceltiProssima[ing.id]) : null;
          if (!idLotto) continue;
          if (ing.tipo === 'SEMILAVORATO') {
            if (!giàAssociatiSl.has(idLotto)) {
              await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/sl`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idMagazzinoPf: idLotto }),
              });
            }
          } else {
            if (!giàAssociati.has(idLotto)) {
              await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/mp`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idMagazzinoMp: idLotto }),
              });
            }
          }
        }
      };

      if (ricettaFaseProssima) {
        const pzInGiacenza = giacenzeFase.reduce((s, g) => s + parseFloat(g.pz_attuali || 0), 0);
        const usciteValide  = ubicazioniUscita.filter(u => u.idUbicazione);
        const pzUscita      = usciteValide.reduce((s, u) => s + parseFloat(u.quantitaPz || 0), 0);
        if (pzInGiacenza > 0 && pzUscita > pzInGiacenza) {
          toast(`I pezzi in uscita (${pzUscita}) superano la giacenza (${pzInGiacenza})`);
          return false;
        }
        if (pzInGiacenza > 0 && usciteValide.length === 0) {
          if (!window.confirm(`Ci sono ${pzInGiacenza} pz in giacenza ma non hai specificato dove vanno nella fase successiva. Continuare?`)) return false;
        }
        if (giacenzeFase.length > 1 && usciteValide.some(u => !u.idUbicazioneDa)) {
          toast('Con più ubicazioni attive devi specificare da quale prelevare in ogni riga');
          return false;
        }
      }

      if (fuoriLimite.length > 0) {
        const prodNome = dettaglio?.prodotto_nome || '';
        const lotto    = dettaglio?.lotto_prodotto || '';
        const righe = fuoriLimite.map(p => {
          const val   = parametri[p.nome];
          const umStr = p.um || p.unita || '';
          const v     = parseFloat(val);
          let limStr  = '';
          if (p.min != null && p.min !== '' && v < parseFloat(p.min))
            limStr = `limite min ${p.min}${umStr}`;
          else if (p.max != null && p.max !== '' && v > parseFloat(p.max))
            limStr = `limite max ${p.max}${umStr}`;
          return `parametro "${p.nome}" rilevato ${val}${umStr}${limStr ? ` — ${limStr}` : ''}`;
        });
        const evento = `Fase "${nomeFase}" - Lavorazione ${prodNome}${lotto ? ` (Lotto: ${lotto})` : ''}:\n${righe.join('\n')}`;

        const res = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${faseEseguita.id}/chiudi-con-nc`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dataInizio: dataInizio ? dataInizio + ':00' : null,
            dataFine:   dataFine   ? dataFine   + ':00' : null,
            parametriRilevati: pr,
            evento,
            idOperatore: operatore.id,
          }),
        });
        if (!res.ok) { toast('Errore chiusura fase'); return false; }
        const data = await res.json();
        await avviaFaseSuccessiva();
        toast(`✓ Fase chiusa — NC #${data.nc.progressivo} creata automaticamente`);
      } else {
        const resPut = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${faseEseguita.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dataInizio: dataInizio ? dataInizio + ':00' : null,
            dataFine:   dataFine   ? dataFine   + ':00' : null,
            parametriRilevati: pr,
          }),
        });
        if (!resPut.ok) { toast('Errore chiusura fase'); return false; }
        await avviaFaseSuccessiva();
        toast('✓ Fase completata');
      }
      return true;
    };

    if (faseEseguita && faseEseguita.id) {
      const ok = await chiudiSingolaFase();
      if (!ok) return;
    }

    setModalChiusura(null);
    setUbicazioniFase([]);
    setUbicazioniUscita([]);
    setGiacenzeFase([]);
    setDestinazioniChiusura([]);
    setGiacenzeLavorazione([]);
    setMostraAddUbFase(false);

    if (dopoChiudeLavorazione) {
      let caloTxt = '';
      if (pesoInizialeFase1 && pesoKgReale) {
        const diff = (parseFloat(pesoInizialeFase1) - parseFloat(pesoKgReale)).toFixed(2);
        caloTxt = `Calopeso autocalcolato: ${diff} Kg (Inizio: ${pesoInizialeFase1} Kg, Finale: ${pesoKgReale} Kg)`;
      }

      const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stato: 'CHIUSA', dataFine: dataFine ? dataFine + ':00' : oggi,
          quantitaPzReale: quantitaPzReale ? parseFloat(quantitaPzReale) : null,
          pesoKgReale: pesoKgReale ? parseFloat(pesoKgReale) : null,
          noteAggiuntive: caloTxt || undefined,
          destinazioni: destValide.length ? destValide.map(d => ({
            idFaseDa:       d.idFaseDa       ? parseInt(d.idFaseDa)       : null,
            idUbicazioneDa: d.idUbicazioneDa ? parseInt(d.idUbicazioneDa) : null,
            idUbicazione:   parseInt(d.idUbicazione),
            quantitaPz:     parseFloat(d.quantitaPz) || null,
            pesoKg:         parseFloat(d.pesoKg)     || null,
          })) : undefined
        }),
      });
      if (res.ok) {
        toast('✓ Lavorazione chiusa — magazzino aggiornato e PF creato');
      } else {
        // La fase in corso e' gia' stata chiusa qui sopra: se la chiusura della
        // lavorazione viene rifiutata, l'operatore deve sapere che cosa e'
        // successo e che puo' riprovare, altrimenti resta con una lavorazione
        // aperta e nessuna spiegazione.
        const bodyErr = await res.json().catch(() => ({}));
        window.alert(
          'La lavorazione NON è stata chiusa.\n\n' +
          (bodyErr.errore || 'Errore sconosciuto') +
          '\n\nLe fasi già chiuse restano chiuse: sistema quanto indicato e premi di nuovo "Chiudi lavorazione".'
        );
        toast(bodyErr.errore || 'Errore chiusura lavorazione', 'errore');
      }
    }
    
    await aggiornaDettaglio();
    await caricaLista();
  };

  const annullaLavorazione = async () => {
    if (!modalAnnulla?.motivazione?.trim()) return toast('Inserire la motivazione');
    const res = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/annulla`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivazione: modalAnnulla.motivazione, idOperatore: operatore.id }),
    });
    const data = await res.json();
    if (!res.ok) return toast('Errore: ' + (data.errore || 'sconosciuto'));
    toast('Lavorazione annullata');
    setModalAnnulla(null);
    await aggiornaDettaglio();
    await caricaLista();
  };

  const filtrati = lista.filter(l =>
    !ricerca ||
    l.prodotto_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.lotto_prodotto?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.ricetta_nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  // ─── Vista Lista ───────────────────────────────────────────────
  const statoChipKindLav = (s) => ({ APERTA: 'verde', CHIUSA: 'blu', ANNULLATA: 'neutro' }[s] || 'neutro');

  if (vista === 'lista') return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans, color: LT.navy }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon={<Ico d={I.arrowLeft} s={18} />} onClick={onBack}>Menu</Btn>
        <div style={{ fontSize: 13, fontWeight: 700, color: LT.n700, letterSpacing: 0.3 }}>
          TRACCIABILITÀ · LAVORAZIONI
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.verde} 0%, #16a34a 100%)`,
          color: LT.n0, borderRadius: LT.r.lg, padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
          boxShadow: '0 8px 24px rgba(34,197,94,.18)',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.md,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.factory} s={32} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Lavorazioni di produzione</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              Ordini di lavoro · fasi, ingredienti e tracciamento lotti
            </div>
          </div>
          <Btn kind="secondary" icon={<Ico d={I.plus} s={16} />}
               onClick={() => { setForm(FORM_VUOTO); setVista('nuovo'); }}
               style={{ background: LT.n0, color: LT.verde, border: 'none' }}>
            Nuova lavorazione
          </Btn>
        </div>

        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={TR_LABEL}>Cerca</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n500, pointerEvents: 'none' }}>
                  <Ico d={I.search} s={16} />
                </div>
                <input
                  style={{ ...TR_INPUT, paddingLeft: 36 }}
                  placeholder="Prodotto, lotto, ricetta…"
                  value={ricerca}
                  onChange={e => setRicerca(e.target.value)}
                  onFocus={e => trFocus(e, LT.verde)}
                  onBlur={trBlur}
                />
              </div>
            </div>
            <div>
              <label style={TR_LABEL}>Stato</label>
              <select
                style={TR_INPUT}
                value={filtroStato}
                onChange={e => setFiltroStato(e.target.value)}
                onFocus={e => trFocus(e, LT.verde)}
                onBlur={trBlur}
              >
                <option value="">Tutte</option>
                <option value="APERTA">Aperte</option>
                <option value="CHIUSA">Chiuse</option>
                <option value="ANNULLATA">Annullate</option>
              </select>
            </div>
          </div>
        </Card>

        {caricando && (
          <Card padding="md" style={{ textAlign: 'center', color: LT.n600 }}>Caricamento…</Card>
        )}

        {/* Grid lavorazioni */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {filtrati.map(lav => (
            <Card key={lav.id} padding="md" style={{ cursor: 'pointer' }} onClick={() => apriDettaglio(lav)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                  <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: LT.verdeSoft, color: LT.verde, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Ico d={I.factory} s={22} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy, overflow: 'hidden', textOverflow: 'ellipsis' }}>{lav.prodotto_nome || '—'}</div>
                    {lav.lotto_prodotto && <div style={{ fontSize: 12, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>{lav.lotto_prodotto}</div>}
                  </div>
                </div>
                <Chip kind={statoChipKindLav(lav.stato)} size="sm">
                  {STATO_LAV_LABEL[lav.stato] || lav.stato}
                </Chip>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: LT.n700, marginBottom: 8 }}>
                {lav.ricetta_nome && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={I.book} s={12} /> {lav.ricetta_nome}</div>}
                <div>📅 {lav.data_inizio}</div>
                {lav.quantita_prevista && <div>{lav.quantita_prevista} {lav.unita_misura}</div>}
              </div>

              {lav.committente_nome && (
                <Chip kind="arancio" size="sm" icon={<Ico d={I.users} s={12} />}>
                  Conto terzi: {lav.committente_nome}
                </Chip>
              )}
            </Card>
          ))}
        </div>

        {!caricando && filtrati.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center', marginTop: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: LT.n100, color: LT.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Ico d={I.factory} s={28} />
            </div>
            <div style={{ fontSize: 14, color: LT.n600 }}>Nessuna lavorazione trovata</div>
          </Card>
        )}
      </div>
    </div>
  );

  // ─── Vista Nuova Lavorazione ────────────────────────────────────
  if (vista === 'nuovo') return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans, color: LT.navy }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon={<Ico d={I.arrowLeft} s={18} />} onClick={() => setVista('lista')}>Lista</Btn>
        <div style={{ fontSize: 13, fontWeight: 700, color: LT.n700, letterSpacing: 0.3 }}>
          NUOVA LAVORAZIONE
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero compatto */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.verde} 0%, #16a34a 100%)`,
          color: LT.n0, borderRadius: LT.r.lg, padding: '16px 20px',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20,
          boxShadow: '0 8px 24px rgba(34,197,94,.18)',
        }}>
          <div style={{ width: 44, height: 44, borderRadius: LT.r.md, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ico d={I.plus} s={22} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Nuova lavorazione</div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Crea un ordine di produzione partendo da una ricetta o ad hoc</div>
          </div>
        </div>

        <SezioneForm titolo="Dati lavorazione" icona={<Ico d={I.edit} s={16} />} tinta={LT.verde}>
          <label style={TR_LABEL}>Ricetta</label>
          <select style={TR_INPUT} value={form.idRicetta}
                  onChange={e => onRicettaChange(e.target.value)}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}>
            <option value="">— nessuna ricetta —</option>
            {ricette.map(r => (
              <option key={r.id} value={r.id}>{r.nome}{r.prodotto_nome ? ` (${r.prodotto_nome})` : ''}</option>
            ))}
          </select>

          <label style={TR_LABEL}>Prodotto</label>
          <select style={TR_INPUT} value={form.idProdotto}
                  onChange={e => setForm(f => ({ ...f, idProdotto: e.target.value }))}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}>
            <option value="">— seleziona prodotto —</option>
            {prodotti.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>

          <div className="lt-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={TR_LABEL}>Lotto prodotto</label>
              <input style={{ ...TR_INPUT, fontFamily: LT.mono }} value={form.lottoProdotto}
                     onChange={e => setForm(f => ({ ...f, lottoProdotto: e.target.value }))}
                     onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}
                     placeholder="L.GGMMAA" />
            </div>
            <div>
              <label style={TR_LABEL}>Data inizio *</label>
              <input type="date" style={TR_INPUT} value={form.dataInizio}
                     onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}
                     onChange={e => {
                       const d = e.target.value;
                       setForm(f => ({
                         ...f, dataInizio: d,
                         lottoProdotto: f.lottoProdotto === genLotto(f.dataInizio) ? genLotto(d) : f.lottoProdotto,
                       }));
                     }} />
            </div>
          </div>

          <div className="lt-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={TR_LABEL}>Quantità prevista</label>
              <input type="text" inputMode="decimal" style={TR_INPUT} value={form.quantitaPrevista}
                     onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}
                     onChange={e => setForm(f => ({ ...f, quantitaPrevista: decVal(e.target.value) }))} />
            </div>
            <div>
              <label style={TR_LABEL}>Unità di misura</label>
              <select style={TR_INPUT} value={form.unitaMisura}
                      onChange={e => setForm(f => ({ ...f, unitaMisura: e.target.value }))}
                      onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}>
                <option value="">—</option>
                {['kg', 'g', 'l', 'ml', 'pz', 'conf', 'sacchi', 'cartoni'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <label style={TR_LABEL}>Committente (conto lavorazione) — opzionale</label>
          <select style={TR_INPUT} value={form.idCommittente}
                  onChange={e => setForm(f => ({ ...f, idCommittente: e.target.value }))}
                  onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}>
            <option value="">— lavorazione standard —</option>
            {clientiLav.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
          </select>

          <label style={TR_LABEL}>Note</label>
          <textarea style={{ ...TR_INPUT, resize: 'vertical', minHeight: 72 }} rows={3} value={form.note}
                    onFocus={e => trFocus(e, LT.verde)} onBlur={trBlur}
                    onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
        </SezioneForm>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <Btn kind="secondary" onClick={() => setVista('lista')}>Annulla</Btn>
          <Btn kind="primary" icon={<Ico d={I.check} s={16} />} onClick={salvaLavorazione}>
            Salva lavorazione
          </Btn>
        </div>
      </div>

      {/* Modal lotti MP suggeriti */}
      {modalMpSuggeriti.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: LT.n0, borderRadius: LT.r.lg, padding: 20, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: LT.sh3, fontFamily: LT.sans }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.bluSoft, color: LT.blu, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.package} s={20} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: LT.navy }}>Lotti coerenti con la ricetta</div>
            </div>
            <p style={{ fontSize: 13, color: LT.n600, marginBottom: 16 }}>
              Abbiamo trovato questi lotti MP in uso compatibili con gli ingredienti della ricetta. Seleziona quelli da associare.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {modalMpSuggeriti.map(mp => {
                const sel = mpSuggeriteSelezionate.has(mp.id);
                return (
                  <label key={mp.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px', borderRadius: LT.r.md, cursor: 'pointer',
                    background: sel ? LT.verdeSoft : LT.n50,
                    border: `1.5px solid ${sel ? LT.verde : LT.n200}`,
                  }}>
                    <input type="checkbox" checked={sel} style={{ marginTop: 3, accentColor: LT.verde }}
                           onChange={e => setMpSuggeriteSelezionate(prev => {
                             const next = new Set(prev);
                             e.target.checked ? next.add(mp.id) : next.delete(mp.id);
                             return next;
                           })} />
                    <div style={{ fontSize: 13 }}>
                      <div style={{ fontWeight: 700, color: LT.navy }}>{mp.ingrediente_nome}</div>
                      <div style={{ color: LT.n600, marginTop: 3, fontSize: 12 }}>
                        <span style={{ fontFamily: LT.mono }}>{mp.lotto_interno || mp.lotto_fornitore}</span>
                        {mp.lotto_fornitore && mp.lotto_interno && <span style={{ marginLeft: 6, opacity: 0.7 }}>· forn. {mp.lotto_fornitore}</span>}
                        <span style={{ marginLeft: 6 }}>· {mp.quantita} {mp.unita_misura}</span>
                        {mp.data_scadenza && <span style={{ marginLeft: 6, color: LT.arancio, fontWeight: 600 }}>· Scad. {mp.data_scadenza}</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <Btn kind="secondary" onClick={() => { setModalMpSuggeriti([]); setNuovaLavorazioneId(null); setMpSuggeriteSelezionate(new Set()); setVista('lista'); caricaLista(); }}>
                Salta
              </Btn>
              <Btn kind="primary" icon={<Ico d={I.check} s={16} />} onClick={confermaAssociazioneMp}>
                Associa ({mpSuggeriteSelezionate.size})
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Vista Dettaglio ────────────────────────────────────────────
  if (vista === 'dettaglio' && dettaglio) {
    const isAperta    = dettaglio.stato === 'APERTA';
    const _cfg        = STATO_LAV_COLORI[dettaglio.stato] || {};
    const ricFasi     = ricettaDett?.fasi || [];
    const eseguite    = dettaglio.fasi_eseguite || [];
    const faseInCorso = eseguite.find(f => !f.data_fine);
    const parametriFuoriLimite = (modalChiusura?.parDef || []).filter(p => {
      const val = modalChiusura?.parametri?.[p.nome];
      if (!val && val !== 0) return false;
      const v = parseFloat(val);
      if (isNaN(v)) return false;
      const hasMin = p.min != null && p.min !== '';
      const hasMax = p.max != null && p.max !== '';
      return (hasMin && v < parseFloat(p.min)) || (hasMax && v > parseFloat(p.max));
    });

    // Calcola stato di ogni fase della ricetta
    const fasiCombinate = ricFasi.map(rf => {
      const eseguita  = eseguite.find(e => e.id_fase === rf.id_fase);
      const statoFase = !eseguita ? 'attesa' : (!eseguita.data_fine ? 'in_corso' : 'completata');
      return { ...rf, eseguita, statoFase };
    });
    const prossima = fasiCombinate.find(f => f.statoFase === 'attesa');

    return (
      <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans, color: LT.navy }}>
        {/* Top bar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10, background: LT.n0,
          borderBottom: `1px solid ${LT.n200}`, padding: '10px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <Btn kind="ghost" size="sm" icon={<Ico d={I.arrowLeft} s={18} />}
               onClick={() => { setVista('lista'); setDettaglio(null); setRicettaDett(null); }}>
            Lista
          </Btn>
          <div style={{ fontSize: 13, fontWeight: 700, color: LT.n700, letterSpacing: 0.3 }}>
            TRACCIABILITÀ · DETTAGLIO LAVORAZIONE
          </div>
        </div>

        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 20px 60px' }}>
          {/* Hero info lavorazione */}
          <Card padding="none" style={{ marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ display: 'flex' }}>
              <div style={{
                width: 6, alignSelf: 'stretch',
                background: dettaglio.stato === 'APERTA' ? LT.verde : dettaglio.stato === 'CHIUSA' ? LT.blu : LT.n400,
              }} />
              <div style={{ flex: 1, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ width: 48, height: 48, borderRadius: LT.r.md, background: LT.verdeSoft, color: LT.verde, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Ico d={I.factory} s={26} />
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: LT.navy }}>{dettaglio.prodotto_nome || '—'}</div>
                      {dettaglio.lotto_prodotto && (
                        <div style={{ fontSize: 13, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>{dettaglio.lotto_prodotto}</div>
                      )}
                    </div>
                  </div>
                  <Chip kind={statoChipKindLav(dettaglio.stato)} size="sm">
                    {STATO_LAV_LABEL[dettaglio.stato] || dettaglio.stato}
                  </Chip>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, padding: '10px 12px', background: LT.n50, borderRadius: LT.r.md }}>
                  {dettaglio.ricetta_nome && (
                    <div>
                      <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Ricetta</div>
                      <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{dettaglio.ricetta_nome}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Inizio</div>
                    <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{dettaglio.data_inizio}</div>
                  </div>
                  {dettaglio.data_fine && (
                    <div>
                      <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Fine</div>
                      <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{dettaglio.data_fine}</div>
                    </div>
                  )}
                  {dettaglio.quantita_prevista && (
                    <div>
                      <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Prevista</div>
                      <div style={{ fontSize: 13, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{dettaglio.quantita_prevista} {dettaglio.unita_misura}</div>
                    </div>
                  )}
                  {dettaglio.quantita_reale && (
                    <div>
                      <div style={{ fontSize: 11, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Reale</div>
                      <div style={{ fontSize: 13, color: LT.verde, fontWeight: 700, marginTop: 2 }}>{dettaglio.quantita_reale} {dettaglio.unita_misura}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

        {/* Disponibilità ingredienti (solo informativa) */}
        {ricettaDett && disponibilita.length > 0 && (() => {
          // Fattore di scala: quantità produzione / quantità base ricetta
          const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
          const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
          const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;

          const ingMap = new Map();
          disponibilita.forEach(row => {
            if (!ingMap.has(row.id)) {
              ingMap.set(row.id, {
                id: row.id,
                nome_ingrediente: row.nome_ingrediente,
                quantita_ricetta: row.quantita_ricetta,
                unita_misura: row.unita_misura,
                id_ricetta_fase: row.id_ricetta_fase,
                lotti: [],
              });
            }
            if (row.id_lotto) ingMap.get(row.id).lotti.push({
              id: row.id_lotto, stato: row.stato,
              quantita: row.quantita, consumo_stimato: row.consumo_stimato,
              consumo_altre: row.consumo_altre,
            });
          });
          const ingredienti = Array.from(ingMap.values());
          return (
            <div style={{ marginBottom: 20 }}>
              <strong>🗂 Disponibilità ingredienti</strong>
              {scala !== 1 && <span style={{ marginLeft: 8, fontSize: 11, color: '#1976d2' }}>× {scala.toFixed(3)} (scala {qLav} / {qRic} {ricettaDett.unita_misura || ''})</span>}
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {ingredienti.map(ing => {
                  const hasInUso      = ing.lotti.some(l => l.stato === 'IN_USO');
                  const hasInMag      = ing.lotti.some(l => l.stato === 'IN_MAGAZZINO');
                  const indicatore    = hasInUso ? '✅' : hasInMag ? '⚠️' : '❌';
                  const bg            = hasInUso ? '#eaf4ea' : hasInMag ? '#fff3e0' : '#fdecea';
                  const color         = hasInUso ? '#16a34a' : hasInMag ? '#b45309' : '#b00020';
                  const etichetta     = hasInUso ? 'In uso' : hasInMag ? 'In magazzino' : 'Non disponibile';
                  const qtScalata     = ing.quantita_ricetta ? (parseFloat(ing.quantita_ricetta) * scala).toFixed(3).replace(/\.?0+$/, '') : null;
                  // Disponibilità virtuale stimata per il lotto IN_USO
                  const lottoInUso    = ing.lotti.find(l => l.stato === 'IN_USO');
                  const residuoStimato = residuoLotto(lottoInUso);
                  const esaurito       = lottoEsaurito(lottoInUso);
                  return (
                    <div key={ing.id} style={{ padding: '5px 10px', background: bg, borderRadius: 7, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          {indicatore} <strong>{ing.nome_ingrediente}</strong>
                          {qtScalata && <span style={{ marginLeft: 6, color: '#666' }}>{qtScalata} {ing.unita_misura}</span>}
                          {!ing.id_ricetta_fase && <span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>(generale)</span>}
                        </span>
                        <span style={{ fontWeight: 600, color, fontSize: 11 }}>{etichetta}</span>
                      </div>
                      {residuoStimato !== null && (
                        <div style={{ fontSize: 11, color: esaurito ? '#b00020' : residuoStimato < parseFloat(qtScalata || 0) ? '#b45309' : '#16a34a', marginTop: 2, paddingLeft: 2 }}>
                          ~ disponibile stimata: <strong>{residuoStimato.toFixed(2).replace(/\.?0+$/, '')} {ing.unita_misura}</strong>
                          {esaurito && ' ⚠️ lotto presumibilmente esaurito'}
                          {!esaurito && residuoStimato < parseFloat(qtScalata || 0) && ' ⚠️ quantità potrebbe non bastare'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Sezione MP / MOCA */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>📦 MP / MOCA utilizzate</strong>
            {isAperta && (
              <button style={{ ...styles.buttonSmall, fontSize: 12 }} onClick={() => setMostraAddMp(v => !v)}>
                {mostraAddMp ? 'Annulla' : '+ Aggiungi'}
              </button>
            )}
          </div>

          {mostraAddMp && (() => {
            // Calcola ingredienti presenti nella ricetta per ordinare il select
            const idIngrRicetta = new Set();
            if (ricettaDett?.fasi) ricettaDett.fasi.forEach(f => f.ingredienti?.forEach(i => i.id_ingrediente && idIngrRicetta.add(i.id_ingrediente)));
            if (ricettaDett?.ingredienti_senza_fase) ricettaDett.ingredienti_senza_fase.forEach(i => i.id_ingrediente && idIngrRicetta.add(i.id_ingrediente));
            const lottiInRicetta = lottiMp.filter(l => idIngrRicetta.has(l.id_ingrediente));
            const lottiAltri     = lottiMp.filter(l => !idIngrRicetta.has(l.id_ingrediente));
            // Escludi già associati
            const giàAssociati = new Set((dettaglio.mp_utilizzate || []).map(mp => mp.id_magazzino_mp));
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ ...styles.inputText, flex: 3 }} value={mpSel} onChange={e => setMpSel(e.target.value)}>
                    <option value="">— seleziona lotto MP in uso —</option>
                    {lottiInRicetta.length > 0 && (
                      <optgroup label="▶ In ricetta">
                        {lottiInRicetta.filter(l => !giàAssociati.has(l.id)).map(l => (
                          <option key={l.id} value={l.id}>
                            ★ {l.ingrediente_nome} · {l.lotto_interno || l.lotto_fornitore} · {l.quantita} {l.unita_misura}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {lottiAltri.length > 0 && (
                      <optgroup label="Altri lotti in uso">
                        {lottiAltri.filter(l => !giàAssociati.has(l.id)).map(l => (
                          <option key={l.id} value={l.id}>
                            {l.ingrediente_nome} · {l.lotto_interno || l.lotto_fornitore} · {l.quantita} {l.unita_misura}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <button style={{ ...styles.button, padding: '8px 14px', fontSize: 13 }} onClick={aggiungiMp}>OK</button>
                </div>
              </div>
            );
          })()}

          {(dettaglio.mp_utilizzate || []).length === 0 && (
            <p style={{ color: '#999', fontSize: 13, margin: 0 }}>Nessuna MP associata</p>
          )}
          {(dettaglio.mp_utilizzate || []).map(mp => (
            <div key={mp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 13, flex: '1 1 220px', minWidth: 0 }}>
                <strong>{mp.ingrediente_nome}</strong>
                <span style={{ marginLeft: 8, color: '#666' }}>🏷 {mp.lotto_interno}</span>
                {mp.lotto_fornitore && <span style={{ color: '#888' }}> · forn: {mp.lotto_fornitore}</span>}
                {mp.quantita && <span style={{ marginLeft: 8, color: '#888' }}>{mp.quantita} {mp.unita_misura}</span>}
              </div>
              {/* Quantità dichiarata usata (morbida: vuota = stima da ricetta nel bilancio) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#666' }}
                   title="Quantità di questo lotto usata nella lavorazione. Facoltativa e correggibile: vuota = il bilancio usa la stima da ricetta. Non vincola le giacenze.">
                <span>usata:</span>
                <input
                  type="text" inputMode="decimal" placeholder="auto"
                  defaultValue={mp.quantita_usata ?? ''}
                  onBlur={async e => {
                    const v = decVal(e.target.value);
                    if (String(mp.quantita_usata ?? '') === v) return; // invariata
                    const r = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/mp/${mp.id}`, {
                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ quantitaUsata: v === '' ? null : parseFloat(v) }),
                    });
                    if (r.ok) { mp.quantita_usata = v === '' ? null : parseFloat(v); toast('✓ Quantità usata aggiornata'); }
                    else toast('Errore aggiornamento quantità', 'errore');
                  }}
                  style={{ width: 74, padding: '4px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
                />
                <span>{mp.unita_misura || ''}</span>
              </div>
              {isAperta && (
                <button style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} onClick={() => rimuoviMp(mp.id)}>✕</button>
              )}
            </div>
          ))}
        </div>

        {/* Sezione Semilavorati */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>🧩 Semilavorati utilizzati</strong>
            {isAperta && (
              <button style={{ ...styles.buttonSmall, fontSize: 12 }} onClick={() => setMostraAddSl(v => !v)}>
                {mostraAddSl ? 'Annulla' : '+ Aggiungi'}
              </button>
            )}
          </div>

          {mostraAddSl && (() => {
            const giàAssociati = new Set((dettaglio.sl_utilizzati || []).map(sl => sl.id_magazzino_pf));
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ ...styles.inputText, flex: 3 }} value={slSel} onChange={e => setSlSel(e.target.value)}>
                    <option value="">— seleziona lotto semilavorato —</option>
                    {lottiSl.filter(l => !giàAssociati.has(l.id)).map(l => (
                      <option key={l.id} value={l.id}>
                        {l.prodotto_nome} · {l.lotto} · {l.quantita_residua ?? l.quantita} {l.unita_misura}
                      </option>
                    ))}
                  </select>
                  <button style={{ ...styles.button, padding: '8px 14px', fontSize: 13 }} onClick={aggiungiSl}>OK</button>
                </div>
              </div>
            );
          })()}

          {(dettaglio.sl_utilizzati || []).length === 0 && (
            <p style={{ color: '#999', fontSize: 13, margin: 0 }}>Nessun semilavorato associato</p>
          )}
          {(dettaglio.sl_utilizzati || []).map(sl => (
            <div key={sl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 10px', background: '#f5f0ff', borderRadius: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 13, flex: '1 1 220px', minWidth: 0 }}>
                <strong>{sl.prodotto}</strong>
                <span style={{ marginLeft: 8, color: '#666' }}>🏷 {sl.lotto}</span>
                {sl.quantita && <span style={{ marginLeft: 8, color: '#888' }}>{sl.quantita} {sl.unita_misura}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#666' }}
                   title="Quantità di questo lotto usata nella lavorazione. Facoltativa e correggibile: vuota = il bilancio usa la stima da ricetta. Non vincola le giacenze.">
                <span>usata:</span>
                <input
                  type="text" inputMode="decimal" placeholder="auto"
                  defaultValue={sl.quantita_usata ?? ''}
                  onBlur={async e => {
                    const v = decVal(e.target.value);
                    if (String(sl.quantita_usata ?? '') === v) return; // invariata
                    const r = await apiFetch(`${API}/tracciabilita/lavorazioni/${dettaglio.id}/sl/${sl.id}`, {
                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ quantitaUsata: v === '' ? null : parseFloat(v) }),
                    });
                    if (r.ok) { sl.quantita_usata = v === '' ? null : parseFloat(v); toast('✓ Quantità usata aggiornata'); }
                    else toast('Errore aggiornamento quantità', 'errore');
                  }}
                  style={{ width: 74, padding: '4px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
                />
                <span>{sl.unita_misura || ''}</span>
              </div>
              {isAperta && (
                <button style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} onClick={() => rimuoviSl(sl.id)}>✕</button>
              )}
            </div>
          ))}
        </div>

        {/* Sezione Fasi */}
        <div style={{ marginBottom: 20 }}>
          <strong>⚙️ Fasi produttive</strong>

          {ricFasi.length === 0 && (
            <p style={{ color: '#999', fontSize: 13, margin: '8px 0 0' }}>Nessuna fase definita nella ricetta</p>
          )}

          {fasiCombinate.map((rf, idx) => {
            const { statoFase, eseguita } = rf;
            const colori = {
              attesa:     { bg: '#f5f5f5', color: '#999',     border: '#ddd' },
              in_corso:   { bg: '#eaf4ea', color: '#16a34a',  border: '#16a34a' },
              completata: { bg: '#e3f0ff', color: '#1a56b0',  border: '#a0c4ff' },
            }[statoFase] || {};

            let durataTesto = null;
            if (statoFase === 'completata' && eseguita?.data_inizio && eseguita?.data_fine) {
              const diff = Math.round((new Date(eseguita.data_fine) - new Date(eseguita.data_inizio)) / 60000);
              durataTesto = diff >= 60 ? `${Math.floor(diff / 60)}h ${diff % 60}min` : `${diff} min`;
            }

            return (
              <div key={rf.id} style={{ border: `1px solid ${colori.border}`, borderRadius: 8, padding: '10px 14px', marginTop: 8, background: colori.bg }}>
                {(() => {
                  let badgeLabel, badgeBg, badgeColor;
                  if (statoFase === 'attesa') {
                    badgeLabel = 'IN ATTESA'; badgeBg = '#f5f5f5'; badgeColor = '#999';
                  } else if (statoFase === 'in_corso') {
                    badgeLabel = '● IN CORSO'; badgeBg = '#e8f5e9'; badgeColor = '#16a34a';
                  } else if (eseguita?.stato_avanzamento === 'COMPLETA') {
                    badgeLabel = '✓ COMPLETA'; badgeBg = '#e3f0ff'; badgeColor = '#1a56b0';
                  } else if (eseguita?.stato_avanzamento === 'IN_COMPLETAMENTO') {
                    badgeLabel = '◑ IN COMPLETAMENTO'; badgeBg = '#fff3e0'; badgeColor = '#e65100';
                  } else {
                    badgeLabel = '✓ COMPLETATA'; badgeBg = '#e3f0ff'; badgeColor = '#1a56b0';
                  }
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: colori.color }}>
                        {idx + 1}. {rf.fase_nome}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: badgeBg, color: badgeColor, letterSpacing: '0.02em' }}>
                        {badgeLabel}
                      </span>
                    </div>
                  );
                })()}

                {rf.durata_prevista && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>Durata prevista: {rf.durata_prevista} min</div>
                )}

                {/* Ingredienti previsti dalla ricetta per questa fase */}
                {rf.ingredienti && rf.ingredienti.length > 0 && (() => {
                  const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
                  const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
                  const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;
                  return (
                    <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {rf.ingredienti.map(ing => {
                        const qt = ing.quantita ? (parseFloat(ing.quantita) * scala).toFixed(3).replace(/\.?0+$/, '') : null;
                        return (
                          <span key={ing.id} style={{ fontSize: 11, background: '#fff8e1', color: '#795548', border: '1px solid #ffe082', borderRadius: 10, padding: '1px 8px' }}>
                            🧂 {ing.nome_riferimento}{qt ? ` ${qt} ${ing.unita_misura || ''}` : ''}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}

                {statoFase === 'in_corso' && eseguita && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                      <span>Iniziata: {eseguita.data_inizio?.slice(0, 16).replace('T', ' ')}</span>
                      {isAperta && prossima && (
                        <button style={{ ...styles.button, padding: '5px 12px', fontSize: 12 }}
                          onClick={() => apriModalChiusura(eseguita, rf, prossima)}>
                          Avvia fase successiva →
                        </button>
                      )}
                    </div>
                    {/* Giacenze attuali fase in corso */}
                    {giacenzeInCorso.length > 0 && (
                      <div style={{ background: '#f0faf0', border: '1px solid #c8e6c9', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, color: '#2563EB', marginBottom: 4 }}>📍 Posizioni prodotto</div>
                        {giacenzeInCorso.map((g, gi) => (
                          <div key={gi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span style={{ flex: 1 }}>
                              {g.nome_ubicazione}:{g.pz_attuali > 0 ? ` ${g.pz_attuali} pz` : ''}{g.peso_attuale > 0 ? ` / ${Number(g.peso_attuale).toFixed(2)} kg` : ''}
                            </span>
                            {isAperta && (
                              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                <button style={{ fontSize: 10, padding: '2px 6px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 4, cursor: 'pointer' }}
                                  onClick={() => { setModalSposta({ idFase: eseguita.id, idUbicazioneDa: g.id_ubicazione, nomeDa: g.nome_ubicazione, pzAttuali: g.pz_attuali, kgAttuali: g.peso_attuale, dataInizioFase: eseguita.data_inizio }); setDatiSposta({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: oraLocaleInput() }); }}>
                                  ↔ Sposta
                                </button>
                                <button style={{ fontSize: 10, padding: '2px 6px', background: '#fce4ec', color: '#b71c1c', border: '1px solid #f48fb1', borderRadius: 4, cursor: 'pointer' }}
                                  onClick={() => { setModalScarta({ idFase: eseguita.id, idUbicazione: g.id_ubicazione, nome: g.nome_ubicazione, dataInizioFase: eseguita.data_inizio }); setDatiScarta({ quantitaPz: '', pesoKg: '', motivazione: '', dataMovimento: oraLocaleInput() }); }}>
                                  🗑 Scarta
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Aggiungi ubicazione entrata manuale */}
                    {isAperta && !mostraAddUbFase && (
                      <button style={{ fontSize: 11, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => { setMostraAddUbFase(true); setFormAddUbFase({ idUbicazione: '', quantitaPz: '', pesoKg: '', dataMovimento: oraLocaleInput() }); }}>
                        + Aggiungi ubicazione
                      </button>
                    )}
                    {isAperta && mostraAddUbFase && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                        <select value={formAddUbFase.idUbicazione}
                          onChange={e => setFormAddUbFase(f => ({ ...f, idUbicazione: e.target.value }))}
                          style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #ccc', fontSize: 12 }}>
                          <option value="">-- Ubicazione --</option>
                          {ubicazioni.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                        </select>
                        <input type="text" inputMode="decimal" placeholder="Pz" value={formAddUbFase.quantitaPz}
                          onChange={e => setFormAddUbFase(f => ({ ...f, quantitaPz: decVal(e.target.value) }))}
                          style={{ width: 60, padding: '4px 6px', borderRadius: 5, border: '1px solid #ccc', fontSize: 12 }} />
                        <input type="text" inputMode="decimal" placeholder="Kg" value={formAddUbFase.pesoKg}
                          onChange={e => setFormAddUbFase(f => ({ ...f, pesoKg: decVal(e.target.value) }))}
                          style={{ width: 60, padding: '4px 6px', borderRadius: 5, border: '1px solid #ccc', fontSize: 12 }} />
                        <input type="datetime-local" value={formAddUbFase.dataMovimento || ''}
                          min={perInputDataOra(eseguita.data_inizio)}
                          title="Data e ora del movimento"
                          onChange={e => setFormAddUbFase(f => ({ ...f, dataMovimento: e.target.value }))}
                          style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid #ccc', fontSize: 12 }} />
                        <button style={{ fontSize: 11, padding: '3px 8px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                          onClick={async () => {
                            if (!formAddUbFase.idUbicazione) return;
                            const resAdd = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${eseguita.id}/ubicazioni`, {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ idUbicazione: parseInt(formAddUbFase.idUbicazione), quantitaPz: parseFloat(formAddUbFase.quantitaPz) || null, pesoKg: parseFloat(formAddUbFase.pesoKg) || null, tipo: 'ENTRATA', dataMovimento: formAddUbFase.dataMovimento || null }),
                            });
                            if (!resAdd.ok) {
                              const bodyAdd = await resAdd.json().catch(() => ({}));
                              toast(bodyAdd.errore || 'Errore nell\'aggiunta dell\'ubicazione', 'errore');
                              return; // la riga resta aperta per correggere
                            }
                            setMostraAddUbFase(false);
                            await aggiornaGiacenzeInCorso(eseguita.id);
                          }}>✓</button>
                        <button style={{ fontSize: 11, padding: '3px 6px', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                          onClick={() => setMostraAddUbFase(false)}>✕</button>
                      </div>
                    )}
                  </div>
                )}

                {statoFase === 'completata' && eseguita && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>Iniziata: {eseguita.data_inizio?.slice(0, 16)}</span>
                    <span>Conclusa: {eseguita.data_fine?.slice(0, 16)}</span>
                    {durataTesto && (
                      <span>⏱ {durataTesto}{rf.durata_prevista ? ` / ${rf.durata_prevista} min previsti` : ''}</span>
                    )}
                    {isAperta && eseguita.stato_avanzamento !== 'COMPLETA' && (() => {
                      // Usa la fase immediatamente successiva per ordine (non la prima "attesa")
                      const prossimaRicetta  = fasiCombinate[idx + 1];
                      const prossimaEseguita = prossimaRicetta ? eseguite.find(e => e.id_fase === prossimaRicetta.id_fase) : null;
                      return (
                        <button
                          style={{ padding: '3px 10px', fontSize: 11, background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 6, cursor: 'pointer' }}
                          onClick={async () => {
                            await aggiornaGiacenzeFase(eseguita.id);
                            setModalAvanza({
                              idFaseCorrente: eseguita.id,
                              idFaseProssima: prossimaEseguita?.id ?? null,
                              nomeFase: rf.fase_nome,
                              nomeFaseSuccessiva: prossimaRicetta?.fase_nome ?? null,
                              dataInizioFase: eseguita.data_inizio,
                              dataMovimento: oraLocaleInput(),
                            });
                            setAvanziAvanza([{ idUbicazioneDa: '', idUbicazioneA: '', quantitaPz: '', pesoKg: '' }]);
                          }}>
                          📦 Avanza →
                        </button>
                      );
                    })()}
                  </div>
                )}
                {statoFase === 'completata' && eseguita && eseguita.ubicazioni && eseguita.ubicazioni.length > 0 && (
                  <div style={{ marginTop: '0.4rem', fontSize: '0.82em' }}>
                    {eseguita.ubicazioni.map((m, i) => {
                      const coloriTipo = { ENTRATA: '#4caf50', USCITA: '#1976d2', SPOSTAMENTO: '#ff9800', SCARTO: '#d32f2f' };
                      if (m.tipo) {
                        return (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8, marginBottom: 2 }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: coloriTipo[m.tipo] || '#999' }} />
                            {m.tipo} {m.nome_ubicazione}{m.quantita_pz ? ` ${m.quantita_pz} pz` : ''}{m.peso_kg ? ` / ${m.peso_kg} kg` : ''}
                            {m.tipo === 'SCARTO' && m.motivazione && ` — ${m.motivazione}`}
                          </span>
                        );
                      }
                      // Compatibilità con record vecchi senza tipo
                      return (
                        <span key={i}>📍 {m.nome_ubicazione || m.nome}{m.quantita ? ` — ${m.quantita} ${m.unita_misura || ''}` : ''}{i < eseguita.ubicazioni.length - 1 ? ' · ' : ''}</span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {isAperta && !faseInCorso && prossima && (
            <button style={{ ...styles.button, marginTop: 10, padding: '8px 16px', fontSize: 13 }}
              onClick={async () => apriModalAvvio(prossima)}>
              ▶ {eseguite.length === 0 ? 'Avvia prima fase' : 'Avvia fase successiva'}
            </button>
          )}
        </div>

        {/* Sezione Chiusura */}
        {isAperta && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
            <strong>✅ Chiusura lavorazione</strong>
            <div style={{ marginTop: 8 }}>
              <button style={{ ...styles.button, padding: '10px 20px', background: '#1a56b0' }}
                onClick={avviaChiusuraLavorazione}>
                Chiudi lavorazione
              </button>
            </div>
          </div>
        )}

        {/* Annullamento lavorazione (solo admin/master su lavorazione APERTA) */}
        {isAperta && (operatore.admin || operatore.master) && (
          <div style={{ borderTop: '1px solid #fdecea', paddingTop: 16, marginTop: 8 }}>
            <button
              style={{ background: '#fdecea', color: '#b00020', border: '1px solid #f5c2c7', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              onClick={() => setModalAnnulla({ motivazione: '' })}
            >
              🚫 Annulla lavorazione
            </button>
          </div>
        )}

        {/* Riepilogo magazzino PF (lavorazione chiusa) */}
        {dettaglio.stato === 'CHIUSA' && (dettaglio.magazzino_pf || []).length > 0 && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 8 }}>
            <strong>📦 Lotto in magazzino prodotto finito</strong>
            {dettaglio.magazzino_pf.map(pf => (
              <div key={pf.id} style={{ background: '#e3f0ff', borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: 13, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <span>🏷 {pf.lotto}</span>
                {pf.quantita    && <span>⚖️ {pf.quantita} {pf.unita_misura}</span>}
                {pf.data_inizio && <span>📅 {pf.data_inizio}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Modal avvio fase */}
        {modalAvvio && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 500, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>▶ Avvio fase: {modalAvvio.ricettaFase.fase_nome}</div>

              {/* Ingredienti di questa fase */}
              {(modalAvvio.ingredienti || []).length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#444' }}>📦 Ingredienti di questa fase</div>
                  {(modalAvvio.ingredienti || []).map(ing => {
                    const lottiInUso  = ing.lotti.filter(l => l.stato === 'IN_USO');
                    // MP: stato 'IN_MAGAZZINO'; semilavorati: stato 'DISPONIBILE' o NULL (legacy)
                    const lottiInMag  = ing.lotti.filter(l => l.stato === 'IN_MAGAZZINO' || l.stato === 'DISPONIBILE' || l.stato === null);
                    const selezionato = (modalAvvio.lottiScelti || {})[ing.id] || '';
                    return (
                      <div key={ing.id} style={{ background: '#f9f9f9', borderRadius: 8, padding: '10px 12px', marginBottom: 8, border: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            {ing.nome_ingrediente}
                            {ing.tipo === 'SEMILAVORATO' && <span style={{ marginLeft: 6, fontSize: 10, background: '#e3f0ff', color: '#1a56b0', borderRadius: 8, padding: '1px 6px' }}>SL</span>}
                          </span>
                          {ing.quantita_ricetta && (() => {
                            const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
                            const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
                            const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;
                            const qtSc = (parseFloat(ing.quantita_ricetta) * scala).toFixed(3).replace(/\.?0+$/, '');
                            return <span style={{ fontSize: 12, color: '#666' }}>{qtSc} {ing.unita_misura}{scala !== 1 && <span style={{ color: '#1976d2', marginLeft: 4 }}>×{scala.toFixed(2)}</span>}</span>;
                          })()}
                        </div>

                        {ing.lotti.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#b00020' }}>❌ Nessun lotto disponibile in magazzino</div>
                        ) : (
                          <>
                            {lottiInUso.length > 0 && (
                              <select
                                style={{ ...styles.inputText, fontSize: 13, marginBottom: lottiInMag.length > 0 ? 6 : 0 }}
                                value={selezionato}
                                onChange={e => setModalAvvio(m => ({ ...m, lottiScelti: { ...m.lottiScelti, [ing.id]: e.target.value } }))}
                              >
                                <option value="">— nessun lotto —</option>
                                {lottiInUso.map(l => {
                                  const residuo = residuoLotto(l);
                                  const residuoTxt = residuo !== null ? ` · ~${residuo.toFixed(2).replace(/\.?0+$/, '')} ${ing.unita_misura} stim.` : '';
                                  return (
                                    <option key={l.id} value={String(l.id)}>
                                      {l.lotto_interno || l.lotto_fornitore}{residuoTxt}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                            {/* Proposta cambio lotto se residuo stimato insufficiente */}
                            {(() => {
                              if (lottiInUso.length === 0 || lottiInMag.length === 0 || ing.tipo === 'SEMILAVORATO') return null;
                              const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
                              const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
                              const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;
                              const qtSc = ing.quantita_ricetta ? parseFloat(ing.quantita_ricetta) * scala : null;
                              const idSel = selezionato || String(lottiInUso[0]?.id);
                              const lottoAtt = lottiInUso.find(l => String(l.id) === idSel) || lottiInUso[0];
                              if (!lottoAtt || qtSc === null) return null;
                              const residuo = residuoLotto(lottoAtt);
                              if (residuo === null || residuo >= qtSc) return null;
                              const prossimoLotto = lottiInMag[0];
                              return (
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '8px 10px', marginTop: 6, fontSize: 12 }}>
                                  <div style={{ color: '#856404', marginBottom: 6 }}>
                                    ⚠️ Residuo stimato insufficiente (~{residuo.toFixed(2)} {ing.unita_misura} su {qtSc.toFixed(2)} necessari).
                                    {' '}Lotto successivo disponibile: <strong>{prossimoLotto.lotto_interno || prossimoLotto.lotto_fornitore}</strong> ({(residuoLotto(prossimoLotto) || 0).toFixed(2).replace(/\.?0+$/, '')} {ing.unita_misura})
                                  </div>
                                  <button
                                    style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, background: '#e65100', color: '#fff', border: 'none', cursor: 'pointer' }}
                                    onClick={async () => {
                                      const r1 = await apiFetch(`${API}/tracciabilita/magazzino-mp/${lottoAtt.id}/esaurito`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                      if (!r1.ok) return toast('Errore: impossibile segnare lotto come esaurito');
                                      const r2 = await apiFetch(`${API}/tracciabilita/magazzino-mp/${prossimoLotto.id}/in-uso`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                      if (!r2.ok) return toast('Errore: impossibile attivare lotto successivo');
                                      toast('✓ Lotto corrente esaurito, lotto successivo attivato');
                                      setModalAvvio(m => ({
                                        ...m,
                                        lottiScelti: { ...m.lottiScelti, [ing.id]: String(prossimoLotto.id) },
                                        ingredienti: m.ingredienti.map(i =>
                                          i.id === ing.id ? {
                                            ...i,
                                            lotti: i.lotti.map(l =>
                                              l.id === lottoAtt.id ? { ...l, stato: 'ESAURITO' } :
                                              l.id === prossimoLotto.id ? { ...l, stato: 'IN_USO' } : l
                                            ),
                                          } : i
                                        ),
                                      }));
                                    }}
                                  >
                                    ↩ Segna esaurito e attiva lotto successivo
                                  </button>
                                </div>
                              );
                            })()}
                            {lottiInUso.length === 0 && (
                              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 4 }}>
                                ⚠️ Nessun lotto in uso — {ing.tipo === 'SEMILAVORATO' ? 'semilavorati disponibili:' : 'lotti in magazzino:'}
                              </div>
                            )}
                            {lottiInMag.map(l => (
                              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#666', marginTop: 4 }}>
                                <span>{l.lotto_interno || l.lotto_fornitore} · {(residuoLotto(l) || 0).toFixed(2).replace(/\.?0+$/, '')} {ing.unita_misura}</span>
                                <button
                                  style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6, background: '#fff3e0', color: '#b45309', border: '1px solid #f0c070', cursor: 'pointer' }}
                                  onClick={() => mettiInUsoLotto(l.id, ing.id, ing.tipo)}
                                >
                                  Metti in uso
                                </button>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Data/ora inizio */}
              <label style={styles.label}>Data e ora inizio</label>
              <input
                type="datetime-local"
                style={styles.inputText}
                value={modalAvvio.dataInizio}
                onChange={e => setModalAvvio(m => ({ ...m, dataInizio: e.target.value }))}
              />

              {/* UBICAZIONI IN ENTRATA */}
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>📍 Ubicazioni in entrata <span style={{ fontWeight: 400, color: '#888', fontSize: '0.85em' }}>(opzionale)</span></div>
                {giacenzeFasePrecedente && giacenzeFasePrecedente.length > 0 && (
                  <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85em' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#1976d2' }}>Dalla fase precedente:</div>
                    {giacenzeFasePrecedente.map((g, i) => (
                      <div key={i}>📍 {g.nome_ubicazione}: {g.pz_attuali > 0 ? `${g.pz_attuali} pz` : ''}{g.peso_attuale > 0 ? ` / ${g.peso_attuale} kg` : ''}</div>
                    ))}
                  </div>
                )}
                {ubicazioniFase.map((u, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <select value={u.idUbicazione}
                            onChange={e => setUbicazioniFase(prev => prev.map((x, j) => j === i ? { ...x, idUbicazione: e.target.value } : x))}
                            style={{ flex: 2, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}>
                      <option value="">-- Ubicazione --</option>
                      {ubicazioni.map(ub => <option key={ub.id} value={ub.id}>{ub.nome}</option>)}
                    </select>
                    <input type="text" inputMode="decimal" placeholder="Pz" value={u.quantitaPz || ''}
                           onChange={e => setUbicazioniFase(prev => prev.map((x, j) => j === i ? { ...x, quantitaPz: decVal(e.target.value) } : x))}
                           style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} />
                    <input type="text" inputMode="decimal" placeholder="Kg" value={u.pesoKg || ''}
                           onChange={e => setUbicazioniFase(prev => prev.map((x, j) => j === i ? { ...x, pesoKg: decVal(e.target.value) } : x))}
                           style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} />
                    <button onClick={() => setUbicazioniFase(prev => prev.filter((_, j) => j !== i))}
                            style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1em' }}>✕</button>
                  </div>
                ))}
                <button onClick={() => setUbicazioniFase(prev => [...prev, { idUbicazione: '', quantitaPz: '', pesoKg: '' }])}
                        style={{ fontSize: '0.9em', color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Aggiungi ubicazione</button>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button style={styles.btnAnnulla} onClick={() => { setModalAvvio(null); setUbicazioniFase([]); }}>Annulla</button>
                <button style={styles.button} onClick={confermaAvvio}>✓ Conferma</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal annullamento lavorazione */}
        {modalAnnulla && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#b00020' }}>🚫 Annulla lavorazione</div>
              <div style={{ background: '#fdecea', border: '1px solid #f5c2c7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#7a0018' }}>
                ⚠️ Stai per annullare questa lavorazione. L'operazione non può essere annullata.
              </div>
              <label style={styles.label}>Motivazione *</label>
              <textarea
                style={{ ...styles.textarea, border: '1px solid #f5c2c7' }}
                rows={3}
                value={modalAnnulla.motivazione}
                onChange={e => setModalAnnulla(m => ({ ...m, motivazione: e.target.value }))}
                placeholder="Indica il motivo dell'annullamento…"
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={styles.btnAnnulla} onClick={() => setModalAnnulla(null)}>Annulla</button>
                <button
                  style={{ ...styles.button, background: '#b00020', flex: 1 }}
                  onClick={annullaLavorazione}
                >
                  Conferma annullamento
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal chiusura fase */}
        {modalChiusura && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
                {modalChiusura.dopoChiudeLavorazione ? '🏁 Chiusura Lavorazione' : '✓ Chiusura fase: ' + modalChiusura.nomeFase}
              </div>
              <label style={styles.label}>Data e ora inizio</label>
              <input
                type="datetime-local"
                style={styles.inputText}
                value={modalChiusura.dataInizio}
                onChange={e => setModalChiusura(m => ({ ...m, dataInizio: e.target.value }))}
              />
              <label style={styles.label}>Data e ora fine</label>
              <input
                type="datetime-local"
                style={styles.inputText}
                value={modalChiusura.dataFine}
                onChange={e => setModalChiusura(m => ({ ...m, dataFine: e.target.value }))}
              />
              {modalChiusura.parDef.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Parametri rilevati</div>
                  {modalChiusura.parDef.map((p, i) => {
                    const um     = p.um || p.unita || '';
                    const val    = modalChiusura.parametri[p.nome] || '';
                    const hasMin = p.min != null && p.min !== '';
                    const hasMax = p.max != null && p.max !== '';
                    const v      = val !== '' ? parseFloat(val) : null;
                    const fuoriLimite = v !== null && (
                      (hasMin && hasMax && (v < parseFloat(p.min) || v > parseFloat(p.max))) ||
                      (hasMin && !hasMax && v < parseFloat(p.min)) ||
                      (!hasMin && hasMax && v > parseFloat(p.max))
                    );
                    const rangeMsg = (() => {
                      const u = um ? ` ${um}` : '';
                      if (hasMin && hasMax) return `Accettabile: ${p.min}${u} — ${p.max}${u}`;
                      if (hasMin)          return `Minimo accettabile: ${p.min}${u}`;
                      if (hasMax)          return `Massimo accettabile: ${p.max}${u}`;
                      return null;
                    })();
                    return (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ flex: 2, fontSize: 13, color: '#444' }}>{p.nome}{um ? ` (${um})` : ''}</label>
                          <input
                            type="text" inputMode="decimal"
                            style={{ ...styles.inputText, flex: 1, borderColor: fuoriLimite ? '#b00020' : undefined }}
                            value={val}
                            onChange={e => setModalChiusura(m => ({ ...m, parametri: { ...m.parametri, [p.nome]: decVal(e.target.value) } }))}
                          />
                        </div>
                        {rangeMsg && (
                          <div style={{ fontSize: 11, color: fuoriLimite ? '#b00020' : '#999', marginTop: 3, paddingLeft: 2 }}>
                            {fuoriLimite ? `⚠️ Valore fuori limite (${rangeMsg.toLowerCase()})` : rangeMsg}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Giacenze attuali nella fase */}
              {giacenzeFase.length > 0 && (
                <div style={{ background: '#f0faf0', border: '1px solid #c8e6c9', borderRadius: 8, padding: '8px 12px', marginTop: 12, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#2563EB', marginBottom: 4 }}>📍 Posizioni attuali</div>
                  {giacenzeFase.map((g, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                      <span style={{ flex: 1 }}>{g.nome_ubicazione}</span>
                      <span>{g.pz_attuali > 0 ? `${g.pz_attuali} pz` : ''}{g.peso_attuale > 0 ? ` / ${Number(g.peso_attuale).toFixed(2)} kg` : ''}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Dove va il prodotto? */}
              {modalChiusura.ricettaFaseProssima && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>📦 Dove va il prodotto? <span style={{ fontWeight: 400, color: '#888', fontSize: '0.85em' }}>(opzionale)</span></div>
                  {giacenzeFase.length > 1
                    ? <div style={{ fontSize: 12, color: '#e65100', marginBottom: 6 }}>⚠️ Più ubicazioni attive — specifica da quale prelevare in ogni riga</div>
                    : <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Ubicazioni di entrata per la fase successiva</div>
                  }
                  {ubicazioniUscita.map((u, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Sorgente: mostrata solo se ci sono più ubicazioni attive */}
                      {giacenzeFase.length > 1 && (
                        <select value={u.idUbicazioneDa || ''}
                          onChange={e => setUbicazioniUscita(prev => prev.map((x, j) => j === i ? { ...x, idUbicazioneDa: e.target.value } : x))}
                          style={{ flex: 1, minWidth: 100, padding: '5px 7px', borderRadius: 6, border: u.idUbicazioneDa ? '1px solid #ccc' : '1px solid #e65100', fontSize: 12 }}>
                          <option value="">Da ubicazione *</option>
                          {giacenzeFase.map(g => <option key={g.id_ubicazione} value={g.id_ubicazione}>{g.nome_ubicazione} ({g.pz_attuali} pz)</option>)}
                        </select>
                      )}
                      <select value={u.idUbicazione}
                        onChange={e => setUbicazioniUscita(prev => prev.map((x, j) => j === i ? { ...x, idUbicazione: e.target.value } : x))}
                        style={{ flex: 2, minWidth: 100, padding: '5px 7px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}>
                        <option value="">A ubicazione</option>
                        {ubicazioni.map(ub => <option key={ub.id} value={ub.id}>{ub.nome}</option>)}
                      </select>
                      <input type="text" inputMode="decimal" placeholder="Pz" value={u.quantitaPz || ''}
                        onChange={e => setUbicazioniUscita(prev => prev.map((x, j) => j === i ? { ...x, quantitaPz: decVal(e.target.value) } : x))}
                        style={{ width: 60, padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }} />
                      <input type="text" inputMode="decimal" placeholder="Kg" value={u.pesoKg || ''}
                        onChange={e => setUbicazioniUscita(prev => prev.map((x, j) => j === i ? { ...x, pesoKg: decVal(e.target.value) } : x))}
                        style={{ width: 60, padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }} />
                      <button onClick={() => setUbicazioniUscita(prev => prev.filter((_, j) => j !== i))}
                        style={{ color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1em' }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => {
                    const idUbicazioneDa = giacenzeFase.length === 1 ? String(giacenzeFase[0].id_ubicazione) : '';
                    setUbicazioniUscita(prev => [...prev, { idUbicazione: '', idUbicazioneDa, quantitaPz: '', pesoKg: '' }]);
                  }}
                    style={{ fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    + Aggiungi ubicazione destinazione
                  </button>

                  {/* Calo peso */}
                  {(() => {
                    const pesoIn  = parseFloat(modalChiusura.peso_totale_kg || 0) ||
                                    giacenzeFase.reduce((s, g) => s + parseFloat(g.peso_attuale || 0), 0);
                    const pesoOut = ubicazioniUscita.reduce((s, u) => s + (parseFloat(u.pesoKg) || 0), 0);
                    if (pesoIn <= 0 || pesoOut <= 0) return null;
                    const calo    = pesoIn - pesoOut;
                    const percCalo = pesoIn > 0 ? ((calo / pesoIn) * 100).toFixed(1) : 0;
                    return (
                      <div style={{ marginTop: 10, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: '#795548', marginBottom: 3 }}>⚖️ Calo peso stimato</div>
                        <div style={{ display: 'flex', gap: 14, color: '#555' }}>
                          <span>Entrata: <strong>{Number(pesoIn).toFixed(2)} kg</strong></span>
                          <span>Uscita: <strong>{Number(pesoOut).toFixed(2)} kg</strong></span>
                          <span style={{ color: calo > 0 ? '#e65100' : '#2e7d32' }}>
                            Calo: <strong>{Number(calo).toFixed(2)} kg ({percCalo}%)</strong>
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Ingredienti specifici della fase successiva */}
              {modalChiusura.ingredientiProssima?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#444' }}>
                    🧂 Ingredienti fase successiva: <em>{modalChiusura.ricettaFaseProssima?.fase_nome}</em>
                  </div>
                  {modalChiusura.ingredientiProssima.map(ing => {
                    const lottiInUso = ing.lotti.filter(l => l.stato === 'IN_USO');
                    const lottiInMag = ing.lotti.filter(l => l.stato === 'IN_MAGAZZINO' || l.stato === 'DISPONIBILE' || l.stato === null);
                    const selezionato = (modalChiusura.lottiSceltiProssima || {})[ing.id] || '';
                    const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
                    const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
                    const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;
                    const qtSc = ing.quantita_ricetta ? (parseFloat(ing.quantita_ricetta) * scala).toFixed(3).replace(/\.?0+$/, '') : null;
                    return (
                      <div key={ing.id} style={{ background: '#f9f9f9', borderRadius: 8, padding: '10px 12px', marginBottom: 8, border: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            {ing.nome_ingrediente}
                            {ing.tipo === 'SEMILAVORATO' && <span style={{ marginLeft: 6, fontSize: 10, background: '#e3f0ff', color: '#1a56b0', borderRadius: 8, padding: '1px 6px' }}>SL</span>}
                          </span>
                          {qtSc && <span style={{ fontSize: 12, color: '#666' }}>{qtSc} {ing.unita_misura}</span>}
                        </div>
                        {ing.lotti.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#b00020' }}>❌ Nessun lotto disponibile in magazzino</div>
                        ) : (
                          <>
                            {lottiInUso.length > 0 && (
                              <select
                                style={{ ...styles.inputText, fontSize: 13, marginBottom: lottiInMag.length > 0 ? 6 : 0 }}
                                value={selezionato}
                                onChange={e => setModalChiusura(m => ({ ...m, lottiSceltiProssima: { ...m.lottiSceltiProssima, [ing.id]: e.target.value } }))}
                              >
                                <option value="">— nessun lotto —</option>
                                {lottiInUso.map(l => {
                                  const residuo = residuoLotto(l);
                                  const residuoTxt = residuo !== null ? ` · ~${residuo.toFixed(2).replace(/\.?0+$/, '')} ${ing.unita_misura} stim.` : '';
                                  return (
                                    <option key={l.id} value={String(l.id)}>
                                      {l.lotto_interno || l.lotto_fornitore}{residuoTxt}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                            {/* Proposta cambio lotto se residuo stimato insufficiente */}
                            {(() => {
                              if (lottiInUso.length === 0 || lottiInMag.length === 0 || ing.tipo === 'SEMILAVORATO') return null;
                              const qRic = parseFloat(ricettaDett?.quantita_prevista || 0);
                              const qLav = parseFloat(dettaglio?.quantita_prevista || 0);
                              const scala = (qRic > 0 && qLav > 0) ? qLav / qRic : 1;
                              const qtSc = ing.quantita_ricetta ? parseFloat(ing.quantita_ricetta) * scala : null;
                              const idSel = selezionato || String(lottiInUso[0]?.id);
                              const lottoAtt = lottiInUso.find(l => String(l.id) === idSel) || lottiInUso[0];
                              if (!lottoAtt || qtSc === null) return null;
                              const residuo = residuoLotto(lottoAtt);
                              if (residuo === null || residuo >= qtSc) return null;
                              const prossimoLotto = lottiInMag[0];
                              return (
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '8px 10px', marginTop: 6, fontSize: 12 }}>
                                  <div style={{ color: '#856404', marginBottom: 6 }}>
                                    ⚠️ Residuo stimato insufficiente (~{residuo.toFixed(2)} {ing.unita_misura} su {qtSc.toFixed(2)} necessari).
                                    {' '}Lotto successivo disponibile: <strong>{prossimoLotto.lotto_interno || prossimoLotto.lotto_fornitore}</strong> ({prossimoLotto.quantita} {ing.unita_misura})
                                  </div>
                                  <button
                                    style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, background: '#e65100', color: '#fff', border: 'none', cursor: 'pointer' }}
                                    onClick={async () => {
                                      const r1 = await apiFetch(`${API}/tracciabilita/magazzino-mp/${lottoAtt.id}/esaurito`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                      if (!r1.ok) return toast('Errore: impossibile segnare lotto come esaurito');
                                      const r2 = await apiFetch(`${API}/tracciabilita/magazzino-mp/${prossimoLotto.id}/in-uso`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                      if (!r2.ok) return toast('Errore: impossibile attivare lotto successivo');
                                      toast('✓ Lotto corrente esaurito, lotto successivo attivato');
                                      setModalChiusura(m => ({
                                        ...m,
                                        lottiSceltiProssima: { ...m.lottiSceltiProssima, [ing.id]: String(prossimoLotto.id) },
                                        ingredientiProssima: m.ingredientiProssima.map(i =>
                                          i.id === ing.id ? {
                                            ...i,
                                            lotti: i.lotti.map(l =>
                                              l.id === lottoAtt.id ? { ...l, stato: 'ESAURITO' } :
                                              l.id === prossimoLotto.id ? { ...l, stato: 'IN_USO' } : l
                                            ),
                                          } : i
                                        ),
                                      }));
                                    }}
                                  >
                                    ↩ Segna esaurito e attiva lotto successivo
                                  </button>
                                </div>
                              );
                            })()}
                            {lottiInUso.length === 0 && (
                              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 4 }}>
                                ⚠️ Nessun lotto in uso — {ing.tipo === 'SEMILAVORATO' ? 'semilavorati disponibili:' : 'lotti in magazzino:'}
                              </div>
                            )}
                            {lottiInMag.map(l => (
                              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#666', marginTop: 4 }}>
                                <span>{l.lotto_interno || l.lotto_fornitore} · {l.quantita} {ing.unita_misura}</span>
                                <button
                                  style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6, background: '#fff3e0', color: '#b45309', border: '1px solid #f0c070', cursor: 'pointer' }}
                                  onClick={async () => {
                                    const endpoint = ing.tipo === 'SEMILAVORATO'
                                      ? `${API}/tracciabilita/magazzino-pf/${l.id}/in-uso`
                                      : `${API}/tracciabilita/magazzino-mp/${l.id}/in-uso`;
                                    const res = await apiFetch(endpoint, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                                    if (!res.ok) return toast('Errore aggiornamento lotto');
                                    toast('✓ Lotto messo in uso');
                                    setModalChiusura(m => ({
                                      ...m,
                                      lottiSceltiProssima: { ...m.lottiSceltiProssima, [ing.id]: String(l.id) },
                                      ingredientiProssima: m.ingredientiProssima.map(i =>
                                        i.id === ing.id ? { ...i, lotti: i.lotti.map(x => x.id === l.id ? { ...x, stato: 'IN_USO' } : x) } : i
                                      ),
                                    }));
                                  }}
                                >
                                  Metti in uso
                                </button>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {modalChiusura.dopoChiudeLavorazione && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '2px solid #eee' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#1a56b0' }}>📦 Dati finali lavorazione</div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Pezzi totali prodotti</label>
                      <input type="text" inputMode="decimal"
                             style={styles.inputText}
                             value={modalChiusura.quantitaPzReale}
                             onChange={e => setModalChiusura(m => ({ ...m, quantitaPzReale: decVal(e.target.value) }))}
                             placeholder="es. 100" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Peso totale prodotto (Kg)</label>
                      <input type="text" inputMode="decimal"
                             style={styles.inputText}
                             value={modalChiusura.pesoKgReale}
                             onChange={e => setModalChiusura(m => ({ ...m, pesoKgReale: decVal(e.target.value) }))}
                             placeholder="es. 45.5" />
                    </div>
                  </div>
                  {modalChiusura.pesoInizialeFase1 && modalChiusura.pesoKgReale && (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#555', background: '#f5f5f5', padding: '6px 10px', borderRadius: 4 }}>
                      📉 Calopeso lavorazione: <strong>{(parseFloat(modalChiusura.pesoInizialeFase1) - parseFloat(modalChiusura.pesoKgReale)).toFixed(2)} Kg</strong>
                      <span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>(Iniziale: {modalChiusura.pesoInizialeFase1} Kg)</span>
                    </div>
                  )}
                  {modalChiusura.pesoInizialeFase1 && !modalChiusura.pesoKgReale && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                      Inserisci il peso finale per calcolare il calopeso. Peso iniziale rilevato: {modalChiusura.pesoInizialeFase1} Kg.
                    </div>
                  )}

                  {/* ITEM B — dove resta il prodotto dopo la chiusura */}
                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px dashed #ddd' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#1a56b0' }}>📍 Dove resta il prodotto</div>
                    <div style={{ fontSize: 12, color: '#777', marginBottom: 8, lineHeight: 1.4 }}>
                      Chiudendo la lavorazione il prodotto non sparisce: resta nelle ubicazioni indicate qui e lo ritrovi
                      nelle giacenze aziendali. Le righe sono già compilate con quello che c'è adesso: correggile se il
                      prodotto va altrove o se il peso è calato.
                    </div>

                    {(() => {
                      const residui = giacenzeLavorazione.filter(g => !destinazioniChiusura.some(d =>
                        String(d.idFaseDa) === String(g.id_fase) &&
                        String(d.idUbicazioneDa) === String(g.id_ubicazione)));
                      if (!residui.length) return null;
                      const r3 = n => Math.round((parseFloat(n) || 0) * 1000) / 1000;
                      const q = g => [r3(g.pz_attuali) > 0 ? r3(g.pz_attuali) + ' pz' : null,
                                      r3(g.peso_attuale) > 0 ? r3(g.peso_attuale) + ' kg' : null]
                                     .filter(Boolean).join(' / ');
                      return (
                        <div style={{ fontSize: 12, color: '#6d4c41', background: '#fffde7', border: '1px solid #ffe082', borderRadius: 6, padding: '7px 10px', marginBottom: 8, lineHeight: 1.45 }}>
                          Non assegnati a nessuna riga: {residui.map(g => `${g.nome_ubicazione} (${g.nome_fase}) ${q(g)}`).join(' · ')}.
                          {' '}Alla chiusura queste quantità vengono registrate come calo di lavorazione e l'ubicazione torna a zero.
                          {' '}Se invece è prodotto ancora presente, aggiungi una riga e scegli quell'ubicazione nel campo "Da".
                        </div>
                      );
                    })()}

                    {destinazioniChiusura.length === 0 && (
                      <div style={{ fontSize: 12, color: '#e65100', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>
                        Nessuna giacenza rilevata nelle ubicazioni: la lavorazione verrà chiusa senza indicare dove resta il prodotto.
                      </div>
                    )}

                    {destinazioniChiusura.map((d, i) => {
                      const gOrig = giacenzeLavorazione.find(g =>
                        String(g.id_fase) === String(d.idFaseDa) && String(g.id_ubicazione) === String(d.idUbicazioneDa));
                      return (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {giacenzeLavorazione.length > 1 && (
                              <select
                                value={`${d.idFaseDa || ''}|${d.idUbicazioneDa || ''}`}
                                onChange={e => {
                                  const parti = e.target.value.split('|');
                                  setDestinazioniChiusura(prev => prev.map((x, j) => j === i
                                    ? { ...x, idFaseDa: parti[0] || '', idUbicazioneDa: parti[1] || '' } : x));
                                }}
                                style={{ flex: 2, minWidth: 130, padding: '6px 8px', borderRadius: 6, fontSize: 12,
                                         border: d.idUbicazioneDa ? '1px solid #ccc' : '1px solid #e65100' }}>
                                <option value="|">Da ubicazione *</option>
                                {giacenzeLavorazione.map(g => (
                                  <option key={`${g.id_fase}|${g.id_ubicazione}`} value={`${g.id_fase}|${g.id_ubicazione}`}>
                                    {g.nome_ubicazione} — {g.nome_fase}
                                  </option>
                                ))}
                              </select>
                            )}
                            <select
                              value={d.idUbicazione}
                              onChange={e => setDestinazioniChiusura(prev => prev.map((x, j) => j === i ? { ...x, idUbicazione: e.target.value } : x))}
                              style={{ flex: 2, minWidth: 130, padding: '6px 8px', borderRadius: 6, fontSize: 12,
                                       border: d.idUbicazione ? '1px solid #ccc' : '1px solid #e65100' }}>
                              <option value="">Ubicazione finale *</option>
                              {ubicazioni.map(ub => <option key={ub.id} value={ub.id}>{ub.nome}</option>)}
                            </select>
                            <input type="text" inputMode="decimal" placeholder="Pz" value={d.quantitaPz || ''}
                              onChange={e => setDestinazioniChiusura(prev => prev.map((x, j) => j === i ? { ...x, quantitaPz: decVal(e.target.value) } : x))}
                              style={{ width: 70, padding: '6px 7px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }} />
                            <input type="text" inputMode="decimal" placeholder="Kg" value={d.pesoKg || ''}
                              onChange={e => setDestinazioniChiusura(prev => prev.map((x, j) => j === i ? { ...x, pesoKg: decVal(e.target.value) } : x))}
                              style={{ width: 80, padding: '6px 7px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }} />
                            <button onClick={() => setDestinazioniChiusura(prev => prev.filter((_, j) => j !== i))}
                              title="Togli questa riga"
                              style={{ color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1em' }}>✕</button>
                          </div>
                          {gOrig && (
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2, marginLeft: 2 }}>
                              da <strong>{gOrig.nome_ubicazione}</strong> ({gOrig.nome_fase}) — in giacenza{' '}
                              {parseFloat(gOrig.pz_attuali) > 0 ? `${Math.round(parseFloat(gOrig.pz_attuali) * 1000) / 1000} pz` : ''}
                              {parseFloat(gOrig.pz_attuali) > 0 && parseFloat(gOrig.peso_attuale) > 0 ? ' / ' : ''}
                              {parseFloat(gOrig.peso_attuale) > 0 ? `${Math.round(parseFloat(gOrig.peso_attuale) * 1000) / 1000} kg` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {giacenzeLavorazione.length > 0 && (
                      <button onClick={() => {
                        const g0 = giacenzeLavorazione.length === 1 ? giacenzeLavorazione[0] : null;
                        setDestinazioniChiusura(prev => [...prev, {
                          idFaseDa:       g0 ? String(g0.id_fase) : '',
                          idUbicazioneDa: g0 ? String(g0.id_ubicazione) : '',
                          idUbicazione: '', quantitaPz: '', pesoKg: '',
                        }]);
                      }}
                        style={{ fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        + Aggiungi ubicazione finale
                      </button>
                    )}

                    {destinazioniChiusura.length > 0 && (() => {
                      const somma = k => destinazioniChiusura.reduce((t, d) => t + (parseFloat(d[k]) || 0), 0);
                      const r3 = n => Math.round(n * 1000) / 1000;
                      const pzDest = somma('quantitaPz'), kgDest = somma('pesoKg');
                      const pzDich = parseFloat(modalChiusura.quantitaPzReale || 0);
                      const kgDich = parseFloat(modalChiusura.pesoKgReale || 0);
                      const fuoriPz = pzDich > 0 && Math.abs(pzDest - pzDich) > 0.0005;
                      const fuoriKg = kgDich > 0 && Math.abs(kgDest - kgDich) > 0.0005;
                      const allinea = () => setDestinazioniChiusura(prev => {
                        const tPz = prev.reduce((t, d) => t + (parseFloat(d.quantitaPz) || 0), 0);
                        const tKg = prev.reduce((t, d) => t + (parseFloat(d.pesoKg) || 0), 0);
                        return prev.map(d => {
                          const pz = parseFloat(d.quantitaPz) || 0;
                          const kg = parseFloat(d.pesoKg) || 0;
                          return {
                            ...d,
                            quantitaPz: (pzDich > 0 && tPz > 0) ? String(r3(pz / tPz * pzDich)) : d.quantitaPz,
                            pesoKg:     (kgDich > 0 && tKg > 0) ? String(r3(kg / tKg * kgDich)) : d.pesoKg,
                          };
                        });
                      });
                      return (
                        <div style={{ marginTop: 10, background: fuoriPz || fuoriKg ? '#fff8e1' : '#f0faf0',
                                      border: `1px solid ${fuoriPz || fuoriKg ? '#ffe082' : '#c8e6c9'}`,
                                      borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', color: '#555' }}>
                            <span>Totale nelle ubicazioni: <strong>{r3(pzDest)} pz</strong> / <strong>{r3(kgDest)} kg</strong></span>
                            {(pzDich > 0 || kgDich > 0) && (
                              <span>Dati finali dichiarati: <strong>{r3(pzDich)} pz</strong> / <strong>{r3(kgDich)} kg</strong></span>
                            )}
                          </div>
                          {(fuoriPz || fuoriKg) && (
                            <div style={{ marginTop: 6, color: '#795548' }}>
                              ⚖️ I due totali devono coincidere. Abbassa le righe di quanto è andato perso — la differenza diventa calo da sola — oppure
                              <button onClick={allinea}
                                style={{ marginLeft: 8, fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                                allinea ai dati finali
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {parametriFuoriLimite.length > 0 && (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107',
                  borderRadius: 8, padding: '10px 14px', marginTop: 14,
                  fontSize: 13, color: '#856404'
                }}>
                  ⚠️ {parametriFuoriLimite.length === 1
                    ? '1 parametro fuori limite'
                    : `${parametriFuoriLimite.length} parametri fuori limite`} — verrà creata automaticamente una Non Conformità
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button style={styles.btnAnnulla} onClick={() => { setModalChiusura(null); setUbicazioniFase([]); setUbicazioniUscita([]); setGiacenzeFase([]); setDestinazioniChiusura([]); setGiacenzeLavorazione([]); }}>Annulla</button>
                <button style={styles.button} onClick={confermaChiusura}>✓ Conferma</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Sposta prodotto */}
        {modalSposta && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', width: 400, maxWidth: '90vw' }}>
              <h3 style={{ margin: '0 0 1rem' }}>📦 Sposta prodotto</h3>
              <div style={{ marginBottom: '0.75rem', fontSize: '0.9em', color: '#555' }}>
                Da: <strong>{modalSposta.nomeDa}</strong> ({modalSposta.pzAttuali} pz / {Number(modalSposta.kgAttuali || 0).toFixed(2)} kg disponibili)
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ ...styles.label, marginTop: 0 }}>A ubicazione:</label>
                <select value={datiSposta.idUbicazioneA}
                        onChange={e => setDatiSposta(p => ({ ...p, idUbicazioneA: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem' }}>
                  <option value="">-- Seleziona --</option>
                  {ubicazioni.filter(u => u.id !== modalSposta.idUbicazioneDa).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginTop: 0 }}>Pezzi:</label>
                  <input type="text" inputMode="decimal" value={datiSposta.quantitaPz}
                         onChange={e => setDatiSposta(p => ({ ...p, quantitaPz: decVal(e.target.value) }))}
                         style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginTop: 0 }}>Kg:</label>
                  <input type="text" inputMode="decimal" value={datiSposta.pesoKg}
                         onChange={e => setDatiSposta(p => ({ ...p, pesoKg: decVal(e.target.value) }))}
                         style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ ...styles.label, marginTop: 0 }}>Data e ora dello spostamento:</label>
                <input type="datetime-local" value={datiSposta.dataMovimento}
                       min={perInputDataOra(modalSposta.dataInizioFase)}
                       onChange={e => setDatiSposta(p => ({ ...p, dataMovimento: e.target.value }))}
                       style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
                  Precompilata con l'ora attuale. Si puo' correggere, ma non prima dell'inizio della fase.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => { setModalSposta(null); setDatiSposta({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: '' }); }}
                        style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#fff' }}>Annulla</button>
                <button onClick={async () => {
                  if (!datiSposta.idUbicazioneA) { alert('Seleziona ubicazione destinazione'); return; }
                  const idFaseSp = modalSposta.idFase;
                  const resSp = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${idFaseSp}/sposta`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idUbicazioneDa: modalSposta.idUbicazioneDa, idUbicazioneA: parseInt(datiSposta.idUbicazioneA), quantitaPz: parseFloat(datiSposta.quantitaPz) || null, pesoKg: parseFloat(datiSposta.pesoKg) || null, dataMovimento: datiSposta.dataMovimento || null })
                  });
                  if (!resSp.ok) {
                    const body = await resSp.json().catch(() => ({}));
                    toast(body.errore || 'Errore nello spostamento', 'errore');
                    return; // il modal resta aperto per correggere
                  }
                  setModalSposta(null);
                  setDatiSposta({ idUbicazioneA: '', quantitaPz: '', pesoKg: '', dataMovimento: '' });
                  await aggiornaGiacenzeInCorso(idFaseSp);
                }}
                        style={{ padding: '0.5rem 1rem', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Conferma spostamento</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Scarta prodotto */}
        {modalScarta && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', width: 420, maxWidth: '90vw' }}>
              <h3 style={{ margin: '0 0 1rem' }}>🗑️ Scarta prodotto</h3>
              <div style={{ marginBottom: '0.75rem', background: '#fff3e0', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85em', color: '#e65100' }}>
                ⚠️ Verrà creata automaticamente una Non Conformità
              </div>
              <div style={{ marginBottom: '0.5rem', fontSize: '0.9em' }}>
                Ubicazione: <strong>{modalScarta.nome}</strong>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginTop: 0 }}>Pezzi:</label>
                  <input type="text" inputMode="decimal" value={datiScarta.quantitaPz}
                         onChange={e => setDatiScarta(p => ({ ...p, quantitaPz: decVal(e.target.value) }))}
                         style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginTop: 0 }}>Kg:</label>
                  <input type="text" inputMode="decimal" value={datiScarta.pesoKg}
                         onChange={e => setDatiScarta(p => ({ ...p, pesoKg: decVal(e.target.value) }))}
                         style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ ...styles.label, marginTop: 0 }}>Motivazione <span style={{ color: 'red' }}>*</span>:</label>
                <input value={datiScarta.motivazione}
                       onChange={e => setDatiScarta(p => ({ ...p, motivazione: e.target.value }))}
                       placeholder="es. difettosità, contaminazione..."
                       style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ ...styles.label, marginTop: 0 }}>Data e ora dello scarto:</label>
                <input type="datetime-local" value={datiScarta.dataMovimento}
                       min={perInputDataOra(modalScarta.dataInizioFase)}
                       onChange={e => setDatiScarta(p => ({ ...p, dataMovimento: e.target.value }))}
                       style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
                  Precompilata con l'ora attuale. Si puo' correggere, ma non prima dell'inizio della fase.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => { setModalScarta(null); setDatiScarta({ quantitaPz: '', pesoKg: '', motivazione: '', dataMovimento: '' }); }}
                        style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#fff' }}>Annulla</button>
                <button onClick={async () => {
                  if (!datiScarta.motivazione.trim()) { alert('Motivazione obbligatoria'); return; }
                  const idFaseSc = modalScarta.idFase;
                  const resSc = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${idFaseSc}/scarta`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idUbicazione: modalScarta.idUbicazione, quantitaPz: parseFloat(datiScarta.quantitaPz) || null, pesoKg: parseFloat(datiScarta.pesoKg) || null, motivazione: datiScarta.motivazione, idOperatore: operatore?.id, dataMovimento: datiScarta.dataMovimento || null })
                  });
                  if (!resSc.ok) {
                    const body = await resSc.json().catch(() => ({}));
                    toast(body.errore || 'Errore nello scarto', 'errore');
                    return; // il modal resta aperto per correggere
                  }
                  setModalScarta(null);
                  setDatiScarta({ quantitaPz: '', pesoKg: '', motivazione: '', dataMovimento: '' });
                  await aggiornaGiacenzeInCorso(idFaseSc);
                }}
                        style={{ padding: '0.5rem 1rem', background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Conferma scarto</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Avanza fase — USCITA su fase corrente + ENTRATA su fase successiva */}
        {modalAvanza && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', width: 520, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto' }}>
              <h3 style={{ margin: '0 0 0.25rem' }}>📦 Avanza prodotto</h3>
              <div style={{ fontSize: 13, color: '#666', marginBottom: '1rem' }}>
                Da: <strong>{modalAvanza.nomeFase}</strong>
                {modalAvanza.nomeFaseSuccessiva && <> → <strong>{modalAvanza.nomeFaseSuccessiva}</strong></>}
              </div>

              {/* Giacenze disponibili (riferimento) */}
              {giacenzeFase.length > 0 && (
                <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 4 }}>Giacenze nella fase:</div>
                  {giacenzeFase.map((g, i) => (
                    <div key={i}>📍 {g.nome_ubicazione}: {g.pz_attuali > 0 ? `${g.pz_attuali} pz` : ''}{g.peso_attuale > 0 ? ` / ${Number(g.peso_attuale).toFixed(2)} kg` : ''}</div>
                  ))}
                </div>
              )}

              {/* Righe avanzi */}
              {(() => {
                const hasGiacenze = giacenzeFase.length > 0;
                return (
                  <>
                    {avanziAvanza.map((a, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: hasGiacenze ? '1fr 1fr 70px 70px 28px' : '1fr 70px 70px 28px', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                        {hasGiacenze && (
                          <select value={a.idUbicazioneDa}
                                  onChange={e => setAvanziAvanza(prev => prev.map((x, j) => j === i ? { ...x, idUbicazioneDa: e.target.value } : x))}
                                  style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}>
                            <option value="">Da ubicazione</option>
                            {giacenzeFase.map(g => <option key={g.id_ubicazione} value={g.id_ubicazione}>{g.nome_ubicazione}</option>)}
                          </select>
                        )}
                        <select value={a.idUbicazioneA}
                                onChange={e => setAvanziAvanza(prev => prev.map((x, j) => j === i ? { ...x, idUbicazioneA: e.target.value } : x))}
                                style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}>
                          <option value="">A ubicazione</option>
                          {ubicazioni.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                        </select>
                        <input type="text" inputMode="decimal" placeholder="Pz" value={a.quantitaPz}
                               onChange={e => setAvanziAvanza(prev => prev.map((x, j) => j === i ? { ...x, quantitaPz: decVal(e.target.value) } : x))}
                               style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} />
                        <input type="text" inputMode="decimal" placeholder="Kg" value={a.pesoKg}
                               onChange={e => setAvanziAvanza(prev => prev.map((x, j) => j === i ? { ...x, pesoKg: decVal(e.target.value) } : x))}
                               style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} />
                        <button onClick={() => setAvanziAvanza(prev => prev.filter((_, j) => j !== i))}
                                style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 18 }}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => setAvanziAvanza(prev => [...prev, { idUbicazioneDa: '', idUbicazioneA: '', quantitaPz: '', pesoKg: '' }])}
                            style={{ fontSize: 13, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '1rem' }}>+ Aggiungi riga</button>
                  </>
                );
              })()}

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ ...styles.label, marginTop: 0 }}>Data e ora del passaggio:</label>
                <input type="datetime-local" value={modalAvanza.dataMovimento || ''}
                       min={perInputDataOra(modalAvanza.dataInizioFase)}
                       onChange={e => setModalAvanza(p => ({ ...p, dataMovimento: e.target.value }))}
                       style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginTop: '0.25rem', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
                  E' la data che comparira' sui movimenti, a video e in stampa. Precompilata con l'ora attuale.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => { setModalAvanza(null); setAvanziAvanza([]); setGiacenzeFase([]); }}
                        style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#fff' }}>Annulla</button>
                <button onClick={async () => {
                  const hasGiacenze = giacenzeFase.length > 0;
                  const righe = avanziAvanza.filter(a => hasGiacenze ? (a.idUbicazioneDa && a.idUbicazioneA) : a.idUbicazioneA);
                  if (righe.length === 0) { alert(hasGiacenze ? 'Seleziona almeno una riga con da/a ubicazione' : 'Seleziona almeno una riga con A ubicazione'); return; }
                  const res = await apiFetch(`${API}/tracciabilita/lavorazioni/fasi/${modalAvanza.idFaseCorrente}/avanza`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      idFaseProssima: modalAvanza.idFaseProssima,
                      dataMovimento: modalAvanza.dataMovimento || null,
                      avanzi: righe.map(a => ({
                        idUbicazioneDa: hasGiacenze ? parseInt(a.idUbicazioneDa) : null,
                        idUbicazioneA:  parseInt(a.idUbicazioneA),
                        quantitaPz:     parseFloat(a.quantitaPz) || null,
                        pesoKg:         parseFloat(a.pesoKg) || null,
                      })),
                    }),
                  });
                  if (!res.ok) { const d = await res.json(); toast('Errore: ' + (d.errore || 'sconosciuto')); return; }
                  const d = await res.json();
                  toast(`✓ Avanzamento registrato${d.stato_avanzamento ? ` — ${d.stato_avanzamento}` : ''}`);
                  // Il passaggio non viene mai bloccato per una data sbagliata: il
                  // backend ripiega sull'ora di sistema e lo segnala qui.
                  if (d.avviso) toast(d.avviso, 'errore');
                  setModalAvanza(null);
                  setAvanziAvanza([]);
                  setGiacenzeFase([]);
                  await aggiornaDettaglio();
                }}
                        style={{ padding: '0.5rem 1rem', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  📦 Conferma avanzamento
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

  return null;
}

// ─── Gestione Magazzino Prodotti Finiti ───────────────────────
const MOTIVO_RETTIFICA_LABEL = {
  SCARTO:     'Scarto/scadenza',
  SFRIDO:     'Calo/sfrido lavorazione',
  ROTTURA:    'Rottura/danneggiamento',
  INVENTARIO: 'Rettifica inventariale',
  ALTRO:      'Altro',
};

function GestioneMagazzinoPF({ operatore, onBack, onNuovaBolla }) {
  const idAzienda = operatore.id_azienda;
  const [lista,           setLista]           = useState([]);
  const [prodotti,        setProdotti]        = useState([]);
  const [filtroStato,     setFiltroStato]     = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroProd,      setFiltroProd]      = useState('');
  const [filtroPropr,     setFiltroPropr]     = useState('');
  const [haContoTerzi,    setHaContoTerzi]    = useState(false);
  const [ricerca,         setRicerca]         = useState('');
  const [caricando,       setCaricando]       = useState(false);
  const [modalRettifica,  setModalRettifica]  = useState(null);
  const [modalStorico,    setModalStorico]    = useState(null);

  useEffect(() => { carica(); }, [filtroStato, filtroCategoria, filtroProd, filtroPropr]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    apiFetch(`${API}/tracciabilita/prodotti?idAzienda=${idAzienda}`)
      .then(r => r.json()).then(d => setProdotti(Array.isArray(d) ? d : []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = async () => {
    setCaricando(true);
    let url = `${API}/tracciabilita/magazzino-pf?idAzienda=${idAzienda}`;
    if (filtroStato)     url += `&stato=${filtroStato}`;
    if (filtroCategoria) url += `&categoriaProdotto=${filtroCategoria}`;
    if (filtroProd)      url += `&idProdotto=${filtroProd}`;
    if (filtroPropr)     url += `&proprieta=${filtroPropr}`;
    const res  = await apiFetch(url);
    const data = await res.json();
    const righe = Array.isArray(data) ? data : [];
    setLista(righe);
    if (righe.some(r => r.id_committente)) setHaContoTerzi(true);
    setCaricando(false);
  };

  const mettiInUso = async (id) => {
    const res = await apiFetch(`${API}/tracciabilita/magazzino-pf/${id}/in-uso`, { method: 'PUT' });
    if (res.ok) { toast('✓ Lotto messo in uso'); carica(); }
    else toast('Errore aggiornamento stato');
  };

  const segnaEsaurito = async (id) => {
    const res = await apiFetch(`${API}/tracciabilita/magazzino-pf/${id}/esaurito`, { method: 'PUT' });
    if (res.ok) { toast('✓ Lotto segnato come esaurito'); carica(); }
    else toast('Errore aggiornamento stato');
  };

  const apriRettifica = (pf) => {
    setModalRettifica({ pf, nuovaQuantita: '', motivo: 'INVENTARIO', note: '' });
  };

  const confermaRettifica = async () => {
    const { pf, nuovaQuantita, motivo, note } = modalRettifica;
    if (nuovaQuantita === '' || isNaN(parseFloat(nuovaQuantita)) || parseFloat(nuovaQuantita) < 0)
      return toast('Inserire una quantità valida (≥ 0)');
    if (motivo === 'ALTRO' && !note.trim())
      return toast('Le note sono obbligatorie per il motivo "Altro"');
    const res = await apiFetch(`${API}/tracciabilita/magazzino-pf/${pf.id}/rettifica`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantitaDopo: parseFloat(nuovaQuantita), motivo, note: note || null, idOperatore: operatore.id }),
    });
    if (res.ok) { toast('✓ Rettifica registrata'); setModalRettifica(null); carica(); }
    else { const d = await res.json(); toast(d.errore || 'Errore registrazione rettifica'); }
  };

  const apriStorico = async (pf) => {
    const res  = await apiFetch(`${API}/tracciabilita/magazzino-pf/${pf.id}/rettifiche`);
    const data = await res.json();
    setModalStorico({ pf, righe: Array.isArray(data) ? data : [] });
  };

  const filtrati = lista.filter(l =>
    !ricerca ||
    l.lotto?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.prodotto_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.committente_nome?.toLowerCase().includes(ricerca.toLowerCase())
  );

  const statoKindPF = (stato) => ({
    IN_MAGAZZINO: 'viola',
    IN_USO:       'verde',
    PARZIALE:     'arancio',
    ESAURITO:     'neutro',
  }[stato] || 'neutro');

  return (
    <div style={{ minHeight: '100vh', background: LT.n50, fontFamily: LT.sans, color: LT.navy }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: LT.n0,
        borderBottom: `1px solid ${LT.n200}`, padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon={<Ico d={I.arrowLeft} s={18} />} onClick={onBack}>Menu</Btn>
        <div style={{ fontSize: 13, fontWeight: 700, color: LT.n700, letterSpacing: 0.3 }}>
          TRACCIABILITÀ · MAGAZZINO PRODOTTI FINITI
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.viola} 0%, #5b3fd6 100%)`,
          color: LT.n0, borderRadius: LT.r.lg, padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
          boxShadow: '0 8px 24px rgba(124,90,237,.18)',
        }}>
          <div style={{ width: 56, height: 56, borderRadius: LT.r.md, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ico d={I.package} s={32} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Magazzino prodotti finiti</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              Lotti produzione · stato, residui e storico rettifiche
            </div>
          </div>
        </div>

        {/* Filtri */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 160px 180px', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={TR_LABEL}>Cerca</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n500, pointerEvents: 'none' }}>
                  <Ico d={I.search} s={16} />
                </div>
                <input
                  style={{ ...TR_INPUT, paddingLeft: 36 }}
                  placeholder="Lotto o prodotto…"
                  value={ricerca}
                  onChange={e => setRicerca(e.target.value)}
                  onFocus={e => trFocus(e, LT.viola)}
                  onBlur={trBlur}
                />
              </div>
            </div>
            <div>
              <label style={TR_LABEL}>Tipo</label>
              <select style={TR_INPUT} value={filtroCategoria}
                      onChange={e => setFiltroCategoria(e.target.value)}
                      onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                <option value="">Tutti</option>
                <option value="PRODOTTO_FINITO">Solo PF</option>
                <option value="SEMILAVORATO">Solo SL</option>
              </select>
            </div>
            <div>
              <label style={TR_LABEL}>Stato</label>
              <select style={TR_INPUT} value={filtroStato}
                      onChange={e => setFiltroStato(e.target.value)}
                      onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                <option value="">Tutti</option>
                <option value="IN_MAGAZZINO">In magazzino</option>
                <option value="IN_USO">In uso</option>
                <option value="PARZIALE">Parziale</option>
                <option value="ESAURITO">Esaurito</option>
              </select>
            </div>
            <div>
              <label style={TR_LABEL}>Prodotto</label>
              <select style={TR_INPUT} value={filtroProd}
                      onChange={e => setFiltroProd(e.target.value)}
                      onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                <option value="">Tutti</option>
                {prodotti.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            {haContoTerzi && (
              <FiltroProprieta valore={filtroPropr} onChange={setFiltroPropr} colore={LT.viola} />
            )}
          </div>
        </Card>

        {caricando && (
          <Card padding="md" style={{ textAlign: 'center', color: LT.n600 }}>Caricamento…</Card>
        )}

        {/* Grid lotti */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtrati.map(pf => {
            const isSL  = pf.categoria === 'SEMILAVORATO';
            const label = isSL ? (STATO_SL_LABEL[pf.stato] || pf.stato) : (STATO_PF_LABEL[pf.stato] || pf.stato);
            const bordoLaterale = isSL ? LT.ciano : LT.viola;
            return (
              <Card key={pf.id} padding="none" style={{ overflow: 'hidden', opacity: pf.stato === 'ESAURITO' ? 0.72 : 1 }}>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 6, alignSelf: 'stretch', background: bordoLaterale }} />
                  <div style={{ flex: 1, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                        <div style={{ width: 40, height: 40, borderRadius: LT.r.md, background: isSL ? LT.cianoSoft : LT.violaSoft, color: isSL ? LT.ciano : LT.viola, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Ico d={isSL ? I.flask : I.package} s={22} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy, overflow: 'hidden', textOverflow: 'ellipsis' }}>{pf.prodotto_nome}</div>
                          <div style={{ fontSize: 12, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>{pf.lotto}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                        {isSL && <Chip kind="ciano" size="sm">SL</Chip>}
                        <Chip kind={statoKindPF(pf.stato)} size="sm">{label}</Chip>
                      </div>
                    </div>

                    {/* Merce di un committente: non e' magazzino dell'azienda,
                        va restituita. Sta sopra ai numeri perche' cambia il
                        significato di tutto quello che c'e' sotto. */}
                    {pf.committente_nome && (
                      <div style={{ marginBottom: 8 }}>
                        <ChipContoTerzi committente={pf.committente_nome} />
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, padding: '8px 12px', background: LT.n50, borderRadius: LT.r.md, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Prodotta</div>
                        <div style={{ fontSize: 12, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{pf.quantita} <span style={{ color: LT.n600, fontWeight: 400 }}>{pf.unita_misura}</span></div>
                      </div>
                      {!isSL && (
                        <div>
                          <div style={{ fontSize: 10, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Residuo</div>
                          <div style={{ fontSize: 12, color: pf.quantita_residua > 0 ? LT.verde : LT.n600, fontWeight: 700, marginTop: 2 }}>{pf.quantita_residua} <span style={{ color: LT.n600, fontWeight: 400 }}>{pf.unita_misura}</span></div>
                        </div>
                      )}
                      {!isSL && pf.quantita_uscita > 0 && (
                        <div>
                          <div style={{ fontSize: 10, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Spedito</div>
                          <div style={{ fontSize: 12, color: LT.blu, fontWeight: 600, marginTop: 2 }}>{pf.quantita_uscita} <span style={{ color: LT.n600, fontWeight: 400 }}>{pf.unita_misura}</span></div>
                        </div>
                      )}
                      {pf.data_lavorazione && (
                        <div>
                          <div style={{ fontSize: 10, color: LT.n600, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Data</div>
                          <div style={{ fontSize: 12, color: LT.navy, fontWeight: 600, marginTop: 2 }}>{pf.data_lavorazione}</div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {!isSL && (pf.stato === 'IN_MAGAZZINO' || pf.stato === 'PARZIALE') && (
                        <Btn kind="primary" size="sm" icon={<Ico d={I.truck} s={14} />} onClick={() => onNuovaBolla(pf)}
                             style={{ background: LT.viola, borderColor: LT.viola }}>
                          Bolla uscita
                        </Btn>
                      )}
                      {isSL && pf.stato === 'IN_MAGAZZINO' && (
                        <Btn kind="primary" size="sm" icon={<Ico d={I.check} s={14} />} onClick={() => mettiInUso(pf.id)}>
                          Metti in uso
                        </Btn>
                      )}
                      {isSL && pf.stato === 'IN_USO' && (
                        <Btn kind="secondary" size="sm" icon={<Ico d={I.x} s={14} />} onClick={() => segnaEsaurito(pf.id)}>
                          Esaurito
                        </Btn>
                      )}
                      <Btn kind="ghost" size="sm" icon={<Ico d={I.edit} s={14} />} onClick={() => apriRettifica(pf)}>
                        Rettifica
                      </Btn>
                      <Btn kind="ghost" size="sm" icon={<Ico d={I.list} s={14} />} onClick={() => apriStorico(pf)}>
                        Storico
                      </Btn>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {!caricando && filtrati.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center', marginTop: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: LT.n100, color: LT.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Ico d={I.package} s={28} />
            </div>
            <div style={{ fontSize: 14, color: LT.n600 }}>Nessun lotto trovato</div>
          </Card>
        )}
      </div>

      {/* Modal rettifica */}
      {modalRettifica && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: LT.n0, borderRadius: LT.r.lg, padding: 20, maxWidth: 440, width: '100%', boxShadow: LT.sh3, fontFamily: LT.sans }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.arancioSoft, color: LT.arancio, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.edit} s={20} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: LT.navy }}>Rettifica quantità</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n600, marginBottom: 16 }}>
              {modalRettifica.pf.prodotto_nome} — <span style={{ fontFamily: LT.mono }}>{modalRettifica.pf.lotto}</span>
            </div>

            <label style={TR_LABEL}>Quantità attuale</label>
            <input
              style={{ ...TR_INPUT, background: LT.n100, color: LT.n600 }}
              value={`${modalRettifica.pf.quantita} ${modalRettifica.pf.unita_misura}`}
              readOnly
            />

            <label style={TR_LABEL}>Nuova quantità ({modalRettifica.pf.unita_misura})</label>
            <input
              type="text" inputMode="decimal"
              style={TR_INPUT}
              value={modalRettifica.nuovaQuantita}
              onChange={e => setModalRettifica(m => ({ ...m, nuovaQuantita: decVal(e.target.value) }))}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}
              placeholder="es. 12.5"
            />

            <label style={TR_LABEL}>Motivo</label>
            <select
              style={TR_INPUT}
              value={modalRettifica.motivo}
              onChange={e => setModalRettifica(m => ({ ...m, motivo: e.target.value }))}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}
            >
              {Object.entries(MOTIVO_RETTIFICA_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>

            <label style={TR_LABEL}>
              Note {modalRettifica.motivo === 'ALTRO' ? <span style={{ color: LT.rosso }}>*</span> : '(opzionale)'}
            </label>
            <textarea
              style={{ ...TR_INPUT, minHeight: 70, resize: 'vertical' }}
              value={modalRettifica.note}
              onChange={e => setModalRettifica(m => ({ ...m, note: e.target.value }))}
              onFocus={e => trFocus(e, LT.arancio)} onBlur={trBlur}
              placeholder={modalRettifica.motivo === 'ALTRO' ? 'Descrivi il motivo…' : ''}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <Btn kind="secondary" onClick={() => setModalRettifica(null)}>Annulla</Btn>
              <Btn kind="primary" icon={<Ico d={I.check} s={16} />} onClick={confermaRettifica}>
                Conferma
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal storico rettifiche */}
      {modalStorico && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: LT.n0, borderRadius: LT.r.lg, padding: 20, maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: LT.sh3, fontFamily: LT.sans }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: LT.r.md, background: LT.bluSoft, color: LT.blu, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ico d={I.list} s={20} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: LT.navy }}>Storico rettifiche</div>
            </div>
            <div style={{ fontSize: 13, color: LT.n600, marginBottom: 16 }}>
              {modalStorico.pf.prodotto_nome} — <span style={{ fontFamily: LT.mono }}>{modalStorico.pf.lotto}</span>
            </div>
            {modalStorico.righe.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: LT.n600, fontSize: 13 }}>Nessuna rettifica registrata</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {modalStorico.righe.map(r => (
                  <div key={r.id} style={{ padding: 12, background: LT.n50, borderRadius: LT.r.md, border: `1px solid ${LT.n200}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: LT.navy }}>
                        {r.quantita_prima} → {r.quantita_dopo}
                      </span>
                      <span style={{ fontSize: 11, color: LT.n600 }}>{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                      <Chip kind="arancio" size="sm">{MOTIVO_RETTIFICA_LABEL[r.motivo] || r.motivo}</Chip>
                      {r.operatore_nome && <Chip kind="neutro" size="sm" icon={<Ico d={I.users} s={12} />}>{r.operatore_nome}</Chip>}
                    </div>
                    {r.note && <div style={{ fontSize: 12, color: LT.n700, marginTop: 6, fontStyle: 'italic' }}>{r.note}</div>}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Btn kind="secondary" onClick={() => setModalStorico(null)}>Chiudi</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Gestione Bolle di Uscita ──────────────────────────────────
function GestioneBolleUscita({ operatore, onBack, lottoPrecompilato }) {
  const idAzienda = operatore.id_azienda;
  const oggi = new Date().toISOString().slice(0, 10);

  const RIGA_VUOTA = { idMagazzinoPf: '', quantita: '', unitaMisura: '', note: '' };

  const righeIniziali = lottoPrecompilato
    ? [{ idMagazzinoPf: String(lottoPrecompilato.id), quantita: '', unitaMisura: lottoPrecompilato.unita_misura || '', note: '' }]
    : [{ ...RIGA_VUOTA }];

  const [vista,     setVista]     = useState(lottoPrecompilato ? 'nuovo' : 'lista');
  const [lista,          setLista]          = useState([]);
  const [dettaglio,      setDettaglio]      = useState(null);
  const [allegatiBolla,  setAllegatiBolla]  = useState(null); // bolla di cui si vede la cartella allegati
  const [ricerca,        setRicerca]        = useState('');
  const [caricando,      setCaricando]      = useState(false);
  const [espanse,        setEspanse]        = useState({});
  const [caricandoRighe, setCaricandoRighe] = useState({});
  const [header,    setHeader]    = useState({ numeroBolla: '', dataBolla: oggi, idCliente: '', note: '' });
  const [righe,     setRighe]     = useState(righeIniziali);
  const [lottiFilt, setLottiFilt] = useState([]);
  const [clienti,   setClienti]   = useState([]);
  const [mostraNuovoCliente, setMostraNuovoCliente] = useState(false);
  const [nuovoClienteNome,   setNuovoClienteNome]   = useState('');

  useEffect(() => { if (vista === 'lista') caricaLista(); }, [vista]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    apiFetch(`${API}/tracciabilita/magazzino-pf?idAzienda=${idAzienda}&categoriaProdotto=PRODOTTO_FINITO`)
      .then(r => r.json())
      .then(d => setLottiFilt(Array.isArray(d) ? d.filter(l => l.stato !== 'ESAURITO') : []));
    caricaClienti();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaClienti = async () => {
    const res  = await apiFetch(`${API}/tracciabilita/clienti?idAzienda=${idAzienda}`);
    const data = await res.json();
    setClienti(Array.isArray(data) ? data.filter(c => c.attivo !== false) : []);
  };

  // Quick-add cliente inline (stesso pattern del quick-add fornitore in bolla ingresso)
  const creaClienteRapido = async () => {
    const nome = nuovoClienteNome.trim();
    if (!nome) return toast('Inserisci la ragione sociale');
    const res = await apiFetch(`${API}/tracciabilita/clienti`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idAzienda, ragioneSociale: nome }),
    });
    const data = await res.json();
    if (!res.ok) return toast('Errore: ' + (data.errore || 'creazione cliente fallita'));
    await caricaClienti();
    setHeader(h => ({ ...h, idCliente: String(data.id) }));
    setMostraNuovoCliente(false);
    setNuovoClienteNome('');
    toast('✓ Cliente creato e selezionato');
  };

  const caricaLista = async () => {
    setCaricando(true);
    const res  = await apiFetch(`${API}/tracciabilita/bolle-uscita?idAzienda=${idAzienda}`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
    setCaricando(false);
  };

  const apriDettaglio = async (b) => {
    const res  = await apiFetch(`${API}/tracciabilita/bolle-uscita/${b.id}`);
    const data = await res.json();
    setDettaglio(data);
    setVista('dettaglio');
  };

  const aggiungiRiga = () => setRighe(r => [...r, { ...RIGA_VUOTA }]);

  const rimuoviRiga = (idx) => setRighe(r => r.filter((_, i) => i !== idx));

  const aggiornaRiga = (idx, campo, valore) =>
    setRighe(r => r.map((riga, i) => i === idx ? { ...riga, [campo]: valore } : riga));

  const salvaBolla = async () => {
    if (!header.dataBolla || !header.idCliente)
      return toast('Compilare data e cliente (dall\'anagrafica)');
    const righeValide = righe.filter(r => r.idMagazzinoPf && r.quantita);
    if (righeValide.length === 0)
      return toast('Aggiungere almeno una riga con lotto e quantità');
    const res = await apiFetch(`${API}/tracciabilita/bolle-uscita`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        numeroBolla: header.numeroBolla || null,
        dataBolla:   header.dataBolla,
        idCliente:   parseInt(header.idCliente),
        note:        header.note || null,
        idAzienda,
        idOperatore: operatore.id,
        righe: righeValide.map(r => ({
          idMagazzinoPf: parseInt(r.idMagazzinoPf),
          quantita:      parseFloat(r.quantita),
          unitaMisura:   r.unitaMisura || null,
          note:          r.note || null,
        })),
      }),
    });
    const data = await res.json();
    if (!res.ok) return toast('Errore: ' + (data.errore || 'sconosciuto'));
    toast('✓ Bolla uscita creata');
    setHeader({ numeroBolla: '', dataBolla: oggi, idCliente: '', note: '' });
    setRighe([{ ...RIGA_VUOTA }]);
    setVista('lista');
  };

  const toggleEspansa = async (b) => {
    if (espanse[b.id]) {
      setEspanse(prev => { const next = { ...prev }; delete next[b.id]; return next; });
      return;
    }
    setCaricandoRighe(prev => ({ ...prev, [b.id]: true }));
    const res  = await apiFetch(`${API}/tracciabilita/bolle-uscita/${b.id}`);
    const data = await res.json();
    setEspanse(prev => ({ ...prev, [b.id]: data.righe || [] }));
    setCaricandoRighe(prev => { const next = { ...prev }; delete next[b.id]; return next; });
  };

  const filtrati = lista.filter(b => {
    if (!ricerca) return true;
    const q = ricerca.toLowerCase();
    return b.numero_bolla?.toLowerCase().includes(q) ||
           b.cliente?.toLowerCase().includes(q) ||
           b.lotti?.toLowerCase().includes(q) ||
           b.prodotti?.toLowerCase().includes(q);
  });

  // ─── Top bar + hero (comune a tutte le viste) ────────────────
  const breadcrumb = vista === 'nuovo' ? 'TRACCIABILITÀ · BOLLE USCITA · NUOVA'
                    : vista === 'dettaglio' ? 'TRACCIABILITÀ · BOLLE USCITA · DETTAGLIO'
                    : 'TRACCIABILITÀ · BOLLE USCITA';
  const backAction = vista === 'lista' ? onBack : () => setVista('lista');
  const backLabel  = vista === 'lista' ? 'Menu' : 'Lista';

  const TopBar = (
    <div style={{
      position: 'sticky', top: 0, zIndex: 20, background: LT.nBg,
      padding: '14px 20px 10px', borderBottom: `1px solid ${LT.n150}`,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <Btn kind="ghost" size="sm" icon="arrowLeft" onClick={backAction}>{backLabel}</Btn>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: LT.n500 }}>{breadcrumb}</div>
    </div>
  );

  // ─── Lista ───────────────────────────────────────────────────
  if (vista === 'lista') return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {TopBar}
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.viola} 0%, #5b3fd6 100%)`,
          color: '#fff', borderRadius: LT.r.xl, padding: '22px 24px',
          boxShadow: '0 8px 24px rgba(124,90,237,.25)',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: 'rgba(255,255,255,.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          }}>
            <Ico d={I.truck} size={30} stroke="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>Bolle di uscita</div>
            <div style={{ fontSize: 13, opacity: .9, marginTop: 3 }}>
              Spedizioni dei lotti di prodotto finito verso i clienti
            </div>
          </div>
          <Btn kind="secondary" size="md" icon="plus"
            onClick={() => { setHeader({ numeroBolla: '', dataBolla: oggi, cliente: '', note: '' }); setRighe([{ ...RIGA_VUOTA }]); setVista('nuovo'); }}
            style={{ background: '#fff', color: LT.viola, borderColor: '#fff' }}>
            Nuova bolla
          </Btn>
        </div>

        {/* Filtro */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
              <Ico d={I.search} size={16} stroke={LT.n400} />
            </div>
            <input
              style={{ ...TR_INPUT, paddingLeft: 36 }}
              placeholder="Cerca per numero bolla, cliente, prodotto o lotto…"
              value={ricerca} onChange={e => setRicerca(e.target.value)}
              onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}
            />
          </div>
        </Card>

        {caricando && (
          <Card padding="lg" style={{ textAlign: 'center', color: LT.n500 }}>Caricamento…</Card>
        )}

        {!caricando && filtrati.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{ color: LT.n400, marginBottom: 8 }}>
              <Ico d={I.truck} size={40} stroke={LT.n300} />
            </div>
            <div style={{ fontSize: 14, color: LT.n500 }}>Nessuna bolla trovata</div>
          </Card>
        )}

        {/* Lista */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrati.map(b => {
            const isEspansa = !!espanse[b.id];
            const rigSp     = espanse[b.id] || [];
            const loading   = !!caricandoRighe[b.id];
            return (
              <Card key={b.id} padding="none" style={{ overflow: 'hidden' }}>
                {/* Color stripe viola */}
                <div style={{ height: 6, background: LT.viola }} />
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: LT.r.md,
                      background: LT.violaSoft, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                    }}>
                      <Ico d={I.truck} size={22} stroke={LT.viola} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => apriDettaglio(b)}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: LT.navy }}>{b.cliente}</span>
                        {b.numero_bolla && (
                          <Chip kind="viola" size="sm" icon="receipt">{b.numero_bolla}</Chip>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: LT.n500, marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Ico d={I.calendar} size={12} stroke={LT.n500} />
                          {b.data_bolla}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Ico d={I.package} size={12} stroke={LT.n500} />
                          {b.n_righe} prodott{b.n_righe === 1 ? 'o' : 'i'}
                        </span>
                        {b.num_allegati > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Ico d={I.folder} size={12} stroke={LT.n500} />
                            {b.num_allegati}
                          </span>
                        )}
                      </div>
                    </div>
                    <Btn kind="ghost" size="sm" icon={isEspansa ? 'chevronD' : 'chevronR'}
                      onClick={() => toggleEspansa(b)}
                      title={isEspansa ? 'Comprimi' : 'Espandi righe'}>
                      {loading ? '…' : isEspansa ? 'Comprimi' : 'Righe'}
                    </Btn>
                  </div>

                  {/* Righe espanse */}
                  {isEspansa && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${LT.n150}` }}>
                      {rigSp.length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic' }}>Nessuna riga</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {rigSp.map(r => (
                            <div key={r.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 10px', background: LT.n50, borderRadius: LT.r.sm,
                              fontSize: 13,
                            }}>
                              <Ico d={I.package} size={14} stroke={LT.viola} />
                              <span style={{ fontWeight: 600, color: LT.navy, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.prodotto_nome || '—'}
                              </span>
                              {r.lotto && (
                                <Chip kind="neutro" size="sm">{r.lotto}</Chip>
                              )}
                              <span style={{ color: LT.n700, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {r.quantita} {r.unita_misura}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ─── Nuova bolla ─────────────────────────────────────────────
  if (vista === 'nuovo') return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {TopBar}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero compatta */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.viola} 0%, #5b3fd6 100%)`,
          color: '#fff', borderRadius: LT.r.xl, padding: '18px 22px',
          boxShadow: '0 8px 24px rgba(124,90,237,.25)',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: LT.r.md,
            background: 'rgba(255,255,255,.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          }}>
            <Ico d={I.plus} size={26} stroke="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Nuova bolla uscita</div>
            <div style={{ fontSize: 12, opacity: .9, marginTop: 2 }}>
              Registra una spedizione di prodotti finiti verso un cliente
            </div>
          </div>
        </div>

        <Card padding="lg">
          <SezioneForm titolo="Dati bolla" color="viola" icon="receipt">
            <div className="lt-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={TR_LABEL}>Numero bolla</label>
                <input style={TR_INPUT} value={header.numeroBolla}
                  onChange={e => setHeader(h => ({ ...h, numeroBolla: e.target.value }))}
                  placeholder="opzionale"
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
              <div>
                <label style={TR_LABEL}>Data bolla *</label>
                <input type="date" style={TR_INPUT} value={header.dataBolla}
                  onChange={e => setHeader(h => ({ ...h, dataBolla: e.target.value }))}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
            </div>
            <label style={TR_LABEL}>Cliente * <span style={{ fontWeight: 400, color: LT.n500 }}>(da anagrafica — mantiene i contatti per il richiamo)</span></label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select style={{ ...TR_INPUT, flex: 1 }} value={header.idCliente}
                onChange={e => setHeader(h => ({ ...h, idCliente: e.target.value }))}
                onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                <option value="">— seleziona cliente —</option>
                {clienti.map(c => (
                  <option key={c.id} value={c.id}>{c.ragione_sociale}{c.citta ? ` — ${c.citta}` : ''}</option>
                ))}
              </select>
              <button type="button"
                onClick={() => setMostraNuovoCliente(v => !v)}
                style={{ padding: '8px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, border: `1px solid ${LT.viola}55`, background: '#fff', color: LT.viola, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {mostraNuovoCliente ? 'Annulla' : '+ Nuovo'}
              </button>
            </div>
            {mostraNuovoCliente && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input style={{ ...TR_INPUT, flex: 1, marginTop: 0 }} value={nuovoClienteNome}
                  onChange={e => setNuovoClienteNome(e.target.value)}
                  placeholder="Ragione sociale nuovo cliente"
                  onKeyDown={e => { if (e.key === 'Enter') creaClienteRapido(); }}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
                <button type="button" onClick={creaClienteRapido}
                  style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: LT.viola, color: '#fff', cursor: 'pointer' }}>
                  Crea
                </button>
              </div>
            )}
            <label style={TR_LABEL}>Note bolla</label>
            <textarea style={{ ...TR_INPUT, resize: 'vertical', minHeight: 60 }} rows={2} value={header.note}
              onChange={e => setHeader(h => ({ ...h, note: e.target.value }))}
              onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
          </SezioneForm>

          <SezioneForm titolo="Prodotti spediti" color="viola" icon="package"
            sotto="Aggiungi le righe con i lotti di PF da spedire e le quantità">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {righe.map((riga, idx) => {
                const lottoSel = lottiFilt.find(l => String(l.id) === String(riga.idMagazzinoPf));
                return (
                  <div key={idx} style={{
                    background: LT.n50, border: `1px solid ${LT.n200}`,
                    borderRadius: LT.r.md, padding: 14,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <Chip kind="viola" size="sm">Riga {idx + 1}</Chip>
                      {righe.length > 1 && (
                        <Btn kind="ghost" size="sm" icon="x"
                          onClick={() => rimuoviRiga(idx)}
                          style={{ color: LT.rosso }}>
                          Rimuovi
                        </Btn>
                      )}
                    </div>

                    <label style={TR_LABEL}>Lotto da spedire *</label>
                    <select style={TR_INPUT} value={riga.idMagazzinoPf}
                      onChange={e => {
                        const sel = lottiFilt.find(l => String(l.id) === e.target.value);
                        aggiornaRiga(idx, 'idMagazzinoPf', e.target.value);
                        if (sel) aggiornaRiga(idx, 'unitaMisura', sel.unita_misura || '');
                      }}
                      onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                      <option value="">— seleziona lotto —</option>
                      {lottiFilt.map(l => (
                        <option key={l.id} value={l.id}>
                          {l.prodotto_nome} · {l.lotto} · residuo: {l.quantita_residua} {l.unita_misura}
                        </option>
                      ))}
                    </select>
                    {lottoSel && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 12, color: LT.n700, marginTop: 6,
                        padding: '6px 10px', background: LT.verdeSoft,
                        borderRadius: LT.r.sm, border: `1px solid ${LT.verde}33`,
                      }}>
                        <Ico d={I.checkCircle} size={14} stroke={LT.verde} />
                        <span>Disponibile: <strong style={{ color: LT.verde }}>{lottoSel.quantita_residua} {lottoSel.unita_misura}</strong></span>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginTop: 10 }}>
                      <div>
                        <label style={TR_LABEL}>Quantità *</label>
                        <input type="text" inputMode="decimal" style={TR_INPUT}
                          value={riga.quantita}
                          onChange={e => aggiornaRiga(idx, 'quantita', decVal(e.target.value))}
                          onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
                      </div>
                      <div>
                        <label style={TR_LABEL}>Unità</label>
                        <select style={TR_INPUT} value={riga.unitaMisura}
                          onChange={e => aggiornaRiga(idx, 'unitaMisura', e.target.value)}
                          onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur}>
                          <option value="">—</option>
                          {['kg', 'g', 'l', 'ml', 'pz', 'conf', 'sacchi', 'cartoni'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>

                    <label style={TR_LABEL}>Note riga</label>
                    <input style={TR_INPUT} value={riga.note}
                      onChange={e => aggiornaRiga(idx, 'note', e.target.value)}
                      placeholder="opzionale"
                      onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12 }}>
              <Btn kind="ghost" size="sm" icon="plus" onClick={aggiungiRiga}
                style={{ width: '100%', justifyContent: 'center', color: LT.viola, borderColor: `${LT.viola}44`, borderStyle: 'dashed', borderWidth: 1.5, borderRadius: LT.r.md }}>
                Aggiungi prodotto
              </Btn>
            </div>
          </SezioneForm>

          <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
            <Btn kind="ghost" size="md" onClick={() => setVista('lista')}>Annulla</Btn>
            <Btn kind="primary" size="md" icon="check" onClick={salvaBolla}
              style={{ background: LT.viola, borderColor: LT.viola }}>
              Salva bolla uscita
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  );

  // ─── Dettaglio ───────────────────────────────────────────────
  if (vista === 'dettaglio' && dettaglio) return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {TopBar}
      {allegatiBolla && (
        <ModalAllegatiBolla
          tipo="uscita"
          idBolla={allegatiBolla.id}
          titolo={`${allegatiBolla.numero_bolla ? 'Nº ' + allegatiBolla.numero_bolla + ' · ' : ''}${allegatiBolla.cliente || ''}`}
          operatore={operatore}
          onChiudi={() => { setAllegatiBolla(null); caricaLista(); }}
        />
      )}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero con info cliente */}
        <Card padding="none" style={{ overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ height: 6, background: LT.viola }} />
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{
                width: 52, height: 52, borderRadius: LT.r.lg,
                background: LT.violaSoft, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
              }}>
                <Ico d={I.truck} size={28} stroke={LT.viola} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.8, marginBottom: 4 }}>
                  CLIENTE
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>
                  {dettaglio.cliente}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {dettaglio.numero_bolla && (
                    <Chip kind="viola" size="sm" icon="receipt">Nº {dettaglio.numero_bolla}</Chip>
                  )}
                  <Chip kind="neutro" size="sm" icon="calendar">{dettaglio.data_bolla}</Chip>
                  <Btn kind="ghost" size="sm" icon="folder" onClick={() => setAllegatiBolla(dettaglio)}
                       title="DDT firmato, foto della spedizione — si possono aggiungere anche dopo">
                    Allegati{dettaglio.num_allegati > 0 ? ` (${dettaglio.num_allegati})` : ''}
                  </Btn>
                  {dettaglio.operatore_nome && (
                    <Chip kind="blu" size="sm" icon="user">{dettaglio.operatore_nome}</Chip>
                  )}
                </div>
                {dettaglio.note && (
                  <div style={{
                    marginTop: 12, padding: '10px 12px',
                    background: LT.arancioSoft, borderRadius: LT.r.sm,
                    borderLeft: `3px solid ${LT.arancio}`,
                    fontSize: 13, color: LT.n700, fontStyle: 'italic',
                  }}>
                    <span style={{ fontWeight: 700, color: LT.arancio, marginRight: 6 }}>Note:</span>
                    {dettaglio.note}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Prodotti spediti */}
        <Card padding="lg">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 32, height: 32, borderRadius: LT.r.md,
              background: LT.violaSoft, color: LT.viola,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Ico d={I.package} size={17} stroke={LT.viola} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Prodotti spediti</div>
              <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                {(dettaglio.righe || []).length} rig{(dettaglio.righe || []).length === 1 ? 'a' : 'he'} di dettaglio
              </div>
            </div>
          </div>

          {(dettaglio.righe || []).length === 0 ? (
            <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', textAlign: 'center', padding: 16 }}>
              Nessun prodotto nella bolla
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(dettaglio.righe || []).map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: 12, background: LT.n50,
                  borderRadius: LT.r.md, border: `1px solid ${LT.n150}`,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: LT.r.sm,
                    background: LT.violaSoft, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                  }}>
                    <Ico d={I.package} size={18} stroke={LT.viola} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>
                      {r.prodotto_nome}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {r.lotto && <Chip kind="neutro" size="sm">Lotto {r.lotto}</Chip>}
                      <Chip kind="viola" size="sm">
                        {r.quantita} {r.unita_misura}
                      </Chip>
                    </div>
                    {r.note && (
                      <div style={{ fontSize: 12, color: LT.n500, fontStyle: 'italic', marginTop: 6 }}>
                        {r.note}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );

  return null;
}

// ─── Test Tracciabilità ────────────────────────────────────────
// ─── Bilancio di Massa ─────────────────────────────────────────
function BilancioMassa({ operatore, onBack }) {
  const idAzienda = operatore.id_azienda;
  const [lista,      setLista]     = useState([]);
  const [ricerca,    setRicerca]   = useState('');
  const [caricando,  setCaricando] = useState(false);
  const [bilancio,   setBilancio]  = useState(null);

  const statoChipKindLav = (s) => ({ APERTA: 'verde', CHIUSA: 'blu', ANNULLATA: 'neutro' }[s] || 'neutro');

  useEffect(() => { caricaLista(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaLista = async () => {
    setCaricando(true);
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni?idAzienda=${idAzienda}&stato=CHIUSA`);
    const data = await res.json();
    setLista(Array.isArray(data) ? data : []);
    setCaricando(false);
  };

  const caricaBilancio = async (id) => {
    setCaricando(true);
    const res  = await apiFetch(`${API}/tracciabilita/lavorazioni/${id}/bilancio`);
    const data = await res.json();
    if (!res.ok) { toast('Errore: ' + (data.errore || 'sconosciuto')); setCaricando(false); return; }
    setBilancio(data);
    setCaricando(false);
  };

  const toKg = (q, um) => {
    const n = parseFloat(q) || 0;
    if (um === 'kg')   return n;
    if (um === 'g')    return n / 1000;
    if (um === 'l' || um === 'lt' || um === 'L') return n;
    if (um === 'ml')   return n / 1000;
    return null; // non convertibile
  };

  // Input di bilancio: usa quantita_input dal backend (dichiarata → stimata da
  // ricetta → intera quantità lotto, in quest'ordine di priorità) con la sua UM.
  const calcolaTotaleInput = (bilancio) => {
    let tot = 0, nonConvertibili = 0;
    const items = [...(bilancio.input.materie_prime || []), ...(bilancio.input.semilavorati || [])];
    items.forEach(r => {
      const kg = toKg(r.quantita_input ?? r.quantita, r.um_input ?? r.unita_misura);
      if (kg === null) nonConvertibili++;
      else tot += kg;
    });
    return { tot, nonConvertibili };
  };

  const FONTE_QTA_LABEL = { dichiarata: 'misurata', stimata: 'da ricetta', lotto: 'lotto intero' };
  const FONTE_QTA_KIND  = { dichiarata: 'verde',    stimata: 'blu',        lotto: 'arancio' };

  if (bilancio) {
    const lav = bilancio.lavorazione;
    const { tot: totInputKg, nonConvertibili } = calcolaTotaleInput(bilancio);
    const totOutputKg = (bilancio.output.prodotti || []).reduce((s, p) => s + (toKg(p.quantita, p.unita_misura) || 0), 0);
    const resa = totInputKg > 0 ? (totOutputKg / totInputKg) * 100 : null;
    // Teorica RISCALATA sulla dimensione della lavorazione (quantità prevista),
    // con fallback alla resa base della ricetta se la lavorazione non la dichiara.
    const resaTeoricaKg = (lav.quantita_prevista != null ? toKg(lav.quantita_prevista, lav.unita_misura) : null)
      ?? toKg(lav.resa_teorica_ricetta, lav.um_ricetta) ?? 0;
    const delta = resa !== null ? totOutputKg - resaTeoricaKg : null;
    const scostPerc = resaTeoricaKg > 0 ? Math.abs((totOutputKg - resaTeoricaKg) / resaTeoricaKg) : 0;
    const resaKind = resa === null ? 'neutro'
      : scostPerc <= 0.05 ? 'verde'
      : scostPerc <= 0.15 ? 'arancio'
      : 'rosso';
    const resaColor = resa === null ? LT.n700
      : scostPerc <= 0.05 ? LT.verde
      : scostPerc <= 0.15 ? LT.arancio
      : LT.rosso;
    const resaBg = resa === null ? LT.n50
      : scostPerc <= 0.05 ? LT.verdeSoft
      : scostPerc <= 0.15 ? LT.arancioSoft
      : LT.rossoSoft;

    return (
      <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
        {/* Top bar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 20, background: LT.nBg,
          padding: '14px 20px 10px', borderBottom: `1px solid ${LT.n150}`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <Btn kind="ghost" size="sm" icon="arrowLeft" onClick={() => setBilancio(null)}>Lista</Btn>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: LT.n500 }}>
            TRACCIABILITÀ · BILANCIO DI MASSA · DETTAGLIO
          </div>
        </div>

        <div style={{ maxWidth: 980, margin: '0 auto', padding: '20px 20px 60px' }}>
          {/* Hero lavorazione */}
          <Card padding="none" style={{ overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ height: 6, background: LT.ciano }} />
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: LT.r.lg,
                  background: LT.cianoSoft, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                }}>
                  <Ico d={I.chart} size={28} stroke={LT.ciano} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.8, marginBottom: 4 }}>
                    PRODOTTO
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy, lineHeight: 1.2 }}>
                    {lav.prodotto}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {lav.lotto_prodotto && (
                      <Chip kind="ciano" size="sm" icon="qr">Lotto {lav.lotto_prodotto}</Chip>
                    )}
                    {lav.ricetta && (
                      <Chip kind="neutro" size="sm" icon="book">{lav.ricetta}</Chip>
                    )}
                    <Chip kind="blu" size="sm" icon="calendar">
                      {lav.data_inizio}{lav.data_fine ? ' → ' + lav.data_fine : ''}
                    </Chip>
                    <Chip kind="verde" size="sm" icon="checkCircle">Chiusa</Chip>
                    {lav.committente_nome && (
                      <Chip kind="arancio" size="sm" icon="users">Conto terzi · {lav.committente_nome}</Chip>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* RESA EFFETTIVA — KPI principale */}
          <Card padding="lg" style={{
            marginBottom: 16,
            background: resaBg,
            borderLeft: `4px solid ${resaColor}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: '#fff', color: resaColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.trend} size={17} stroke={resaColor} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                RESA E SCOSTAMENTO
              </div>
              {resa !== null && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}>
                  {/* Il giudizio NON riguarda la resa: misura di quanto il prodotto
                      ottenuto si discosta dalla QUANTITA' PREVISTA. Senza questa
                      etichetta sembrava un voto sulla resa, che e' un altro numero. */}
                  <span style={{ fontSize: 10.5, color: LT.n500, fontWeight: 600 }}>
                    scostamento dal previsto
                  </span>
                  <Chip kind={resaKind} size="sm">
                    {scostPerc <= 0.05 ? 'Ottimale' : scostPerc <= 0.15 ? 'Tollerabile' : 'Critica'}
                  </Chip>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.6, marginBottom: 4 }}>INPUT TOTALE</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy }}>{totInputKg.toFixed(3)} <span style={{ fontSize: 13, color: LT.n500 }}>kg</span></div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.6, marginBottom: 4 }}>OUTPUT PF</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: LT.navy }}>{totOutputKg.toFixed(3)} <span style={{ fontSize: 13, color: LT.n500 }}>kg</span></div>
              </div>
              {resa !== null && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.6, marginBottom: 4 }}>RESA</div>
                  {/* Volutamente NEUTRA. La resa "giusta" dipende dal prodotto: un
                      prosciutto in stagionatura perde il 30% ed e' fisiologico. Il
                      programma non ha modo di giudicarla, e colorarla col criterio
                      dello scostamento faceva sembrare sbagliata una resa normale. */}
                  <div style={{ fontSize: 26, fontWeight: 800, color: LT.navy }}>{resa.toFixed(1)}<span style={{ fontSize: 16 }}>%</span></div>
                  <div style={{ fontSize: 10.5, color: LT.n500, marginTop: 2 }}>output ÷ input</div>
                </div>
              )}
              {resaTeoricaKg > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: LT.n500, letterSpacing: 0.6, marginBottom: 4 }}>TEORICA RICETTA</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: LT.n700 }}>{resaTeoricaKg.toFixed(3)} <span style={{ fontSize: 12, color: LT.n500 }}>kg</span></div>
                  {delta !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: resaColor, marginTop: 3 }}>
                      Δ {delta >= 0 ? '+' : ''}{delta.toFixed(3)} kg
                    </div>
                  )}
                </div>
              )}
            </div>
            {nonConvertibili > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                padding: '8px 12px', background: '#fff', borderRadius: LT.r.sm,
                borderLeft: `3px solid ${LT.arancio}`, fontSize: 12, color: LT.n700,
              }}>
                <Ico d={I.alert} size={14} stroke={LT.arancio} />
                <span><strong>{nonConvertibili}</strong> rig{nonConvertibili === 1 ? 'a' : 'he'} con unità di misura non convertibile (esclusa/e dal calcolo)</span>
              </div>
            )}
          </Card>

          {/* INPUT — Materie Prime */}
          <Card padding="lg" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: LT.bluSoft, color: LT.blu,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.package} size={17} stroke={LT.blu} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Input — Materie prime</div>
                <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                  {(bilancio.input.materie_prime || []).length} rig{(bilancio.input.materie_prime || []).length === 1 ? 'a' : 'he'} consumate
                </div>
              </div>
            </div>

            {(bilancio.input.materie_prime || []).length === 0 ? (
              <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', textAlign: 'center', padding: 12 }}>
                Nessuna MP registrata
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: LT.bluSoft, textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.blu, letterSpacing: 0.4 }}>INGREDIENTE</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.blu, letterSpacing: 0.4 }}>FORNITORE</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.blu, letterSpacing: 0.4 }}>LOTTO</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.blu, letterSpacing: 0.4, textAlign: 'right' }}>QTÀ USATA</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.blu, letterSpacing: 0.4 }}>FONTE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bilancio.input.materie_prime).map((mp, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${LT.n150}` }}>
                        <td style={{ padding: '8px 10px', fontWeight: 700, color: LT.navy }}>{mp.nome}</td>
                        <td style={{ padding: '8px 10px', color: LT.n600 }}>{mp.fornitore || '—'}</td>
                        <td style={{ padding: '8px 10px', color: LT.n600, fontFamily: LT.mono, fontSize: 12 }}>{mp.lotto_fornitore || mp.lotto_interno || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: LT.navy }}>
                          {mp.quantita_input ?? mp.quantita} <span style={{ color: LT.n500, fontWeight: 400 }}>{mp.um_input || mp.unita_misura}</span>
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <Chip kind={FONTE_QTA_KIND[mp.fonte_quantita] || 'neutro'} size="sm">
                            {FONTE_QTA_LABEL[mp.fonte_quantita] || '—'}
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* INPUT — Semilavorati (opzionale) */}
          {(bilancio.input.semilavorati || []).length > 0 && (
            <Card padding="lg" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: LT.r.md,
                  background: LT.cianoSoft, color: LT.ciano,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.layers} size={17} stroke={LT.ciano} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Input — Semilavorati</div>
                  <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                    {bilancio.input.semilavorati.length} SL consumati
                  </div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: LT.cianoSoft, textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.ciano, letterSpacing: 0.4 }}>PRODOTTO</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.ciano, letterSpacing: 0.4 }}>LOTTO</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.ciano, letterSpacing: 0.4, textAlign: 'right' }}>QTÀ USATA</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.ciano, letterSpacing: 0.4 }}>FONTE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilancio.input.semilavorati.map((sl, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${LT.n150}` }}>
                        <td style={{ padding: '8px 10px', fontWeight: 700, color: LT.navy }}>{sl.prodotto}</td>
                        <td style={{ padding: '8px 10px', color: LT.n600, fontFamily: LT.mono, fontSize: 12 }}>{sl.lotto || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: LT.navy }}>
                          {sl.quantita_input ?? sl.quantita} <span style={{ color: LT.n500, fontWeight: 400 }}>{sl.um_input || sl.unita_misura}</span>
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <Chip kind={FONTE_QTA_KIND[sl.fonte_quantita] || 'neutro'} size="sm">
                            {FONTE_QTA_LABEL[sl.fonte_quantita] || '—'}
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* OUTPUT — Prodotto Finito */}
          <Card padding="lg" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: LT.r.md,
                background: LT.verdeSoft, color: LT.verde,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.checkCircle} size={17} stroke={LT.verde} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Output — Prodotto finito</div>
                <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                  {(bilancio.output.prodotti || []).length} lott{(bilancio.output.prodotti || []).length === 1 ? 'o' : 'i'} prodott{(bilancio.output.prodotti || []).length === 1 ? 'o' : 'i'}
                </div>
              </div>
            </div>

            {(bilancio.output.prodotti || []).length === 0 ? (
              <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', textAlign: 'center', padding: 12 }}>
                Nessun PF registrato
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bilancio.output.prodotti.map((pf, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', background: LT.verdeSoft,
                    borderRadius: LT.r.md, border: `1px solid ${LT.verde}33`,
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: LT.r.sm,
                      background: '#fff', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                    }}>
                      <Ico d={I.package} size={16} stroke={LT.verde} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: LT.navy, fontFamily: LT.mono }}>
                        {pf.lotto}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: LT.verde, whiteSpace: 'nowrap' }}>
                      {pf.quantita} <span style={{ fontSize: 12, fontWeight: 400 }}>{pf.unita_misura}</span>
                    </div>
                    <Chip kind="verde" size="sm">{pf.stato}</Chip>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* SCARTI (opzionale) */}
          {(bilancio.scarti || []).length > 0 && (
            <Card padding="lg" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: LT.r.md,
                  background: LT.rossoSoft, color: LT.rosso,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico d={I.trash} size={17} stroke={LT.rosso} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: LT.navy }}>Scarti documentati</div>
                  <div style={{ fontSize: 11, color: LT.n500, marginTop: 1 }}>
                    {bilancio.scarti.length} rig{bilancio.scarti.length === 1 ? 'a' : 'he'} di scarto registrate
                  </div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: LT.rossoSoft, textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.rosso, letterSpacing: 0.4 }}>MOTIVO</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.rosso, letterSpacing: 0.4, textAlign: 'right' }}>SCARTATO</th>
                      <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: LT.rosso, letterSpacing: 0.4 }}>DATA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilancio.scarti.map((sc, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${LT.n150}` }}>
                        <td style={{ padding: '8px 10px', color: LT.navy }}>
                          <div style={{ fontWeight: 600 }}>{sc.motivo}</div>
                          {sc.note && <div style={{ fontSize: 12, color: LT.n500, fontStyle: 'italic', marginTop: 2 }}>{sc.note}</div>}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: LT.rosso, fontWeight: 700 }}>{sc.quantita_scartata}</td>
                        <td style={{ padding: '8px 10px', color: LT.n600 }}>{sc.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }

  const filtrate = lista.filter(l =>
    !ricerca ||
    l.prodotto_nome?.toLowerCase().includes(ricerca.toLowerCase()) ||
    l.lotto_prodotto?.toLowerCase().includes(ricerca.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: LT.nBg,
        padding: '14px 20px 10px', borderBottom: `1px solid ${LT.n150}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon="arrowLeft" onClick={onBack}>Menu</Btn>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: LT.n500 }}>
          TRACCIABILITÀ · BILANCIO DI MASSA
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.ciano} 0%, #0891b2 100%)`,
          color: '#fff', borderRadius: LT.r.xl, padding: '22px 24px',
          boxShadow: '0 8px 24px rgba(6,182,212,.25)',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: 'rgba(255,255,255,.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          }}>
            <Ico d={I.chart} size={30} stroke="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>Bilancio di massa</div>
            <div style={{ fontSize: 13, opacity: .9, marginTop: 3 }}>
              Verifica la coerenza tra materie prime consumate e prodotto finito ottenuto
            </div>
          </div>
        </div>

        {/* Filtro */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
              <Ico d={I.search} size={16} stroke={LT.n400} />
            </div>
            <input
              style={{ ...TR_INPUT, paddingLeft: 36 }}
              placeholder="Cerca per prodotto o lotto…"
              value={ricerca} onChange={e => setRicerca(e.target.value)}
              onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur}
            />
          </div>
        </Card>

        {caricando && (
          <Card padding="lg" style={{ textAlign: 'center', color: LT.n500 }}>Caricamento…</Card>
        )}

        {!caricando && filtrate.length === 0 && (
          <Card padding="lg" style={{ textAlign: 'center' }}>
            <div style={{ color: LT.n400, marginBottom: 8 }}>
              <Ico d={I.chart} size={40} stroke={LT.n300} />
            </div>
            <div style={{ fontSize: 14, color: LT.n500 }}>Nessuna lavorazione chiusa trovata</div>
          </Card>
        )}

        {/* Lista lavorazioni */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtrate.map(lav => (
            <Card key={lav.id} padding="none"
              style={{ overflow: 'hidden', cursor: 'pointer', transition: 'transform .12s, box-shadow .12s' }}
              onClick={() => caricaBilancio(lav.id)}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = LT.sh3; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = LT.sh2; }}>
              <div style={{ height: 6, background: LT.ciano }} />
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: LT.r.md,
                    background: LT.cianoSoft, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                  }}>
                    <Ico d={I.chart} size={22} stroke={LT.ciano} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy, lineHeight: 1.25 }}>
                      {lav.prodotto_nome || '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      <Chip kind={statoChipKindLav(lav.stato)} size="sm">
                        {STATO_LAV_LABEL[lav.stato] || lav.stato}
                      </Chip>
                      {lav.lotto_prodotto && (
                        <Chip kind="neutro" size="sm">{lav.lotto_prodotto}</Chip>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: LT.n500, marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {lav.ricetta_nome && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Ico d={I.book} size={12} stroke={LT.n500} />
                          {lav.ricetta_nome}
                        </span>
                      )}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ico d={I.calendar} size={12} stroke={LT.n500} />
                        {lav.data_inizio}
                      </span>
                    </div>
                  </div>
                  <Ico d={I.chevronR} size={18} stroke={LT.n400} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function TestTracciabilita({ operatore, onBack, impostazioni = {}, azienda }) {
  const idAzienda = operatore.id_azienda;
  const [tab,      setTab]      = useState('forward');
  const [cercando, setCercando] = useState(false);
  const [inputFwd, setInputFwd] = useState('');
  const [inputBwd, setInputBwd] = useState('');
  const [riFwd,    setRiFwd]    = useState(null);  // array di lotti MP con lavorazioni
  const [riBwd,    setRiBwd]    = useState(null);  // array di lavorazioni PF
  const [espanso,  setEspanso]  = useState({});    // { 'tab-idx': true/false }

  const toggleEspanso = (key) => setEspanso(prev => ({ ...prev, [key]: !prev[key] }));

  const formatParametri = (parametriRilevati, parametriDefiniti) => {
    if (!parametriRilevati) return '—';
    try {
      const rilevati = typeof parametriRilevati === 'string'
        ? JSON.parse(parametriRilevati) : parametriRilevati;
      const umMap = {};
      if (parametriDefiniti) {
        const definiti = typeof parametriDefiniti === 'string'
          ? JSON.parse(parametriDefiniti) : parametriDefiniti;
        const lista = Array.isArray(definiti) ? definiti : (definiti.parametri || []);
        lista.forEach(p => { umMap[p.nome] = p.um || p.unita || ''; });
      }
      return Object.entries(rilevati)
        .map(([k, v]) => `${k}: ${v}${umMap[k] ? umMap[k] : ''}`)
        .join(' · ');
    } catch {
      return String(parametriRilevati);
    }
  };

  const formatData = (data) => {
    if (!data) return '—';
    return new Date(data).toLocaleDateString('it-IT');
  };
  const formatDataOra = (data) => {
    if (!data) return '—';
    // Le date arrivano dal backend nel formato "AAAA-MM-GG HH:MM[:SS]".
    // Safari non accetta lo spazio come separatore e risponderebbe
    // "Invalid Date": lo sostituiamo con la T prevista dallo standard.
    // Su Chrome e Firefox il risultato è identico a prima.
    const grezza = typeof data === 'string' ? data.replace(' ', 'T') : data;
    return new Date(grezza).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const cercaForward = async () => {
    if (!inputFwd.trim()) return;
    setCercando(true); setRiFwd(null); setEspanso({});
    try {
      const res  = await apiFetch(`${API}/tracciabilita/test-forward?idAzienda=${idAzienda}&lottoMp=${encodeURIComponent(inputFwd.trim())}`);
      const data = await res.json();
      if (!res.ok) { toast('Errore: ' + (data.errore || 'sconosciuto')); return; }
      setRiFwd(Array.isArray(data) ? data : []);
    } finally { setCercando(false); }
  };

  const cercaBackward = async () => {
    if (!inputBwd.trim()) return;
    setCercando(true); setRiBwd(null); setEspanso({});
    try {
      const res  = await apiFetch(`${API}/tracciabilita/test-backward?idAzienda=${idAzienda}&lottoPf=${encodeURIComponent(inputBwd.trim())}`);
      const data = await res.json();
      if (!res.ok) { toast('Errore: ' + (data.errore || 'sconosciuto')); return; }
      setRiBwd(Array.isArray(data) ? data : []);
    } finally { setCercando(false); }
  };

  // La scheda di tracciabilita' e' un documento HTML costruito incollando dentro
  // i dati dell'azienda: nomi di ingredienti, fornitori, lotti, note, motivazioni
  // di scarto. Sono testi scritti a mano dagli utenti, quindi possono contenere
  // <, > e &. Incollati grezzi in una pagina HTML quei caratteri non compaiono:
  // il browser li legge come marcatori e il documento esce mutilato — e questo e'
  // il foglio che si mette in mano a un ispettore.
  //
  // escProfondo percorre tutta la struttura ricevuta dal backend (array e oggetti
  // annidati compresi: materie prime, fasi, ubicazioni, bolle) e mette al sicuro
  // ogni testo, lasciando stare numeri e date. Una riga sola invece di ricordarsi
  // di proteggere trentacinque punti diversi.
  //
  // ATTENZIONE alle virgolette. Dentro il TESTO di una pagina HTML sono
  // innocue, e vanno lasciate stare: il campo parametri_rilevati e' una
  // stringa JSON che formatParametri passa a JSON.parse, e trasformare " in
  // &quot; la renderebbe illeggibile — la colonna Parametri mostrerebbe il
  // JSON grezzo invece dei valori. Si proteggono solo dove servono davvero,
  // cioe' dentro gli ATTRIBUTI (qui: l'indirizzo del logo).
  const escTesto = (v) => String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const escAttributo = (v) => escTesto(v).replace(/"/g, '&quot;');
  // Foglio di stile condiviso dalle due schede stampabili. Sta in un posto solo
  // perche' due copie prima o poi divergono, e le schede finiscono in mano a un
  // ispettore: devono sembrare lo stesso documento.
  const CSS_SCHEDA = `
  body { font-family: Arial, sans-serif; font-size: 12px; color: #222; margin: 0; padding: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; } h2 { font-size: 14px; margin: 20px 0 6px; color: #2563EB; border-bottom: 1px solid #2563EB; padding-bottom: 3px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #2563EB; padding-bottom: 12px; margin-bottom: 16px; }
  .meta { font-size: 11px; color: #666; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th { background: #2563EB; color: #fff; padding: 5px 8px; text-align: left; font-size: 11px; }
  td { padding: 4px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .summary-grid { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 4px; }
  .summary-grid div { flex: 1; min-width: 150px; }
  .summary-grid span { font-weight: bold; }
  .footer { margin-top: 32px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 10px; color: #888; display: flex; justify-content: space-between; align-items: center; }
  .allerta { border: 2px solid #b00020; background: #fdecea; padding: 10px 12px; margin-bottom: 14px; }
  .allerta h2 { color: #b00020; border-color: #b00020; margin-top: 0; }
  .nota { font-size: 10px; color: #888; margin-top: -4px; margin-bottom: 10px; }
  @page { size: A4 landscape; margin: 1cm; }
  @media print { body { padding: 0; } }
  `;

  // Intestazione comune alle due schede: logo, azienda, cosa e' stato cercato,
  // da chi e quando. E' la parte che trasforma un elenco in un documento.
  const intestazioneScheda = (titolo, etichettaRicerca, valoreRicerca, now, logoUrl, aziendaNome) => `
<div class="header">
  <div>
    ${logoUrl ? `<img src="${logoUrl}" style="height:48px;margin-bottom:6px;display:block;" alt="logo">` : ''}
    <h1>${titolo}</h1>
    <div class="meta">${aziendaNome}</div>
  </div>
  <div class="meta" style="text-align:right">
    <div><strong>Data ricerca:</strong> ${now}</div>
    <div><strong>${etichettaRicerca}:</strong> ${valoreRicerca}</div>
    <div><strong>Operatore:</strong> ${escTesto(operatore.nome)} ${escTesto(operatore.cognome)}</div>
  </div>
</div>`;

  const escProfondo = (v) => {
    if (v === null || v === undefined) return v;
    if (typeof v === 'string') return escTesto(v);
    if (Array.isArray(v))      return v.map(escProfondo);
    if (typeof v === 'object') {
      const fuori = {};
      Object.keys(v).forEach(k => { fuori[k] = escProfondo(v[k]); });
      return fuori;
    }
    return v;
  };

  const stampaBackward = (lavGrezza) => {
    const lav = escProfondo(lavGrezza);
    const now = new Date().toLocaleString('it-IT');
    const logoUrl = impostazioni.fornitore_logo ? `${BACKEND}${escAttributo(impostazioni.fornitore_logo)}` : null;
    const aziendaNome = escTesto(azienda?.ragione_sociale || '');
    const forn = escTesto(impostazioni.fornitore_nome || '');
    const fornTel = escTesto(impostazioni.fornitore_telefono || '');
    const fornEmail = escTesto(impostazioni.fornitore_email || '');

    const righeMP = (lav.materie_prime || []).map(mp => `
      <tr>
        <td>${mp.ingrediente}</td>
        <td>${mp.categoria || ''}</td>
        <td>${mp.lotto_fornitore}${mp.lotto_interno ? ' / ' + mp.lotto_interno : ''}</td>
        <td>${mp.fornitore}</td>
        <td>${mp.numero_bolla || '—'} del ${formatData(mp.data_bolla)}</td>
        <td>${formatData(mp.data_scadenza)}</td>
      </tr>`).join('');

    const righeSL = (lav.semilavorati || []).map(sl => `
      <tr>
        <td>${sl.prodotto}</td>
        <td>${sl.lotto}</td>
        <td>${sl.quantita ? sl.quantita + ' ' + sl.unita_misura : '—'}</td>
        <td>${sl.lavorazione_origine ? sl.lavorazione_origine.prodotto + ' · ' + sl.lavorazione_origine.lotto_prodotto + ' (' + formatData(sl.lavorazione_origine.data_inizio) + ')' : '—'}</td>
        <td>${sl.lavorazione_origine?.materie_prime?.map(mp => mp.ingrediente + ' (' + (mp.lotto_fornitore || mp.lotto_interno || '—') + ')').join(', ') || '—'}</td>
      </tr>`).join('');

    // Quantità di un movimento: "12 pz / 3.5 kg", oppure il formato libero
    // (quantita + unità di misura) usato dai movimenti senza tipo.
    const qtaMovimento = (m) => {
      const parti = [];
      if (m.quantita_pz) parti.push(`${m.quantita_pz} pz`);
      if (m.peso_kg)     parti.push(`${m.peso_kg} kg`);
      if (parti.length === 0 && m.quantita) parti.push(`${m.quantita} ${m.unita_misura || ''}`.trim());
      return parti.join(' / ');
    };

    // Etichetta di un movimento, identica a quella mostrata a video.
    // ATTENZIONE: il campo si chiama `nome_ubicazione` — è l'alias della query
    // del backend (tracciabilita.js: "u.nome AS nome_ubicazione"). Un `u.nome`
    // qui non esiste e stampava colonne vuote.
    const etichettaMovimento = (m) => {
      const dove = m.nome_ubicazione || '';
      const base = m.tipo ? `${m.tipo} ${dove}` : dove;
      const qta  = qtaMovimento(m);
      return base
        + (qta ? ` · ${qta}` : '')
        + (m.tipo === 'SCARTO' && m.motivazione ? ` — ${m.motivazione}` : '');
    };

    const righeFasi = (lav.fasi || []).map((f, i) => `
      <tr>
        <td>${i + 1}. ${f.fase}</td>
        <td>${formatDataOra(f.data_inizio)}</td>
        <td>${f.data_fine ? formatDataOra(f.data_fine) : 'In corso'}</td>
        <td>${f.operatore || ''}</td>
        <td>${formatParametri(f.parametri_rilevati, f.parametri_definiti)}</td>
        <td>${f.note || ''}</td>
        <td>${f.ubicazioni && f.ubicazioni.length > 0 ? f.ubicazioni.map(etichettaMovimento).join('<br>') : ''}</td>
      </tr>`).join('');

    // Movimenti del prodotto per ubicazione, in ordine cronologico: è la stessa
    // sezione "MOVIMENTI PRODOTTO PER UBICAZIONE" che si vede a schermo, che
    // però non finiva nella scheda stampata. Senza, il documento consegnato
    // all'ASL non dice dove il prodotto sia fisicamente passato.
    const movimenti = (lav.fasi || [])
      .flatMap(f => (f.ubicazioni || []).filter(m => m.tipo).map(m => ({ ...m, fase: f.fase })))
      .sort((a, b) => String(a.data_movimento || a.created_at || '').localeCompare(String(b.data_movimento || b.created_at || '')));

    const righeMovimenti = movimenti.map(m => `
      <tr>
        <td>${(m.data_movimento || m.created_at) ? formatDataOra(m.data_movimento || m.created_at) : '—'}</td>
        <td>${m.tipo}</td>
        <td>${m.nome_ubicazione || ''}${m.nome_ubicazione_destinazione ? ' → ' + m.nome_ubicazione_destinazione : ''}</td>
        <td>${qtaMovimento(m) || '—'}</td>
        <td>${m.fase || ''}</td>
        <td>${m.motivazione || m.note || ''}</td>
      </tr>`).join('');

    const righeBolle = (lav.bolle_uscita || []).map(b => `
      <tr>
        <td>${b.numero_bolla || '—'}</td>
        <td>${formatData(b.data_bolla)}</td>
        <td>${b.cliente}</td>
        <td>${b.quantita} ${b.unita_misura}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8">
<title>Scheda Tracciabilità — ${lav.lotto_pf}</title>
<style>${CSS_SCHEDA}</style></head><body>
${intestazioneScheda('Scheda di tracciabilità a monte', 'Lotto ricercato', lav.lotto_pf, now, logoUrl, aziendaNome)}

<h2>Prodotto finito</h2>
<div class="summary-grid">
  <div><span>Prodotto:</span> ${lav.prodotto}</div>
  <div><span>Lotto:</span> ${lav.lotto_pf}</div>
  <div><span>Ricetta:</span> ${lav.ricetta}</div>
  <div><span>Data produzione:</span> ${formatData(lav.data_inizio)}</div>
  <div><span>Quantità:</span> ${lav.quantita_reale || lav.quantita_prevista || '—'} ${lav.unita_misura_pf}</div>
  <div><span>Stato:</span> ${lav.stato_pf || '—'}</div>
</div>

<h2>Materie prime utilizzate</h2>
${lav.materie_prime && lav.materie_prime.length > 0 ? `
<table><thead><tr>
  <th>Ingrediente</th><th>Categoria</th><th>Lotto</th><th>Fornitore</th><th>Bolla ingresso</th><th>Scadenza</th>
</tr></thead><tbody>${righeMP}</tbody></table>` : '<p style="color:#888">Nessuna materia prima registrata</p>'}

<h2>Semilavorati utilizzati</h2>
${lav.semilavorati && lav.semilavorati.length > 0 ? `
<table><thead><tr>
  <th>Prodotto</th><th>Lotto</th><th>Quantità</th><th>Lavorazione origine</th><th>MP origine</th>
</tr></thead><tbody>${righeSL}</tbody></table>` : '<p style="color:#888">Nessun semilavorato utilizzato</p>'}

<h2>Fasi produttive</h2>
${lav.fasi && lav.fasi.length > 0 ? `
<table><thead><tr>
  <th>Fase</th><th>Inizio</th><th>Fine</th><th>Operatore</th><th>Parametri</th><th>Note</th><th>Ubicazione</th>
</tr></thead><tbody>${righeFasi}</tbody></table>` : '<p style="color:#888">Nessuna fase registrata</p>'}

${movimenti.length > 0 ? `
<h2>Movimenti prodotto per ubicazione</h2>
<table><thead><tr>
  <th>Data e ora</th><th>Movimento</th><th>Ubicazione</th><th>Quantità</th><th>Fase</th><th>Note / motivazione</th>
</tr></thead><tbody>${righeMovimenti}</tbody></table>` : ''}

<h2>Bolle di uscita</h2>
${lav.bolle_uscita && lav.bolle_uscita.length > 0 ? `
<table><thead><tr>
  <th>N. Bolla</th><th>Data</th><th>Cliente</th><th>Quantità</th>
</tr></thead><tbody>${righeBolle}</tbody></table>` : '<p style="color:#888">Nessuna bolla di uscita registrata</p>'}

<div class="footer">
  <div>${forn}${fornTel ? ' · ' + fornTel : ''}${fornEmail ? ' · ' + fornEmail : ''}</div>
  <div>Stampato il ${now}</div>
</div>
</body></html>`;

    apriEStampa(html);
  };

  // Apre la finestra e lancia la stampa. Stessa cosa per le due schede.
  const apriEStampa = (html) => {
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // ── Scheda a valle ─────────────────────────────────────────────────────────
  // Da un lotto di materia prima ai clienti che hanno ricevuto i prodotti che lo
  // contengono. E' il documento dell'ALLERTA: la prima sezione non e' un elenco
  // ordinato per data ma "chi devo chiamare", perche' e' l'unica cosa che serve
  // nei primi dieci minuti. Il resto viene dopo.
  const stampaForward = (mpGrezza) => {
    const mp = escProfondo(mpGrezza);
    const now = new Date().toLocaleString('it-IT');
    const logoUrl = impostazioni.fornitore_logo ? `${BACKEND}${escAttributo(impostazioni.fornitore_logo)}` : null;
    const aziendaNome = escTesto(azienda?.ragione_sociale || '');
    const forn = escTesto(impostazioni.fornitore_nome || '');
    const fornTel = escTesto(impostazioni.fornitore_telefono || '');
    const fornEmail = escTesto(impostazioni.fornitore_email || '');

    const lavorazioni = mp.lavorazioni || [];

    // Somma quantita' tenendo separate le unita' di misura: sommare kg e pezzi
    // darebbe un numero senza significato.
    const accumula = (dest, q, um) => {
      const n = parseFloat(q);
      if (!(n > 0)) return;
      const k = um || '';
      dest[k] = (dest[k] || 0) + n;
    };
    const scriviQta = (dest) => Object.entries(dest)
      .map(([um, n]) => `${Math.round(n * 1000) / 1000}${um ? ' ' + um : ''}`)
      .join(' · ') || '—';

    // CHI DEVE ESSERE AVVISATO: un cliente per riga, con quanto ha ricevuto in
    // totale e i lotti coinvolti. Se lo stesso cliente compare su tre bolle,
    // qui e' una riga sola: in emergenza si telefona una volta, non tre.
    const perCliente = {};
    const residuoTot = {};
    lavorazioni.forEach(lav => {
      (lav.bolle_uscita || []).forEach(b => {
        const nome = b.cliente || '(cliente non indicato)';
        if (!perCliente[nome]) perCliente[nome] = { qta: {}, lotti: new Set(), bolle: new Set() };
        accumula(perCliente[nome].qta, b.quantita, b.unita_misura);
        if (b.lotto_pf) perCliente[nome].lotti.add(b.lotto_pf);
        if (b.numero_bolla) perCliente[nome].bolle.add(b.numero_bolla);
      });
      (lav.magazzino_pf || []).forEach(pf => accumula(residuoTot, pf.quantita_residua, pf.unita_misura));
    });
    const righeClienti = Object.entries(perCliente).map(([nome, d]) => `
      <tr>
        <td><strong>${nome}</strong></td>
        <td>${scriviQta(d.qta)}</td>
        <td>${[...d.lotti].join(', ') || '—'}</td>
        <td>${[...d.bolle].join(', ') || '—'}</td>
      </tr>`).join('');

    const righeLav = lavorazioni.map(lav => {
      const pfTxt = (lav.magazzino_pf || []).map(pf =>
        `${pf.lotto}: prodotti ${pf.quantita_totale || '—'} ${pf.unita_misura || ''} · spediti ${pf.quantita_uscita || 0} · in magazzino ${pf.quantita_residua || 0}`
      ).join('<br>') || '—';
      return `
      <tr>
        <td>#${lav.id_lavorazione}</td>
        <td>${lav.prodotto}</td>
        <td>${lav.lotto_prodotto || '—'}</td>
        <td>${formatData(lav.data_inizio)}${lav.data_fine ? ' → ' + formatData(lav.data_fine) : ''}</td>
        <td>${lav.stato || '—'}</td>
        <td>${lav.committente_nome ? 'conto terzi · ' + lav.committente_nome : '—'}</td>
        <td>${pfTxt}</td>
      </tr>`;
    }).join('');

    const righeSpedizioni = lavorazioni.flatMap(lav => (lav.bolle_uscita || []).map(b => `
      <tr>
        <td>${b.numero_bolla || '—'}</td>
        <td>${formatData(b.data_bolla)}</td>
        <td>${b.cliente}</td>
        <td>${b.lotto_pf || '—'}</td>
        <td>${b.quantita} ${b.unita_misura || ''}</td>
      </tr>`)).join('');

    const nClienti = Object.keys(perCliente).length;

    const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8">
<title>Scheda Tracciabilità a valle — ${mp.lotto_fornitore || mp.lotto_interno || ''}</title>
<style>${CSS_SCHEDA}</style></head><body>
${intestazioneScheda('Scheda di tracciabilità a valle', 'Lotto ricercato',
   `${mp.lotto_fornitore || '—'}${mp.lotto_interno ? ' / ' + mp.lotto_interno : ''}`, now, logoUrl, aziendaNome)}

<h2>Materia prima di partenza</h2>
<div class="summary-grid">
  <div><span>Ingrediente:</span> ${mp.ingrediente}</div>
  <div><span>Lotto fornitore:</span> ${mp.lotto_fornitore || '—'}</div>
  <div><span>Lotto interno:</span> ${mp.lotto_interno || '—'}</div>
  <div><span>Fornitore:</span> ${mp.fornitore || '—'}</div>
  <div><span>Bolla di ingresso:</span> ${mp.bolla_ingresso || '—'} del ${formatData(mp.data_bolla_ingresso)}</div>
</div>

<div class="allerta">
<h2>Clienti da avvisare — ${nClienti}</h2>
${nClienti > 0 ? `<table>
<thead><tr><th>Cliente</th><th>Quantità ricevuta</th><th>Lotti coinvolti</th><th>Bolle</th></tr></thead>
<tbody>${righeClienti}</tbody></table>` : '<p style="margin:4px 0">Nessuna spedizione registrata: nessun prodotto contenente questo lotto risulta uscito.</p>'}
<div class="nota">Ancora in magazzino, da bloccare: <strong>${scriviQta(residuoTot)}</strong></div>
</div>

<h2>Lavorazioni che hanno usato questo lotto</h2>
${lavorazioni.length > 0 ? `<table>
<thead><tr><th>Lav.</th><th>Prodotto</th><th>Lotto PF</th><th>Date</th><th>Stato</th><th>Proprietà</th><th>Prodotto finito</th></tr></thead>
<tbody>${righeLav}</tbody></table>` : '<p style="color:#888">Questo lotto non risulta usato in nessuna lavorazione.</p>'}

<h2>Dettaglio delle spedizioni</h2>
${righeSpedizioni ? `<table>
<thead><tr><th>Bolla</th><th>Data</th><th>Cliente</th><th>Lotto PF</th><th>Quantità</th></tr></thead>
<tbody>${righeSpedizioni}</tbody></table>` : '<p style="color:#888">Nessuna bolla di uscita registrata.</p>'}

<div class="footer">
  <div>${forn}${fornTel ? ' · ' + fornTel : ''}${fornEmail ? ' · ' + fornEmail : ''}</div>
  <div>Stampato il ${now}</div>
</div>
</body></html>`;

    apriEStampa(html);
  };

  // Tab pill selector (D2 Evoluto)
  const TabPill = ({ t, label, icon, color }) => {
    const active = tab === t;
    return (
      <button onClick={() => setTab(t)} style={{
        flex: 1, padding: '12px 16px', fontSize: 13, fontWeight: 700, fontFamily: LT.sans,
        cursor: 'pointer', border: 'none', borderRadius: LT.r.md,
        background: active ? color : 'transparent',
        color: active ? '#fff' : LT.n700,
        boxShadow: active ? '0 2px 8px ' + color + '40' : 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'all .15s',
      }}>
        <Ico d={I[icon]} size={15} stroke={active ? '#fff' : LT.n600} />
        {label}
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: LT.nBg, fontFamily: LT.sans }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: LT.nBg,
        padding: '14px 20px 10px', borderBottom: `1px solid ${LT.n150}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Btn kind="ghost" size="sm" icon="arrowLeft" onClick={onBack}>Menu</Btn>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: LT.n500 }}>
          TRACCIABILITÀ · TEST
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Hero */}
        <div className="lt-hero" style={{
          background: `linear-gradient(135deg, ${LT.ciano} 0%, #0891b2 100%)`,
          color: '#fff', borderRadius: LT.r.xl, padding: '22px 24px',
          boxShadow: '0 8px 24px rgba(6,182,212,.25)',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: LT.r.lg,
            background: 'rgba(255,255,255,.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          }}>
            <Ico d={I.search} size={30} stroke="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>Test di tracciabilità</div>
            <div style={{ fontSize: 13, opacity: .9, marginTop: 3 }}>
              Verifica forward (MP → PF) e backward (PF → MP) con stampa della scheda
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Card padding="sm" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 6, padding: 4, background: LT.n100, borderRadius: LT.r.md }}>
            <TabPill t="forward"  label="Forward · MP → PF"  icon="arrowRight" color={LT.ciano} />
            <TabPill t="backward" label="Backward · PF → MP" icon="arrowLeft"  color={LT.viola} />
          </div>
        </Card>

        {/* ─── TAB FORWARD ─── */}
        {tab === 'forward' && (<>
          <Card padding="md" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: LT.n600, marginBottom: 12 }}>
              Cerca per <strong>lotto fornitore</strong>, <strong>lotto interno</strong> o <strong>nome ingrediente</strong> per trovare tutti i prodotti finiti che lo contengono.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
                  <Ico d={I.search} size={16} stroke={LT.n400} />
                </div>
                <input style={{ ...TR_INPUT, paddingLeft: 36 }}
                  placeholder="Es. F230326, LI-2024-001 oppure Farina"
                  value={inputFwd}
                  onChange={e => setInputFwd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && cercaForward()}
                  onFocus={e => trFocus(e, LT.ciano)} onBlur={trBlur} />
              </div>
              <Btn kind="primary" size="md" icon="search"
                onClick={cercaForward} disabled={cercando}
                style={{ background: LT.ciano, borderColor: LT.ciano }}>
                {cercando ? '…' : 'Cerca'}
              </Btn>
            </div>
          </Card>

          {riFwd !== null && riFwd.length === 0 && (
            <Card padding="lg" style={{ textAlign: 'center' }}>
              <div style={{ color: LT.n400, marginBottom: 8 }}>
                <Ico d={I.search} size={40} stroke={LT.n300} />
              </div>
              <div style={{ fontSize: 14, color: LT.n500 }}>Nessuna corrispondenza trovata per <strong>"{inputFwd}"</strong></div>
            </Card>
          )}

          {riFwd && riFwd.length > 0 && (
            <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600, marginBottom: 10, letterSpacing: 0.4 }}>
              {riFwd.length} LOTT{riFwd.length === 1 ? 'O' : 'I'} TROVAT{riFwd.length === 1 ? 'O' : 'I'}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {riFwd && riFwd.map((mp, idx) => {
              const key = `fwd-${idx}`;
              const aperto = !!espanso[key];
              return (
                <Card key={idx} padding="none" style={{ overflow: 'hidden' }}>
                  <div style={{ height: 6, background: LT.ciano }} />
                  <div onClick={() => toggleEspanso(key)} style={{
                    padding: 16, cursor: 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: LT.r.md,
                      background: LT.cianoSoft, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                    }}>
                      <Ico d={I.package} size={22} stroke={LT.ciano} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy }}>{mp.ingrediente}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        <Chip kind="ciano" size="sm">
                          {mp.lotto_fornitore}{mp.lotto_interno ? ` / ${mp.lotto_interno}` : ''}
                        </Chip>
                        {mp.fornitore && <Chip kind="arancio" size="sm" icon="factory">{mp.fornitore}</Chip>}
                        {mp.bolla_ingresso && (
                          <Chip kind="blu" size="sm" icon="receipt">
                            BDI {mp.bolla_ingresso} · {formatData(mp.data_bolla_ingresso)}
                          </Chip>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: LT.n500, marginTop: 6 }}>
                        <strong style={{ color: LT.navy }}>{mp.num_lavorazioni}</strong> lavorazion{mp.num_lavorazioni === 1 ? 'e' : 'i'} collegat{mp.num_lavorazioni === 1 ? 'a' : 'e'}
                      </div>
                    </div>
                    <Btn kind="secondary" size="sm" icon="download"
                         onClick={(e) => { e.stopPropagation(); stampaForward(mp); }}
                         title="Stampa la scheda con i clienti da avvisare">
                      Stampa scheda
                    </Btn>
                    <Ico d={aperto ? I.chevronD : I.chevronR} size={20} stroke={LT.ciano} />
                  </div>

                  {aperto && (
                    <div style={{ borderTop: `1px solid ${LT.n150}`, padding: 16, background: LT.n50 }}>
                      {(mp.lavorazioni || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
                          Nessuna lavorazione collegata
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                          {(mp.lavorazioni || []).map((lav, li) => (
                            <div key={li} style={{
                              background: '#fff', borderRadius: LT.r.md,
                              borderLeft: `4px solid ${LT.verde}`,
                              padding: 14,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                  width: 36, height: 36, borderRadius: LT.r.sm,
                                  background: LT.verdeSoft, display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                                }}>
                                  <Ico d={I.chefHat} size={18} stroke={LT.verde} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>
                                    {lav.prodotto}
                                  </div>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                                    <Chip kind="neutro" size="sm">Lotto {lav.lotto_prodotto}</Chip>
                                    <Chip kind="blu" size="sm" icon="calendar">{formatData(lav.data_inizio)}</Chip>
                                    <Chip kind={lav.stato === 'CHIUSA' ? 'blu' : lav.stato === 'APERTA' ? 'verde' : 'neutro'} size="sm">
                                      {lav.stato}
                                    </Chip>
                                    {lav.quantita_reale && (
                                      <Chip kind="verde" size="sm">{lav.quantita_reale} {lav.unita_misura}</Chip>
                                    )}
                                    {lav.committente_nome && (
                                      <Chip kind="arancio" size="sm" icon="users">Conto terzi · {lav.committente_nome}</Chip>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Magazzino PF */}
                              {(lav.magazzino_pf || []).length > 0 && (
                                <div style={{ marginTop: 10, paddingLeft: 46 }}>
                                  {(lav.magazzino_pf || []).map((pf, pi) => (
                                    <div key={pi} style={{
                                      display: 'flex', alignItems: 'center', gap: 8,
                                      padding: '6px 10px', marginTop: 4,
                                      background: LT.violaSoft, borderRadius: LT.r.sm,
                                      borderLeft: `3px solid ${LT.viola}`,
                                      fontSize: 12,
                                    }}>
                                      <Ico d={I.package} size={14} stroke={LT.viola} />
                                      <span style={{ fontWeight: 700, color: LT.navy }}>Lotto PF {pf.lotto}</span>
                                      <span style={{ color: LT.n600 }}>· {pf.quantita_totale} {pf.unita_misura}</span>
                                      <span style={{ marginLeft: 'auto', color: LT.n700 }}>
                                        Residuo: <strong>{pf.quantita_residua} {pf.unita_misura}</strong>
                                      </span>
                                      <Chip kind="viola" size="sm">{pf.stato}</Chip>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Bolle uscita */}
                              {(lav.bolle_uscita || []).length > 0 && (
                                <div style={{ marginTop: 8, paddingLeft: 46 }}>
                                  {lav.bolle_uscita.map((b, bi) => (
                                    <div key={bi} style={{
                                      display: 'flex', alignItems: 'center', gap: 8,
                                      padding: '6px 10px', marginTop: 4,
                                      background: LT.arancioSoft, borderRadius: LT.r.sm,
                                      borderLeft: `3px solid ${LT.arancio}`,
                                      fontSize: 12,
                                    }}>
                                      <Ico d={I.truck} size={14} stroke={LT.arancio} />
                                      <strong style={{ color: LT.navy }}>{b.numero_bolla || '(n.d.)'}</strong>
                                      <span style={{ color: LT.n600 }}>· {b.cliente}</span>
                                      <span style={{ color: LT.n500 }}>· {formatData(b.data_bolla)}</span>
                                      <span style={{ marginLeft: 'auto', color: LT.n700, fontWeight: 700 }}>
                                        {b.quantita} {b.unita_misura}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(lav.bolle_uscita || []).length === 0 && (
                                <div style={{ marginTop: 8, paddingLeft: 46, fontSize: 12, color: LT.n400, fontStyle: 'italic' }}>
                                  Nessuna bolla di uscita
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>)}

        {/* ─── TAB BACKWARD ─── */}
        {tab === 'backward' && (<>
          <Card padding="md" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: LT.n600, marginBottom: 12 }}>
              Cerca per <strong>lotto PF</strong> o <strong>nome prodotto</strong> per risalire alle materie prime, fasi e destinazioni.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: LT.n400, pointerEvents: 'none' }}>
                  <Ico d={I.search} size={16} stroke={LT.n400} />
                </div>
                <input style={{ ...TR_INPUT, paddingLeft: 36 }}
                  placeholder="Es. L.190326 oppure Pane"
                  value={inputBwd}
                  onChange={e => setInputBwd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && cercaBackward()}
                  onFocus={e => trFocus(e, LT.viola)} onBlur={trBlur} />
              </div>
              <Btn kind="primary" size="md" icon="search"
                onClick={cercaBackward} disabled={cercando}
                style={{ background: LT.viola, borderColor: LT.viola }}>
                {cercando ? '…' : 'Cerca'}
              </Btn>
            </div>
          </Card>

          {riBwd !== null && riBwd.length === 0 && (
            <Card padding="lg" style={{ textAlign: 'center' }}>
              <div style={{ color: LT.n400, marginBottom: 8 }}>
                <Ico d={I.search} size={40} stroke={LT.n300} />
              </div>
              <div style={{ fontSize: 14, color: LT.n500 }}>Nessuna corrispondenza trovata per <strong>"{inputBwd}"</strong></div>
            </Card>
          )}

          {riBwd && riBwd.length > 0 && (
            <div style={{ fontSize: 12, color: LT.n500, fontWeight: 600, marginBottom: 10, letterSpacing: 0.4 }}>
              {riBwd.length} LOTT{riBwd.length === 1 ? 'O' : 'I'} TROVAT{riBwd.length === 1 ? 'O' : 'I'}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {riBwd && riBwd.map((lav, idx) => {
              const key = `bwd-${idx}`;
              const aperto = !!espanso[key];
              return (
                <Card key={idx} padding="none" style={{ overflow: 'hidden' }}>
                  <div style={{ height: 6, background: LT.viola }} />
                  <div onClick={() => toggleEspanso(key)} style={{
                    padding: 16, cursor: 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: LT.r.md,
                      background: LT.violaSoft, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                    }}>
                      <Ico d={I.package} size={22} stroke={LT.viola} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy }}>{lav.prodotto}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        <Chip kind="viola" size="sm">Lotto {lav.lotto_pf}</Chip>
                        {lav.ricetta && <Chip kind="neutro" size="sm" icon="book">{lav.ricetta}</Chip>}
                        <Chip kind="blu" size="sm" icon="calendar">
                          {formatData(lav.data_inizio)}{lav.data_fine ? ' → ' + formatData(lav.data_fine) : ''}
                        </Chip>
                        {lav.quantita_reale || lav.quantita_prevista ? (
                          <Chip kind="verde" size="sm">
                            {lav.quantita_reale || lav.quantita_prevista} {lav.unita_misura_pf}
                          </Chip>
                        ) : null}
                        {lav.stato_pf && <Chip kind="ciano" size="sm">{lav.stato_pf}</Chip>}
                        {lav.committente_nome && (
                          <Chip kind="arancio" size="sm" icon="users">Conto terzi · {lav.committente_nome}</Chip>
                        )}
                      </div>
                    </div>
                    <Ico d={aperto ? I.chevronD : I.chevronR} size={20} stroke={LT.viola} />
                  </div>

                  {aperto && (
                    <div style={{ borderTop: `1px solid ${LT.n150}`, padding: 16, background: LT.n50 }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                        <Btn kind="secondary" size="sm" icon="download"
                          onClick={() => stampaBackward(lav)}
                          style={{ borderColor: LT.ciano, color: LT.ciano }}>
                          Stampa scheda
                        </Btn>
                      </div>

                      {/* Materie prime */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: LT.r.sm,
                          background: LT.bluSoft, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Ico d={I.package} size={13} stroke={LT.blu} />
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                          MATERIE PRIME UTILIZZATE
                        </div>
                      </div>
                      {(lav.materie_prime || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', marginBottom: 14 }}>
                          Nessuna registrata
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                          {(lav.materie_prime || []).map((mp, i) => (
                            <div key={i} style={{
                              background: '#fff', borderRadius: LT.r.sm,
                              borderLeft: `3px solid ${LT.blu}`,
                              padding: '10px 12px', fontSize: 13,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, color: LT.navy }}>{mp.ingrediente}</span>
                                {mp.categoria && <span style={{ color: LT.n500, fontSize: 12 }}>{mp.categoria}</span>}
                              </div>
                              <div style={{ color: LT.n600, marginTop: 4, fontSize: 12 }}>
                                Lotto: <strong>{mp.lotto_fornitore}</strong>{mp.lotto_interno ? ' / ' + mp.lotto_interno : ''}
                                {' · '}{mp.fornitore}
                                {mp.numero_bolla && <> · BDI {mp.numero_bolla} del {formatData(mp.data_bolla)}</>}
                                {mp.data_scadenza && <> · Scad. {formatData(mp.data_scadenza)}</>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Semilavorati */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: LT.r.sm,
                          background: LT.cianoSoft, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Ico d={I.layers} size={13} stroke={LT.ciano} />
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                          SEMILAVORATI UTILIZZATI
                        </div>
                      </div>
                      {(lav.semilavorati || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', marginBottom: 14 }}>
                          Nessuno registrato
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                          {(lav.semilavorati || []).map((sl, i) => (
                            <div key={i} style={{
                              background: '#fff', borderRadius: LT.r.sm,
                              borderLeft: `3px solid ${LT.ciano}`,
                              padding: '10px 12px', fontSize: 13,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, color: LT.navy }}>{sl.prodotto}</span>
                                <Chip kind="neutro" size="sm">Lotto {sl.lotto}</Chip>
                                {sl.quantita && <Chip kind="ciano" size="sm">{sl.quantita} {sl.unita_misura}</Chip>}
                              </div>
                              {sl.lavorazione_origine && (
                                <div style={{
                                  marginTop: 6, padding: '6px 10px',
                                  background: LT.cianoSoft, borderRadius: LT.r.sm,
                                  fontSize: 11, color: LT.n700,
                                }}>
                                  <div>
                                    <strong>Origine:</strong> {sl.lavorazione_origine.prodotto} · {sl.lavorazione_origine.lotto_prodotto} · {formatData(sl.lavorazione_origine.data_inizio)}
                                  </div>
                                  {(sl.lavorazione_origine.materie_prime || []).length > 0 && (
                                    <div style={{ marginTop: 3, color: LT.n600 }}>
                                      <strong>MP:</strong> {sl.lavorazione_origine.materie_prime.map(mp => `${mp.ingrediente} (${mp.lotto_fornitore || mp.lotto_interno || '—'})`).join(', ')}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Fasi */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: LT.r.sm,
                          background: LT.verdeSoft, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Ico d={I.route} size={13} stroke={LT.verde} />
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                          FASI PRODUTTIVE
                        </div>
                      </div>
                      {(lav.fasi || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic', marginBottom: 14 }}>
                          Nessuna registrata
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                          {(lav.fasi || []).map((f, i) => (
                            <div key={i} style={{
                              background: '#fff', borderRadius: LT.r.sm,
                              borderLeft: `3px solid ${LT.verde}`,
                              padding: '10px 12px', fontSize: 13,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 22, height: 22, borderRadius: '50%',
                                  background: LT.verdeSoft, color: LT.verde,
                                  fontSize: 11, fontWeight: 800,
                                }}>{i + 1}</span>
                                <span style={{ fontWeight: 700, color: LT.navy }}>{f.fase}</span>
                                {f.operatore && <Chip kind="blu" size="sm" icon="user">{f.operatore}</Chip>}
                                {f.stato_avanzamento && (
                                  <Chip kind={f.stato_avanzamento === 'COMPLETA' ? 'verde' : 'arancio'} size="sm">
                                    {f.stato_avanzamento}
                                  </Chip>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: LT.n600, marginTop: 6 }}>
                                {formatDataOra(f.data_inizio)} → {f.data_fine ? formatDataOra(f.data_fine) : 'In corso'}
                              </div>
                              {f.parametri_rilevati && (
                                <div style={{ fontSize: 12, color: LT.n700, marginTop: 4, fontFamily: LT.mono }}>
                                  {formatParametri(f.parametri_rilevati, f.parametri_definiti)}
                                </div>
                              )}
                              {f.note && (
                                <div style={{ fontSize: 12, color: LT.n500, marginTop: 4, fontStyle: 'italic' }}>
                                  {f.note}
                                </div>
                              )}
                              {f.ubicazioni && f.ubicazioni.length > 0 && (
                                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {f.ubicazioni.map((m, ui) => {
                                    const coloriTipo = { ENTRATA: LT.verde, USCITA: LT.blu, SPOSTAMENTO: LT.arancio, SCARTO: LT.rosso };
                                    if (m.tipo) {
                                      return (
                                        <span key={ui} style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4,
                                          padding: '2px 8px', borderRadius: LT.r.full,
                                          background: `${coloriTipo[m.tipo] || LT.n400}18`,
                                          color: coloriTipo[m.tipo] || LT.n600,
                                          fontSize: 11, fontWeight: 600,
                                        }}>
                                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: coloriTipo[m.tipo] || LT.n400 }} />
                                          {m.tipo} {m.nome_ubicazione}
                                          {m.quantita_pz ? ` · ${m.quantita_pz} pz` : ''}{m.peso_kg ? ` / ${m.peso_kg} kg` : ''}
                                          {m.tipo === 'SCARTO' && m.motivazione && <span> — {m.motivazione}</span>}
                                        </span>
                                      );
                                    }
                                    return (
                                      <span key={ui} style={{ fontSize: 11, color: LT.n500 }}>
                                        📍 {m.nome_ubicazione || m.nome}{m.quantita ? ` — ${m.quantita} ${m.unita_misura || ''}` : ''}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Timeline movimenti ubicazioni */}
                      {(lav.fasi || []).some(f => f.ubicazioni && f.ubicazioni.some(m => m.tipo)) && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, marginTop: 14 }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: LT.r.sm,
                              background: LT.bluSoft, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Ico d={I.route} size={13} stroke={LT.blu} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                              MOVIMENTI PRODOTTO PER UBICAZIONE
                            </div>
                          </div>
                          <div style={{
                            background: '#fff', borderRadius: LT.r.sm,
                            borderLeft: `3px solid ${LT.n300}`,
                            padding: '12px 16px 12px 28px', marginBottom: 14,
                          }}>
                            {(lav.fasi || []).flatMap(f =>
                              (f.ubicazioni || []).filter(m => m.tipo).map(m => ({ ...m, fase: f.fase }))
                            ).sort((a, b) => (a.data_movimento || a.created_at || '').localeCompare(b.data_movimento || b.created_at || '')).map((m, i) => {
                              const icone  = { ENTRATA: '⬇', USCITA: '⬆', SPOSTAMENTO: '↔', SCARTO: '✕' };
                              const colori = { ENTRATA: LT.verde, USCITA: LT.blu, SPOSTAMENTO: LT.arancio, SCARTO: LT.rosso };
                              return (
                                <div key={i} style={{ position: 'relative', paddingBottom: 10, fontSize: 12 }}>
                                  <div style={{
                                    position: 'absolute', left: -19, top: 4,
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: colori[m.tipo] || LT.n400,
                                  }} />
                                  <span style={{ color: LT.n500, marginRight: 6, fontFamily: LT.mono, fontSize: 11 }}>
                                    {(m.data_movimento || m.created_at) ? formatDataOra(m.data_movimento || m.created_at) : ''}
                                  </span>
                                  <span style={{ fontWeight: 700, color: colori[m.tipo] || LT.n600 }}>
                                    {icone[m.tipo]} {m.tipo}
                                  </span>
                                  <span style={{ color: LT.n700 }}> → {m.nome_ubicazione}</span>
                                  {m.quantita_pz ? <span style={{ color: LT.n500 }}> · {m.quantita_pz} pz</span> : null}
                                  {m.peso_kg ? <span style={{ color: LT.n500 }}> / {m.peso_kg} kg</span> : null}
                                  {m.tipo === 'SCARTO' && m.motivazione && (
                                    <span style={{ color: LT.rosso }}> — {m.motivazione}</span>
                                  )}
                                  <span style={{ color: LT.n400, marginLeft: 6, fontSize: 11 }}>({m.fase})</span>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {/* Bolle uscita */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: LT.r.sm,
                          background: LT.arancioSoft, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Ico d={I.truck} size={13} stroke={LT.arancio} />
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                          BOLLE DI USCITA
                        </div>
                      </div>
                      {(lav.bolle_uscita || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: LT.n400, fontStyle: 'italic' }}>
                          Nessuna bolla emessa
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(lav.bolle_uscita || []).map((b, i) => (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '10px 12px',
                              background: '#fff', borderRadius: LT.r.sm,
                              borderLeft: `3px solid ${LT.arancio}`,
                              fontSize: 13,
                            }}>
                              <Ico d={I.truck} size={16} stroke={LT.arancio} />
                              <strong style={{ color: LT.navy }}>{b.numero_bolla || '(n.d.)'}</strong>
                              <span style={{ color: LT.n600 }}>· {b.cliente}</span>
                              <span style={{ color: LT.n500 }}>· {formatData(b.data_bolla)}</span>
                              <span style={{ marginLeft: 'auto', color: LT.n700, fontWeight: 700 }}>
                                {b.quantita} {b.unita_misura}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>)}
      </div>
    </div>
  );
}

export default function App() {
  const [operatore,         setOperatore]         = useState(null);
  const [sezione,           setSezione]           = useState('home');
  const [scadInitOggetto,   setScadInitOggetto]   = useState(null); // oggetto preselezionato per nuova voce scadenziario
  const [bolleInitId,       setBolleInitId]       = useState(null); // bolla da aprire in dettaglio arrivando dal registro
  const [bollaPrecompilata, setBollaPrecompilata] = useState(null);
  const [impostazioni, setImpostazioni] = useState({});
  const [azienda,      setAzienda]      = useState(null);

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setOperatore(null);
  };

  // Registra il callback di logout per la gestione 401 fuori da React
  useEffect(() => {
    _onLogout = handleLogout;
    return () => { _onLogout = null; };
  });

  // Cambiare schermata qui non ricarica la pagina: e' la stessa pagina che
  // cambia contenuto. Il browser quindi TIENE la posizione di scorrimento, e
  // una schermata nuova si apre gia' a meta', con l'intestazione fuori campo.
  // Si riporta in cima a mano a ogni cambio di sezione.
  useEffect(() => { window.scrollTo(0, 0); }, [sezione]);

  // Carica impostazioni fornitore una volta sola
  useEffect(() => {
    apiFetch(`${API}/uploads/impostazioni`)
      .then(r => r.json())
      .then(setImpostazioni)
      .catch(() => {});
  }, []);

  // Carica dati azienda quando l'operatore fa login
  useEffect(() => {
    if (operatore && operatore.id_azienda) {
      apiFetch(`${API}/admin/aziende`)
        .then(r => r.json())
        .then(lista => {
          const az = lista.find(a => a.id === operatore.id_azienda);
          if (az) setAzienda(az);
        })
        .catch(() => {});
    }
  }, [operatore]);

  const toastEl   = <Toast />;
  const confirmEl = <ModalConferma />;
  const isAdmin = operatore && (operatore.admin || operatore.master);
  const backDest = isAdmin ? 'dashboard' : 'home';

  if (!operatore) {
    const percorso = window.location.pathname.replace(/^\/+/, '').toLowerCase();
    if (percorso === 'registrati') return (
      <>
        {toastEl}{confirmEl}
        <PaginaRegistrazione />
      </>
    );
    return (
      <>
        {toastEl}{confirmEl}
        <Login onLogin={op => { setSezione(op.admin || op.master ? 'dashboard' : 'home'); setOperatore(op); }} />
        <Footer impostazioni={impostazioni} />
      </>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Routing post-login: tutto avvolto da AppShell (D2 Evoluto).
  // `wrap(content, { bare })` — se bare=true, rende il contenuto senza shell
  // (per schermate che hanno già una propria intestazione interna).
  // ───────────────────────────────────────────────────────────────────────────
  const wrap = (content, opts = {}) => {
    if (opts.bare) {
      return <>{toastEl}{confirmEl}{content}</>;
    }
    return (
      <>
        {toastEl}{confirmEl}
        <AppShell
          operatore={operatore}
          azienda={azienda}
          impostazioni={impostazioni}
          sezione={sezione}
          onSezione={setSezione}
          onLogout={handleLogout}
        >
          {content}
        </AppShell>
      </>
    );
  };

  // Home: operatore base vede HomeOperatore, admin è meglio che veda la Dashboard.
  if (sezione === 'home') {
    if (isAdmin) {
      return wrap(
        <Dashboard operatore={operatore} azienda={azienda} onSezione={setSezione} onLogout={handleLogout} />
      );
    }
    return wrap(
      <HomeOperatore operatore={operatore} azienda={azienda} onSezione={setSezione} />
    );
  }

  // `gestione` non è più un branch autonomo: la sidebar porta direttamente alle
  // sotto-voci. Per compatibilità, se mai si arriva qui si mostra la Dashboard.
  if (sezione === 'gestione') {
    return wrap(
      <Dashboard operatore={operatore} azienda={azienda} onSezione={setSezione} onLogout={handleLogout} />
    );
  }

  if (sezione === 'attivita')
    return wrap(<Attivita operatore={operatore} azienda={azienda} />);

  if (sezione === 'verifiche')
    return wrap(<Verifiche operatore={operatore} azienda={azienda} />);

  if (sezione === 'organico')
    return wrap(<GestioneOrganico operatore={operatore} onBack={() => setSezione('dashboard')} />);

  if (sezione === 'oggetti')
    return wrap(<GestioneOggetti operatore={operatore} onBack={() => setSezione('dashboard')}
      onAssegnaAttivita={(idOgg) => { setScadInitOggetto(idOgg); setSezione('scadenziario'); }} />);

  if (sezione === 'dashboard')
    return wrap(<Dashboard operatore={operatore} azienda={azienda} onSezione={setSezione} onLogout={handleLogout} />);

  if (sezione === 'scadenziario')
    return wrap(<GestioneScadenziario operatore={operatore} onBack={() => setSezione('dashboard')}
      initOggetto={scadInitOggetto} onInitConsumed={() => setScadInitOggetto(null)} />);

  if (sezione === 'ambiti')
    return wrap(<GestioneAmbiti operatore={operatore} onBack={() => setSezione('dashboard')} />);

  if (sezione === 'categorie-nc')
    return wrap(<GestioneCategorieNc operatore={operatore} onBack={() => setSezione('dashboard')} />);

  if (sezione === 'origini-nc')
    return wrap(
      <GestioneOrigineNc
        operatore={operatore}
        onBack={() => setSezione('dashboard')}
        apiBase={API}
        apiFetch={apiFetch}
        toast={toast}
        chiediConferma={chiediConferma}
      />
    );

  if (sezione === 'aziende')
    return wrap(<GestioneAziende onBack={() => setSezione('dashboard')} />);

  if (sezione === 'registro-nc')
    return wrap(<RegistroNC operatore={operatore} onBack={() => setSezione(backDest)} azienda={azienda} impostazioni={impostazioni} />);

  if (sezione === 'correzioni')
    return wrap(<StoricoCorrezioni operatore={operatore} onBack={() => setSezione('dashboard')} />);

  if (sezione === 'storico')
    return wrap(<Storico operatore={operatore} azienda={azienda} impostazioni={impostazioni} onBack={() => setSezione(backDest)} />);

  if (sezione === 'documenti')
    return wrap(<GestioneDocumenti operatore={operatore} onBack={() => setSezione(backDest)} />);

  // mia-azienda e tr-clienti non avevano Header nel routing originale: le
  // lasciamo "bare" per non introdurre doppi header. Saranno ricomposte in
  // Fase 3 quando restyleremo quei componenti.
  if (sezione === 'mia-azienda')
    return wrap(<ProfiloAzienda operatore={operatore} onBack={() => setSezione('dashboard')} />, { bare: true });

  if (sezione === 'impostazioni')
    return wrap(<Impostazioni onBack={() => setSezione('dashboard')} />);

  if (sezione === 'tracciabilita')
    return wrap(<MenuTracciabilita operatore={operatore} onSezione={setSezione} onBack={() => setSezione(backDest)} />);

  if (sezione === 'tr-clienti')
    return wrap(<GestioneClienti operatore={operatore} onBack={() => setSezione('tracciabilita')} />, { bare: true });

  if (sezione === 'tr-fornitori')
    return wrap(<GestioneFornitori operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-ingredienti')
    return wrap(<GestioneIngredienti operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-prodotti')
    return wrap(<GestioneProdotti operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-ubicazioni')
    return wrap(<GestioneUbicazioni operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-giacenze')
    return wrap(<GiacenzeUbicazioni operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-fasi')
    return wrap(<GestioneFasi operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-ricette')
    return wrap(<GestioneRicette operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-bolle-ingresso')
    return wrap(<GestioneBolleIngresso operatore={operatore} onBack={() => setSezione('tracciabilita')}
      initBollaId={bolleInitId} onInitConsumed={() => setBolleInitId(null)} />);

  if (sezione === 'tr-accettazione-modelli')
    return wrap(<GestioneModelliAccettazione operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-ricevimento')
    return wrap(<GestioneRicevimentoMP operatore={operatore} azienda={azienda}
      onBack={() => setSezione('tracciabilita')}
      onApriBolle={() => setSezione('tr-bolle-ingresso')}
      onApriBolla={(id) => { setBolleInitId(id); setSezione('tr-bolle-ingresso'); }} />);

  if (sezione === 'tr-magazzino-mp')
    return wrap(<GestioneMagazzinoMP operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-lavorazioni')
    return wrap(<GestioneLavorazioni operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-magazzino-pf')
    return wrap(
      <GestioneMagazzinoPF
        operatore={operatore}
        onBack={() => setSezione('tracciabilita')}
        onNuovaBolla={pf => { setBollaPrecompilata(pf); setSezione('tr-bolle-uscita'); }}
      />
    );

  if (sezione === 'tr-bolle-uscita')
    return wrap(
      <GestioneBolleUscita
        operatore={operatore}
        onBack={() => { setBollaPrecompilata(null); setSezione('tracciabilita'); }}
        lottoPrecompilato={bollaPrecompilata}
      />
    );

  if (sezione === 'tr-test-tracciabilita')
    return wrap(
      <TestTracciabilita
        operatore={operatore}
        onBack={() => setSezione('tracciabilita')}
        impostazioni={impostazioni}
        azienda={azienda}
      />
    );

  if (sezione === 'tr-bilancio-massa')
    return wrap(<BilancioMassa operatore={operatore} onBack={() => setSezione('tracciabilita')} />);

  if (sezione === 'tr-dashboard')
    return wrap(
      <DashboardTracciabilita
        operatore={operatore}
        onBack={() => setSezione('tracciabilita')}
        onSezione={setSezione}
      />
    );

  if (sezione === 'aiuto')
    return wrap(<CentroAssistenza operatore={operatore} azienda={azienda} />);

  // Fallback finale: sezione sconosciuta → home operatore (o dashboard admin).
  if (isAdmin) {
    return wrap(
      <Dashboard operatore={operatore} azienda={azienda} onSezione={setSezione} onLogout={handleLogout} />
    );
  }
  return wrap(
    <HomeOperatore operatore={operatore} azienda={azienda} onSezione={setSezione} />
  );
}

const styles = {
  container:    { maxWidth: 480, margin: '40px auto', padding: '0 16px', fontFamily: 'sans-serif' },
  titolo:       { textAlign: 'center', color: '#2563EB', marginBottom: 4 },
  sottotitolo:  { textAlign: 'center', color: '#888', marginTop: 0, marginBottom: 32 },
  card:         { background: '#f9f9f9', borderRadius: 12, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 12 },
  label:        { fontWeight: 'bold', color: '#333', display: 'block', marginBottom: 4, marginTop: 8 },
  input:        { padding: '10px 14px', fontSize: 18, borderRadius: 8, border: '1px solid #ccc', textAlign: 'center', letterSpacing: 8 },
  button:       { padding: '12px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  buttonSmall:  { padding: '8px 16px', background: '#eee', border: 'none', borderRadius: 8, cursor: 'pointer' },
  errore:       { color: 'red', margin: 0, fontSize: 14 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  tuttoOk:      { textAlign: 'center', color: '#22C55E', marginTop: 60 },
  attivitaCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  attivitaHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  ambito:       { background: '#eff6ff', color: '#2563EB', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' },
  frequenza:    { color: '#aaa', fontSize: 12 },
  oggetto:      { margin: '4px 0', fontWeight: 'bold', fontSize: 16 },
  dettaglio:    { margin: '2px 0', color: '#666', fontSize: 14 },
  data:         { margin: '4px 0', color: '#888', fontSize: 13 },
  datamai:      { margin: '4px 0', color: '#e07000', fontSize: 13, fontWeight: 'bold' },
  bottoni:      { display: 'flex', gap: 8, marginTop: 12 },
  btnConforme:      { flex: 1, padding: '10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 'bold' },
  btnNonConforme:   { flex: 1, padding: '10px', background: '#c0392b', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 'bold' },
  overlay:      { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:        { background: 'white', borderRadius: 16, padding: 24, width: '90%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  ncOggetto:    { fontWeight: 'bold', margin: '4px 0' },
  ncTipo:       { color: '#666', fontSize: 14, margin: '0 0 16px 0' },
  textarea:     { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' },
  btnAnnulla:   { flex: 1, padding: '10px', background: '#eee', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  limiti:       { margin: '4px 0', color: '#1e40af', fontSize: 13, fontWeight: 'bold' },
  inputText:    { padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', fontSize: 15, width: '100%', boxSizing: 'border-box' },
  tabella:      { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:           { padding: '8px 10px', textAlign: 'left', fontWeight: 'bold', whiteSpace: 'nowrap' },
  td:           { padding: '6px 10px', borderBottom: '1px solid #eee', verticalAlign: 'top' },
  calce:        { marginTop: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 8, borderLeft: '4px solid #2563EB' },
  reportIntestazione: { marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #2563EB' },
  gruppoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#2563EB',
    color: 'white',
    padding: '8px 16px',
    borderRadius: 8,
    marginBottom: 8,
    marginTop: 16,
    fontWeight: 'bold',
    fontSize: 15,
  },
appHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    background: 'white',
    borderBottom: '1px solid #eee',
    marginBottom: 8,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  appHeaderLogo:      { display: 'flex', alignItems: 'center' },
  appHeaderNome:      { fontWeight: 'bold', fontSize: 16, color: '#2563EB' },
  appHeaderFornitore: { display: 'flex', alignItems: 'center' },
  loginBox:     { background: '#f9f9f9', borderRadius: 12, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 12 },
  logo:         { textAlign: 'center', color: '#2563EB', marginBottom: 4 },
  menuButton:   { width: '100%', padding: '14px', background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 12, fontSize: 16, cursor: 'pointer', textAlign: 'left', marginBottom: 8 },

appFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderTop: '1px solid #eee',
    marginTop: 24,
    background: 'white',
  },
};